// src/permissions.ts
var Permission = {
  // ---- Patient records ---------------------------------------------------
  PATIENT_VIEW: "patient.view",
  PATIENT_VIEW_ALL: "patient.view_all",
  PATIENT_CREATE: "patient.create",
  PATIENT_EDIT: "patient.edit",
  PATIENT_DELETE: "patient.delete",
  PATIENT_EXPORT: "patient.export",
  PATIENT_VIEW_SENSITIVE_NOTES: "patient.view_sensitive_notes",
  // ---- Clinical photography ----------------------------------------------
  PHOTO_VIEW: "photo.view",
  PHOTO_UPLOAD: "photo.upload",
  PHOTO_DELETE: "photo.delete",
  PHOTO_EXPORT: "photo.export",
  PHOTO_MARK_MARKETING: "photo.mark_marketing",
  // ---- Consent -----------------------------------------------------------
  CONSENT_VIEW: "consent.view",
  CONSENT_CREATE: "consent.create",
  CONSENT_VOID: "consent.void",
  // ---- Scheduling --------------------------------------------------------
  APPOINTMENT_VIEW: "appointment.view",
  APPOINTMENT_VIEW_ALL: "appointment.view_all",
  APPOINTMENT_CREATE: "appointment.create",
  APPOINTMENT_EDIT: "appointment.edit",
  APPOINTMENT_CANCEL: "appointment.cancel",
  APPOINTMENT_CONFIRM_REQUESTS: "appointment.confirm_requests",
  // ---- Treatment sessions ------------------------------------------------
  SESSION_VIEW: "session.view",
  SESSION_VIEW_ALL: "session.view_all",
  SESSION_CREATE: "session.create",
  SESSION_EDIT: "session.edit",
  SESSION_DELETE: "session.delete",
  // ---- Procedure catalog -------------------------------------------------
  PROCEDURE_VIEW: "procedure.view",
  PROCEDURE_MANAGE: "procedure.manage",
  // ---- Inventory ---------------------------------------------------------
  INVENTORY_VIEW: "inventory.view",
  INVENTORY_VIEW_COSTS: "inventory.view_costs",
  INVENTORY_RECEIVE: "inventory.receive",
  INVENTORY_CONSUME: "inventory.consume",
  INVENTORY_ADJUST: "inventory.adjust",
  INVENTORY_MANAGE_MATERIALS: "inventory.manage_materials",
  // ---- Finance -----------------------------------------------------------
  FINANCE_VIEW_REVENUE: "finance.view_revenue",
  FINANCE_VIEW_PROFIT: "finance.view_profit",
  FINANCE_VIEW_COSTS: "finance.view_costs",
  FINANCE_MANAGE_EXPENSES: "finance.manage_expenses",
  FINANCE_MANAGE_PAYROLL: "finance.manage_payroll",
  FINANCE_MANAGE_REVENUE_SHARE: "finance.manage_revenue_share",
  FINANCE_VIEW_REPORTS: "finance.view_reports",
  FINANCE_EXPORT: "finance.export",
  FINANCE_RECORD_PAYMENT: "finance.record_payment",
  // ---- Packages ----------------------------------------------------------
  PACKAGE_VIEW: "package.view",
  PACKAGE_SELL: "package.sell",
  PACKAGE_MANAGE: "package.manage",
  // ---- CRM ---------------------------------------------------------------
  CRM_VIEW_LEADS: "crm.view_leads",
  CRM_MANAGE_LEADS: "crm.manage_leads",
  CRM_SEND_CAMPAIGNS: "crm.send_campaigns",
  // ---- Loyalty -----------------------------------------------------------
  LOYALTY_VIEW: "loyalty.view",
  LOYALTY_ADJUST: "loyalty.adjust",
  // ---- Staff & roles -----------------------------------------------------
  STAFF_VIEW: "staff.view",
  STAFF_INVITE: "staff.invite",
  STAFF_EDIT: "staff.edit",
  STAFF_MANAGE_ROLES: "staff.manage_roles",
  // ---- Branches («شعبه») --------------------------------------------------
  BRANCH_MANAGE: "branch.manage",
  BRANCH_VIEW_ALL: "branch.view_all",
  // ---- Clinic settings ---------------------------------------------------
  SETTINGS_VIEW: "settings.view",
  SETTINGS_EDIT: "settings.edit",
  SETTINGS_BILLING: "settings.billing",
  SETTINGS_INTEGRATIONS: "settings.integrations",
  // ---- Data import -------------------------------------------------------
  IMPORT_AI: "import.ai",
  // ---- Platform surface --------------------------------------------------
  AUDIT_VIEW: "audit.view",
  NOTIFICATION_MANAGE: "notification.manage",
  DIRECTORY_MANAGE: "directory.manage"
};
var ALL_PERMISSIONS = Object.freeze(
  Object.values(Permission)
);
var PERMISSION_SET = new Set(ALL_PERMISSIONS);
function isPermission(value) {
  return typeof value === "string" && PERMISSION_SET.has(value);
}
function sanitizePermissions(values) {
  if (!values) return [];
  const out = [];
  for (const value of values) {
    if (isPermission(value) && !out.includes(value)) out.push(value);
  }
  return out;
}
var meta = (key, label, description, options = {}) => ({
  key,
  label,
  description,
  sensitive: options.sensitive ?? false,
  requires: options.requires ?? []
});
var PERMISSION_GROUPS = Object.freeze([
  {
    key: "patient",
    label: "\u067E\u0631\u0648\u0646\u062F\u0647 \u0628\u06CC\u0645\u0627\u0631\u0627\u0646",
    description: "\u062F\u0633\u062A\u0631\u0633\u06CC \u0628\u0647 \u0627\u0637\u0644\u0627\u0639\u0627\u062A \u0647\u0648\u06CC\u062A\u06CC\u060C \u0633\u0648\u0627\u0628\u0642 \u062F\u0631\u0645\u0627\u0646\u06CC \u0648 \u06CC\u0627\u062F\u062F\u0627\u0634\u062A\u200C\u0647\u0627\u06CC \u0628\u06CC\u0645\u0627\u0631\u0627\u0646.",
    permissions: [
      meta(
        Permission.PATIENT_VIEW,
        "\u0645\u0634\u0627\u0647\u062F\u0647 \u0628\u06CC\u0645\u0627\u0631\u0627\u0646",
        "\u062F\u06CC\u062F\u0646 \u0641\u0647\u0631\u0633\u062A \u0628\u06CC\u0645\u0627\u0631\u0627\u0646 \u0648 \u0628\u0627\u0632 \u06A9\u0631\u062F\u0646 \u067E\u0631\u0648\u0646\u062F\u0647 \u0627\u0648\u0646\u200C\u0647\u0627."
      ),
      meta(
        Permission.PATIENT_VIEW_ALL,
        "\u0645\u0634\u0627\u0647\u062F\u0647 \u0628\u06CC\u0645\u0627\u0631\u0627\u0646 \u0647\u0645\u0647 \u067E\u0632\u0634\u06A9\u0627\u0646",
        "\u0628\u062F\u0648\u0646 \u0627\u06CC\u0646 \u062F\u0633\u062A\u0631\u0633\u06CC\u060C \u0641\u0642\u0637 \u0628\u06CC\u0645\u0627\u0631\u0627\u0646\u06CC \u062F\u06CC\u062F\u0647 \u0645\u06CC\u200C\u0634\u0646 \u06A9\u0647 \u0627\u06CC\u0646 \u06A9\u0627\u0631\u0628\u0631 \u067E\u0632\u0634\u06A9 \u0645\u0639\u0627\u0644\u062C \u0622\u0646\u200C\u0647\u0627\u0633\u062A.",
        { requires: [Permission.PATIENT_VIEW] }
      ),
      meta(Permission.PATIENT_CREATE, "\u062B\u0628\u062A \u0628\u06CC\u0645\u0627\u0631 \u062C\u062F\u06CC\u062F", "\u0627\u06CC\u062C\u0627\u062F \u067E\u0631\u0648\u0646\u062F\u0647 \u0628\u0631\u0627\u06CC \u0628\u06CC\u0645\u0627\u0631 \u062C\u062F\u06CC\u062F.", {
        requires: [Permission.PATIENT_VIEW]
      }),
      meta(Permission.PATIENT_EDIT, "\u0648\u06CC\u0631\u0627\u06CC\u0634 \u0628\u06CC\u0645\u0627\u0631", "\u0627\u0635\u0644\u0627\u062D \u0645\u0634\u062E\u0635\u0627\u062A\u060C \u0628\u0631\u0686\u0633\u0628\u200C\u0647\u0627 \u0648 \u0633\u0648\u0627\u0628\u0642 \u067E\u0632\u0634\u06A9\u06CC \u0628\u06CC\u0645\u0627\u0631.", {
        requires: [Permission.PATIENT_VIEW]
      }),
      meta(
        Permission.PATIENT_DELETE,
        "\u062D\u0630\u0641 \u0628\u06CC\u0645\u0627\u0631",
        "\u0627\u0646\u062A\u0642\u0627\u0644 \u067E\u0631\u0648\u0646\u062F\u0647 \u0628\u0647 \u0633\u0637\u0644 \u0628\u0627\u0632\u06CC\u0627\u0641\u062A. \u067E\u0631\u0648\u0646\u062F\u0647 \u0628\u0639\u062F \u0627\u0632 \u067E\u0627\u06CC\u0627\u0646 \u0645\u0647\u0644\u062A \u0646\u06AF\u0647\u062F\u0627\u0631\u06CC \u0628\u0631\u0627\u06CC \u0647\u0645\u06CC\u0634\u0647 \u067E\u0627\u06A9 \u0645\u06CC\u200C\u0634\u0647.",
        { sensitive: true, requires: [Permission.PATIENT_VIEW] }
      ),
      meta(
        Permission.PATIENT_EXPORT,
        "\u062E\u0631\u0648\u062C\u06CC \u06AF\u0631\u0641\u062A\u0646 \u0627\u0632 \u0628\u06CC\u0645\u0627\u0631\u0627\u0646",
        "\u062F\u0631\u06CC\u0627\u0641\u062A \u0641\u0627\u06CC\u0644 \u0627\u06A9\u0633\u0644 \u06CC\u0627 \u0628\u0633\u062A\u0647 \u06A9\u0627\u0645\u0644 \u0627\u0637\u0644\u0627\u0639\u0627\u062A \u0628\u06CC\u0645\u0627\u0631. \u0627\u06AF\u0631 \u06A9\u0644\u06CC\u0646\u06CC\u06A9 \u062A\u0623\u06CC\u06CC\u062F \u062F\u0648\u0645\u0631\u062D\u0644\u0647\u200C\u0627\u06CC \u0631\u0648 \u0627\u062C\u0628\u0627\u0631\u06CC \u06A9\u0631\u062F\u0647 \u0628\u0627\u0634\u0647\u060C \u06A9\u062F \u062A\u0623\u06CC\u06CC\u062F \u0647\u0645 \u0644\u0627\u0632\u0645\u0647.",
        { sensitive: true, requires: [Permission.PATIENT_VIEW] }
      ),
      meta(
        Permission.PATIENT_VIEW_SENSITIVE_NOTES,
        "\u0645\u0634\u0627\u0647\u062F\u0647 \u06CC\u0627\u062F\u062F\u0627\u0634\u062A\u200C\u0647\u0627\u06CC \u0645\u062D\u0631\u0645\u0627\u0646\u0647",
        "\u062F\u06CC\u062F\u0646 \u06CC\u0627\u062F\u062F\u0627\u0634\u062A\u200C\u0647\u0627\u06CC\u06CC \u06A9\u0647 \u0641\u0642\u0637 \u0628\u0631\u0627\u06CC \u067E\u0632\u0634\u06A9\u0627\u0646 \u0639\u0644\u0627\u0645\u062A \u062E\u0648\u0631\u062F\u0647\u200C\u0627\u0646\u062F.",
        { sensitive: true, requires: [Permission.PATIENT_VIEW] }
      )
    ]
  },
  {
    key: "photo",
    label: "\u0639\u06A9\u0633\u200C\u0647\u0627\u06CC \u0642\u0628\u0644 \u0648 \u0628\u0639\u062F",
    description: "\u0639\u06A9\u0633\u200C\u0647\u0627\u06CC \u0628\u0627\u0644\u06CC\u0646\u06CC \u0628\u06CC\u0645\u0627\u0631\u0627\u0646. \u0647\u0631 \u0628\u0627\u0631 \u0645\u0634\u0627\u0647\u062F\u0647 \u062F\u0631 \u06AF\u0632\u0627\u0631\u0634 \u062F\u0633\u062A\u0631\u0633\u06CC \u062B\u0628\u062A \u0645\u06CC\u200C\u0634\u0647.",
    permissions: [
      meta(Permission.PHOTO_VIEW, "\u0645\u0634\u0627\u0647\u062F\u0647 \u0639\u06A9\u0633\u200C\u0647\u0627", "\u062F\u06CC\u062F\u0646 \u06AF\u0627\u0644\u0631\u06CC \u0639\u06A9\u0633\u200C\u0647\u0627\u06CC \u0628\u06CC\u0645\u0627\u0631.", {
        sensitive: true,
        requires: [Permission.PATIENT_VIEW]
      }),
      meta(Permission.PHOTO_UPLOAD, "\u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC \u0639\u06A9\u0633", "\u0627\u0641\u0632\u0648\u062F\u0646 \u0639\u06A9\u0633 \u0642\u0628\u0644\u060C \u0628\u0639\u062F \u06CC\u0627 \u062D\u06CC\u0646 \u062F\u0631\u0645\u0627\u0646.", {
        requires: [Permission.PHOTO_VIEW]
      }),
      meta(Permission.PHOTO_DELETE, "\u062D\u0630\u0641 \u0639\u06A9\u0633", "\u062D\u0630\u0641 \u0639\u06A9\u0633 \u0627\u0632 \u067E\u0631\u0648\u0646\u062F\u0647 \u0628\u06CC\u0645\u0627\u0631.", {
        sensitive: true,
        requires: [Permission.PHOTO_VIEW]
      }),
      meta(
        Permission.PHOTO_EXPORT,
        "\u062F\u0627\u0646\u0644\u0648\u062F \u0639\u06A9\u0633\u200C\u0647\u0627",
        "\u062F\u0631\u06CC\u0627\u0641\u062A \u0641\u0627\u06CC\u0644 \u0627\u0635\u0644\u06CC \u0639\u06A9\u0633. \u0646\u0627\u0645 \u062F\u0631\u06CC\u0627\u0641\u062A\u200C\u06A9\u0646\u0646\u062F\u0647 \u0631\u0648\u06CC \u0639\u06A9\u0633 \u062F\u0631\u062C \u0645\u06CC\u200C\u0634\u0647.",
        { sensitive: true, requires: [Permission.PHOTO_VIEW] }
      ),
      meta(
        Permission.PHOTO_MARK_MARKETING,
        "\u062A\u0623\u06CC\u06CC\u062F \u0639\u06A9\u0633 \u0628\u0631\u0627\u06CC \u062A\u0628\u0644\u06CC\u063A\u0627\u062A",
        "\u062A\u0623\u06CC\u06CC\u062F \u0639\u06A9\u0633 \u0628\u0631\u0627\u06CC \u0627\u0633\u062A\u0641\u0627\u062F\u0647 \u062A\u0628\u0644\u06CC\u063A\u0627\u062A\u06CC\u060C \u0641\u0642\u0637 \u0628\u0627 \u0631\u0636\u0627\u06CC\u062A \u06A9\u062A\u0628\u06CC \u0628\u06CC\u0645\u0627\u0631.",
        { sensitive: true, requires: [Permission.PHOTO_VIEW] }
      )
    ]
  },
  {
    key: "consent",
    label: "\u0631\u0636\u0627\u06CC\u062A\u200C\u0646\u0627\u0645\u0647",
    description: "\u0641\u0631\u0645\u200C\u0647\u0627\u06CC \u0631\u0636\u0627\u06CC\u062A \u0622\u06AF\u0627\u0647\u0627\u0646\u0647\u060C \u0627\u0645\u0636\u0627\u06CC \u062F\u06CC\u062C\u06CC\u062A\u0627\u0644 \u0648 \u0646\u0633\u062E\u0647\u200C\u0647\u0627\u06CC \u0627\u0633\u06A9\u0646\u200C\u0634\u062F\u0647.",
    permissions: [
      meta(Permission.CONSENT_VIEW, "\u0645\u0634\u0627\u0647\u062F\u0647 \u0631\u0636\u0627\u06CC\u062A\u200C\u0646\u0627\u0645\u0647\u200C\u0647\u0627", "\u062F\u06CC\u062F\u0646 \u0631\u0636\u0627\u06CC\u062A\u200C\u0646\u0627\u0645\u0647\u200C\u0647\u0627\u06CC \u0627\u0645\u0636\u0627\u0634\u062F\u0647 \u0628\u06CC\u0645\u0627\u0631.", {
        requires: [Permission.PATIENT_VIEW]
      }),
      meta(
        Permission.CONSENT_CREATE,
        "\u06AF\u0631\u0641\u062A\u0646 \u0631\u0636\u0627\u06CC\u062A\u200C\u0646\u0627\u0645\u0647",
        "\u0627\u0631\u0633\u0627\u0644 \u0641\u0631\u0645 \u0628\u0631\u0627\u06CC \u0627\u0645\u0636\u0627\u06CC \u0628\u06CC\u0645\u0627\u0631 \u06CC\u0627 \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC \u0646\u0633\u062E\u0647 \u0627\u0633\u06A9\u0646\u200C\u0634\u062F\u0647.",
        { requires: [Permission.CONSENT_VIEW] }
      ),
      meta(
        Permission.CONSENT_VOID,
        "\u0627\u0628\u0637\u0627\u0644 \u0631\u0636\u0627\u06CC\u062A\u200C\u0646\u0627\u0645\u0647",
        "\u0628\u0627\u0637\u0644 \u06A9\u0631\u062F\u0646 \u06CC\u06A9 \u0631\u0636\u0627\u06CC\u062A\u200C\u0646\u0627\u0645\u0647 \u0627\u0645\u0636\u0627\u0634\u062F\u0647. \u0645\u062A\u0646 \u0648 \u0627\u0645\u0636\u0627\u06CC \u0627\u0635\u0644\u06CC \u0628\u0631\u0627\u06CC \u0647\u0645\u06CC\u0634\u0647 \u0628\u0627\u06CC\u06AF\u0627\u0646\u06CC \u0645\u06CC\u200C\u0645\u0648\u0646\u0647.",
        { sensitive: true, requires: [Permission.CONSENT_VIEW] }
      )
    ]
  },
  {
    key: "appointment",
    label: "\u0646\u0648\u0628\u062A\u200C\u062F\u0647\u06CC",
    description: "\u062A\u0642\u0648\u06CC\u0645 \u06A9\u0644\u06CC\u0646\u06CC\u06A9\u060C \u0631\u0632\u0631\u0648\u060C \u062C\u0627\u0628\u0647\u200C\u062C\u0627\u06CC\u06CC \u0648 \u0644\u063A\u0648 \u0646\u0648\u0628\u062A.",
    permissions: [
      meta(Permission.APPOINTMENT_VIEW, "\u0645\u0634\u0627\u0647\u062F\u0647 \u0646\u0648\u0628\u062A\u200C\u0647\u0627", "\u062F\u06CC\u062F\u0646 \u062A\u0642\u0648\u06CC\u0645 \u0646\u0648\u0628\u062A\u200C\u0647\u0627."),
      meta(
        Permission.APPOINTMENT_VIEW_ALL,
        "\u0645\u0634\u0627\u0647\u062F\u0647 \u0646\u0648\u0628\u062A \u0647\u0645\u0647 \u067E\u0632\u0634\u06A9\u0627\u0646",
        "\u0628\u062F\u0648\u0646 \u0627\u06CC\u0646 \u062F\u0633\u062A\u0631\u0633\u06CC\u060C \u0641\u0642\u0637 \u0646\u0648\u0628\u062A\u200C\u0647\u0627\u06CC \u062E\u0648\u062F\u0650 \u06A9\u0627\u0631\u0628\u0631 \u062F\u0631 \u062A\u0642\u0648\u06CC\u0645 \u062F\u06CC\u062F\u0647 \u0645\u06CC\u200C\u0634\u0647.",
        { requires: [Permission.APPOINTMENT_VIEW] }
      ),
      meta(Permission.APPOINTMENT_CREATE, "\u062B\u0628\u062A \u0646\u0648\u0628\u062A", "\u0631\u0632\u0631\u0648 \u0646\u0648\u0628\u062A \u062C\u062F\u06CC\u062F \u0628\u0631\u0627\u06CC \u0628\u06CC\u0645\u0627\u0631.", {
        requires: [Permission.APPOINTMENT_VIEW]
      }),
      meta(
        Permission.APPOINTMENT_EDIT,
        "\u0648\u06CC\u0631\u0627\u06CC\u0634 \u0648 \u062C\u0627\u0628\u0647\u200C\u062C\u0627\u06CC\u06CC \u0646\u0648\u0628\u062A",
        "\u062A\u063A\u06CC\u06CC\u0631 \u0633\u0627\u0639\u062A\u060C \u067E\u0632\u0634\u06A9\u060C \u0627\u062A\u0627\u0642 \u06CC\u0627 \u062E\u062F\u0645\u062A \u06CC\u06A9 \u0646\u0648\u0628\u062A \u062B\u0628\u062A\u200C\u0634\u062F\u0647.",
        { requires: [Permission.APPOINTMENT_VIEW] }
      ),
      meta(Permission.APPOINTMENT_CANCEL, "\u0644\u063A\u0648 \u0646\u0648\u0628\u062A", "\u0644\u063A\u0648 \u0646\u0648\u0628\u062A \u0648 \u062B\u0628\u062A \u062F\u0644\u06CC\u0644 \u0622\u0646.", {
        requires: [Permission.APPOINTMENT_VIEW]
      }),
      meta(
        Permission.APPOINTMENT_CONFIRM_REQUESTS,
        "\u062A\u0623\u06CC\u06CC\u062F \u062F\u0631\u062E\u0648\u0627\u0633\u062A\u200C\u0647\u0627\u06CC \u0646\u0648\u0628\u062A \u0622\u0646\u0644\u0627\u06CC\u0646",
        "\u0628\u0631\u0631\u0633\u06CC \u0648 \u062A\u0623\u06CC\u06CC\u062F \u0646\u0648\u0628\u062A\u200C\u0647\u0627\u06CC\u06CC \u06A9\u0647 \u0627\u0632 \u0633\u0627\u06CC\u062A \u06CC\u0627 \u0627\u067E \u0645\u0631\u0627\u062C\u0639\u0627\u0646 \u062F\u0631\u062E\u0648\u0627\u0633\u062A \u0634\u062F\u0647\u200C\u0627\u0646\u062F.",
        { requires: [Permission.APPOINTMENT_VIEW] }
      )
    ]
  },
  {
    key: "session",
    label: "\u062C\u0644\u0633\u0627\u062A \u062F\u0631\u0645\u0627\u0646",
    description: "\u062B\u0628\u062A \u0627\u0642\u062F\u0627\u0645 \u0627\u0646\u062C\u0627\u0645\u200C\u0634\u062F\u0647\u060C \u0646\u0642\u0634\u0647 \u062A\u0632\u0631\u06CC\u0642 \u0648 \u0645\u0628\u0644\u063A \u062F\u0631\u06CC\u0627\u0641\u062A\u06CC.",
    permissions: [
      meta(Permission.SESSION_VIEW, "\u0645\u0634\u0627\u0647\u062F\u0647 \u062C\u0644\u0633\u0627\u062A", "\u062F\u06CC\u062F\u0646 \u0633\u0648\u0627\u0628\u0642 \u062C\u0644\u0633\u0627\u062A \u062F\u0631\u0645\u0627\u0646 \u0628\u06CC\u0645\u0627\u0631.", {
        requires: [Permission.PATIENT_VIEW]
      }),
      meta(
        Permission.SESSION_VIEW_ALL,
        "\u0645\u0634\u0627\u0647\u062F\u0647 \u062C\u0644\u0633\u0627\u062A \u0647\u0645\u0647 \u067E\u0632\u0634\u06A9\u0627\u0646",
        "\u0628\u062F\u0648\u0646 \u0627\u06CC\u0646 \u062F\u0633\u062A\u0631\u0633\u06CC\u060C \u0641\u0642\u0637 \u062C\u0644\u0633\u0627\u062A\u06CC \u062F\u06CC\u062F\u0647 \u0645\u06CC\u200C\u0634\u0647 \u06A9\u0647 \u0627\u06CC\u0646 \u06A9\u0627\u0631\u0628\u0631 \u0627\u0646\u062C\u0627\u0645 \u062F\u0627\u062F\u0647 \u0627\u0633\u062A.",
        { requires: [Permission.SESSION_VIEW] }
      ),
      meta(
        Permission.SESSION_CREATE,
        "\u062B\u0628\u062A \u062C\u0644\u0633\u0647 \u062F\u0631\u0645\u0627\u0646",
        "\u062B\u0628\u062A \u0627\u0642\u062F\u0627\u0645 \u0627\u0646\u062C\u0627\u0645\u200C\u0634\u062F\u0647 \u0647\u0645\u0631\u0627\u0647 \u0628\u0627 \u0646\u0642\u0634\u0647 \u062A\u0632\u0631\u06CC\u0642 \u0648 \u0645\u0648\u0627\u062F \u0645\u0635\u0631\u0641\u06CC.",
        { requires: [Permission.SESSION_VIEW] }
      ),
      meta(Permission.SESSION_EDIT, "\u0648\u06CC\u0631\u0627\u06CC\u0634 \u062C\u0644\u0633\u0647", "\u0627\u0635\u0644\u0627\u062D \u062C\u0632\u0626\u06CC\u0627\u062A \u06CC\u0627 \u0645\u0628\u0644\u063A \u06CC\u06A9 \u062C\u0644\u0633\u0647 \u062B\u0628\u062A\u200C\u0634\u062F\u0647.", {
        requires: [Permission.SESSION_VIEW]
      }),
      meta(
        Permission.SESSION_DELETE,
        "\u062D\u0630\u0641 \u062C\u0644\u0633\u0647",
        "\u062D\u0630\u0641 \u062C\u0644\u0633\u0647 \u062F\u0631\u0645\u0627\u0646. \u0645\u0635\u0631\u0641 \u0645\u0648\u0627\u062F \u0648 \u0627\u062B\u0631 \u0645\u0627\u0644\u06CC \u0622\u0646 \u0646\u06CC\u0632 \u0628\u0631\u06AF\u0631\u062F\u0627\u0646\u062F\u0647 \u0645\u06CC\u200C\u0634\u0647.",
        { sensitive: true, requires: [Permission.SESSION_VIEW] }
      )
    ]
  },
  {
    key: "procedure",
    label: "\u062E\u062F\u0645\u0627\u062A \u06A9\u0644\u06CC\u0646\u06CC\u06A9",
    description: "\u0641\u0647\u0631\u0633\u062A \u062E\u062F\u0645\u0627\u062A\u060C \u0645\u062F\u062A\u200C\u0632\u0645\u0627\u0646 \u0648 \u062A\u0639\u0631\u0641\u0647\u200C\u0647\u0627.",
    permissions: [
      meta(Permission.PROCEDURE_VIEW, "\u0645\u0634\u0627\u0647\u062F\u0647 \u062E\u062F\u0645\u0627\u062A", "\u062F\u06CC\u062F\u0646 \u0641\u0647\u0631\u0633\u062A \u062E\u062F\u0645\u0627\u062A \u0648 \u062A\u0639\u0631\u0641\u0647\u200C\u0647\u0627\u06CC \u06A9\u0644\u06CC\u0646\u06CC\u06A9."),
      meta(
        Permission.PROCEDURE_MANAGE,
        "\u0645\u062F\u06CC\u0631\u06CC\u062A \u062E\u062F\u0645\u0627\u062A \u0648 \u062A\u0639\u0631\u0641\u0647",
        "\u0641\u0639\u0627\u0644\u200C\u0633\u0627\u0632\u06CC \u062E\u062F\u0645\u062A\u060C \u062A\u0639\u06CC\u06CC\u0646 \u0642\u06CC\u0645\u062A\u060C \u0645\u062F\u062A\u200C\u0632\u0645\u0627\u0646 \u0648 \u0642\u0648\u0627\u0639\u062F \u06CC\u0627\u062F\u0622\u0648\u0631\u06CC.",
        { requires: [Permission.PROCEDURE_VIEW] }
      )
    ]
  },
  {
    key: "inventory",
    label: "\u0627\u0646\u0628\u0627\u0631 \u0648 \u0645\u0648\u0627\u062F \u0645\u0635\u0631\u0641\u06CC",
    description: "\u0633\u0631\u06CC\u200C\u0647\u0627\u06CC \u0633\u0627\u062E\u062A\u060C \u062A\u0627\u0631\u06CC\u062E \u0627\u0646\u0642\u0636\u0627\u060C \u0645\u0635\u0631\u0641 \u062F\u0631 \u062C\u0644\u0633\u0647 \u0648 \u0628\u0647\u0627\u06CC \u062A\u0645\u0627\u0645\u200C\u0634\u062F\u0647.",
    permissions: [
      meta(Permission.INVENTORY_VIEW, "\u0645\u0634\u0627\u0647\u062F\u0647 \u0645\u0648\u062C\u0648\u062F\u06CC", "\u062F\u06CC\u062F\u0646 \u0645\u0648\u062C\u0648\u062F\u06CC\u060C \u0633\u0631\u06CC\u200C\u0647\u0627\u06CC \u0633\u0627\u062E\u062A \u0648 \u062A\u0627\u0631\u06CC\u062E \u0627\u0646\u0642\u0636\u0627."),
      meta(
        Permission.INVENTORY_VIEW_COSTS,
        "\u0645\u0634\u0627\u0647\u062F\u0647 \u0642\u06CC\u0645\u062A \u062E\u0631\u06CC\u062F",
        "\u062F\u06CC\u062F\u0646 \u0642\u06CC\u0645\u062A \u062E\u0631\u06CC\u062F \u0647\u0631 \u0633\u0631\u06CC \u0633\u0627\u062E\u062A \u0648 \u0628\u0647\u0627\u06CC \u062A\u0645\u0627\u0645\u200C\u0634\u062F\u0647 \u0647\u0631 \u0648\u0627\u062D\u062F.",
        { sensitive: true, requires: [Permission.INVENTORY_VIEW] }
      ),
      meta(
        Permission.INVENTORY_RECEIVE,
        "\u062B\u0628\u062A \u0648\u0631\u0648\u062F \u06A9\u0627\u0644\u0627",
        "\u062B\u0628\u062A \u062E\u0631\u06CC\u062F \u0648 \u0633\u0631\u06CC \u0633\u0627\u062E\u062A \u062C\u062F\u06CC\u062F \u0628\u0627 \u062A\u0627\u0631\u06CC\u062E \u0627\u0646\u0642\u0636\u0627.",
        {
          requires: [Permission.INVENTORY_VIEW]
        }
      ),
      meta(
        Permission.INVENTORY_CONSUME,
        "\u062B\u0628\u062A \u0645\u0635\u0631\u0641",
        "\u062B\u0628\u062A \u0645\u0648\u0627\u062F \u0645\u0635\u0631\u0641\u200C\u0634\u062F\u0647 \u0627\u0632 \u0647\u0631 \u0633\u0631\u06CC \u0633\u0627\u062E\u062A \u062F\u0631 \u062C\u0644\u0633\u0647 \u062F\u0631\u0645\u0627\u0646\u060C \u0628\u0627 \u067E\u06CC\u0634\u0646\u0647\u0627\u062F \u0633\u0631\u06CC \u062F\u0627\u0631\u0627\u06CC \u0646\u0632\u062F\u06CC\u06A9\u200C\u062A\u0631\u06CC\u0646 \u062A\u0627\u0631\u06CC\u062E \u0627\u0646\u0642\u0636\u0627.",
        { requires: [Permission.INVENTORY_VIEW] }
      ),
      meta(
        Permission.INVENTORY_ADJUST,
        "\u0627\u0635\u0644\u0627\u062D \u0645\u0648\u062C\u0648\u062F\u06CC \u0648 \u0636\u0627\u06CC\u0639\u0627\u062A",
        "\u0627\u0635\u0644\u0627\u062D \u062F\u0633\u062A\u06CC \u0645\u0648\u062C\u0648\u062F\u06CC\u060C \u062B\u0628\u062A \u0636\u0627\u06CC\u0639\u0627\u062A \u0648 \u062E\u0631\u0648\u062C \u0627\u0642\u0644\u0627\u0645 \u0645\u0646\u0642\u0636\u06CC.",
        { sensitive: true, requires: [Permission.INVENTORY_VIEW] }
      ),
      meta(
        Permission.INVENTORY_MANAGE_MATERIALS,
        "\u0645\u062F\u06CC\u0631\u06CC\u062A \u06A9\u0627\u0644\u0627\u0647\u0627",
        "\u062A\u0639\u0631\u06CC\u0641 \u06A9\u0627\u0644\u0627\u060C \u0628\u0631\u0646\u062F\u060C \u0648\u0627\u062D\u062F \u0634\u0645\u0627\u0631\u0634 \u0648 \u062D\u062F \u0633\u0641\u0627\u0631\u0634.",
        { requires: [Permission.INVENTORY_VIEW] }
      )
    ]
  },
  {
    key: "finance",
    label: "\u0645\u0627\u0644\u06CC \u0648 \u062D\u0633\u0627\u0628\u062F\u0627\u0631\u06CC",
    description: "\u062F\u0631\u0622\u0645\u062F\u060C \u0647\u0632\u06CC\u0646\u0647\u060C \u0633\u0648\u062F \u0648 \u0632\u06CC\u0627\u0646\u060C \u062D\u0642\u0648\u0642 \u0648 \u0633\u0647\u0645 \u067E\u0632\u0634\u06A9\u0627\u0646.",
    permissions: [
      meta(Permission.FINANCE_VIEW_REVENUE, "\u0645\u0634\u0627\u0647\u062F\u0647 \u062F\u0631\u0622\u0645\u062F", "\u062F\u06CC\u062F\u0646 \u062F\u0631\u0622\u0645\u062F \u062C\u0644\u0633\u0627\u062A \u0648 \u0628\u0633\u062A\u0647\u200C\u0647\u0627\u06CC \u062E\u062F\u0645\u0627\u062A.", {
        sensitive: true
      }),
      meta(
        Permission.FINANCE_VIEW_PROFIT,
        "\u0645\u0634\u0627\u0647\u062F\u0647 \u0633\u0648\u062F \u0648 \u0632\u06CC\u0627\u0646",
        "\u062F\u06CC\u062F\u0646 \u0633\u0648\u062F \u062E\u0627\u0644\u0635 \u0628\u0639\u062F \u0627\u0632 \u06A9\u0633\u0631 \u0628\u0647\u0627\u06CC \u0645\u0648\u0627\u062F\u060C \u0647\u0632\u06CC\u0646\u0647\u200C\u0647\u0627 \u0648 \u062D\u0642\u0648\u0642.",
        { sensitive: true, requires: [Permission.FINANCE_VIEW_REVENUE] }
      ),
      meta(
        Permission.FINANCE_VIEW_COSTS,
        "\u0645\u0634\u0627\u0647\u062F\u0647 \u0647\u0632\u06CC\u0646\u0647\u200C\u0647\u0627",
        "\u062F\u06CC\u062F\u0646 \u0647\u0632\u06CC\u0646\u0647\u200C\u0647\u0627\u06CC \u062B\u0628\u062A\u200C\u0634\u062F\u0647 \u0648 \u0628\u0647\u0627\u06CC \u062A\u0645\u0627\u0645\u200C\u0634\u062F\u0647 \u0647\u0631 \u062C\u0644\u0633\u0647.",
        { sensitive: true }
      ),
      meta(Permission.FINANCE_MANAGE_EXPENSES, "\u062B\u0628\u062A \u0647\u0632\u06CC\u0646\u0647", "\u062B\u0628\u062A\u060C \u0648\u06CC\u0631\u0627\u06CC\u0634 \u0648 \u062D\u0630\u0641 \u0647\u0632\u06CC\u0646\u0647\u200C\u0647\u0627\u06CC \u06A9\u0644\u06CC\u0646\u06CC\u06A9.", {
        requires: [Permission.FINANCE_VIEW_COSTS]
      }),
      meta(
        Permission.FINANCE_MANAGE_PAYROLL,
        "\u0645\u062F\u06CC\u0631\u06CC\u062A \u062D\u0642\u0648\u0642 \u0648 \u062F\u0633\u062A\u0645\u0632\u062F",
        "\u0645\u062D\u0627\u0633\u0628\u0647 \u0648 \u062B\u0628\u062A \u0641\u06CC\u0634 \u062D\u0642\u0648\u0642\u06CC \u06A9\u0627\u0631\u06A9\u0646\u0627\u0646 \u0648 \u067E\u0648\u0631\u0633\u0627\u0646\u062A \u067E\u0632\u0634\u06A9\u0627\u0646.",
        { sensitive: true, requires: [Permission.FINANCE_VIEW_COSTS] }
      ),
      meta(
        Permission.FINANCE_MANAGE_REVENUE_SHARE,
        "\u062A\u0646\u0638\u06CC\u0645 \u0633\u0647\u0645 \u067E\u0632\u0634\u06A9\u0627\u0646",
        "\u062A\u0639\u06CC\u06CC\u0646 \u062F\u0631\u0635\u062F \u06CC\u0627 \u0645\u0628\u0644\u063A \u062B\u0627\u0628\u062A \u0633\u0647\u0645 \u0647\u0631 \u067E\u0632\u0634\u06A9 \u0627\u0632 \u0647\u0631 \u062F\u0633\u062A\u0647 \u062E\u062F\u0645\u062A.",
        { sensitive: true, requires: [Permission.FINANCE_VIEW_REVENUE] }
      ),
      meta(
        Permission.FINANCE_VIEW_REPORTS,
        "\u0645\u0634\u0627\u0647\u062F\u0647 \u06AF\u0632\u0627\u0631\u0634\u200C\u0647\u0627\u06CC \u0645\u0627\u0644\u06CC",
        "\u06AF\u0632\u0627\u0631\u0634\u200C\u0647\u0627\u06CC \u062F\u0648\u0631\u0647\u200C\u0627\u06CC \u062F\u0631\u0622\u0645\u062F\u060C \u0633\u0648\u062F \u0648 \u0639\u0645\u0644\u06A9\u0631\u062F \u067E\u0632\u0634\u06A9\u0627\u0646.",
        { requires: [Permission.FINANCE_VIEW_REVENUE] }
      ),
      meta(
        Permission.FINANCE_EXPORT,
        "\u062E\u0631\u0648\u062C\u06CC \u06AF\u0632\u0627\u0631\u0634 \u0645\u0627\u0644\u06CC",
        "\u062F\u0631\u06CC\u0627\u0641\u062A \u0641\u0627\u06CC\u0644 \u0627\u06A9\u0633\u0644 \u06AF\u0632\u0627\u0631\u0634\u200C\u0647\u0627. \u0627\u06AF\u0631 \u06A9\u0644\u06CC\u0646\u06CC\u06A9 \u062A\u0623\u06CC\u06CC\u062F \u062F\u0648\u0645\u0631\u062D\u0644\u0647\u200C\u0627\u06CC \u0631\u0648 \u0627\u062C\u0628\u0627\u0631\u06CC \u06A9\u0631\u062F\u0647 \u0628\u0627\u0634\u0647\u060C \u06A9\u062F \u062A\u0623\u06CC\u06CC\u062F \u0647\u0645 \u0644\u0627\u0632\u0645\u0647.",
        { sensitive: true, requires: [Permission.FINANCE_VIEW_REPORTS] }
      ),
      // The front desk takes money without seeing the books: no `requires`,
      // so a receptionist gets the till and the receipt form, not the ledger.
      meta(
        Permission.FINANCE_RECORD_PAYMENT,
        "\u062B\u0628\u062A \u062F\u0631\u06CC\u0627\u0641\u062A \u0627\u0632 \u0628\u06CC\u0645\u0627\u0631",
        "\u062B\u0628\u062A \u067E\u0648\u0644\u06CC \u06A9\u0647 \u0628\u06CC\u0645\u0627\u0631 \u067E\u0631\u062F\u0627\u062E\u062A \u06A9\u0631\u062F\u060C \u0628\u0631\u06AF\u0634\u062A \u0648\u062C\u0647\u060C \u0648\u0635\u0644 \u06A9\u0631\u062F\u0646 \u062F\u0631\u06CC\u0627\u0641\u062A \u0628\u0647 \u062C\u0644\u0633\u0647 \u06CC\u0627 \u0628\u0633\u062A\u0647 \u0648 \u0628\u0633\u062A\u0646 \u0635\u0646\u062F\u0648\u0642."
      )
    ]
  },
  {
    key: "package",
    label: "\u0628\u0633\u062A\u0647\u200C\u0647\u0627\u06CC \u062E\u062F\u0645\u0627\u062A",
    description: "\u0628\u0633\u062A\u0647\u200C\u0647\u0627\u06CC \u062E\u062F\u0645\u0627\u062A \u0686\u0646\u062F\u062C\u0644\u0633\u0647\u200C\u0627\u06CC \u0648 \u0641\u0631\u0648\u0634 \u0627\u0648\u0646\u200C\u0647\u0627 \u0628\u0647 \u0628\u06CC\u0645\u0627\u0631.",
    permissions: [
      meta(
        Permission.PACKAGE_VIEW,
        "\u0645\u0634\u0627\u0647\u062F\u0647 \u0628\u0633\u062A\u0647\u200C\u0647\u0627\u06CC \u062E\u062F\u0645\u0627\u062A",
        "\u062F\u06CC\u062F\u0646 \u0628\u0633\u062A\u0647\u200C\u0647\u0627\u06CC \u062E\u062F\u0645\u0627\u062A \u0648 \u062C\u0644\u0633\u0627\u062A \u0628\u0627\u0642\u06CC\u200C\u0645\u0627\u0646\u062F\u0647 \u0628\u06CC\u0645\u0627\u0631\u0627\u0646."
      ),
      meta(Permission.PACKAGE_SELL, "\u0641\u0631\u0648\u0634 \u0628\u0633\u062A\u0647 \u062E\u062F\u0645\u0627\u062A", "\u062B\u0628\u062A \u062E\u0631\u06CC\u062F \u0628\u0633\u062A\u0647 \u062E\u062F\u0645\u0627\u062A \u0628\u0631\u0627\u06CC \u0628\u06CC\u0645\u0627\u0631.", {
        requires: [Permission.PACKAGE_VIEW]
      }),
      meta(
        Permission.PACKAGE_MANAGE,
        "\u0645\u062F\u06CC\u0631\u06CC\u062A \u0628\u0633\u062A\u0647\u200C\u0647\u0627\u06CC \u062E\u062F\u0645\u0627\u062A",
        "\u062A\u0639\u0631\u06CC\u0641 \u0628\u0633\u062A\u0647 \u062E\u062F\u0645\u0627\u062A\u060C \u062A\u0639\u062F\u0627\u062F \u062C\u0644\u0633\u0627\u062A\u060C \u0642\u06CC\u0645\u062A \u0648 \u0645\u062F\u062A \u0627\u0639\u062A\u0628\u0627\u0631.",
        { requires: [Permission.PACKAGE_VIEW] }
      )
    ]
  },
  {
    key: "crm",
    label: "\u0645\u0634\u062A\u0631\u06CC\u200C\u06CC\u0627\u0628\u06CC \u0648 \u067E\u06CC\u0627\u0645\u06A9 \u06AF\u0631\u0648\u0647\u06CC",
    description: "\u062F\u0631\u062E\u0648\u0627\u0633\u062A\u200C\u0647\u0627\u06CC \u0645\u0634\u0627\u0648\u0631\u0647\u200C\u0627\u06CC \u06A9\u0647 \u0647\u0646\u0648\u0632 \u0628\u0647 \u067E\u0631\u0648\u0646\u062F\u0647 \u0628\u06CC\u0645\u0627\u0631 \u0645\u062A\u0635\u0644 \u0646\u0634\u062F\u0647\u200C\u0627\u0646\u062F\u060C \u067E\u06CC\u06AF\u06CC\u0631\u06CC \u062A\u0645\u0627\u0633\u200C\u0647\u0627 \u0648 \u067E\u06CC\u0627\u0645\u06A9 \u06AF\u0631\u0648\u0647\u06CC.",
    permissions: [
      meta(
        Permission.CRM_VIEW_LEADS,
        "\u0645\u0634\u0627\u0647\u062F\u0647 \u062F\u0631\u062E\u0648\u0627\u0633\u062A\u200C\u0647\u0627\u06CC \u0645\u0634\u0627\u0648\u0631\u0647",
        "\u062F\u06CC\u062F\u0646 \u062A\u0645\u0627\u0633\u200C\u0647\u0627 \u0648 \u062F\u0631\u062E\u0648\u0627\u0633\u062A\u200C\u0647\u0627\u06CC \u0645\u0634\u0627\u0648\u0631\u0647."
      ),
      meta(
        Permission.CRM_MANAGE_LEADS,
        "\u067E\u06CC\u06AF\u06CC\u0631\u06CC \u062F\u0631\u062E\u0648\u0627\u0633\u062A\u200C\u0647\u0627\u06CC \u0645\u0634\u0627\u0648\u0631\u0647",
        "\u062B\u0628\u062A \u062A\u0645\u0627\u0633\u060C \u062A\u063A\u06CC\u06CC\u0631 \u0648\u0636\u0639\u06CC\u062A \u0648 \u0633\u0627\u062E\u062A\u0646 \u067E\u0631\u0648\u0646\u062F\u0647 \u0628\u06CC\u0645\u0627\u0631 \u0627\u0632 \u062F\u0631\u062E\u0648\u0627\u0633\u062A.",
        { requires: [Permission.CRM_VIEW_LEADS] }
      ),
      meta(
        Permission.CRM_SEND_CAMPAIGNS,
        "\u0627\u0631\u0633\u0627\u0644 \u067E\u06CC\u0627\u0645\u06A9 \u06AF\u0631\u0648\u0647\u06CC",
        "\u0627\u0631\u0633\u0627\u0644 \u067E\u06CC\u0627\u0645\u06A9 \u06AF\u0631\u0648\u0647\u06CC \u0627\u0632 \u0627\u0639\u062A\u0628\u0627\u0631 \u06A9\u0644\u06CC\u0646\u06CC\u06A9\u060C \u0628\u0627 \u0631\u0639\u0627\u06CC\u062A \u0633\u0627\u0639\u0627\u062A \u0645\u062C\u0627\u0632 \u0648 \u0627\u0646\u0635\u0631\u0627\u0641 \u0628\u06CC\u0645\u0627\u0631\u0627\u0646.",
        { sensitive: true }
      )
    ]
  },
  {
    key: "loyalty",
    label: "\u0628\u0627\u0634\u06AF\u0627\u0647 \u0645\u0634\u062A\u0631\u06CC\u0627\u0646",
    description: "\u0627\u0645\u062A\u06CC\u0627\u0632\u060C \u0627\u0639\u062A\u0628\u0627\u0631 \u0647\u062F\u06CC\u0647 \u0648 \u0645\u0639\u0631\u0641\u06CC \u0628\u06CC\u0645\u0627\u0631 \u062C\u062F\u06CC\u062F.",
    permissions: [
      meta(Permission.LOYALTY_VIEW, "\u0645\u0634\u0627\u0647\u062F\u0647 \u0627\u0645\u062A\u06CC\u0627\u0632\u0647\u0627", "\u062F\u06CC\u062F\u0646 \u0627\u0645\u062A\u06CC\u0627\u0632 \u0648 \u0627\u0639\u062A\u0628\u0627\u0631 \u0647\u0631 \u0628\u06CC\u0645\u0627\u0631."),
      meta(
        Permission.LOYALTY_ADJUST,
        "\u062A\u063A\u06CC\u06CC\u0631 \u0627\u0645\u062A\u06CC\u0627\u0632 \u0648 \u0627\u0639\u062A\u0628\u0627\u0631",
        "\u0627\u0641\u0632\u0648\u062F\u0646 \u06CC\u0627 \u06A9\u0633\u0631 \u062F\u0633\u062A\u06CC \u0627\u0645\u062A\u06CC\u0627\u0632 \u0648 \u0627\u0639\u062A\u0628\u0627\u0631 \u0628\u06CC\u0645\u0627\u0631.",
        { sensitive: true, requires: [Permission.LOYALTY_VIEW] }
      )
    ]
  },
  {
    key: "staff",
    label: "\u06A9\u0627\u0631\u06A9\u0646\u0627\u0646 \u0648 \u062F\u0633\u062A\u0631\u0633\u06CC\u200C\u0647\u0627",
    description: "\u0627\u0639\u0636\u0627\u06CC \u062A\u06CC\u0645 \u06A9\u0644\u06CC\u0646\u06CC\u06A9\u060C \u0646\u0642\u0634\u200C\u0647\u0627 \u0648 \u0633\u0637\u062D \u062F\u0633\u062A\u0631\u0633\u06CC \u0627\u0648\u0646\u200C\u0647\u0627.",
    permissions: [
      meta(Permission.STAFF_VIEW, "\u0645\u0634\u0627\u0647\u062F\u0647 \u06A9\u0627\u0631\u06A9\u0646\u0627\u0646", "\u062F\u06CC\u062F\u0646 \u0641\u0647\u0631\u0633\u062A \u0647\u0645\u06A9\u0627\u0631\u0627\u0646 \u0648 \u0646\u0642\u0634 \u0647\u0631 \u06A9\u062F\u0627\u0645."),
      meta(Permission.STAFF_INVITE, "\u062F\u0639\u0648\u062A \u0647\u0645\u06A9\u0627\u0631", "\u062F\u0639\u0648\u062A \u0639\u0636\u0648 \u062C\u062F\u06CC\u062F \u0628\u0647 \u06A9\u0644\u06CC\u0646\u06CC\u06A9 \u0628\u0627 \u0634\u0645\u0627\u0631\u0647 \u0645\u0648\u0628\u0627\u06CC\u0644.", {
        requires: [Permission.STAFF_VIEW]
      }),
      meta(
        Permission.STAFF_EDIT,
        "\u0648\u06CC\u0631\u0627\u06CC\u0634 \u06A9\u0627\u0631\u06A9\u0646\u0627\u0646",
        "\u062A\u063A\u06CC\u06CC\u0631 \u0645\u0634\u062E\u0635\u0627\u062A\u060C \u062A\u062E\u0635\u0635\u060C \u062F\u0631\u0635\u062F \u0633\u0647\u0645 \u0648 \u0648\u0636\u0639\u06CC\u062A \u0641\u0639\u0627\u0644 \u0628\u0648\u062F\u0646 \u0647\u0645\u06A9\u0627\u0631.",
        { requires: [Permission.STAFF_VIEW] }
      ),
      meta(
        Permission.STAFF_MANAGE_ROLES,
        "\u0645\u062F\u06CC\u0631\u06CC\u062A \u0646\u0642\u0634\u200C\u0647\u0627",
        "\u0633\u0627\u062E\u062A \u0646\u0642\u0634 \u0633\u0641\u0627\u0631\u0634\u06CC \u0648 \u062A\u063A\u06CC\u06CC\u0631 \u0633\u0637\u062D \u062F\u0633\u062A\u0631\u0633\u06CC \u0647\u0645\u06A9\u0627\u0631\u0627\u0646.",
        { sensitive: true, requires: [Permission.STAFF_VIEW] }
      )
    ]
  },
  {
    key: "branch",
    label: "\u0634\u0639\u0628\u0647\u200C\u0647\u0627",
    description: "\u0634\u0639\u0628\u0647\u200C\u0647\u0627\u06CC \u06A9\u0644\u06CC\u0646\u06CC\u06A9 \u0648 \u062F\u0633\u062A\u0631\u0633\u06CC \u06A9\u0627\u0631\u06A9\u0646\u0627\u0646 \u0628\u0647 \u0647\u0631 \u0634\u0639\u0628\u0647.",
    permissions: [
      meta(
        Permission.BRANCH_MANAGE,
        "\u0645\u062F\u06CC\u0631\u06CC\u062A \u0634\u0639\u0628\u0647\u200C\u0647\u0627",
        "\u0627\u0641\u0632\u0648\u062F\u0646 \u0634\u0639\u0628\u0647\u060C \u0648\u06CC\u0631\u0627\u06CC\u0634 \u0646\u0634\u0627\u0646\u06CC\u060C \u062A\u0644\u0641\u0646 \u0648 \u0633\u0627\u0639\u0627\u062A \u06A9\u0627\u0631\u06CC \u0634\u0639\u0628\u0647\u060C \u062A\u0639\u06CC\u06CC\u0646 \u0634\u0639\u0628\u0647\u0654 \u0627\u0635\u0644\u06CC \u0648 \u0628\u0627\u06CC\u06AF\u0627\u0646\u06CC \u0634\u0639\u0628\u0647.",
        { sensitive: true, requires: [Permission.SETTINGS_VIEW] }
      ),
      meta(
        Permission.BRANCH_VIEW_ALL,
        "\u062F\u0633\u062A\u0631\u0633\u06CC \u0628\u0647 \u0647\u0645\u0647\u0654 \u0634\u0639\u0628\u0647\u200C\u0647\u0627",
        "\u0628\u062F\u0648\u0646 \u0627\u06CC\u0646 \u062F\u0633\u062A\u0631\u0633\u06CC\u060C \u062A\u0642\u0648\u06CC\u0645\u060C \u062C\u0644\u0633\u0627\u062A\u060C \u062F\u0631\u06CC\u0627\u0641\u062A\u200C\u0647\u0627\u060C \u0647\u0632\u06CC\u0646\u0647\u200C\u0647\u0627\u060C \u0627\u0646\u0628\u0627\u0631 \u0648 \u06AF\u0632\u0627\u0631\u0634\u200C\u0647\u0627 \u0641\u0642\u0637 \u0628\u0631\u0627\u06CC \u0634\u0639\u0628\u0647\u200C\u0647\u0627\u06CC\u06CC \u062F\u06CC\u062F\u0647 \u0645\u06CC\u200C\u0634\u0647 \u06A9\u0647 \u0627\u06CC\u0646 \u06A9\u0627\u0631\u0628\u0631 \u062F\u0631 \u0627\u0648\u0646\u200C\u0647\u0627 \u06A9\u0627\u0631 \u0645\u06CC\u200C\u06A9\u0646\u0647. \u067E\u0631\u0648\u0646\u062F\u0647 \u0628\u06CC\u0645\u0627\u0631 \u062F\u0631 \u0647\u0645\u0647\u0654 \u0634\u0639\u0628\u0647\u200C\u0647\u0627 \u06CC\u06A9\u06CC \u0627\u0633\u062A."
      )
    ]
  },
  {
    key: "settings",
    label: "\u062A\u0646\u0638\u06CC\u0645\u0627\u062A \u06A9\u0644\u06CC\u0646\u06CC\u06A9",
    description: "\u0645\u0634\u062E\u0635\u0627\u062A \u06A9\u0644\u06CC\u0646\u06CC\u06A9\u060C \u0627\u0634\u062A\u0631\u0627\u06A9\u060C \u0627\u0639\u062A\u0628\u0627\u0631 \u067E\u06CC\u0627\u0645\u06A9 \u0648 \u0633\u0631\u0648\u06CC\u0633\u200C\u0647\u0627\u06CC \u0645\u062A\u0635\u0644.",
    permissions: [
      meta(Permission.SETTINGS_VIEW, "\u0645\u0634\u0627\u0647\u062F\u0647 \u062A\u0646\u0638\u06CC\u0645\u0627\u062A", "\u062F\u06CC\u062F\u0646 \u062A\u0646\u0638\u06CC\u0645\u0627\u062A \u06A9\u0644\u06CC\u0646\u06CC\u06A9 \u0628\u062F\u0648\u0646 \u0627\u0645\u06A9\u0627\u0646 \u062A\u063A\u06CC\u06CC\u0631."),
      meta(
        Permission.SETTINGS_EDIT,
        "\u0648\u06CC\u0631\u0627\u06CC\u0634 \u062A\u0646\u0638\u06CC\u0645\u0627\u062A",
        "\u062A\u063A\u06CC\u06CC\u0631 \u0645\u0634\u062E\u0635\u0627\u062A \u06A9\u0644\u06CC\u0646\u06CC\u06A9\u060C \u0633\u0627\u0639\u0627\u062A \u06A9\u0627\u0631\u06CC\u060C \u0627\u062A\u0627\u0642\u200C\u0647\u0627 \u0648 \u0642\u0648\u0627\u0639\u062F \u0646\u0648\u0628\u062A\u200C\u062F\u0647\u06CC.",
        { requires: [Permission.SETTINGS_VIEW] }
      ),
      meta(
        Permission.SETTINGS_BILLING,
        "\u0627\u0634\u062A\u0631\u0627\u06A9 \u0648 \u0635\u0648\u0631\u062A\u200C\u062D\u0633\u0627\u0628",
        "\u062A\u0645\u062F\u06CC\u062F \u0627\u0634\u062A\u0631\u0627\u06A9\u060C \u0645\u0634\u0627\u0647\u062F\u0647 \u0635\u0648\u0631\u062A\u200C\u062D\u0633\u0627\u0628\u200C\u0647\u0627 \u0648 \u0634\u0627\u0631\u0698 \u0627\u0639\u062A\u0628\u0627\u0631 \u067E\u06CC\u0627\u0645\u06A9.",
        { sensitive: true, requires: [Permission.SETTINGS_VIEW] }
      ),
      meta(
        Permission.SETTINGS_INTEGRATIONS,
        "\u0627\u062A\u0635\u0627\u0644\u200C\u0647\u0627 (\u06A9\u0644\u06CC\u062F API \u0648 \u0648\u0628\u200C\u0647\u0648\u06A9)",
        "\u0633\u0627\u062E\u062A\u060C \u062A\u0639\u0648\u06CC\u0636 \u0648 \u0627\u0628\u0637\u0627\u0644 \u06A9\u0644\u06CC\u062F\u0647\u0627\u06CC API \u0648 \u0645\u062F\u06CC\u0631\u06CC\u062A \u0646\u0634\u0627\u0646\u06CC\u200C\u0647\u0627\u06CC \u0648\u0628\u200C\u0647\u0648\u06A9. \u0647\u0631 \u06A9\u0644\u06CC\u062F \u0641\u0642\u0637 \u0628\u0647 \u0628\u062E\u0634\u200C\u0647\u0627\u06CC\u06CC \u062F\u0633\u062A\u0631\u0633\u06CC \u0645\u06CC\u200C\u06AF\u06CC\u0631\u0647 \u06A9\u0647 \u0633\u0627\u0632\u0646\u062F\u0647\u200C\u0627\u0634 \u062E\u0648\u062F\u0634 \u062F\u0627\u0631\u062F.",
        { sensitive: true, requires: [Permission.SETTINGS_VIEW] }
      )
    ]
  },
  {
    key: "import",
    label: "\u0648\u0631\u0648\u062F \u0627\u0637\u0644\u0627\u0639\u0627\u062A",
    description: "\u0648\u0627\u0631\u062F \u06A9\u0631\u062F\u0646 \u062F\u0627\u062F\u0647 \u0627\u0632 \u0641\u0627\u06CC\u0644 \u06CC\u0627 \u0639\u06A9\u0633 \u062F\u0641\u062A\u0631\u0647\u0627 \u0648 \u0641\u0627\u06A9\u062A\u0648\u0631\u0647\u0627.",
    permissions: [
      meta(
        Permission.IMPORT_AI,
        "\u062B\u0628\u062A \u0627\u0637\u0644\u0627\u0639\u0627\u062A \u0627\u0632 \u0631\u0648\u06CC \u0639\u06A9\u0633",
        "\u0627\u0631\u0633\u0627\u0644 \u0639\u06A9\u0633 \u062F\u0641\u062A\u0631\u060C \u0641\u0647\u0631\u0633\u062A \u06CC\u0627 \u0641\u0627\u06A9\u062A\u0648\u0631 \u0628\u0631\u0627\u06CC \u062E\u0648\u0627\u0646\u062F\u0646 \u0627\u0637\u0644\u0627\u0639\u0627\u062A \u0648 \u062B\u0628\u062A \u0627\u0648\u0646\u200C\u0647\u0627 \u0628\u0639\u062F \u0627\u0632 \u0628\u0627\u0632\u0628\u06CC\u0646\u06CC. \u0639\u06A9\u0633 \u0628\u0631\u0627\u06CC \u067E\u0631\u062F\u0627\u0632\u0634 \u0627\u0632 \u0637\u0631\u06CC\u0642 \u0633\u0631\u0648\u0631 \u0648\u0627\u0633\u0637 \u0628\u0648\u06A9\u0648\u0631\u0627 \u062F\u0631 \u062E\u0627\u0631\u062C \u0627\u0632 \u06A9\u0634\u0648\u0631 \u0628\u0647 OpenAI \u0641\u0631\u0633\u062A\u0627\u062F\u0647 \u0645\u06CC\u200C\u0634\u0647.",
        { sensitive: true }
      )
    ]
  },
  {
    key: "platform",
    label: "\u06AF\u0632\u0627\u0631\u0634 \u0648 \u0627\u0646\u062A\u0634\u0627\u0631",
    description: "\u06AF\u0632\u0627\u0631\u0634 \u0641\u0639\u0627\u0644\u06CC\u062A \u06A9\u0627\u0631\u0628\u0631\u0627\u0646\u060C \u0627\u0639\u0644\u0627\u0646\u200C\u0647\u0627 \u0648 \u0635\u0641\u062D\u0647 \u0639\u0645\u0648\u0645\u06CC \u06A9\u0644\u06CC\u0646\u06CC\u06A9.",
    permissions: [
      meta(
        Permission.AUDIT_VIEW,
        "\u0645\u0634\u0627\u0647\u062F\u0647 \u06AF\u0632\u0627\u0631\u0634 \u0641\u0639\u0627\u0644\u06CC\u062A",
        "\u062F\u06CC\u062F\u0646 \u0633\u0627\u0628\u0642\u0647 \u062A\u063A\u06CC\u06CC\u0631\u0627\u062A\u060C \u0648\u0631\u0648\u062F\u0647\u0627 \u0648 \u062F\u0633\u062A\u0631\u0633\u06CC\u200C\u0647\u0627 \u0628\u0647 \u067E\u0631\u0648\u0646\u062F\u0647 \u0628\u06CC\u0645\u0627\u0631\u0627\u0646.",
        { sensitive: true }
      ),
      meta(
        Permission.NOTIFICATION_MANAGE,
        "\u0645\u062F\u06CC\u0631\u06CC\u062A \u0627\u0639\u0644\u0627\u0646 \u0648 \u06CC\u0627\u062F\u0622\u0648\u0631\u06CC",
        "\u062A\u0646\u0638\u06CC\u0645 \u0642\u0627\u0644\u0628 \u067E\u06CC\u0627\u0645\u06A9\u200C\u0647\u0627\u060C \u06CC\u0627\u062F\u0622\u0648\u0631\u06CC\u200C\u0647\u0627 \u0648 \u0627\u0639\u0644\u0627\u0646\u200C\u0647\u0627\u06CC \u062F\u0627\u062E\u0644 \u067E\u0646\u0644."
      ),
      meta(
        Permission.DIRECTORY_MANAGE,
        "\u0645\u062F\u06CC\u0631\u06CC\u062A \u0635\u0641\u062D\u0647 \u0639\u0645\u0648\u0645\u06CC",
        "\u0648\u06CC\u0631\u0627\u06CC\u0634 \u0635\u0641\u062D\u0647 \u06A9\u0644\u06CC\u0646\u06CC\u06A9 \u062F\u0631 \u0628\u0648\u06A9\u0648\u0631\u0627\u060C \u06AF\u0627\u0644\u0631\u06CC \u0639\u0645\u0648\u0645\u06CC \u0648 \u067E\u0627\u0633\u062E \u0628\u0647 \u0646\u0638\u0631\u0627\u062A."
      )
    ]
  }
]);
var PERMISSION_META = Object.freeze(
  PERMISSION_GROUPS.reduce(
    (acc, group) => {
      for (const item of group.permissions) acc[item.key] = item;
      return acc;
    },
    {}
  )
);
function permissionLabel(permission) {
  return isPermission(permission) ? PERMISSION_META[permission].label : permission;
}
function permissionGroupOf(permission) {
  return PERMISSION_GROUPS.find((g) => g.permissions.some((p) => p.key === permission));
}
function expandPermissions(permissions) {
  const resolved = /* @__PURE__ */ new Set();
  const walk = (permission) => {
    if (resolved.has(permission)) return;
    resolved.add(permission);
    for (const required of PERMISSION_META[permission].requires) walk(required);
  };
  for (const permission of permissions) walk(permission);
  return sortPermissions(resolved);
}
function sortPermissions(permissions) {
  const wanted = new Set(permissions);
  return ALL_PERMISSIONS.filter((p) => wanted.has(p));
}
var SystemRoleKey = {
  CLINIC_OWNER: "clinic_owner",
  DOCTOR: "doctor",
  NURSE: "nurse",
  RECEPTIONIST: "receptionist",
  ACCOUNTANT: "accountant"
};
var OWNER_PERMISSIONS = ALL_PERMISSIONS;
var DOCTOR_PERMISSIONS = expandPermissions([
  Permission.PATIENT_VIEW,
  Permission.PATIENT_CREATE,
  Permission.PATIENT_EDIT,
  Permission.PATIENT_VIEW_SENSITIVE_NOTES,
  Permission.PHOTO_VIEW,
  Permission.PHOTO_UPLOAD,
  Permission.PHOTO_EXPORT,
  Permission.PHOTO_MARK_MARKETING,
  Permission.CONSENT_VIEW,
  Permission.CONSENT_CREATE,
  Permission.APPOINTMENT_VIEW,
  Permission.APPOINTMENT_CREATE,
  Permission.APPOINTMENT_EDIT,
  Permission.APPOINTMENT_CANCEL,
  Permission.SESSION_VIEW,
  Permission.SESSION_CREATE,
  Permission.SESSION_EDIT,
  Permission.PROCEDURE_VIEW,
  Permission.INVENTORY_VIEW,
  Permission.INVENTORY_CONSUME,
  Permission.FINANCE_VIEW_REVENUE,
  Permission.FINANCE_VIEW_REPORTS,
  Permission.FINANCE_RECORD_PAYMENT,
  Permission.PACKAGE_VIEW,
  Permission.PACKAGE_SELL,
  Permission.CRM_VIEW_LEADS,
  Permission.LOYALTY_VIEW,
  Permission.STAFF_VIEW,
  Permission.SETTINGS_VIEW
]);
var NURSE_PERMISSIONS = expandPermissions([
  Permission.PATIENT_VIEW,
  Permission.PATIENT_CREATE,
  Permission.PATIENT_EDIT,
  Permission.PHOTO_VIEW,
  Permission.PHOTO_UPLOAD,
  Permission.CONSENT_VIEW,
  Permission.CONSENT_CREATE,
  Permission.APPOINTMENT_VIEW,
  Permission.APPOINTMENT_VIEW_ALL,
  Permission.APPOINTMENT_CREATE,
  Permission.APPOINTMENT_EDIT,
  Permission.SESSION_VIEW,
  Permission.SESSION_CREATE,
  Permission.PROCEDURE_VIEW,
  Permission.INVENTORY_VIEW,
  Permission.INVENTORY_RECEIVE,
  Permission.INVENTORY_CONSUME,
  Permission.PACKAGE_VIEW,
  Permission.LOYALTY_VIEW,
  Permission.IMPORT_AI
]);
var RECEPTIONIST_PERMISSIONS = expandPermissions([
  Permission.PATIENT_VIEW,
  Permission.PATIENT_VIEW_ALL,
  Permission.PATIENT_CREATE,
  Permission.PATIENT_EDIT,
  Permission.CONSENT_VIEW,
  Permission.APPOINTMENT_VIEW,
  Permission.APPOINTMENT_VIEW_ALL,
  Permission.APPOINTMENT_CREATE,
  Permission.APPOINTMENT_EDIT,
  Permission.APPOINTMENT_CANCEL,
  Permission.APPOINTMENT_CONFIRM_REQUESTS,
  Permission.SESSION_VIEW,
  Permission.SESSION_VIEW_ALL,
  Permission.PROCEDURE_VIEW,
  Permission.PACKAGE_VIEW,
  Permission.PACKAGE_SELL,
  Permission.FINANCE_RECORD_PAYMENT,
  Permission.CRM_VIEW_LEADS,
  Permission.CRM_MANAGE_LEADS,
  Permission.LOYALTY_VIEW,
  Permission.NOTIFICATION_MANAGE,
  Permission.IMPORT_AI
]);
var ACCOUNTANT_PERMISSIONS = expandPermissions([
  Permission.PATIENT_VIEW,
  Permission.PATIENT_VIEW_ALL,
  Permission.SESSION_VIEW,
  Permission.SESSION_VIEW_ALL,
  Permission.PROCEDURE_VIEW,
  Permission.INVENTORY_VIEW,
  Permission.INVENTORY_VIEW_COSTS,
  Permission.FINANCE_VIEW_REVENUE,
  Permission.FINANCE_VIEW_PROFIT,
  Permission.FINANCE_VIEW_COSTS,
  Permission.FINANCE_MANAGE_EXPENSES,
  Permission.FINANCE_MANAGE_PAYROLL,
  Permission.FINANCE_MANAGE_REVENUE_SHARE,
  Permission.FINANCE_VIEW_REPORTS,
  Permission.FINANCE_EXPORT,
  Permission.FINANCE_RECORD_PAYMENT,
  Permission.PACKAGE_VIEW,
  Permission.PACKAGE_MANAGE,
  Permission.STAFF_VIEW,
  Permission.SETTINGS_VIEW,
  Permission.SETTINGS_BILLING,
  Permission.IMPORT_AI,
  // Branch-restricted by default like every other preset — except the books:
  // an accountant reconciles the whole clinic (multi-branch design §5).
  Permission.BRANCH_VIEW_ALL
]);
var SYSTEM_ROLE_PRESETS = Object.freeze(
  {
    [SystemRoleKey.CLINIC_OWNER]: {
      key: SystemRoleKey.CLINIC_OWNER,
      name: "\u0645\u0627\u0644\u06A9 \u06A9\u0644\u06CC\u0646\u06CC\u06A9",
      description: "\u062F\u0633\u062A\u0631\u0633\u06CC \u06A9\u0627\u0645\u0644 \u0628\u0647 \u0647\u0645\u0647 \u0628\u062E\u0634\u200C\u0647\u0627\u060C \u0634\u0627\u0645\u0644 \u0645\u0627\u0644\u06CC\u060C \u06A9\u0627\u0631\u06A9\u0646\u0627\u0646 \u0648 \u062A\u0646\u0638\u06CC\u0645\u0627\u062A.",
      permissions: OWNER_PERMISSIONS,
      ownPatientsOnly: false
    },
    [SystemRoleKey.DOCTOR]: {
      key: SystemRoleKey.DOCTOR,
      name: "\u067E\u0632\u0634\u06A9",
      description: "\u067E\u0631\u0648\u0646\u062F\u0647 \u0648 \u062C\u0644\u0633\u0627\u062A \u0628\u06CC\u0645\u0627\u0631\u0627\u0646 \u062E\u0648\u062F\u0634\u060C \u0639\u06A9\u0633\u200C\u0647\u0627\u060C \u0631\u0636\u0627\u06CC\u062A\u200C\u0646\u0627\u0645\u0647 \u0648 \u06AF\u0632\u0627\u0631\u0634 \u062F\u0631\u0622\u0645\u062F \u062E\u0648\u062F\u0634.",
      permissions: DOCTOR_PERMISSIONS,
      ownPatientsOnly: true
    },
    [SystemRoleKey.NURSE]: {
      key: SystemRoleKey.NURSE,
      name: "\u067E\u0631\u0633\u062A\u0627\u0631",
      description: "\u06A9\u0645\u06A9 \u062F\u0631 \u0627\u0645\u0648\u0631 \u0628\u0627\u0644\u06CC\u0646\u06CC\u060C \u0639\u06A9\u0633\u200C\u0647\u0627 \u0648 \u0627\u0646\u0628\u0627\u0631. \u0628\u062F\u0648\u0646 \u062F\u0633\u062A\u0631\u0633\u06CC \u0628\u0647 \u0627\u0637\u0644\u0627\u0639\u0627\u062A \u0645\u0627\u0644\u06CC.",
      permissions: NURSE_PERMISSIONS,
      ownPatientsOnly: true
    },
    [SystemRoleKey.RECEPTIONIST]: {
      key: SystemRoleKey.RECEPTIONIST,
      name: "\u0645\u0646\u0634\u06CC",
      description: "\u0646\u0648\u0628\u062A\u200C\u062F\u0647\u06CC\u060C \u067E\u0630\u06CC\u0631\u0634 \u0628\u06CC\u0645\u0627\u0631\u060C \u062F\u0631\u06CC\u0627\u0641\u062A \u0627\u0632 \u0628\u06CC\u0645\u0627\u0631 \u0648 \u0628\u0633\u062A\u0646 \u0635\u0646\u062F\u0648\u0642\u060C \u0641\u0631\u0648\u0634 \u0628\u0633\u062A\u0647 \u062E\u062F\u0645\u0627\u062A \u0648 \u067E\u06CC\u06AF\u06CC\u0631\u06CC \u062F\u0631\u062E\u0648\u0627\u0633\u062A\u200C\u0647\u0627\u06CC \u0645\u0634\u0627\u0648\u0631\u0647. \u0628\u062F\u0648\u0646 \u062F\u0633\u062A\u0631\u0633\u06CC \u0628\u0647 \u0639\u06A9\u0633\u200C\u0647\u0627 \u0648 \u06AF\u0632\u0627\u0631\u0634\u200C\u0647\u0627\u06CC \u0645\u0627\u0644\u06CC.",
      permissions: RECEPTIONIST_PERMISSIONS,
      ownPatientsOnly: false
    },
    [SystemRoleKey.ACCOUNTANT]: {
      key: SystemRoleKey.ACCOUNTANT,
      name: "\u062D\u0633\u0627\u0628\u062F\u0627\u0631",
      description: "\u062F\u0631\u06CC\u0627\u0641\u062A\u200C\u0647\u0627 \u0648 \u0635\u0646\u062F\u0648\u0642\u060C \u062F\u0631\u0622\u0645\u062F\u060C \u0647\u0632\u06CC\u0646\u0647\u060C \u062D\u0642\u0648\u0642\u060C \u0633\u0647\u0645 \u067E\u0632\u0634\u06A9\u0627\u0646 \u0648 \u06AF\u0632\u0627\u0631\u0634\u200C\u0647\u0627\u06CC \u0645\u0627\u0644\u06CC. \u0628\u062F\u0648\u0646 \u062F\u0633\u062A\u0631\u0633\u06CC \u0628\u0627\u0644\u06CC\u0646\u06CC.",
      permissions: ACCOUNTANT_PERMISSIONS,
      ownPatientsOnly: false
    }
  }
);
var SYSTEM_ROLE_LIST = Object.freeze([
  SYSTEM_ROLE_PRESETS[SystemRoleKey.CLINIC_OWNER],
  SYSTEM_ROLE_PRESETS[SystemRoleKey.DOCTOR],
  SYSTEM_ROLE_PRESETS[SystemRoleKey.NURSE],
  SYSTEM_ROLE_PRESETS[SystemRoleKey.RECEPTIONIST],
  SYSTEM_ROLE_PRESETS[SystemRoleKey.ACCOUNTANT]
]);
function isSystemRoleKey(value) {
  return typeof value === "string" && value in SYSTEM_ROLE_PRESETS;
}
function resolvePermissions(rolePermissions, overrides) {
  const effective = new Set(sanitizePermissions(rolePermissions));
  for (const granted of sanitizePermissions(overrides?.granted)) effective.add(granted);
  for (const revoked of sanitizePermissions(overrides?.revoked)) effective.delete(revoked);
  return sortPermissions(effective);
}
function hasPermission(granted, required) {
  if (Array.isArray(granted)) return granted.includes(required);
  if (granted instanceof Set) return granted.has(required);
  for (const permission of granted) if (permission === required) return true;
  return false;
}
function hasAnyPermission(granted, required) {
  const set = new Set(granted);
  return required.some((permission) => set.has(permission));
}
function hasAllPermissions(granted, required) {
  const set = new Set(granted);
  return required.every((permission) => set.has(permission));
}
function isScopedToOwnRecords(granted, viewAllPermission) {
  return !hasPermission(granted, viewAllPermission);
}

// src/persian.ts
var ZWNJ = "\u200C";
var ZWJ = "\u200D";
var TATWEEL = "\u0640";
var PERSIAN_DIGITS = ["\u06F0", "\u06F1", "\u06F2", "\u06F3", "\u06F4", "\u06F5", "\u06F6", "\u06F7", "\u06F8", "\u06F9"];
var DIACRITICS = /[\u064b-\u065f\u0670\u06d6-\u06ed]/g;
var INVISIBLES = /[\u200b\u200e\u200f\u202a-\u202e\u2066-\u2069\ufeff]/g;
var LETTER_FOLD = [
  [/[يى]/g, "\u06CC"],
  // ي ARABIC YEH, ى ALEF MAKSURA → ی
  [/ك/g, "\u06A9"],
  // ك ARABIC KAF → ک
  [/ة/g, "\u0647"],
  // ة TEH MARBUTA → ه
  [/[\u0623\u0625\u0671]/g, "\u0627"],
  // hamza-above / hamza-below / wasla alef → ا
  [/ؤ/g, "\u0648"],
  // ؤ → و
  [/ئ/g, "\u06CC"],
  // ئ → ی
  [/ۀ/g, "\u0647"]
  // ۀ → ه
];
function toPersianDigits(input) {
  return String(input ?? "").replace(/[0-9]/g, (d) => PERSIAN_DIGITS[Number(d)]);
}
function toEnglishDigits(input) {
  return String(input ?? "").replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 1776)).replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 1632));
}
function hasNonAsciiDigits(input) {
  return /[۰-۹٠-٩]/.test(input);
}
function normalizeZwnj(input) {
  return String(input ?? "").replace(new RegExp(ZWJ, "g"), "").replace(new RegExp(`${ZWNJ}{2,}`, "g"), ZWNJ).replace(new RegExp(`\\s*${ZWNJ}\\s*`, "g"), (match) => /\s/.test(match) ? " " : ZWNJ).replace(new RegExp(`^${ZWNJ}+|${ZWNJ}+$`, "g"), "");
}
function removeZwnj(input, replacement = " ") {
  return String(input ?? "").split(ZWNJ).join(replacement);
}
function normalizePersian(input, options = {}) {
  const foldLetters = options.foldLetters ?? true;
  const stripDiacritics = options.stripDiacritics ?? true;
  const asciiDigits = options.asciiDigits ?? false;
  const collapseWhitespace = options.collapseWhitespace ?? true;
  let text2 = String(input ?? "").replace(INVISIBLES, "");
  if (stripDiacritics) {
    text2 = text2.replace(DIACRITICS, "").split(TATWEEL).join("");
  }
  if (foldLetters) {
    for (const [pattern, replacement] of LETTER_FOLD) {
      text2 = text2.replace(pattern, replacement);
    }
  }
  if (asciiDigits) text2 = toEnglishDigits(text2);
  text2 = normalizeZwnj(text2);
  if (collapseWhitespace) {
    text2 = text2.replace(
      /[\t\r\n\f\v\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+/g,
      " "
    );
    text2 = text2.replace(/ {2,}/g, " ").trim();
  }
  return text2;
}
function normalizeForSearch(input) {
  return normalizePersian(input, { asciiDigits: true }).split(ZWNJ).join(" ").toLowerCase().replace(/[^\p{L}\p{N}\s]+/gu, " ").replace(/\s{2,}/g, " ").trim();
}
function toPersianPunctuation(input) {
  return String(input ?? "").replace(/\?/g, "\u061F").replace(/;/g, "\u061B").replace(/,/g, "\u060C");
}
function containsPersian(input) {
  return /[\u0600-\u06ff\u0750-\u077f\ufb50-\ufdff\ufe70-\ufeff]/.test(String(input ?? ""));
}
function isPersianText(input) {
  const match = /[A-Za-z]|(?=\p{Script=Arabic})\p{L}/u.exec(String(input ?? ""));
  return match !== null && !/[A-Za-z]/.test(match[0]);
}
var MAX_SLUG_LENGTH = 80;
function slugifyFa(input, maxLength = MAX_SLUG_LENGTH) {
  const normalized = normalizePersian(input, { asciiDigits: true });
  const slug = normalized.split(ZWNJ).join("-").toLowerCase().replace(/[^\p{L}0-9]+/gu, "-").replace(/-{2,}/g, "-").replace(/^-+|-+$/g, "");
  return capSlug(slug, maxLength);
}
var LATIN_TRANSLITERATION = {
  \u0622: "a",
  \u0627: "a",
  \u0628: "b",
  \u067E: "p",
  \u062A: "t",
  \u062B: "s",
  \u062C: "j",
  \u0686: "ch",
  \u062D: "h",
  \u062E: "kh",
  \u062F: "d",
  \u0630: "z",
  \u0631: "r",
  \u0632: "z",
  \u0698: "zh",
  \u0633: "s",
  \u0634: "sh",
  \u0635: "s",
  \u0636: "z",
  \u0637: "t",
  \u0638: "z",
  \u0639: "a",
  \u063A: "gh",
  \u0641: "f",
  \u0642: "gh",
  \u06A9: "k",
  \u06AF: "g",
  \u0644: "l",
  \u0645: "m",
  \u0646: "n",
  \u0648: "v",
  \u0647: "h",
  \u06CC: "y",
  \u0621: ""
};
var CONTEXTUAL_TRANSLITERATION = {
  \u0648: ["v", "u"],
  \u06CC: ["y", "i"]
};
function slugifyLatin(input, maxLength = MAX_SLUG_LENGTH) {
  const normalized = normalizePersian(input, { asciiDigits: true });
  const source = normalized.split(ZWNJ).join(" ");
  let out = "";
  let atWordStart = true;
  for (const char of source) {
    const contextual = CONTEXTUAL_TRANSLITERATION[char];
    if (contextual) {
      out += contextual[atWordStart ? 0 : 1];
    } else {
      out += LATIN_TRANSLITERATION[char] ?? char;
    }
    atWordStart = !/[\p{L}\p{N}]/u.test(char);
  }
  const slug = out.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-{2,}/g, "-").replace(/^-+|-+$/g, "");
  return capSlug(slug, maxLength);
}
function capSlug(slug, maxLength) {
  if (slug.length <= maxLength) return slug;
  const cut = slug.slice(0, maxLength).replace(/-+[^-]*$/, "").replace(/-+$/, "");
  return cut.length > 0 ? cut : slug.slice(0, maxLength).replace(/-+$/, "");
}
function joinSlugPath(...segments) {
  const parts = segments.filter((s) => typeof s === "string" && s.length > 0);
  return `/${parts.join("/")}`;
}
function truncateFa(input, maxLength, options = {}) {
  const ellipsis = options.ellipsis ?? "\u2026";
  const wordBoundary = options.wordBoundary ?? true;
  const text2 = String(input ?? "").trim();
  if (maxLength <= 0) return "";
  const chars = Array.from(text2);
  if (chars.length <= maxLength) return text2;
  const budget = Math.max(maxLength - Array.from(ellipsis).length, 1);
  let cut = chars.slice(0, budget).join("");
  if (wordBoundary) {
    const lastSpace = cut.lastIndexOf(" ");
    if (lastSpace > budget * 0.5) cut = cut.slice(0, lastSpace);
  }
  cut = cut.replace(new RegExp(`[\\s${ZWNJ}]+$`), "").replace(/[.,،؛:؟!-]+$/, "");
  return cut.length > 0 ? `${cut}${ellipsis}` : chars.slice(0, maxLength).join("");
}
function countWordsFa(input) {
  const text2 = normalizePersian(input).split(ZWNJ).join("");
  const words = text2.split(/[\s\u060c.!?\u061f\u061b:()\u00ab\u00bb"'\-\u2013\u2014]+/u).filter((w) => w.length > 0);
  return words.length;
}
function readingTimeFa(input, wordsPerMinute = 200) {
  return Math.max(1, Math.ceil(countWordsFa(input) / wordsPerMinute));
}
function readingTimeLabelFa(input, wordsPerMinute = 200) {
  return `${toPersianDigits(readingTimeFa(input, wordsPerMinute))} \u062F\u0642\u06CC\u0642\u0647 \u0645\u0637\u0627\u0644\u0639\u0647`;
}
function joinFa(items, conjunction = "\u0648") {
  const parts = items.filter((item) => item.trim().length > 0);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join("\u060C ")} ${conjunction} ${parts[parts.length - 1]}`;
}
function pluralizeFa(count, noun) {
  return `${toPersianDigits(count)} ${noun}`;
}
function initialsFa(fullName) {
  const words = normalizePersian(fullName).split(/\s+/).filter((w) => w.length > 0);
  if (words.length === 0) return "";
  if (words.length === 1) return Array.from(words[0])[0] ?? "";
  const first = Array.from(words[0])[0] ?? "";
  const last = Array.from(words[words.length - 1])[0] ?? "";
  return `${first}${ZWNJ}${last}`;
}

// src/money.ts
var RIALS_PER_TOMAN = 10;
var THOUSANDS_SEPARATOR = "\u066C";
var DECIMAL_SEPARATOR = "\u066B";
function applyRounding(value, mode) {
  switch (mode) {
    case "floor":
      return Math.floor(value);
    case "ceil":
      return Math.ceil(value);
    case "trunc":
      return Math.trunc(value);
    case "round":
    default:
      return value < 0 ? -Math.round(-value) : Math.round(value);
  }
}
function toIntegerAmount(value, label = "amount") {
  if (typeof value === "bigint") {
    if (value > MAX_SAFE_BIGINT_RIAL || value < -MAX_SAFE_BIGINT_RIAL) {
      throw new RangeError(
        `${label} is ${String(value)} rials, beyond the exactly-representable range (\xB1${String(MAX_SAFE_BIGINT_RIAL)}). This is not a real amount.`
      );
    }
    return Number(value);
  }
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new RangeError(`${label} must be a finite number, received ${String(value)}`);
  }
  return Math.trunc(value);
}
var MAX_SAFE_BIGINT_RIAL = BigInt(Number.MAX_SAFE_INTEGER);
function rials(value, label = "amount") {
  if (value === null || value === void 0) return 0;
  return toIntegerAmount(value, label);
}
function tomanToRial(toman) {
  return toIntegerAmount(toman, "toman") * RIALS_PER_TOMAN;
}
function rialToToman(rial, mode = "round") {
  return applyRounding(toIntegerAmount(rial, "rial") / RIALS_PER_TOMAN, mode);
}
function isWholeToman(rial) {
  return toIntegerAmount(rial, "rial") % RIALS_PER_TOMAN === 0;
}
function rialToTomanExact(rial) {
  return toIntegerAmount(rial, "rial") / RIALS_PER_TOMAN;
}
function mulDiv(a, numerator, denominator, mode = "round") {
  const value = toIntegerAmount(a, "amount");
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator)) {
    throw new RangeError("mulDiv requires finite numerator and denominator");
  }
  if (denominator === 0) throw new RangeError("mulDiv denominator must not be zero");
  const quotient = Math.trunc(value / denominator);
  const remainder = value - quotient * denominator;
  const exactPart = quotient * numerator;
  const fractionalPart = remainder * numerator / denominator;
  return exactPart + applyRounding(fractionalPart, mode);
}
function sumRials(amounts) {
  let total = 0;
  for (const amount of amounts) total += toIntegerAmount(amount, "amount");
  return total;
}
var BASIS_POINTS_SCALE = 1e4;
function percentToBasisPoints(percent) {
  if (!Number.isFinite(percent)) throw new RangeError("percent must be a finite number");
  return applyRounding(percent * 100, "round");
}
function basisPointsToPercent(basisPoints) {
  return toIntegerAmount(basisPoints, "basisPoints") / 100;
}
function applyBasisPoints(amount, basisPoints, mode = "round") {
  return mulDiv(amount, toIntegerAmount(basisPoints, "basisPoints"), BASIS_POINTS_SCALE, mode);
}
function percentOf(amount, percent, mode = "round") {
  return applyBasisPoints(amount, percentToBasisPoints(percent), mode);
}
function splitByPercent(amount, percent, mode = "round") {
  const total = toIntegerAmount(amount, "amount");
  const share = percentOf(total, percent, mode);
  return { share, remainder: total - share };
}
function discountAmount(price, percent) {
  const total = toIntegerAmount(price, "price");
  const discount = percentOf(total, percent, "round");
  return Math.min(Math.max(discount, 0), Math.max(total, 0));
}
function applyPercentDiscount(price, percent) {
  return toIntegerAmount(price, "price") - discountAmount(price, percent);
}
function applyFixedDiscount(price, discountRial) {
  const total = toIntegerAmount(price, "price");
  const discount = Math.max(toIntegerAmount(discountRial, "discount"), 0);
  return Math.max(total - discount, 0);
}
function percentChange(previous, current) {
  const before = toIntegerAmount(previous, "previous");
  const after = toIntegerAmount(current, "current");
  if (before === 0) return null;
  return Math.round(((after - before) / Math.abs(before) * 100 + Number.EPSILON) * 10) / 10;
}
function roundRialTo(amount, stepRial, mode = "round") {
  const step = toIntegerAmount(stepRial, "step");
  if (step <= 0) throw new RangeError("step must be a positive integer");
  const total = toIntegerAmount(amount, "amount");
  return applyRounding(total / step, mode) * step;
}
function roundTomanTo(amount, stepToman, mode = "round") {
  return roundRialTo(amount, tomanToRial(stepToman), mode);
}
function clampRial(amount, min, max) {
  const total = toIntegerAmount(amount, "amount");
  const low = toIntegerAmount(min, "min");
  const high = toIntegerAmount(max, "max");
  if (low > high) throw new RangeError("min must not exceed max");
  return Math.min(Math.max(total, low), high);
}
function groupDigits(digits, grouping, separator) {
  return grouping ? digits.replace(/\B(?=(\d{3})+(?!\d))/g, separator) : digits;
}
function formatInteger(value, options) {
  const persian = options.persianDigits ?? true;
  const grouping = options.grouping ?? true;
  const safe = Number.isFinite(value) ? Math.trunc(value) : 0;
  const body = groupDigits(
    Math.abs(safe).toString(),
    grouping,
    persian ? THOUSANDS_SEPARATOR : ","
  );
  const signed = safe < 0 ? `\u2212${body}` : body;
  return persian ? toPersianDigits(signed) : signed;
}
function formatGroupedNumber(value, options = {}) {
  return formatInteger(Number.isFinite(value) ? Math.trunc(value) : 0, options);
}
function formatToman(rial, options = {}) {
  const withUnit = options.withUnit ?? true;
  const toman = Number.isFinite(rial) ? applyRounding(Math.trunc(rial) / RIALS_PER_TOMAN, "round") : 0;
  const body = formatInteger(toman, options);
  return withUnit ? `${body} \u062A\u0648\u0645\u0627\u0646` : body;
}
function formatRial(rial, options = {}) {
  const withUnit = options.withUnit ?? true;
  const body = formatInteger(Number.isFinite(rial) ? Math.trunc(rial) : 0, options);
  return withUnit ? `${body} \u0631\u06CC\u0627\u0644` : body;
}
var SHORT_SCALE = [
  { threshold: 1e9, divisor: 1e9, word: "\u0645\u06CC\u0644\u06CC\u0627\u0631\u062F" },
  { threshold: 1e6, divisor: 1e6, word: "\u0645\u06CC\u0644\u06CC\u0648\u0646" },
  { threshold: 1e3, divisor: 1e3, word: "\u0647\u0632\u0627\u0631" }
];
function formatTomanShort(rial, options = {}) {
  const withUnit = options.withUnit ?? true;
  const persian = options.persianDigits ?? true;
  const toman = Number.isFinite(rial) ? applyRounding(Math.trunc(rial) / RIALS_PER_TOMAN, "round") : 0;
  const abs = Math.abs(toman);
  const scale = SHORT_SCALE.find((entry) => abs >= entry.threshold);
  if (!scale) {
    return formatToman(rial, options);
  }
  const scaled = Math.round(abs / scale.divisor * 10) / 10;
  const whole = Math.trunc(scaled);
  const decimal = Math.round((scaled - whole) * 10);
  const digits = decimal === 0 ? String(whole) : `${whole}${DECIMAL_SEPARATOR}${decimal}`;
  const signed = toman < 0 ? `\u2212${digits}` : digits;
  const body = `${persian ? toPersianDigits(signed) : signed} ${scale.word}`;
  return withUnit ? `${body} \u062A\u0648\u0645\u0627\u0646` : body;
}
function formatTomanRange(minRial, maxRial, options = {}) {
  const withUnit = options.withUnit ?? true;
  const low = Math.min(minRial, maxRial);
  const high = Math.max(minRial, maxRial);
  if (low === high) return formatToman(low, options);
  const body = `\u0627\u0632 ${formatToman(low, { ...options, withUnit: false })} \u062A\u0627 ${formatToman(high, {
    ...options,
    withUnit: false
  })}`;
  return withUnit ? `${body} \u062A\u0648\u0645\u0627\u0646` : body;
}
var DIGIT_NORMALISATION = [
  [/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 1776)],
  [/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 1632)]
];
function normaliseNumericInput(input) {
  let text2 = input.normalize("NFKC");
  for (const [pattern, replacer] of DIGIT_NORMALISATION) {
    text2 = text2.replace(pattern, replacer);
  }
  return text2.replace(/[\u200b-\u200f\u202a-\u202e\u2066-\u2069\ufeff]/g, "").replace(/[\u2212\u2013\u2014]/g, "-").replace(/تومان|تومن|ریال|rials?|tomans?|IRR|IRT/gi, "").replace(/[,\u066c\u060c'`_\s]/g, "").trim();
}
function parseMoneyInput(input, options = {}) {
  const unit = options.unit ?? "toman";
  const allowNegative = options.allowNegative ?? false;
  if (input === null || input === void 0) return null;
  if (typeof input === "number") {
    if (!Number.isFinite(input)) return null;
    const rial = unit === "toman" ? tomanToRial(Math.trunc(input)) : Math.trunc(input);
    return !allowNegative && rial < 0 ? null : rial;
  }
  const text2 = normaliseNumericInput(input);
  if (text2 === "" || !/^[+-]?\d+(?:[.٫]\d+)?$/.test(text2)) return null;
  const negative = text2.startsWith("-");
  const digits = text2.replace(/^[+-]/, "").split(/[.٫]/)[0] ?? "";
  const magnitude = Number(digits);
  if (!Number.isSafeInteger(magnitude)) return null;
  const value = negative ? -magnitude : magnitude;
  if (!allowNegative && value < 0) return null;
  return unit === "toman" ? tomanToRial(value) : value;
}
function parseTomanOrZero(input) {
  return parseMoneyInput(input, { unit: "toman" }) ?? 0;
}
var RIALS_PER_UNIT = {
  rial: 1n,
  toman: 10n,
  thousand_toman: 10000n,
  million_toman: 10000000n
};
var AMOUNT_UNIT_WORDS = /* @__PURE__ */ new Map([
  ["\u0631\u06CC\u0627\u0644", { unit: "rial", multiplier: 1n }],
  ["\u0631", { unit: "rial", multiplier: 1n }],
  ["rial", { unit: "rial", multiplier: 1n }],
  ["rials", { unit: "rial", multiplier: 1n }],
  ["irr", { unit: "rial", multiplier: 1n }],
  ["\u0647\u0632\u0627\u0631\u0631\u06CC\u0627\u0644", { unit: "rial", multiplier: 1000n }],
  ["\u0645\u06CC\u0644\u06CC\u0648\u0646\u0631\u06CC\u0627\u0644", { unit: "rial", multiplier: 1000000n }],
  ["\u062A\u0648\u0645\u0627\u0646", { unit: "toman", multiplier: 1n }],
  ["\u062A\u0648\u0645\u0646", { unit: "toman", multiplier: 1n }],
  ["\u062A", { unit: "toman", multiplier: 1n }],
  ["toman", { unit: "toman", multiplier: 1n }],
  ["\u0647\u0632\u0627\u0631", { unit: "thousand_toman", multiplier: 1n }],
  ["\u0647\u0632\u0627\u0631\u062A\u0648\u0645\u0627\u0646", { unit: "thousand_toman", multiplier: 1n }],
  ["\u0647\u0632\u0627\u0631\u062A\u0648\u0645\u0646", { unit: "thousand_toman", multiplier: 1n }],
  ["\u0647\u0632\u0627\u0631\u062A", { unit: "thousand_toman", multiplier: 1n }],
  ["\u0647\u062A", { unit: "thousand_toman", multiplier: 1n }],
  ["k", { unit: "thousand_toman", multiplier: 1n }],
  ["\u0645\u06CC\u0644\u06CC\u0648\u0646", { unit: "million_toman", multiplier: 1n }],
  ["\u0645\u0644\u06CC\u0648\u0646", { unit: "million_toman", multiplier: 1n }],
  ["\u0645\u06CC\u0644\u06CC\u0648\u0646\u062A\u0648\u0645\u0627\u0646", { unit: "million_toman", multiplier: 1n }],
  ["\u0645\u0644\u06CC\u0648\u0646\u062A\u0648\u0645\u0627\u0646", { unit: "million_toman", multiplier: 1n }],
  ["\u0645\u06CC\u0644\u06CC\u0648\u0646\u062A\u0648\u0645\u0646", { unit: "million_toman", multiplier: 1n }],
  ["\u0645\u06CC\u0644\u06CC\u0648\u0646\u062A", { unit: "million_toman", multiplier: 1n }],
  ["\u0645", { unit: "million_toman", multiplier: 1n }],
  ["m", { unit: "million_toman", multiplier: 1n }]
]);
var AMOUNT_SEPARATOR_CHARS = /* @__PURE__ */ new Set([",", "\u066C", ".", "/", "\u066B"]);
var DECIMAL_CAPABLE = /* @__PURE__ */ new Set([".", "/", "\u066B"]);
function parseAmountPhrase(input, defaultUnit) {
  if (typeof input !== "string") return null;
  const folded = toEnglishDigits(input).replace(/[​-‏‪-‮⁦-⁩﻿]/g, "").replace(/[يى]/g, "\u06CC").replace(/ك/g, "\u06A9").trim();
  if (folded === "" || folded.includes("-")) return null;
  const numberRuns = folded.match(/[0-9][0-9,٬./٫]*/g);
  if (!numberRuns || numberRuns.length !== 1) return null;
  const run = numberRuns[0];
  const numberText = run.replace(/[,٬./٫]+$/, "");
  const unitText = folded.replace(run, " ").replace(/[\s.:ـ]/g, "").toLowerCase();
  let unit = defaultUnit;
  let multiplier = 1n;
  let unitSource = "default";
  if (unitText !== "") {
    const word = AMOUNT_UNIT_WORDS.get(unitText);
    if (!word) return null;
    unit = word.unit;
    multiplier = word.multiplier;
    unitSource = "explicit";
  }
  const parsed = splitAmountNumber(numberText);
  if (!parsed) return null;
  const scale = 10n ** BigInt(parsed.fraction.length);
  const numerator = BigInt(`${parsed.integer}${parsed.fraction}`) * RIALS_PER_UNIT[unit] * multiplier;
  if (numerator % scale !== 0n) return null;
  const value = numerator / scale;
  if (value > MAX_SAFE_BIGINT_RIAL) return null;
  const suggestedUnit = unitSource === "default" && defaultUnit === "thousand_toman" && parsed.fraction.length > 0 && Number(parsed.integer) < 10 ? "million_toman" : null;
  return { rials: Number(value), unit, unitSource, suggestedUnit };
}
function splitAmountNumber(text2) {
  const groups = [];
  const separators = [];
  let current = "";
  for (const ch of text2) {
    if (AMOUNT_SEPARATOR_CHARS.has(ch)) {
      if (current === "") return null;
      groups.push(current);
      separators.push(ch);
      current = "";
    } else {
      current += ch;
    }
  }
  if (current === "") return null;
  groups.push(current);
  if (groups.length === 1) return { integer: groups[0], fraction: "" };
  const [head, ...tail] = groups;
  if (head.length <= 3 && tail.every((group) => group.length === 3)) {
    return { integer: groups.join(""), fraction: "" };
  }
  const lastSeparator = separators[separators.length - 1];
  if (!DECIMAL_CAPABLE.has(lastSeparator)) return null;
  const integerGroups = groups.slice(0, -1);
  const fraction = groups[groups.length - 1];
  if (integerGroups.length > 1) {
    const [first, ...rest] = integerGroups;
    if (first.length > 3 || !rest.every((group) => group.length === 3)) return null;
    if (separators.slice(0, -1).some((sep) => sep === lastSeparator)) return null;
  }
  return { integer: integerGroups.join(""), fraction };
}

// src/jalali.ts
var TEHRAN_TIME_ZONE = "Asia/Tehran";
var JALALI_MONTH_NAMES = [
  "\u0641\u0631\u0648\u0631\u062F\u06CC\u0646",
  "\u0627\u0631\u062F\u06CC\u0628\u0647\u0634\u062A",
  "\u062E\u0631\u062F\u0627\u062F",
  "\u062A\u06CC\u0631",
  "\u0645\u0631\u062F\u0627\u062F",
  "\u0634\u0647\u0631\u06CC\u0648\u0631",
  "\u0645\u0647\u0631",
  "\u0622\u0628\u0627\u0646",
  "\u0622\u0630\u0631",
  "\u062F\u06CC",
  "\u0628\u0647\u0645\u0646",
  "\u0627\u0633\u0641\u0646\u062F"
];
var JALALI_SEASON_NAMES = ["\u0628\u0647\u0627\u0631", "\u062A\u0627\u0628\u0633\u062A\u0627\u0646", "\u067E\u0627\u06CC\u06CC\u0632", "\u0632\u0645\u0633\u062A\u0627\u0646"];
var PERSIAN_WEEKDAY_NAMES = [
  "\u0634\u0646\u0628\u0647",
  "\u06CC\u06A9\u0634\u0646\u0628\u0647",
  "\u062F\u0648\u0634\u0646\u0628\u0647",
  "\u0633\u0647\u200C\u0634\u0646\u0628\u0647",
  "\u0686\u0647\u0627\u0631\u0634\u0646\u0628\u0647",
  "\u067E\u0646\u062C\u0634\u0646\u0628\u0647",
  "\u062C\u0645\u0639\u0647"
];
var PERSIAN_WEEKDAY_SHORT = ["\u0634", "\u06CC", "\u062F", "\u0633", "\u0686", "\u067E", "\u062C"];
var MS_PER_SECOND = 1e3;
var MS_PER_MINUTE = 6e4;
var MS_PER_HOUR = 36e5;
var MS_PER_DAY = 864e5;
var PERSIAN_DIGITS2 = ["\u06F0", "\u06F1", "\u06F2", "\u06F3", "\u06F4", "\u06F5", "\u06F6", "\u06F7", "\u06F8", "\u06F9"];
function persianDigits(input) {
  return String(input).replace(/[0-9]/g, (d) => PERSIAN_DIGITS2[Number(d)]);
}
function pad2(value) {
  return value < 10 ? `0${value}` : String(value);
}
function div(a, b) {
  return Math.trunc(a / b);
}
function mod(a, b) {
  return a - div(a, b) * b;
}
var LEAP_BREAKS = [
  -61,
  9,
  38,
  199,
  426,
  686,
  756,
  818,
  1111,
  1181,
  1210,
  1635,
  1717,
  1795,
  1875,
  2064,
  2096,
  2160,
  2262,
  2324,
  2394,
  2456,
  3178
];
var MIN_JALALI_YEAR = LEAP_BREAKS[0] + 1;
var MAX_JALALI_YEAR = LEAP_BREAKS[LEAP_BREAKS.length - 1] - 1;
function jalaliCalendar(jy) {
  const gy = jy + 621;
  let leapJ = -14;
  let jp = LEAP_BREAKS[0];
  let jump = 0;
  if (jy < jp || jy >= LEAP_BREAKS[LEAP_BREAKS.length - 1]) {
    throw new RangeError(
      `Jalali year out of range (${MIN_JALALI_YEAR}\u2013${MAX_JALALI_YEAR}): ${String(jy)}`
    );
  }
  for (let i = 1; i < LEAP_BREAKS.length; i += 1) {
    const jm = LEAP_BREAKS[i];
    jump = jm - jp;
    if (jy < jm) break;
    leapJ += div(jump, 33) * 8 + div(mod(jump, 33), 4);
    jp = jm;
  }
  let n = jy - jp;
  leapJ += div(n, 33) * 8 + div(mod(n, 33) + 3, 4);
  if (mod(jump, 33) === 4 && jump - n === 4) leapJ += 1;
  const leapG = div(gy, 4) - div((div(gy, 100) + 1) * 3, 4) - 150;
  const march = 20 + leapJ - leapG;
  if (jump - n < 6) n = n - jump + div(jump + 4, 33) * 33;
  let leap = mod(mod(n + 1, 33) - 1, 4);
  if (leap === -1) leap = 4;
  return { leap, gy, march };
}
function gregorianToJdn(gy, gm, gd) {
  let d = div((gy + div(gm - 8, 6) + 100100) * 1461, 4) + div(153 * mod(gm + 9, 12) + 2, 5) + gd - 34840408;
  d = d - div(div(gy + 100100 + div(gm - 8, 6), 100) * 3, 4) + 752;
  return d;
}
function jdnToGregorian(jdn) {
  let j = 4 * jdn + 139361631;
  j = j + div(div(4 * jdn + 183187720, 146097) * 3, 4) * 4 - 3908;
  const i = div(mod(j, 1461), 4) * 5 + 308;
  const gd = div(mod(i, 153), 5) + 1;
  const gm = mod(div(i, 153), 12) + 1;
  const gy = div(j, 1461) - 100100 + div(8 - gm, 6);
  return { gy, gm, gd };
}
function jalaliToJdn(jy, jm, jd) {
  const info = jalaliCalendar(jy);
  return gregorianToJdn(info.gy, 3, info.march) + (jm - 1) * 31 - div(jm, 7) * (jm - 7) + jd - 1;
}
function jdnToJalali(jdn) {
  const gy = jdnToGregorian(jdn).gy;
  let jy = gy - 621;
  const info = jalaliCalendar(jy);
  const farvardinFirstJdn = gregorianToJdn(info.gy, 3, info.march);
  let k = jdn - farvardinFirstJdn;
  if (k >= 0) {
    if (k <= 185) {
      return { jy, jm: 1 + div(k, 31), jd: mod(k, 31) + 1 };
    }
    k -= 186;
  } else {
    jy -= 1;
    k += 179;
    if (info.leap === 1) k += 1;
  }
  return { jy, jm: 7 + div(k, 30), jd: mod(k, 30) + 1 };
}
function isJalaliLeapYear(jy) {
  return jalaliCalendar(jy).leap === 0;
}
function jalaliMonthLength(jy, jm) {
  if (jm < 1 || jm > 12) throw new RangeError(`Jalali month out of range: ${String(jm)}`);
  jalaliCalendar(jy);
  if (jm <= 6) return 31;
  if (jm <= 11) return 30;
  return isJalaliLeapYear(jy) ? 30 : 29;
}
function isValidJalaliDate(jy, jm, jd) {
  if (!Number.isInteger(jy) || !Number.isInteger(jm) || !Number.isInteger(jd)) return false;
  if (jy < MIN_JALALI_YEAR || jy > MAX_JALALI_YEAR) return false;
  if (jm < 1 || jm > 12) return false;
  return jd >= 1 && jd <= jalaliMonthLength(jy, jm);
}
function gregorianToJalali(gy, gm, gd) {
  return jdnToJalali(gregorianToJdn(gy, gm, gd));
}
function jalaliToGregorian(jy, jm, jd) {
  return jdnToGregorian(jalaliToJdn(jy, jm, jd));
}
function jalaliDaysBetween(a, b) {
  return jalaliToJdn(b.jy, b.jm, b.jd) - jalaliToJdn(a.jy, a.jm, a.jd);
}
function addJalaliDays(date, days) {
  return jdnToJalali(jalaliToJdn(date.jy, date.jm, date.jd) + Math.trunc(days));
}
function addJalaliMonths(date, months) {
  const total = date.jy * 12 + (date.jm - 1) + Math.trunc(months);
  const jy = Math.floor(total / 12);
  const jm = (total % 12 + 12) % 12 + 1;
  const jd = Math.min(date.jd, jalaliMonthLength(jy, jm));
  return { jy, jm, jd };
}
var ZONED_PARTS_CACHE = /* @__PURE__ */ new Map();
function zonedFormatter(timeZone) {
  let formatter2 = ZONED_PARTS_CACHE.get(timeZone);
  if (!formatter2) {
    formatter2 = new Intl.DateTimeFormat("en-US-u-ca-gregory", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
      era: "short"
    });
    ZONED_PARTS_CACHE.set(timeZone, formatter2);
  }
  return formatter2;
}
function zonedParts(date, timeZone) {
  const parts = zonedFormatter(timeZone).formatToParts(date);
  const read = (type) => {
    const found = parts.find((p) => p.type === type);
    return found ? Number(found.value) : Number.NaN;
  };
  const era = parts.find((p) => p.type === "era")?.value;
  const year = read("year");
  return {
    // `en-US` renders a BC year as a positive number plus era; the platform has
    // no BC dates, but converting defensively costs nothing.
    year: era === "BC" ? 1 - year : year,
    month: read("month"),
    day: read("day"),
    hour: read("hour"),
    minute: read("minute"),
    second: read("second")
  };
}
function zoneOffsetMinutes(date, timeZone = TEHRAN_TIME_ZONE) {
  const parts = zonedParts(date, timeZone);
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  return (asUtc - Math.floor(date.getTime() / MS_PER_SECOND) * MS_PER_SECOND) / MS_PER_MINUTE;
}
function zonedTimeToUtc(year, month, day, hour, minute, second, millisecond, timeZone) {
  const naive = Date.UTC(year, month - 1, day, hour, minute, second, millisecond);
  const firstOffset = zoneOffsetMinutes(new Date(naive), timeZone);
  let instant = naive - firstOffset * MS_PER_MINUTE;
  const secondOffset = zoneOffsetMinutes(new Date(instant), timeZone);
  if (secondOffset !== firstOffset) instant = naive - secondOffset * MS_PER_MINUTE;
  return new Date(instant);
}
function toJalali(date, timeZone = TEHRAN_TIME_ZONE) {
  const parts = zonedParts(date, timeZone);
  const jdn = gregorianToJdn(parts.year, parts.month, parts.day);
  const { jy, jm, jd } = jdnToJalali(jdn);
  return {
    jy,
    jm,
    jd,
    hour: parts.hour,
    minute: parts.minute,
    second: parts.second,
    // JDN 0 was a Monday; +2 rotates the week so index 0 lands on Saturday.
    weekday: mod(jdn + 2, 7)
  };
}
function fromJalali(jy, jm, jd, hour = 0, minute = 0, second = 0, millisecond = 0, timeZone = TEHRAN_TIME_ZONE) {
  if (!isValidJalaliDate(jy, jm, jd)) {
    throw new RangeError(`Invalid Jalali date: ${String(jy)}/${String(jm)}/${String(jd)}`);
  }
  const { gy, gm, gd } = jalaliToGregorian(jy, jm, jd);
  return zonedTimeToUtc(gy, gm, gd, hour, minute, second, millisecond, timeZone);
}
function jalaliWeekday(date, timeZone = TEHRAN_TIME_ZONE) {
  return toJalali(date, timeZone).weekday;
}
function jalaliWeekdayName(date, timeZone = TEHRAN_TIME_ZONE) {
  return PERSIAN_WEEKDAY_NAMES[jalaliWeekday(date, timeZone)];
}
function jalaliMonthName(month) {
  return JALALI_MONTH_NAMES[month - 1] ?? "";
}
function jalaliSeasonName(month) {
  if (month < 1 || month > 12) return "";
  return JALALI_SEASON_NAMES[Math.floor((month - 1) / 3)];
}
function formatJalali(date, pattern = "YYYY/MM/DD", options = {}) {
  const parts = toJalali(date, options.timeZone ?? TEHRAN_TIME_ZONE);
  const tokens = {
    YYYY: String(parts.jy),
    YY: pad2(parts.jy % 100),
    MMMM: jalaliMonthName(parts.jm),
    MM: pad2(parts.jm),
    M: String(parts.jm),
    DD: pad2(parts.jd),
    D: String(parts.jd),
    dddd: PERSIAN_WEEKDAY_NAMES[parts.weekday],
    dd: PERSIAN_WEEKDAY_SHORT[parts.weekday],
    HH: pad2(parts.hour),
    H: String(parts.hour),
    mm: pad2(parts.minute),
    m: String(parts.minute),
    ss: pad2(parts.second),
    s: String(parts.second)
  };
  const rendered = pattern.replace(
    /\[([^\]]*)\]|YYYY|YY|MMMM|MM|M|DD|D|dddd|dd|HH|H|mm|m|ss|s/g,
    (match, literal) => literal !== void 0 ? literal : tokens[match] ?? match
  );
  return options.persianDigits ?? true ? persianDigits(rendered) : rendered;
}
function formatJalaliDate(date, options = {}) {
  return formatJalali(date, "YYYY/MM/DD", options);
}
function formatJalaliLong(date, options = {}) {
  return formatJalali(date, "D MMMM YYYY", options);
}
function formatJalaliFull(date, options = {}) {
  return formatJalali(date, "dddd D MMMM YYYY", options);
}
function formatJalaliDateTime(date, options = {}) {
  return formatJalali(date, "D MMMM YYYY[\u060C \u0633\u0627\u0639\u062A ]HH:mm", options);
}
function formatTehranTime(date, options = {}) {
  return formatJalali(date, "HH:mm", options);
}
function toJalaliDateKey(date, timeZone = TEHRAN_TIME_ZONE) {
  const { jy, jm, jd } = toJalali(date, timeZone);
  return `${String(jy)}-${pad2(jm)}-${pad2(jd)}`;
}
function parseJalaliDateKey(value) {
  const match = /^(\d{3,4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(value.trim());
  if (!match) return null;
  const jy = Number(match[1]);
  const jm = Number(match[2]);
  const jd = Number(match[3]);
  return isValidJalaliDate(jy, jm, jd) ? { jy, jm, jd } : null;
}
var RELATIVE_UNITS = [
  { limit: 60 * MS_PER_SECOND, ms: MS_PER_SECOND, word: "\u062B\u0627\u0646\u06CC\u0647" },
  { limit: 60 * MS_PER_MINUTE, ms: MS_PER_MINUTE, word: "\u062F\u0642\u06CC\u0642\u0647" },
  { limit: 24 * MS_PER_HOUR, ms: MS_PER_HOUR, word: "\u0633\u0627\u0639\u062A" },
  { limit: 30 * MS_PER_DAY, ms: MS_PER_DAY, word: "\u0631\u0648\u0632" },
  { limit: 365 * MS_PER_DAY, ms: 30 * MS_PER_DAY, word: "\u0645\u0627\u0647" },
  { limit: Number.POSITIVE_INFINITY, ms: 365 * MS_PER_DAY, word: "\u0633\u0627\u0644" }
];
function formatRelativeFa(date, options = {}) {
  const now = options.now ?? /* @__PURE__ */ new Date();
  const timeZone = options.timeZone ?? TEHRAN_TIME_ZONE;
  const diff = date.getTime() - now.getTime();
  const absolute = Math.abs(diff);
  if (absolute < 45 * MS_PER_SECOND) return "\u0647\u0645\u06CC\u0646 \u0627\u0644\u0627\u0646";
  if (options.useDayWords ?? true) {
    const dayDelta = tehranDayDelta(date, now, timeZone);
    if (dayDelta === -1) return "\u062F\u06CC\u0631\u0648\u0632";
    if (dayDelta === 1) return "\u0641\u0631\u062F\u0627";
    if (dayDelta === -2) return "\u067E\u0631\u06CC\u0631\u0648\u0632";
    if (dayDelta === 2) return "\u067E\u0633\u200C\u0641\u0631\u062F\u0627";
  }
  const unit = RELATIVE_UNITS.find((entry) => absolute < entry.limit) ?? RELATIVE_UNITS[RELATIVE_UNITS.length - 1];
  const count = Math.max(1, Math.round(absolute / unit.ms));
  const body = `${persianDigits(count)} ${unit.word}`;
  return diff < 0 ? `${body} \u067E\u06CC\u0634` : `${body} \u062F\u06CC\u06AF\u0631`;
}
function tehranDayDelta(a, b, timeZone = TEHRAN_TIME_ZONE) {
  const dayA = zonedParts(a, timeZone);
  const dayB = zonedParts(b, timeZone);
  return gregorianToJdn(dayA.year, dayA.month, dayA.day) - gregorianToJdn(dayB.year, dayB.month, dayB.day);
}
function isSameTehranDay(a, b, timeZone = TEHRAN_TIME_ZONE) {
  return tehranDayDelta(a, b, timeZone) === 0;
}
function jalaliDayRange(date = /* @__PURE__ */ new Date(), timeZone = TEHRAN_TIME_ZONE) {
  const { jy, jm, jd } = toJalali(date, timeZone);
  const start = fromJalali(jy, jm, jd, 0, 0, 0, 0, timeZone);
  const next = addJalaliDays({ jy, jm, jd }, 1);
  const end = fromJalali(next.jy, next.jm, next.jd, 0, 0, 0, 0, timeZone);
  return { start, end, label: formatJalaliLong(start, { timeZone }) };
}
function jalaliWeekRange(date = /* @__PURE__ */ new Date(), timeZone = TEHRAN_TIME_ZONE) {
  const parts = toJalali(date, timeZone);
  const weekStart = addJalaliDays(parts, -parts.weekday);
  const weekEnd = addJalaliDays(weekStart, 7);
  const start = fromJalali(weekStart.jy, weekStart.jm, weekStart.jd, 0, 0, 0, 0, timeZone);
  const end = fromJalali(weekEnd.jy, weekEnd.jm, weekEnd.jd, 0, 0, 0, 0, timeZone);
  const last = addJalaliDays(weekStart, 6);
  return {
    start,
    end,
    label: `${persianDigits(weekStart.jd)} ${jalaliMonthName(weekStart.jm)} \u062A\u0627 ${persianDigits(
      last.jd
    )} ${jalaliMonthName(last.jm)} ${persianDigits(last.jy)}`
  };
}
function jalaliMonthRange(jy, jm, timeZone = TEHRAN_TIME_ZONE) {
  if (jm < 1 || jm > 12) throw new RangeError(`Jalali month out of range: ${String(jm)}`);
  const next = addJalaliMonths({ jy, jm, jd: 1 }, 1);
  return {
    start: fromJalali(jy, jm, 1, 0, 0, 0, 0, timeZone),
    end: fromJalali(next.jy, next.jm, 1, 0, 0, 0, 0, timeZone),
    label: `${jalaliMonthName(jm)} ${persianDigits(jy)}`
  };
}
function jalaliQuarterRange(jy, quarter, timeZone = TEHRAN_TIME_ZONE) {
  if (quarter < 1 || quarter > 4) throw new RangeError(`Quarter out of range: ${String(quarter)}`);
  const firstMonth = (quarter - 1) * 3 + 1;
  const next = addJalaliMonths({ jy, jm: firstMonth, jd: 1 }, 3);
  return {
    start: fromJalali(jy, firstMonth, 1, 0, 0, 0, 0, timeZone),
    end: fromJalali(next.jy, next.jm, 1, 0, 0, 0, 0, timeZone),
    label: `${JALALI_SEASON_NAMES[quarter - 1]} ${persianDigits(jy)}`
  };
}
function jalaliYearRange(jy, timeZone = TEHRAN_TIME_ZONE) {
  return {
    start: fromJalali(jy, 1, 1, 0, 0, 0, 0, timeZone),
    end: fromJalali(jy + 1, 1, 1, 0, 0, 0, 0, timeZone),
    label: `\u0633\u0627\u0644 ${persianDigits(jy)}`
  };
}
var REPORTING_PERIODS = [
  "today",
  "yesterday",
  "this_week",
  "last_week",
  "this_month",
  "last_month",
  "this_quarter",
  "last_quarter",
  "this_year",
  "last_year",
  "last_7_days",
  "last_30_days",
  "last_90_days"
];
var REPORTING_PERIOD_LABELS = Object.freeze({
  today: "\u0627\u0645\u0631\u0648\u0632",
  yesterday: "\u062F\u06CC\u0631\u0648\u0632",
  this_week: "\u0627\u06CC\u0646 \u0647\u0641\u062A\u0647",
  last_week: "\u0647\u0641\u062A\u0647 \u06AF\u0630\u0634\u062A\u0647",
  this_month: "\u0627\u06CC\u0646 \u0645\u0627\u0647",
  last_month: "\u0645\u0627\u0647 \u06AF\u0630\u0634\u062A\u0647",
  this_quarter: "\u0627\u06CC\u0646 \u0641\u0635\u0644",
  last_quarter: "\u0641\u0635\u0644 \u06AF\u0630\u0634\u062A\u0647",
  this_year: "\u0627\u0645\u0633\u0627\u0644",
  last_year: "\u0633\u0627\u0644 \u06AF\u0630\u0634\u062A\u0647",
  last_7_days: "\u06F7 \u0631\u0648\u0632 \u0627\u062E\u06CC\u0631",
  last_30_days: "\u06F3\u06F0 \u0631\u0648\u0632 \u0627\u062E\u06CC\u0631",
  last_90_days: "\u06F9\u06F0 \u0631\u0648\u0632 \u0627\u062E\u06CC\u0631"
});
function reportingPeriodRange(period, now = /* @__PURE__ */ new Date(), timeZone = TEHRAN_TIME_ZONE) {
  const today = toJalali(now, timeZone);
  const rollingDays = (days, label) => {
    const end = jalaliDayRange(now, timeZone).end;
    const startDay = addJalaliDays(today, -(days - 1));
    return {
      start: fromJalali(startDay.jy, startDay.jm, startDay.jd, 0, 0, 0, 0, timeZone),
      end,
      label
    };
  };
  switch (period) {
    case "today":
      return { ...jalaliDayRange(now, timeZone), label: REPORTING_PERIOD_LABELS.today };
    case "yesterday": {
      const yesterday = addJalaliDays(today, -1);
      const range = jalaliDayRange(
        fromJalali(yesterday.jy, yesterday.jm, yesterday.jd, 12, 0, 0, 0, timeZone),
        timeZone
      );
      return { ...range, label: REPORTING_PERIOD_LABELS.yesterday };
    }
    case "this_week":
      return { ...jalaliWeekRange(now, timeZone), label: REPORTING_PERIOD_LABELS.this_week };
    case "last_week": {
      const previous = new Date(jalaliWeekRange(now, timeZone).start.getTime() - MS_PER_DAY);
      return { ...jalaliWeekRange(previous, timeZone), label: REPORTING_PERIOD_LABELS.last_week };
    }
    case "this_month":
      return jalaliMonthRange(today.jy, today.jm, timeZone);
    case "last_month": {
      const previous = addJalaliMonths({ jy: today.jy, jm: today.jm, jd: 1 }, -1);
      return jalaliMonthRange(previous.jy, previous.jm, timeZone);
    }
    case "this_quarter":
      return jalaliQuarterRange(today.jy, Math.floor((today.jm - 1) / 3) + 1, timeZone);
    case "last_quarter": {
      const quarter = Math.floor((today.jm - 1) / 3) + 1;
      return quarter === 1 ? jalaliQuarterRange(today.jy - 1, 4, timeZone) : jalaliQuarterRange(today.jy, quarter - 1, timeZone);
    }
    case "this_year":
      return jalaliYearRange(today.jy, timeZone);
    case "last_year":
      return jalaliYearRange(today.jy - 1, timeZone);
    case "last_7_days":
      return rollingDays(7, REPORTING_PERIOD_LABELS.last_7_days);
    case "last_30_days":
      return rollingDays(30, REPORTING_PERIOD_LABELS.last_30_days);
    case "last_90_days":
      return rollingDays(90, REPORTING_PERIOD_LABELS.last_90_days);
    default:
      return { ...jalaliDayRange(now, timeZone), label: REPORTING_PERIOD_LABELS.today };
  }
}
function lastJalaliMonths(count, now = /* @__PURE__ */ new Date(), timeZone = TEHRAN_TIME_ZONE) {
  const current = toJalali(now, timeZone);
  const ranges = [];
  for (let offset = count - 1; offset >= 0; offset -= 1) {
    const month = addJalaliMonths({ jy: current.jy, jm: current.jm, jd: 1 }, -offset);
    ranges.push(jalaliMonthRange(month.jy, month.jm, timeZone));
  }
  return ranges;
}
function addDaysTehran(date, days, timeZone = TEHRAN_TIME_ZONE) {
  const parts = toJalali(date, timeZone);
  const shifted = addJalaliDays(parts, days);
  return fromJalali(
    shifted.jy,
    shifted.jm,
    shifted.jd,
    parts.hour,
    parts.minute,
    parts.second,
    date.getMilliseconds(),
    timeZone
  );
}
function todayJalali(now = /* @__PURE__ */ new Date(), timeZone = TEHRAN_TIME_ZONE) {
  const { jy, jm, jd } = toJalali(now, timeZone);
  return { jy, jm, jd };
}
function jalaliAge(birthDate, now = /* @__PURE__ */ new Date(), timeZone = TEHRAN_TIME_ZONE) {
  const birth = toJalali(birthDate, timeZone);
  const today = toJalali(now, timeZone);
  let age = today.jy - birth.jy;
  if (today.jm < birth.jm || today.jm === birth.jm && today.jd < birth.jd) age -= 1;
  return Math.max(age, 0);
}
function jalaliMonthKey(jy, jm) {
  if (!Number.isInteger(jy) || !Number.isInteger(jm) || jm < 1 || jm > 12) {
    throw new RangeError(`Jalali month out of range: ${String(jy)}-${String(jm)}`);
  }
  return `${String(jy)}-${String(jm).padStart(2, "0")}`;
}
function jalaliMonthKeyOf(at, timeZone = TEHRAN_TIME_ZONE) {
  const parts = toJalali(at, timeZone);
  return jalaliMonthKey(parts.jy, parts.jm);
}
var JALALI_MONTH_KEY_RE = /^(\d{4})-(\d{2})$/;
function parseJalaliMonthKey(key) {
  const match = JALALI_MONTH_KEY_RE.exec(key);
  if (!match) return null;
  const jy = Number(match[1]);
  const jm = Number(match[2]);
  if (jm < 1 || jm > 12 || jy < MIN_JALALI_YEAR || jy > MAX_JALALI_YEAR) return null;
  return { jy, jm };
}
function requireMonthKey(key) {
  const parsed = parseJalaliMonthKey(key);
  if (!parsed) throw new RangeError(`Not a Jalali month key: ${key}`);
  return parsed;
}
function jalaliMonthRangeOfKey(key, timeZone = TEHRAN_TIME_ZONE) {
  const { jy, jm } = requireMonthKey(key);
  return jalaliMonthRange(jy, jm, timeZone);
}
function nextJalaliMonthKey(key) {
  const { jy, jm } = requireMonthKey(key);
  return jm === 12 ? jalaliMonthKey(jy + 1, 1) : jalaliMonthKey(jy, jm + 1);
}
function previousJalaliMonthKey(key) {
  const { jy, jm } = requireMonthKey(key);
  return jm === 1 ? jalaliMonthKey(jy - 1, 12) : jalaliMonthKey(jy, jm - 1);
}
function jalaliMonthLabelFa(key) {
  const { jy, jm } = requireMonthKey(key);
  return `${jalaliMonthName(jm)} ${persianDigits(jy)}`;
}

// src/validators.ts
function digitsOnly(input) {
  return toEnglishDigits(String(input ?? "")).replace(/\D+/g, "");
}
function isRepdigit(value) {
  return value.length > 0 && /^(\d)\1*$/.test(value);
}
var IRAN_MOBILE_OPERATORS = Object.freeze({
  MCI: {
    key: "MCI",
    name: "\u0647\u0645\u0631\u0627\u0647 \u0627\u0648\u0644",
    prefixes: Object.freeze([
      "0910",
      "0911",
      "0912",
      "0913",
      "0914",
      "0915",
      "0916",
      "0917",
      "0918",
      "0919",
      "0990",
      "0991",
      "0992",
      "0993",
      "0994",
      "0995",
      "0996"
    ])
  },
  IRANCELL: {
    key: "IRANCELL",
    name: "\u0627\u06CC\u0631\u0627\u0646\u0633\u0644",
    prefixes: Object.freeze([
      "0900",
      "0901",
      "0902",
      "0903",
      "0904",
      "0905",
      "0930",
      "0933",
      "0935",
      "0936",
      "0937",
      "0938",
      "0939",
      "0941"
    ])
  },
  RIGHTEL: {
    key: "RIGHTEL",
    name: "\u0631\u0627\u06CC\u062A\u0644",
    prefixes: Object.freeze(["0920", "0921", "0922", "0923"])
  },
  SHATEL: {
    key: "SHATEL",
    name: "\u0634\u0627\u062A\u0644 \u0645\u0648\u0628\u0627\u06CC\u0644",
    prefixes: Object.freeze(["0998"])
  },
  SAMANTEL: {
    key: "SAMANTEL",
    name: "\u0633\u0627\u0645\u0627\u0646\u062A\u0644",
    prefixes: Object.freeze(["0999"])
  }
});
function normalizeMobile(input) {
  if (input === null || input === void 0) return null;
  let digits = digitsOnly(String(input));
  if (digits.length === 14 && digits.startsWith("0098")) digits = digits.slice(4);
  else if (digits.length === 13 && digits.startsWith("098")) digits = digits.slice(3);
  else if (digits.length === 12 && digits.startsWith("98")) digits = digits.slice(2);
  if (digits.length === 10 && digits.startsWith("9")) digits = `0${digits}`;
  if (digits.length !== 11) return null;
  if (!/^09\d{9}$/.test(digits)) return null;
  return digits;
}
function isValidMobile(input) {
  return normalizeMobile(input) !== null;
}
function toE164Mobile(input) {
  const normalized = normalizeMobile(input);
  return normalized === null ? null : `+98${normalized.slice(1)}`;
}
function mobileOperator(input) {
  const normalized = normalizeMobile(input);
  if (normalized === null) return null;
  const prefix = normalized.slice(0, 4);
  for (const operator of Object.values(IRAN_MOBILE_OPERATORS)) {
    if (operator.prefixes.includes(prefix)) {
      return operator.key;
    }
  }
  return null;
}
function mobileOperatorName(input) {
  const key = mobileOperator(input);
  return key === null ? "" : IRAN_MOBILE_OPERATORS[key].name;
}
function maskMobile(input) {
  const normalized = normalizeMobile(input);
  if (normalized === null) return "";
  return toPersianDigits(`${normalized.slice(0, 4)}***${normalized.slice(7)}`);
}
function formatMobile(input) {
  const normalized = normalizeMobile(input);
  if (normalized === null) return "";
  const grouped = `${normalized.slice(0, 4)} ${normalized.slice(4, 7)} ${normalized.slice(
    7,
    9
  )} ${normalized.slice(9)}`;
  return toPersianDigits(grouped);
}
function normalizeLandline(input) {
  if (input === null || input === void 0) return null;
  let digits = digitsOnly(String(input));
  if (digits.startsWith("0098")) digits = digits.slice(4);
  else if (digits.startsWith("098")) digits = digits.slice(3);
  else if (digits.startsWith("98") && digits.length === 12) digits = digits.slice(2);
  if (digits.length === 10 && !digits.startsWith("0")) digits = `0${digits}`;
  return /^0[1-8]\d{9}$/.test(digits) ? digits : null;
}
function isValidLandline(input) {
  return normalizeLandline(input) !== null;
}
function toE164Phone(input) {
  const mobile = toE164Mobile(input);
  if (mobile !== null) return mobile;
  const landline = normalizeLandline(input);
  return landline === null ? null : `+98${landline.slice(1)}`;
}
function normalizeNationalId(input) {
  if (input === null || input === void 0) return null;
  const digits = digitsOnly(String(input));
  if (digits.length < 8 || digits.length > 10) return null;
  return digits.padStart(10, "0");
}
function isValidNationalId(input) {
  const normalized = normalizeNationalId(input);
  if (normalized === null) return false;
  if (isRepdigit(normalized)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i += 1) {
    sum += Number(normalized[i]) * (10 - i);
  }
  const remainder = sum % 11;
  const check = Number(normalized[9]);
  return remainder < 2 ? check === remainder : check === 11 - remainder;
}
function maskNationalId(input) {
  const normalized = normalizeNationalId(input);
  if (normalized === null) return "";
  return toPersianDigits(`${normalized.slice(0, 4)}***${normalized.slice(7)}`);
}
var POSTAL_CODE_PATTERN = /^(?!(\d)\1{3})[13-9]{4}[1346-9][013-9]{5}$/;
function normalizePostalCode(input) {
  if (input === null || input === void 0) return null;
  const digits = digitsOnly(String(input));
  return digits.length === 10 ? digits : null;
}
function isValidPostalCode(input) {
  const normalized = normalizePostalCode(input);
  return normalized !== null && POSTAL_CODE_PATTERN.test(normalized);
}
function formatPostalCode(input) {
  const normalized = normalizePostalCode(input);
  if (normalized === null) return "";
  return toPersianDigits(`${normalized.slice(0, 5)}-${normalized.slice(5)}`);
}
var LICENCE_PREFIXES = ["\u067E", "\u062F", "\u062A", "\u0645", "\u06A9"];
function normalizeMedicalLicenseNo(input) {
  if (input === null || input === void 0) return null;
  const text2 = toEnglishDigits(String(input)).trim();
  const match = /^([\u0600-\u06ff]?)[\s-]*(\d{2,6})$/.exec(text2);
  if (!match) return null;
  const prefix = match[1] ?? "";
  const serial = match[2] ?? "";
  if (prefix !== "" && !LICENCE_PREFIXES.includes(prefix)) return null;
  return `${prefix}${serial}`;
}
function isValidMedicalLicenseNo(input) {
  return normalizeMedicalLicenseNo(input) !== null;
}
var IRAN_BANK_CODES = Object.freeze({
  "010": "\u0628\u0627\u0646\u06A9 \u0645\u0631\u06A9\u0632\u06CC",
  "011": "\u0628\u0627\u0646\u06A9 \u0635\u0646\u0639\u062A \u0648 \u0645\u0639\u062F\u0646",
  "012": "\u0628\u0627\u0646\u06A9 \u0645\u0644\u062A",
  "013": "\u0628\u0627\u0646\u06A9 \u0631\u0641\u0627\u0647 \u06A9\u0627\u0631\u06AF\u0631\u0627\u0646",
  "014": "\u0628\u0627\u0646\u06A9 \u0645\u0633\u06A9\u0646",
  "015": "\u0628\u0627\u0646\u06A9 \u0633\u067E\u0647",
  "016": "\u0628\u0627\u0646\u06A9 \u06A9\u0634\u0627\u0648\u0631\u0632\u06CC",
  "017": "\u0628\u0627\u0646\u06A9 \u0645\u0644\u06CC \u0627\u06CC\u0631\u0627\u0646",
  "018": "\u0628\u0627\u0646\u06A9 \u062A\u062C\u0627\u0631\u062A",
  "019": "\u0628\u0627\u0646\u06A9 \u0635\u0627\u062F\u0631\u0627\u062A \u0627\u06CC\u0631\u0627\u0646",
  "020": "\u0628\u0627\u0646\u06A9 \u062A\u0648\u0633\u0639\u0647 \u0635\u0627\u062F\u0631\u0627\u062A \u0627\u06CC\u0631\u0627\u0646",
  "021": "\u067E\u0633\u062A \u0628\u0627\u0646\u06A9 \u0627\u06CC\u0631\u0627\u0646",
  "022": "\u0628\u0627\u0646\u06A9 \u062A\u0648\u0633\u0639\u0647 \u062A\u0639\u0627\u0648\u0646",
  "051": "\u0645\u0624\u0633\u0633\u0647 \u0627\u0639\u062A\u0628\u0627\u0631\u06CC \u062A\u0648\u0633\u0639\u0647",
  "053": "\u0628\u0627\u0646\u06A9 \u06A9\u0627\u0631\u0622\u0641\u0631\u06CC\u0646",
  "054": "\u0628\u0627\u0646\u06A9 \u067E\u0627\u0631\u0633\u06CC\u0627\u0646",
  "055": "\u0628\u0627\u0646\u06A9 \u0627\u0642\u062A\u0635\u0627\u062F \u0646\u0648\u06CC\u0646",
  "056": "\u0628\u0627\u0646\u06A9 \u0633\u0627\u0645\u0627\u0646",
  "057": "\u0628\u0627\u0646\u06A9 \u067E\u0627\u0633\u0627\u0631\u06AF\u0627\u062F",
  "058": "\u0628\u0627\u0646\u06A9 \u0633\u0631\u0645\u0627\u06CC\u0647",
  "059": "\u0628\u0627\u0646\u06A9 \u0633\u06CC\u0646\u0627",
  "060": "\u0628\u0627\u0646\u06A9 \u0642\u0631\u0636\u200C\u0627\u0644\u062D\u0633\u0646\u0647 \u0645\u0647\u0631 \u0627\u06CC\u0631\u0627\u0646",
  "061": "\u0628\u0627\u0646\u06A9 \u0634\u0647\u0631",
  "062": "\u0628\u0627\u0646\u06A9 \u0622\u06CC\u0646\u062F\u0647",
  "063": "\u0628\u0627\u0646\u06A9 \u0627\u0646\u0635\u0627\u0631",
  "064": "\u0628\u0627\u0646\u06A9 \u06AF\u0631\u062F\u0634\u06AF\u0631\u06CC",
  "065": "\u0628\u0627\u0646\u06A9 \u062D\u06A9\u0645\u062A \u0627\u06CC\u0631\u0627\u0646\u06CC\u0627\u0646",
  "066": "\u0628\u0627\u0646\u06A9 \u062F\u06CC",
  "069": "\u0628\u0627\u0646\u06A9 \u0627\u06CC\u0631\u0627\u0646 \u0632\u0645\u06CC\u0646",
  "070": "\u0628\u0627\u0646\u06A9 \u0642\u0631\u0636\u200C\u0627\u0644\u062D\u0633\u0646\u0647 \u0631\u0633\u0627\u0644\u062A",
  "073": "\u0628\u0627\u0646\u06A9 \u06A9\u0648\u062B\u0631",
  "075": "\u0645\u0624\u0633\u0633\u0647 \u0627\u0639\u062A\u0628\u0627\u0631\u06CC \u0645\u0644\u0644",
  "078": "\u0628\u0627\u0646\u06A9 \u062E\u0627\u0648\u0631\u0645\u06CC\u0627\u0646\u0647",
  "080": "\u0628\u0627\u0646\u06A9 \u0646\u0648\u0631",
  "090": "\u0628\u0627\u0646\u06A9 \u0645\u0647\u0631 \u0627\u0642\u062A\u0635\u0627\u062F",
  "095": "\u0628\u0627\u0646\u06A9 \u0645\u0634\u062A\u0631\u06A9 \u0627\u06CC\u0631\u0627\u0646 \u0648 \u0648\u0646\u0632\u0648\u0626\u0644\u0627"
});
function normalizeIban(input) {
  if (input === null || input === void 0) return null;
  const text2 = toEnglishDigits(String(input)).toUpperCase().replace(/[\s-]+/g, "").replace(/^IR/, "");
  if (!/^\d{24}$/.test(text2)) return null;
  return `IR${text2}`;
}
function mod97(numeric) {
  let remainder = 0;
  for (let i = 0; i < numeric.length; i += 7) {
    remainder = Number(`${String(remainder)}${numeric.slice(i, i + 7)}`) % 97;
  }
  return remainder;
}
function isValidIranIban(input) {
  const normalized = normalizeIban(input);
  if (normalized === null) return false;
  const rearranged = `${normalized.slice(4)}1827${normalized.slice(2, 4)}`;
  return mod97(rearranged) === 1;
}
function ibanBankCode(input) {
  const normalized = normalizeIban(input);
  return normalized === null ? null : normalized.slice(4, 7);
}
function ibanBankName(input) {
  const code = ibanBankCode(input);
  return code === null ? "" : IRAN_BANK_CODES[code] ?? "";
}
function formatIban(input) {
  const normalized = normalizeIban(input);
  if (normalized === null) return "";
  return (normalized.match(/.{1,4}/g) ?? []).join(" ");
}
function isValidBankCard(input) {
  if (input === null || input === void 0) return false;
  const digits = digitsOnly(String(input));
  if (digits.length !== 16 || isRepdigit(digits)) return false;
  let sum = 0;
  for (let i = 0; i < 16; i += 1) {
    let value = Number(digits[15 - i]);
    if (i % 2 === 1) {
      value *= 2;
      if (value > 9) value -= 9;
    }
    sum += value;
  }
  return sum % 10 === 0;
}
function maskBankCard(input) {
  if (input === null || input === void 0) return "";
  const digits = digitsOnly(String(input));
  if (digits.length !== 16) return "";
  return toPersianDigits(`${digits.slice(0, 4)} ${digits.slice(4, 8)} **** ${digits.slice(12)}`);
}
function isValidEmail(input) {
  if (typeof input !== "string") return false;
  const value = input.trim();
  if (value.length === 0 || value.length > 254) return false;
  return /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/.test(value);
}
function normalizeOtpCode(input, length = 5) {
  if (input === null || input === void 0) return null;
  const digits = digitsOnly(String(input));
  return digits.length === length ? digits : null;
}
function isValidSlug(input) {
  if (typeof input !== "string") return false;
  return /^[\p{L}0-9]+(?:-[\p{L}0-9]+)*$/u.test(input) && input.length <= 96;
}

// src/types.ts
var ClinicType = {
  CLINIC: "CLINIC",
  SOLO_DOCTOR: "SOLO_DOCTOR"
};
var CLINIC_TYPE_LABELS_FA = Object.freeze({
  CLINIC: "\u06A9\u0644\u06CC\u0646\u06CC\u06A9",
  SOLO_DOCTOR: "\u0645\u0637\u0628 \u067E\u0632\u0634\u06A9"
});
var ClinicStatus = {
  TRIAL: "TRIAL",
  ACTIVE: "ACTIVE",
  PAST_DUE: "PAST_DUE",
  SUSPENDED: "SUSPENDED",
  CLOSED: "CLOSED"
};
var CLINIC_STATUS_LABELS_FA = Object.freeze({
  TRIAL: "\u062F\u0648\u0631\u0647 \u0622\u0632\u0645\u0627\u06CC\u0634\u06CC",
  ACTIVE: "\u0641\u0639\u0627\u0644",
  PAST_DUE: "\u062F\u0631 \u0627\u0646\u062A\u0638\u0627\u0631 \u067E\u0631\u062F\u0627\u062E\u062A",
  SUSPENDED: "\u062A\u0639\u0644\u06CC\u0642\u200C\u0634\u062F\u0647",
  CLOSED: "\u0628\u0633\u062A\u0647\u200C\u0634\u062F\u0647"
});
var VerificationStatus = {
  UNVERIFIED: "UNVERIFIED",
  PENDING: "PENDING",
  VERIFIED: "VERIFIED",
  REJECTED: "REJECTED"
};
var VERIFICATION_STATUS_LABELS_FA = Object.freeze({
  UNVERIFIED: "\u062A\u0623\u06CC\u06CC\u062F \u0646\u0634\u062F\u0647",
  PENDING: "\u062F\u0631 \u0627\u0646\u062A\u0638\u0627\u0631 \u0628\u0631\u0631\u0633\u06CC",
  VERIFIED: "\u062A\u0623\u06CC\u06CC\u062F \u0634\u062F\u0647",
  REJECTED: "\u0631\u062F \u0634\u062F\u0647"
});
var VerificationDocumentKind = {
  MEDICAL_LICENSE: "MEDICAL_LICENSE",
  NATIONAL_ID: "NATIONAL_ID",
  CLINIC_PERMIT: "CLINIC_PERMIT",
  TAX_CERTIFICATE: "TAX_CERTIFICATE",
  OTHER: "OTHER"
};
var VERIFICATION_DOCUMENT_KIND_LABELS_FA = Object.freeze({
  MEDICAL_LICENSE: "\u067E\u0631\u0648\u0627\u0646\u0647 \u0646\u0638\u0627\u0645 \u067E\u0632\u0634\u06A9\u06CC",
  NATIONAL_ID: "\u06A9\u0627\u0631\u062A \u0645\u0644\u06CC",
  CLINIC_PERMIT: "\u0645\u062C\u0648\u0632 \u062A\u0623\u0633\u06CC\u0633 \u06A9\u0644\u06CC\u0646\u06CC\u06A9",
  TAX_CERTIFICATE: "\u06AF\u0648\u0627\u0647\u06CC \u0645\u0627\u0644\u06CC\u0627\u062A\u06CC",
  OTHER: "\u0633\u0627\u06CC\u0631 \u0645\u062F\u0627\u0631\u06A9"
});
var UserStatus = {
  ACTIVE: "ACTIVE",
  SUSPENDED: "SUSPENDED",
  DELETED: "DELETED"
};
var USER_STATUS_LABELS_FA = Object.freeze({
  ACTIVE: "\u0641\u0639\u0627\u0644",
  SUSPENDED: "\u0645\u0633\u062F\u0648\u062F",
  DELETED: "\u062D\u0630\u0641\u200C\u0634\u062F\u0647"
});
var OtpPurpose = {
  LOGIN: "LOGIN",
  SIGNUP: "SIGNUP",
  PATIENT_APP: "PATIENT_APP",
  PUBLIC_BOOKING: "PUBLIC_BOOKING",
  PHONE_CHANGE: "PHONE_CHANGE",
  STAFF_INVITE: "STAFF_INVITE"
};
var StaffInviteStatus = {
  PENDING: "PENDING",
  ACCEPTED: "ACCEPTED",
  EXPIRED: "EXPIRED",
  REVOKED: "REVOKED"
};
var STAFF_INVITE_STATUS_LABELS_FA = Object.freeze({
  PENDING: "\u062F\u0631 \u0627\u0646\u062A\u0638\u0627\u0631 \u067E\u0630\u06CC\u0631\u0634",
  ACCEPTED: "\u067E\u0630\u06CC\u0631\u0641\u062A\u0647 \u0634\u062F",
  EXPIRED: "\u0645\u0646\u0642\u0636\u06CC \u0634\u062F",
  REVOKED: "\u0644\u063A\u0648 \u0634\u062F"
});
var PlanInterval = {
  MONTHLY: "MONTHLY",
  QUARTERLY: "QUARTERLY",
  YEARLY: "YEARLY"
};
var PLAN_INTERVAL_LABELS_FA = Object.freeze({
  MONTHLY: "\u0645\u0627\u0647\u0627\u0646\u0647",
  QUARTERLY: "\u0633\u0647\u200C\u0645\u0627\u0647\u0647",
  YEARLY: "\u0633\u0627\u0644\u0627\u0646\u0647"
});
var PlanVisibility = {
  PUBLIC: "PUBLIC",
  PRIVATE: "PRIVATE",
  LEGACY: "LEGACY"
};
var SubscriptionStatus = {
  TRIALING: "TRIALING",
  ACTIVE: "ACTIVE",
  PAST_DUE: "PAST_DUE",
  CANCELED: "CANCELED",
  EXPIRED: "EXPIRED"
};
var SUBSCRIPTION_STATUS_LABELS_FA = Object.freeze({
  TRIALING: "\u062F\u0648\u0631\u0647 \u0622\u0632\u0645\u0627\u06CC\u0634\u06CC",
  ACTIVE: "\u0641\u0639\u0627\u0644",
  PAST_DUE: "\u067E\u0631\u062F\u0627\u062E\u062A\u200C\u0646\u0634\u062F\u0647",
  CANCELED: "\u0644\u063A\u0648 \u0634\u062F\u0647",
  EXPIRED: "\u0645\u0646\u0642\u0636\u06CC \u0634\u062F\u0647"
});
var InvoiceStatus = {
  DRAFT: "DRAFT",
  ISSUED: "ISSUED",
  PAID: "PAID",
  FAILED: "FAILED",
  VOID: "VOID",
  REFUNDED: "REFUNDED"
};
var INVOICE_STATUS_LABELS_FA = Object.freeze({
  DRAFT: "\u067E\u06CC\u0634\u200C\u0646\u0648\u06CC\u0633",
  ISSUED: "\u0635\u0627\u062F\u0631 \u0634\u062F\u0647",
  PAID: "\u067E\u0631\u062F\u0627\u062E\u062A \u0634\u062F\u0647",
  FAILED: "\u0646\u0627\u0645\u0648\u0641\u0642",
  VOID: "\u0627\u0628\u0637\u0627\u0644 \u0634\u062F\u0647",
  REFUNDED: "\u0648\u062C\u0647 \u0628\u0627\u0632\u06AF\u0631\u062F\u0627\u0646\u062F\u0647 \u0634\u062F"
});
var PaymentProvider = {
  ZARINPAL: "ZARINPAL",
  MANUAL: "MANUAL",
  WALLET: "WALLET"
};
var PaymentPurpose = {
  SUBSCRIPTION: "SUBSCRIPTION",
  SMS_TOPUP: "SMS_TOPUP"
};
var PaymentStatus = {
  PENDING: "PENDING",
  SUCCESS: "SUCCESS",
  FAILED: "FAILED",
  REVERSED: "REVERSED"
};
var PAYMENT_STATUS_LABELS_FA = Object.freeze({
  PENDING: "\u062F\u0631 \u0627\u0646\u062A\u0638\u0627\u0631 \u067E\u0631\u062F\u0627\u062E\u062A",
  SUCCESS: "\u0645\u0648\u0641\u0642",
  FAILED: "\u0646\u0627\u0645\u0648\u0641\u0642",
  REVERSED: "\u0628\u0631\u06AF\u0634\u062A \u062E\u0648\u0631\u062F\u0647"
});
var WalletTxType = {
  CREDIT: "CREDIT",
  DEBIT: "DEBIT"
};
var NotificationChannel = {
  SMS: "SMS",
  IN_APP: "IN_APP"
};
var NOTIFICATION_CHANNEL_LABELS_FA = Object.freeze({
  SMS: "\u067E\u06CC\u0627\u0645\u06A9",
  IN_APP: "\u0627\u0639\u0644\u0627\u0646 \u062F\u0631 \u067E\u0646\u0644"
});
var NotificationStatus = {
  PENDING: "PENDING",
  QUEUED: "QUEUED",
  SENT: "SENT",
  DELIVERED: "DELIVERED",
  FAILED: "FAILED",
  SKIPPED: "SKIPPED"
};
var NOTIFICATION_STATUS_LABELS_FA = Object.freeze({
  PENDING: "\u062F\u0631 \u0635\u0641",
  QUEUED: "\u0627\u0631\u0633\u0627\u0644\u200C\u0634\u062F\u0647 \u0628\u0647 \u0633\u0627\u0645\u0627\u0646\u0647",
  SENT: "\u0627\u0631\u0633\u0627\u0644 \u0634\u062F",
  DELIVERED: "\u062A\u062D\u0648\u06CC\u0644 \u0634\u062F",
  FAILED: "\u0646\u0627\u0645\u0648\u0641\u0642",
  SKIPPED: "\u0627\u0631\u0633\u0627\u0644 \u0646\u0634\u062F"
});
var AnnouncementAudience = {
  ALL_CLINICS: "ALL_CLINICS",
  TRIAL: "TRIAL",
  ACTIVE: "ACTIVE",
  PAST_DUE: "PAST_DUE"
};
var AnnouncementSeverity = {
  INFO: "INFO",
  SUCCESS: "SUCCESS",
  WARNING: "WARNING",
  CRITICAL: "CRITICAL"
};
var ContactMessageStatus = {
  NEW: "NEW",
  READ: "READ",
  REPLIED: "REPLIED",
  SPAM: "SPAM"
};
var StaffNotificationType = {
  APPOINTMENT_REQUEST: "APPOINTMENT_REQUEST",
  APPOINTMENT_CANCELLED: "APPOINTMENT_CANCELLED",
  REMINDER_DUE: "REMINDER_DUE",
  LOW_STOCK: "LOW_STOCK",
  LOT_EXPIRING: "LOT_EXPIRING",
  SUBSCRIPTION: "SUBSCRIPTION",
  SMS_CREDIT_LOW: "SMS_CREDIT_LOW",
  NEW_REVIEW: "NEW_REVIEW",
  NEW_LEAD: "NEW_LEAD",
  AI_IMPORT_READY: "AI_IMPORT_READY",
  SYSTEM: "SYSTEM",
  /** Clinic API / webhook health (a key expiring, a webhook auto-disabled). */
  INTEGRATION: "INTEGRATION"
};
var STAFF_NOTIFICATION_TYPE_LABELS_FA = Object.freeze({
  APPOINTMENT_REQUEST: "\u062F\u0631\u062E\u0648\u0627\u0633\u062A \u0646\u0648\u0628\u062A \u062C\u062F\u06CC\u062F",
  APPOINTMENT_CANCELLED: "\u0644\u063A\u0648 \u0646\u0648\u0628\u062A",
  REMINDER_DUE: "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC\u200C\u0647\u0627\u06CC \u0645\u0648\u0639\u062F\u0631\u0633\u06CC\u062F\u0647",
  LOW_STOCK: "\u06A9\u0645\u0628\u0648\u062F \u0645\u0648\u062C\u0648\u062F\u06CC",
  LOT_EXPIRING: "\u0646\u0632\u062F\u06CC\u06A9 \u0634\u062F\u0646 \u062A\u0627\u0631\u06CC\u062E \u0627\u0646\u0642\u0636\u0627",
  SUBSCRIPTION: "\u0627\u0634\u062A\u0631\u0627\u06A9",
  SMS_CREDIT_LOW: "\u0627\u0639\u062A\u0628\u0627\u0631 \u067E\u06CC\u0627\u0645\u06A9 \u06A9\u0645 \u0627\u0633\u062A",
  NEW_REVIEW: "\u0646\u0638\u0631 \u062C\u062F\u06CC\u062F",
  NEW_LEAD: "\u062F\u0631\u062E\u0648\u0627\u0633\u062A \u0645\u0634\u0627\u0648\u0631\u0647 \u062C\u062F\u06CC\u062F",
  AI_IMPORT_READY: "\u062B\u0628\u062A \u0627\u0637\u0644\u0627\u0639\u0627\u062A \u0627\u0632 \u0631\u0648\u06CC \u0639\u06A9\u0633",
  SYSTEM: "\u067E\u06CC\u0627\u0645 \u0633\u06CC\u0633\u062A\u0645",
  INTEGRATION: "\u0627\u062A\u0635\u0627\u0644\u200C\u0647\u0627 (API)"
});
var DataExportScope = {
  PATIENT: "PATIENT",
  CLINIC: "CLINIC"
};
var DataExportStatus = {
  PENDING: "PENDING",
  PROCESSING: "PROCESSING",
  READY: "READY",
  FAILED: "FAILED",
  EXPIRED: "EXPIRED"
};
var ErasureRequestStatus = {
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  COMPLETED: "COMPLETED"
};
var Gender = {
  FEMALE: "FEMALE",
  MALE: "MALE",
  OTHER: "OTHER"
};
var GENDER_LABELS_FA = Object.freeze({
  FEMALE: "\u0632\u0646",
  MALE: "\u0645\u0631\u062F",
  OTHER: "\u0633\u0627\u06CC\u0631"
});
var FitzpatrickType = {
  TYPE_I: "TYPE_I",
  TYPE_II: "TYPE_II",
  TYPE_III: "TYPE_III",
  TYPE_IV: "TYPE_IV",
  TYPE_V: "TYPE_V",
  TYPE_VI: "TYPE_VI"
};
var FITZPATRICK_LABELS_FA = Object.freeze({
  TYPE_I: "\u0646\u0648\u0639 \u06F1: \u0628\u0633\u06CC\u0627\u0631 \u0631\u0648\u0634\u0646\u060C \u0647\u0645\u06CC\u0634\u0647 \u0645\u06CC\u200C\u0633\u0648\u0632\u062F \u0648 \u0628\u0631\u0646\u0632\u0647 \u0646\u0645\u06CC\u200C\u0634\u0647",
  TYPE_II: "\u0646\u0648\u0639 \u06F2: \u0631\u0648\u0634\u0646\u060C \u0645\u0639\u0645\u0648\u0644\u0627\u064B \u0645\u06CC\u200C\u0633\u0648\u0632\u062F \u0648 \u06A9\u0645 \u0628\u0631\u0646\u0632\u0647 \u0645\u06CC\u200C\u0634\u0647",
  TYPE_III: "\u0646\u0648\u0639 \u06F3: \u06AF\u0646\u062F\u0645\u06CC \u0631\u0648\u0634\u0646\u060C \u06AF\u0627\u0647\u06CC \u0645\u06CC\u200C\u0633\u0648\u0632\u062F \u0648 \u0628\u0647\u200C\u062A\u062F\u0631\u06CC\u062C \u0628\u0631\u0646\u0632\u0647 \u0645\u06CC\u200C\u0634\u0647",
  TYPE_IV: "\u0646\u0648\u0639 \u06F4: \u06AF\u0646\u062F\u0645\u06CC\u060C \u06A9\u0645 \u0645\u06CC\u200C\u0633\u0648\u0632\u062F \u0648 \u0628\u0647\u200C\u0631\u0627\u062D\u062A\u06CC \u0628\u0631\u0646\u0632\u0647 \u0645\u06CC\u200C\u0634\u0647",
  TYPE_V: "\u0646\u0648\u0639 \u06F5: \u0633\u0628\u0632\u0647\u060C \u0628\u0647\u200C\u0646\u062F\u0631\u062A \u0645\u06CC\u200C\u0633\u0648\u0632\u062F",
  TYPE_VI: "\u0646\u0648\u0639 \u06F6: \u0628\u0633\u06CC\u0627\u0631 \u062A\u06CC\u0631\u0647\u060C \u0647\u0631\u06AF\u0632 \u0646\u0645\u06CC\u200C\u0633\u0648\u0632\u062F"
});
var AcquisitionSource = {
  INSTAGRAM: "INSTAGRAM",
  DIRECTORY: "DIRECTORY",
  REFERRAL: "REFERRAL",
  WALK_IN: "WALK_IN",
  AD: "AD",
  GOOGLE: "GOOGLE",
  PATIENT_APP: "PATIENT_APP",
  OTHER: "OTHER"
};
var ACQUISITION_SOURCE_LABELS_FA = Object.freeze({
  INSTAGRAM: "\u0627\u06CC\u0646\u0633\u062A\u0627\u06AF\u0631\u0627\u0645",
  DIRECTORY: "\u0641\u0647\u0631\u0633\u062A \u06A9\u0644\u06CC\u0646\u06CC\u06A9\u200C\u0647\u0627\u06CC \u0628\u0648\u06A9\u0648\u0631\u0627",
  REFERRAL: "\u0645\u0639\u0631\u0641\u06CC \u0628\u06CC\u0645\u0627\u0631",
  WALK_IN: "\u0645\u0631\u0627\u062C\u0639\u0647 \u062D\u0636\u0648\u0631\u06CC",
  AD: "\u062A\u0628\u0644\u06CC\u063A\u0627\u062A",
  GOOGLE: "\u062C\u0633\u062A\u062C\u0648\u06CC \u06AF\u0648\u06AF\u0644",
  PATIENT_APP: "\u0627\u067E \u0645\u0631\u0627\u062C\u0639\u0627\u0646",
  OTHER: "\u0633\u0627\u06CC\u0631"
});
var NoteVisibility = {
  ALL_STAFF: "ALL_STAFF",
  DOCTORS_ONLY: "DOCTORS_ONLY",
  AUTHOR_ONLY: "AUTHOR_ONLY"
};
var NOTE_VISIBILITY_LABELS_FA = Object.freeze({
  ALL_STAFF: "\u0647\u0645\u0647 \u06A9\u0627\u0631\u06A9\u0646\u0627\u0646",
  DOCTORS_ONLY: "\u0641\u0642\u0637 \u067E\u0632\u0634\u06A9\u0627\u0646",
  AUTHOR_ONLY: "\u0641\u0642\u0637 \u0646\u0648\u06CC\u0633\u0646\u062F\u0647"
});
var ProcedureCategory = {
  INJECTABLE: "INJECTABLE",
  LASER: "LASER",
  SKIN: "SKIN",
  HAIR: "HAIR",
  SURGICAL: "SURGICAL",
  OTHER: "OTHER"
};
var PROCEDURE_CATEGORY_LABELS_FA = Object.freeze({
  INJECTABLE: "\u062A\u0632\u0631\u06CC\u0642\u06CC",
  LASER: "\u0644\u06CC\u0632\u0631",
  SKIN: "\u067E\u0648\u0633\u062A \u0648 \u0632\u06CC\u0628\u0627\u06CC\u06CC",
  HAIR: "\u0645\u0648",
  SURGICAL: "\u062C\u0631\u0627\u062D\u06CC",
  OTHER: "\u0633\u0627\u06CC\u0631"
});
var BodyMapType = {
  NONE: "NONE",
  FACE_FRONT: "FACE_FRONT",
  FACE_SIDE: "FACE_SIDE",
  SCALP: "SCALP",
  UPPER_BODY: "UPPER_BODY",
  LOWER_BODY: "LOWER_BODY",
  FULL_BODY: "FULL_BODY",
  HANDS: "HANDS"
};
var BODY_MAP_TYPE_LABELS_FA = Object.freeze({
  NONE: "\u0628\u062F\u0648\u0646 \u0646\u0642\u0634\u0647",
  FACE_FRONT: "\u0646\u0645\u0627\u06CC \u0631\u0648\u0628\u0647\u200C\u0631\u0648\u06CC \u0635\u0648\u0631\u062A",
  FACE_SIDE: "\u0646\u0645\u0627\u06CC \u062C\u0627\u0646\u0628\u06CC \u0635\u0648\u0631\u062A",
  SCALP: "\u067E\u0648\u0633\u062A \u0633\u0631",
  UPPER_BODY: "\u0628\u0627\u0644\u0627\u062A\u0646\u0647",
  LOWER_BODY: "\u067E\u0627\u06CC\u06CC\u0646\u200C\u062A\u0646\u0647",
  FULL_BODY: "\u062A\u0645\u0627\u0645 \u0628\u062F\u0646",
  HANDS: "\u062F\u0633\u062A\u200C\u0647\u0627"
});
var SessionStatus = {
  PLANNED: "PLANNED",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED"
};
var SESSION_STATUS_LABELS_FA = Object.freeze({
  PLANNED: "\u0628\u0631\u0646\u0627\u0645\u0647\u200C\u0631\u06CC\u0632\u06CC\u200C\u0634\u062F\u0647",
  COMPLETED: "\u0627\u0646\u062C\u0627\u0645 \u0634\u062F",
  CANCELLED: "\u0644\u063A\u0648 \u0634\u062F"
});
var SessionOrigin = {
  LIVE: "LIVE",
  PAPER_IMPORT: "PAPER_IMPORT"
};
var SESSION_ORIGIN_LABELS_FA = Object.freeze({
  LIVE: "\u062B\u0628\u062A \u062F\u0631 \u0628\u0648\u06A9\u0648\u0631\u0627",
  PAPER_IMPORT: "\u0627\u0632 \u067E\u0631\u0648\u0646\u062F\u0647\u0654 \u06A9\u0627\u063A\u0630\u06CC"
});
var PaymentMethod = {
  CASH: "CASH",
  CARD: "CARD",
  TRANSFER: "TRANSFER",
  PACKAGE: "PACKAGE",
  CREDIT: "CREDIT"
};
var PAYMENT_METHOD_LABELS_FA = Object.freeze({
  CASH: "\u0646\u0642\u062F\u06CC",
  CARD: "\u06A9\u0627\u0631\u062A\u200C\u062E\u0648\u0627\u0646",
  TRANSFER: "\u06A9\u0627\u0631\u062A \u0628\u0647 \u06A9\u0627\u0631\u062A / \u0627\u0646\u062A\u0642\u0627\u0644",
  PACKAGE: "\u0627\u0633\u062A\u0641\u0627\u062F\u0647 \u0627\u0632 \u0628\u0633\u062A\u0647 \u062E\u062F\u0645\u0627\u062A",
  CREDIT: "\u0627\u0639\u062A\u0628\u0627\u0631 \u0628\u06CC\u0645\u0627\u0631"
});
var ConsentMethod = {
  E_SIGN: "E_SIGN",
  SCAN: "SCAN"
};
var CONSENT_METHOD_LABELS_FA = Object.freeze({
  E_SIGN: "\u0627\u0645\u0636\u0627\u06CC \u062F\u06CC\u062C\u06CC\u062A\u0627\u0644",
  SCAN: "\u0646\u0633\u062E\u0647 \u0627\u0633\u06A9\u0646\u200C\u0634\u062F\u0647"
});
var ConsentStatus = {
  SIGNED: "SIGNED",
  VOIDED: "VOIDED"
};
var PhotoKind = {
  BEFORE: "BEFORE",
  AFTER: "AFTER",
  PROGRESS: "PROGRESS",
  CONSENT_SCAN: "CONSENT_SCAN",
  DOCUMENT: "DOCUMENT"
};
var PHOTO_KIND_LABELS_FA = Object.freeze({
  BEFORE: "\u0642\u0628\u0644",
  AFTER: "\u0628\u0639\u062F",
  PROGRESS: "\u062D\u06CC\u0646 \u062F\u0631\u0645\u0627\u0646",
  CONSENT_SCAN: "\u0627\u0633\u06A9\u0646 \u0631\u0636\u0627\u06CC\u062A\u200C\u0646\u0627\u0645\u0647",
  DOCUMENT: "\u0645\u062F\u0631\u06A9"
});
var PhotoAngle = {
  FRONT: "FRONT",
  LEFT: "LEFT",
  RIGHT: "RIGHT",
  LEFT_45: "LEFT_45",
  RIGHT_45: "RIGHT_45",
  TOP: "TOP",
  OTHER: "OTHER"
};
var PHOTO_ANGLE_LABELS_FA = Object.freeze({
  FRONT: "\u0631\u0648\u0628\u0647\u200C\u0631\u0648",
  LEFT: "\u0646\u06CC\u0645\u200C\u0631\u062E \u0686\u067E",
  RIGHT: "\u0646\u06CC\u0645\u200C\u0631\u062E \u0631\u0627\u0633\u062A",
  LEFT_45: "\u0632\u0627\u0648\u06CC\u0647 \u06F4\u06F5 \u062F\u0631\u062C\u0647 \u0686\u067E",
  RIGHT_45: "\u0632\u0627\u0648\u06CC\u0647 \u06F4\u06F5 \u062F\u0631\u062C\u0647 \u0631\u0627\u0633\u062A",
  TOP: "\u0627\u0632 \u0628\u0627\u0644\u0627",
  OTHER: "\u0633\u0627\u06CC\u0631"
});
var PhotoAccessAction = {
  VIEW: "VIEW",
  DOWNLOAD: "DOWNLOAD",
  EXPORT: "EXPORT",
  SIGNED_URL: "SIGNED_URL",
  SHARE: "SHARE"
};
var Weekday = {
  SATURDAY: "SATURDAY",
  SUNDAY: "SUNDAY",
  MONDAY: "MONDAY",
  TUESDAY: "TUESDAY",
  WEDNESDAY: "WEDNESDAY",
  THURSDAY: "THURSDAY",
  FRIDAY: "FRIDAY"
};
var WEEKDAY_LABELS_FA = Object.freeze({
  SATURDAY: "\u0634\u0646\u0628\u0647",
  SUNDAY: "\u06CC\u06A9\u0634\u0646\u0628\u0647",
  MONDAY: "\u062F\u0648\u0634\u0646\u0628\u0647",
  TUESDAY: "\u0633\u0647\u200C\u0634\u0646\u0628\u0647",
  WEDNESDAY: "\u0686\u0647\u0627\u0631\u0634\u0646\u0628\u0647",
  THURSDAY: "\u067E\u0646\u062C\u0634\u0646\u0628\u0647",
  FRIDAY: "\u062C\u0645\u0639\u0647"
});
var WEEKDAY_ORDER = Object.freeze([
  Weekday.SATURDAY,
  Weekday.SUNDAY,
  Weekday.MONDAY,
  Weekday.TUESDAY,
  Weekday.WEDNESDAY,
  Weekday.THURSDAY,
  Weekday.FRIDAY
]);
var AppointmentStatus = {
  REQUESTED: "REQUESTED",
  CONFIRMED: "CONFIRMED",
  ARRIVED: "ARRIVED",
  IN_PROGRESS: "IN_PROGRESS",
  DONE: "DONE",
  CANCELLED: "CANCELLED",
  NO_SHOW: "NO_SHOW"
};
var APPOINTMENT_STATUS_LABELS_FA = Object.freeze({
  REQUESTED: "\u062F\u0631\u062E\u0648\u0627\u0633\u062A\u200C\u0634\u062F\u0647",
  CONFIRMED: "\u062A\u0623\u06CC\u06CC\u062F \u0634\u062F\u0647",
  ARRIVED: "\u0645\u0631\u0627\u062C\u0639\u0647 \u06A9\u0631\u062F",
  IN_PROGRESS: "\u062F\u0631 \u062D\u0627\u0644 \u0627\u0646\u062C\u0627\u0645",
  DONE: "\u0627\u0646\u062C\u0627\u0645 \u0634\u062F",
  CANCELLED: "\u0644\u063A\u0648 \u0634\u062F",
  NO_SHOW: "\u0639\u062F\u0645 \u0645\u0631\u0627\u062C\u0639\u0647"
});
var ACTIVE_APPOINTMENT_STATUSES = Object.freeze([
  AppointmentStatus.REQUESTED,
  AppointmentStatus.CONFIRMED,
  AppointmentStatus.ARRIVED,
  AppointmentStatus.IN_PROGRESS
]);
var AppointmentSource = {
  PANEL: "PANEL",
  PATIENT_APP: "PATIENT_APP",
  PUBLIC_SITE: "PUBLIC_SITE",
  PHONE: "PHONE",
  /** Booked by a clinic's own integration through the clinic API. */
  API: "API"
};
var APPOINTMENT_SOURCE_LABELS_FA = Object.freeze({
  PANEL: "\u067E\u0646\u0644 \u06A9\u0644\u06CC\u0646\u06CC\u06A9",
  PATIENT_APP: "\u0627\u067E \u0645\u0631\u0627\u062C\u0639\u0627\u0646",
  PUBLIC_SITE: "\u0633\u0627\u06CC\u062A \u0628\u0648\u06A9\u0648\u0631\u0627",
  PHONE: "\u062A\u0645\u0627\u0633 \u062A\u0644\u0641\u0646\u06CC",
  API: "\u0627\u0632 \u0637\u0631\u06CC\u0642 API"
});
var AppointmentRequestStatus = {
  PENDING: "PENDING",
  CONFIRMED: "CONFIRMED",
  REJECTED: "REJECTED",
  EXPIRED: "EXPIRED"
};
var WaitListStatus = {
  WAITING: "WAITING",
  NOTIFIED: "NOTIFIED",
  BOOKED: "BOOKED",
  CANCELLED: "CANCELLED",
  EXPIRED: "EXPIRED"
};
var MaterialCategory = {
  BOTOX: "BOTOX",
  FILLER: "FILLER",
  PRP_KIT: "PRP_KIT",
  THREAD: "THREAD",
  MESO: "MESO",
  CONSUMABLE: "CONSUMABLE",
  MEDICATION: "MEDICATION",
  OTHER: "OTHER"
};
var MATERIAL_CATEGORY_LABELS_FA = Object.freeze({
  BOTOX: "\u0628\u0648\u062A\u0627\u06A9\u0633",
  FILLER: "\u0641\u06CC\u0644\u0631 \u0648 \u0698\u0644",
  PRP_KIT: "\u06A9\u06CC\u062A PRP",
  THREAD: "\u0646\u062E \u0644\u06CC\u0641\u062A",
  MESO: "\u06A9\u0648\u06A9\u062A\u0644 \u0645\u0632\u0648\u062A\u0631\u0627\u067E\u06CC",
  CONSUMABLE: "\u0645\u0644\u0632\u0648\u0645\u0627\u062A \u0645\u0635\u0631\u0641\u06CC",
  MEDICATION: "\u062F\u0627\u0631\u0648",
  OTHER: "\u0633\u0627\u06CC\u0631"
});
var MaterialUnit = {
  UNIT: "UNIT",
  ML: "ML",
  SYRINGE: "SYRINGE",
  VIAL: "VIAL",
  PIECE: "PIECE"
};
var MATERIAL_UNIT_LABELS_FA = Object.freeze({
  UNIT: "\u0648\u0627\u062D\u062F",
  ML: "\u0633\u06CC\u200C\u0633\u06CC",
  SYRINGE: "\u0633\u0631\u0646\u06AF",
  VIAL: "\u0648\u06CC\u0627\u0644",
  PIECE: "\u0639\u062F\u062F"
});
var LotStatus = {
  ACTIVE: "ACTIVE",
  DEPLETED: "DEPLETED",
  EXPIRED: "EXPIRED",
  QUARANTINED: "QUARANTINED"
};
var LOT_STATUS_LABELS_FA = Object.freeze({
  ACTIVE: "\u0641\u0639\u0627\u0644",
  DEPLETED: "\u062A\u0645\u0627\u0645\u200C\u0634\u062F\u0647",
  EXPIRED: "\u0645\u0646\u0642\u0636\u06CC",
  QUARANTINED: "\u0642\u0631\u0646\u0637\u06CC\u0646\u0647"
});
var InventoryMovementType = {
  PURCHASE: "PURCHASE",
  CONSUMPTION: "CONSUMPTION",
  WASTAGE: "WASTAGE",
  ADJUSTMENT: "ADJUSTMENT",
  RETURN: "RETURN",
  EXPIRY_WRITE_OFF: "EXPIRY_WRITE_OFF",
  /** Stock leaving a branch for another branch of the same clinic (one half of a `StockTransfer`). */
  TRANSFER_OUT: "TRANSFER_OUT",
  /** Stock arriving from another branch (the other half). */
  TRANSFER_IN: "TRANSFER_IN"
};
var INVENTORY_MOVEMENT_TYPE_LABELS_FA = Object.freeze({
  PURCHASE: "\u0648\u0631\u0648\u062F \u062E\u0631\u06CC\u062F",
  CONSUMPTION: "\u0645\u0635\u0631\u0641 \u062F\u0631 \u062C\u0644\u0633\u0647",
  WASTAGE: "\u0636\u0627\u06CC\u0639\u0627\u062A",
  ADJUSTMENT: "\u0627\u0635\u0644\u0627\u062D \u0645\u0648\u062C\u0648\u062F\u06CC",
  RETURN: "\u0628\u0631\u06AF\u0634\u062A \u0628\u0647 \u062A\u0623\u0645\u06CC\u0646\u200C\u06A9\u0646\u0646\u062F\u0647",
  EXPIRY_WRITE_OFF: "\u062F\u0648\u0631\u0631\u06CC\u0632 \u0628\u0647\u200C\u062F\u0644\u06CC\u0644 \u0627\u0646\u0642\u0636\u0627",
  TRANSFER_OUT: "\u0627\u0646\u062A\u0642\u0627\u0644 \u0628\u0647 \u0634\u0639\u0628\u0647\u0654 \u062F\u06CC\u06AF\u0631",
  TRANSFER_IN: "\u0627\u0646\u062A\u0642\u0627\u0644 \u0627\u0632 \u0634\u0639\u0628\u0647\u0654 \u062F\u06CC\u06AF\u0631"
});
var ExpenseCategory = {
  RENT: "RENT",
  SALARY: "SALARY",
  MARKETING: "MARKETING",
  UTILITIES: "UTILITIES",
  EQUIPMENT: "EQUIPMENT",
  MATERIAL_PURCHASE: "MATERIAL_PURCHASE",
  TAX: "TAX",
  OTHER: "OTHER"
};
var EXPENSE_CATEGORY_LABELS_FA = Object.freeze({
  RENT: "\u0627\u062C\u0627\u0631\u0647",
  SALARY: "\u062D\u0642\u0648\u0642 \u0648 \u062F\u0633\u062A\u0645\u0632\u062F",
  MARKETING: "\u062A\u0628\u0644\u06CC\u063A\u0627\u062A \u0648 \u0628\u0627\u0632\u0627\u0631\u06CC\u0627\u0628\u06CC",
  UTILITIES: "\u0642\u0628\u0648\u0636 \u0648 \u0622\u0628 \u0648 \u0628\u0631\u0642",
  EQUIPMENT: "\u062A\u062C\u0647\u06CC\u0632\u0627\u062A",
  MATERIAL_PURCHASE: "\u062E\u0631\u06CC\u062F \u0645\u0648\u0627\u062F \u0645\u0635\u0631\u0641\u06CC",
  TAX: "\u0645\u0627\u0644\u06CC\u0627\u062A \u0648 \u0639\u0648\u0627\u0631\u0636",
  OTHER: "\u0633\u0627\u06CC\u0631 \u0647\u0632\u06CC\u0646\u0647\u200C\u0647\u0627"
});
var RevenueShareBasis = {
  PERCENT: "PERCENT",
  FIXED: "FIXED"
};
var REVENUE_SHARE_BASIS_LABELS_FA = Object.freeze({
  PERCENT: "\u062F\u0631\u0635\u062F\u06CC",
  FIXED: "\u0645\u0628\u0644\u063A \u062B\u0627\u0628\u062A"
});
var PayrollStatus = {
  DRAFT: "DRAFT",
  APPROVED: "APPROVED",
  PAID: "PAID"
};
var PAYROLL_STATUS_LABELS_FA = Object.freeze({
  DRAFT: "\u067E\u06CC\u0634\u200C\u0646\u0648\u06CC\u0633",
  APPROVED: "\u062A\u0623\u06CC\u06CC\u062F \u0634\u062F\u0647",
  PAID: "\u067E\u0631\u062F\u0627\u062E\u062A \u0634\u062F\u0647"
});
var PatientPackageStatus = {
  ACTIVE: "ACTIVE",
  COMPLETED: "COMPLETED",
  EXPIRED: "EXPIRED",
  CANCELLED: "CANCELLED",
  REFUNDED: "REFUNDED"
};
var PATIENT_PACKAGE_STATUS_LABELS_FA = Object.freeze({
  ACTIVE: "\u0641\u0639\u0627\u0644",
  COMPLETED: "\u062A\u06A9\u0645\u06CC\u0644\u200C\u0634\u062F\u0647",
  EXPIRED: "\u0645\u0646\u0642\u0636\u06CC\u200C\u0634\u062F\u0647",
  CANCELLED: "\u0644\u063A\u0648 \u0634\u062F\u0647",
  REFUNDED: "\u0648\u062C\u0647 \u0628\u0627\u0632\u06AF\u0631\u062F\u0627\u0646\u062F\u0647 \u0634\u062F"
});
var FinancialPeriodType = {
  DAY: "DAY",
  MONTH: "MONTH",
  YEAR: "YEAR"
};
var LeadStatus = {
  NEW: "NEW",
  CONTACTED: "CONTACTED",
  BOOKED: "BOOKED",
  LOST: "LOST"
};
var LEAD_STATUS_LABELS_FA = Object.freeze({
  NEW: "\u062C\u062F\u06CC\u062F",
  CONTACTED: "\u062A\u0645\u0627\u0633 \u06AF\u0631\u0641\u062A\u0647 \u0634\u062F",
  BOOKED: "\u0646\u0648\u0628\u062A \u06AF\u0631\u0641\u062A",
  LOST: "\u0627\u0632 \u062F\u0633\u062A \u0631\u0641\u062A"
});
var LeadActivityType = {
  CALL: "CALL",
  SMS: "SMS",
  WHATSAPP: "WHATSAPP",
  INSTAGRAM_DM: "INSTAGRAM_DM",
  MEETING: "MEETING",
  NOTE: "NOTE"
};
var LEAD_ACTIVITY_TYPE_LABELS_FA = Object.freeze({
  CALL: "\u062A\u0645\u0627\u0633 \u062A\u0644\u0641\u0646\u06CC",
  SMS: "\u067E\u06CC\u0627\u0645\u06A9",
  WHATSAPP: "\u0648\u0627\u062A\u0633\u0627\u067E",
  INSTAGRAM_DM: "\u062F\u0627\u06CC\u0631\u06A9\u062A \u0627\u06CC\u0646\u0633\u062A\u0627\u06AF\u0631\u0627\u0645",
  MEETING: "\u062C\u0644\u0633\u0647 \u062D\u0636\u0648\u0631\u06CC",
  NOTE: "\u06CC\u0627\u062F\u062F\u0627\u0634\u062A"
});
var SurveyStatus = {
  SENT: "SENT",
  COMPLETED: "COMPLETED",
  EXPIRED: "EXPIRED",
  CANCELLED: "CANCELLED"
};
var SURVEY_STATUS_LABELS_FA = Object.freeze({
  SENT: "\u0627\u0631\u0633\u0627\u0644 \u0634\u062F",
  COMPLETED: "\u067E\u0627\u0633\u062E \u062F\u0627\u062F\u0647 \u0634\u062F",
  EXPIRED: "\u0645\u0646\u0642\u0636\u06CC \u0634\u062F",
  CANCELLED: "\u0644\u063A\u0648 \u0634\u062F"
});
var ReferralStatus = {
  PENDING: "PENDING",
  QUALIFIED: "QUALIFIED",
  REWARDED: "REWARDED",
  EXPIRED: "EXPIRED",
  CANCELLED: "CANCELLED"
};
var REFERRAL_STATUS_LABELS_FA = Object.freeze({
  PENDING: "\u062F\u0631 \u0627\u0646\u062A\u0638\u0627\u0631",
  QUALIFIED: "\u0648\u0627\u062C\u062F \u0634\u0631\u0627\u06CC\u0637",
  REWARDED: "\u067E\u0627\u062F\u0627\u0634 \u062F\u0627\u062F\u0647 \u0634\u062F",
  EXPIRED: "\u0645\u0646\u0642\u0636\u06CC \u0634\u062F",
  CANCELLED: "\u0644\u063A\u0648 \u0634\u062F"
});
var LoyaltyEntryType = {
  EARN: "EARN",
  SPEND: "SPEND",
  ADJUST: "ADJUST",
  EXPIRE: "EXPIRE"
};
var LOYALTY_ENTRY_TYPE_LABELS_FA = Object.freeze({
  EARN: "\u06A9\u0633\u0628 \u0627\u0645\u062A\u06CC\u0627\u0632",
  SPEND: "\u0627\u0633\u062A\u0641\u0627\u062F\u0647 \u0627\u0632 \u0627\u0645\u062A\u06CC\u0627\u0632",
  ADJUST: "\u0627\u0635\u0644\u0627\u062D \u062F\u0633\u062A\u06CC",
  EXPIRE: "\u0627\u0646\u0642\u0636\u0627\u06CC \u0627\u0645\u062A\u06CC\u0627\u0632"
});
var ReminderKind = {
  FOLLOW_UP: "FOLLOW_UP",
  TOUCH_UP: "TOUCH_UP",
  NEXT_SESSION: "NEXT_SESSION",
  REVIEW: "REVIEW",
  AFTERCARE: "AFTERCARE"
};
var REMINDER_KIND_LABELS_FA = Object.freeze({
  FOLLOW_UP: "\u067E\u06CC\u06AF\u06CC\u0631\u06CC \u0646\u062A\u06CC\u062C\u0647",
  TOUCH_UP: "\u062A\u0631\u0645\u06CC\u0645 \u0648 \u062A\u06A9\u0645\u06CC\u0644",
  NEXT_SESSION: "\u062C\u0644\u0633\u0647 \u0628\u0639\u062F\u06CC",
  REVIEW: "\u0646\u0638\u0631\u0633\u0646\u062C\u06CC \u0631\u0636\u0627\u06CC\u062A",
  AFTERCARE: "\u0645\u0631\u0627\u0642\u0628\u062A\u200C\u0647\u0627\u06CC \u0628\u0639\u062F \u0627\u0632 \u062F\u0631\u0645\u0627\u0646"
});
var ReminderStatus = {
  SCHEDULED: "SCHEDULED",
  SENT: "SENT",
  DONE: "DONE",
  SNOOZED: "SNOOZED",
  CANCELLED: "CANCELLED"
};
var REMINDER_STATUS_LABELS_FA = Object.freeze({
  SCHEDULED: "\u0632\u0645\u0627\u0646\u200C\u0628\u0646\u062F\u06CC\u200C\u0634\u062F\u0647",
  SENT: "\u0627\u0631\u0633\u0627\u0644 \u0634\u062F",
  DONE: "\u0627\u0646\u062C\u0627\u0645 \u0634\u062F",
  SNOOZED: "\u0628\u0647 \u062A\u0639\u0648\u06CC\u0642 \u0627\u0641\u062A\u0627\u062F",
  CANCELLED: "\u0644\u063A\u0648 \u0634\u062F"
});
var CampaignStatus = {
  DRAFT: "DRAFT",
  SCHEDULED: "SCHEDULED",
  RUNNING: "RUNNING",
  PAUSED: "PAUSED",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED"
};
var CAMPAIGN_STATUS_LABELS_FA = Object.freeze({
  DRAFT: "\u067E\u06CC\u0634\u200C\u0646\u0648\u06CC\u0633",
  SCHEDULED: "\u0632\u0645\u0627\u0646\u200C\u0628\u0646\u062F\u06CC\u200C\u0634\u062F\u0647",
  RUNNING: "\u062F\u0631 \u062D\u0627\u0644 \u0627\u0631\u0633\u0627\u0644",
  PAUSED: "\u0645\u062A\u0648\u0642\u0641\u200C\u0634\u062F\u0647",
  COMPLETED: "\u067E\u0627\u06CC\u0627\u0646\u200C\u06CC\u0627\u0641\u062A\u0647",
  CANCELLED: "\u0644\u063A\u0648 \u0634\u062F"
});
var BlogPostStatus = {
  QUEUED: "QUEUED",
  GENERATING: "GENERATING",
  DRAFT: "DRAFT",
  IN_REVIEW: "IN_REVIEW",
  PUBLISHED: "PUBLISHED",
  ARCHIVED: "ARCHIVED"
};
var BLOG_POST_STATUS_LABELS_FA = Object.freeze({
  QUEUED: "\u062F\u0631 \u0635\u0641 \u062A\u0648\u0644\u06CC\u062F",
  GENERATING: "\u062F\u0631 \u062D\u0627\u0644 \u062A\u0648\u0644\u06CC\u062F",
  DRAFT: "\u067E\u06CC\u0634\u200C\u0646\u0648\u06CC\u0633",
  IN_REVIEW: "\u062F\u0631 \u0627\u0646\u062A\u0638\u0627\u0631 \u0628\u0627\u0632\u0628\u06CC\u0646\u06CC",
  PUBLISHED: "\u0645\u0646\u062A\u0634\u0631 \u0634\u062F\u0647",
  ARCHIVED: "\u0628\u0627\u06CC\u06AF\u0627\u0646\u06CC\u200C\u0634\u062F\u0647"
});
var BlogTopicStatus = {
  QUEUED: "QUEUED",
  CLAIMED: "CLAIMED",
  GENERATING: "GENERATING",
  GENERATED: "GENERATED",
  FAILED: "FAILED",
  CANCELLED: "CANCELLED"
};
var GenerationStatus = {
  PENDING: "PENDING",
  GENERATING: "GENERATING",
  GENERATED: "GENERATED",
  IN_REVIEW: "IN_REVIEW",
  PUBLISHED: "PUBLISHED",
  FAILED: "FAILED"
};
var ModerationStatus = {
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED"
};
var MODERATION_STATUS_LABELS_FA = Object.freeze({
  PENDING: "\u062F\u0631 \u0627\u0646\u062A\u0638\u0627\u0631 \u0628\u0631\u0631\u0633\u06CC",
  APPROVED: "\u062A\u0623\u06CC\u06CC\u062F \u0634\u062F\u0647",
  REJECTED: "\u0631\u062F \u0634\u062F\u0647"
});
var SitemapType = {
  STATIC: "STATIC",
  BLOG: "BLOG",
  CLINICS: "CLINICS",
  DOCTORS: "DOCTORS",
  PROCEDURES: "PROCEDURES",
  LANDING: "LANDING"
};
function toSelectOptions(labels) {
  return Object.keys(labels).map((value) => ({ value, label: labels[value] }));
}

// src/procedures.ts
var SMS = NotificationChannel.SMS;
var aftercare = (templateKey, hour = 20) => ({
  kind: ReminderKind.AFTERCARE,
  offsetDays: 0,
  channel: SMS,
  templateKey,
  label: "\u0627\u0631\u0633\u0627\u0644 \u062F\u0633\u062A\u0648\u0631\u0627\u0644\u0639\u0645\u0644 \u0645\u0631\u0627\u0642\u0628\u062A \u0628\u0639\u062F \u0627\u0632 \u062F\u0631\u0645\u0627\u0646 \u062F\u0631 \u0647\u0645\u0627\u0646 \u0631\u0648\u0632",
  sendHourLocal: hour
});
var followUp = (offsetDays, label, templateKey) => ({
  kind: ReminderKind.FOLLOW_UP,
  offsetDays,
  channel: SMS,
  templateKey,
  label,
  sendHourLocal: 11
});
var touchUp = (offsetDays, label, templateKey) => ({
  kind: ReminderKind.TOUCH_UP,
  offsetDays,
  channel: SMS,
  templateKey,
  label,
  sendHourLocal: 11
});
var nextSession = (offsetDays, label, templateKey) => ({
  kind: ReminderKind.NEXT_SESSION,
  offsetDays,
  channel: SMS,
  templateKey,
  label,
  sendHourLocal: 11
});
var review = (offsetDays = 7) => ({
  kind: ReminderKind.REVIEW,
  offsetDays,
  channel: SMS,
  templateKey: "satisfaction_survey",
  label: "\u0627\u0631\u0633\u0627\u0644 \u0644\u06CC\u0646\u06A9 \u0646\u0638\u0631\u0633\u0646\u062C\u06CC \u0631\u0636\u0627\u06CC\u062A",
  sendHourLocal: 18
});
var PROCEDURE_CATALOG = Object.freeze([
  {
    key: "botox",
    name: "\u062A\u0632\u0631\u06CC\u0642 \u0628\u0648\u062A\u0627\u06A9\u0633",
    nameEn: "Botulinum toxin injection",
    slug: "botox",
    aliases: ["\u0628\u0648\u062A\u0627\u06A9\u0633", "\u0628\u0648\u062A\u0627\u06A9\u0633 \u0635\u0648\u0631\u062A", "\u0628\u0648\u062A\u0627\u06A9\u0633 \u067E\u06CC\u0634\u0627\u0646\u06CC", "\u062A\u0632\u0631\u06CC\u0642 \u0628\u0648\u062A\u0627\u06A9\u0633", "botox"],
    category: ProcedureCategory.INJECTABLE,
    defaultDurationMinutes: 30,
    bufferMinutes: 10,
    defaultPriceRials: 25e6,
    // Botulinum toxin's clinical effect lasts three to five months; four is the
    // interval most Iranian clinics book against and the one the touch-up
    // reminder below is measured from.
    retreatIntervalDays: 120,
    retreatIntervalLabel: "\u062D\u062F\u0648\u062F \u06F4 \u0645\u0627\u0647",
    courseSessions: 1,
    courseIntervalDays: null,
    consumesUnits: true,
    unitLabel: "\u0648\u0627\u062D\u062F",
    materialUnit: MaterialUnit.UNIT,
    materialCategory: MaterialCategory.BOTOX,
    typicalUnitsMilli: { min: 2e4, max: 6e4 },
    bodyMapType: BodyMapType.FACE_FRONT,
    requiresConsent: true,
    reminderRules: [
      aftercare("aftercare_botox"),
      // The toxin reaches full effect around day 14; that is when an
      // under-corrected area is worth topping up, and after which it is not.
      touchUp(14, "\u0628\u0631\u0631\u0633\u06CC \u0646\u062A\u06CC\u062C\u0647 \u0648 \u062A\u0631\u0645\u06CC\u0645 \u0627\u062D\u062A\u0645\u0627\u0644\u06CC\u060C \u06F2 \u0647\u0641\u062A\u0647 \u0628\u0639\u062F \u0627\u0632 \u062A\u0632\u0631\u06CC\u0642", "touchup_botox"),
      review(10),
      nextSession(120, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062A\u0632\u0631\u06CC\u0642 \u0645\u062C\u062F\u062F\u060C \u062D\u062F\u0648\u062F \u06F4 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_botox")
    ],
    aftercareTemplateKey: "aftercare_botox",
    summary: "\u062A\u0632\u0631\u06CC\u0642 \u0628\u0648\u062A\u0627\u06A9\u0633 \u0628\u0627 \u0634\u0644\u200C\u06A9\u0631\u062F\u0646 \u0645\u0648\u0642\u062A \u0639\u0636\u0644\u0627\u062A\u060C \u062E\u0637\u0648\u0637 \u0627\u062E\u0645\u060C \u067E\u06CC\u0634\u0627\u0646\u06CC \u0648 \u062F\u0648\u0631 \u0686\u0634\u0645 \u0631\u0648 \u06A9\u0645\u200C\u0631\u0646\u06AF \u0645\u06CC\u200C\u06A9\u0646\u0647. \u0646\u062A\u06CC\u062C\u0647 \u0627\u0632 \u0631\u0648\u0632 \u0633\u0648\u0645 \u062F\u06CC\u062F\u0647 \u0645\u06CC\u200C\u0634\u0647\u060C \u062F\u0631 \u0631\u0648\u0632 \u0686\u0647\u0627\u0631\u062F\u0647\u0645 \u062A\u062B\u0628\u06CC\u062A \u0645\u06CC\u200C\u0634\u0647 \u0648 \u0628\u0647\u200C\u0637\u0648\u0631 \u0645\u062A\u0648\u0633\u0637 \u0633\u0647 \u062A\u0627 \u067E\u0646\u062C \u0645\u0627\u0647 \u0628\u0627\u0642\u06CC \u0645\u06CC\u200C\u0645\u0648\u0646\u0647.",
    priceBandRials: [15e6, 45e6],
    iconKey: "syringe",
    sortOrder: 10
  },
  {
    key: "botox_hyperhidrosis",
    name: "\u0628\u0648\u062A\u0627\u06A9\u0633 \u0632\u06CC\u0631\u0628\u063A\u0644 (\u062F\u0631\u0645\u0627\u0646 \u062A\u0639\u0631\u06CC\u0642)",
    nameEn: "Botulinum toxin for axillary hyperhidrosis",
    slug: "botox-hyperhidrosis",
    aliases: ["\u0628\u0648\u062A\u0627\u06A9\u0633 \u0632\u06CC\u0631\u0628\u063A\u0644", "\u062F\u0631\u0645\u0627\u0646 \u062A\u0639\u0631\u06CC\u0642 \u0632\u06CC\u0627\u062F", "\u0647\u0627\u06CC\u067E\u0631\u0647\u06CC\u062F\u0631\u0648\u0632\u06CC\u0633", "\u0628\u0648\u062A\u0627\u06A9\u0633 \u06A9\u0641 \u062F\u0633\u062A"],
    category: ProcedureCategory.INJECTABLE,
    defaultDurationMinutes: 40,
    bufferMinutes: 10,
    defaultPriceRials: 45e6,
    // Axillary dosing is far higher than facial dosing, so the effect outlasts
    // it: six to nine months is typical, and seven is a safe default.
    retreatIntervalDays: 210,
    retreatIntervalLabel: "\u062D\u062F\u0648\u062F \u06F7 \u0645\u0627\u0647",
    courseSessions: 1,
    courseIntervalDays: null,
    consumesUnits: true,
    unitLabel: "\u0648\u0627\u062D\u062F",
    materialUnit: MaterialUnit.UNIT,
    materialCategory: MaterialCategory.BOTOX,
    typicalUnitsMilli: { min: 1e5, max: 2e5 },
    bodyMapType: BodyMapType.UPPER_BODY,
    requiresConsent: true,
    reminderRules: [
      aftercare("aftercare_botox"),
      followUp(14, "\u0628\u0631\u0631\u0633\u06CC \u06A9\u0627\u0647\u0634 \u062A\u0639\u0631\u06CC\u0642\u060C \u06F2 \u0647\u0641\u062A\u0647 \u0628\u0639\u062F \u0627\u0632 \u062A\u0632\u0631\u06CC\u0642", "followup_hyperhidrosis"),
      nextSession(210, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062A\u0632\u0631\u06CC\u0642 \u0645\u062C\u062F\u062F\u060C \u062D\u062F\u0648\u062F \u06F7 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_hyperhidrosis")
    ],
    aftercareTemplateKey: "aftercare_botox",
    summary: "\u062A\u0632\u0631\u06CC\u0642 \u0628\u0648\u062A\u0627\u06A9\u0633 \u062F\u0631 \u0632\u06CC\u0631\u0628\u063A\u0644\u060C \u062A\u0631\u0634\u062D \u063A\u062F\u062F \u0639\u0631\u0642 \u0631\u0648 \u0628\u0631\u0627\u06CC \u0686\u0646\u062F \u0645\u0627\u0647 \u0645\u062A\u0648\u0642\u0641 \u0645\u06CC\u200C\u06A9\u0646\u0647. \u0627\u062B\u0631\u0634 \u0627\u0632 \u0647\u0641\u062A\u0647 \u0627\u0648\u0644 \u0634\u0631\u0648\u0639 \u0645\u06CC\u200C\u0634\u0647 \u0648 \u0645\u0639\u0645\u0648\u0644\u0627\u064B \u0634\u0634 \u062A\u0627 \u0646\u0647 \u0645\u0627\u0647 \u062F\u0648\u0627\u0645 \u062F\u0627\u0631\u0647.",
    priceBandRials: [3e7, 7e7],
    iconKey: "droplets",
    sortOrder: 20
  },
  {
    key: "filler_lip",
    name: "\u062A\u0632\u0631\u06CC\u0642 \u0641\u06CC\u0644\u0631 \u0644\u0628",
    nameEn: "Lip filler",
    slug: "filler-lip",
    aliases: ["\u0641\u06CC\u0644\u0631 \u0644\u0628", "\u062A\u0632\u0631\u06CC\u0642 \u0698\u0644 \u0644\u0628", "\u062D\u062C\u0645\u200C\u062F\u0647\u06CC \u0644\u0628", "lip filler"],
    category: ProcedureCategory.INJECTABLE,
    defaultDurationMinutes: 45,
    bufferMinutes: 15,
    defaultPriceRials: 6e7,
    // Lip tissue is mobile and highly vascular, so hyaluronic acid breaks down
    // faster here than anywhere else on the face: 6–12 months, 9 as a default.
    retreatIntervalDays: 270,
    retreatIntervalLabel: "\u062D\u062F\u0648\u062F \u06F9 \u0645\u0627\u0647",
    courseSessions: 1,
    courseIntervalDays: null,
    consumesUnits: true,
    unitLabel: "\u0633\u06CC\u200C\u0633\u06CC",
    materialUnit: MaterialUnit.ML,
    materialCategory: MaterialCategory.FILLER,
    typicalUnitsMilli: { min: 500, max: 2e3 },
    bodyMapType: BodyMapType.FACE_FRONT,
    requiresConsent: true,
    reminderRules: [
      aftercare("aftercare_filler"),
      // Swelling and bruising settle by day 14; before that the patient is not
      // looking at the final result.
      followUp(14, "\u0628\u0631\u0631\u0633\u06CC \u0646\u062A\u06CC\u062C\u0647 \u0628\u0639\u062F \u0627\u0632 \u0641\u0631\u0648\u06A9\u0634\u200C\u06A9\u0631\u062F\u0646 \u062A\u0648\u0631\u0645\u060C \u06F2 \u0647\u0641\u062A\u0647 \u0628\u0639\u062F", "followup_filler"),
      review(14),
      nextSession(270, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062A\u0632\u0631\u06CC\u0642 \u0645\u062C\u062F\u062F \u0641\u06CC\u0644\u0631 \u0644\u0628\u060C \u062D\u062F\u0648\u062F \u06F9 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_filler")
    ],
    aftercareTemplateKey: "aftercare_filler",
    summary: "\u062F\u0631 \u062A\u0632\u0631\u06CC\u0642 \u0641\u06CC\u0644\u0631 \u0644\u0628\u060C \u0627\u0632 \u0698\u0644 \u0647\u06CC\u0627\u0644\u0648\u0631\u0648\u0646\u06CC\u06A9\u200C\u0627\u0633\u06CC\u062F \u0628\u0631\u0627\u06CC \u0627\u0635\u0644\u0627\u062D \u062D\u062C\u0645\u060C \u062A\u0642\u0627\u0631\u0646 \u0648 \u062E\u0637 \u0644\u0628 \u0627\u0633\u062A\u0641\u0627\u062F\u0647 \u0645\u06CC\u200C\u0634\u0647. \u062A\u0648\u0631\u0645 \u0627\u0648\u0644\u06CC\u0647 \u062F\u0648 \u062A\u0627 \u0633\u0647 \u0631\u0648\u0632 \u0637\u0648\u0644 \u0645\u06CC\u200C\u06A9\u0634\u0647 \u0648 \u0646\u062A\u06CC\u062C\u0647 \u0646\u0647\u0627\u06CC\u06CC \u0628\u0639\u062F \u0627\u0632 \u062F\u0648 \u0647\u0641\u062A\u0647 \u062F\u06CC\u062F\u0647 \u0645\u06CC\u200C\u0634\u0647.",
    priceBandRials: [4e7, 12e7],
    iconKey: "smile",
    sortOrder: 30
  },
  {
    key: "filler_cheek",
    name: "\u062A\u0632\u0631\u06CC\u0642 \u0641\u06CC\u0644\u0631 \u06AF\u0648\u0646\u0647",
    nameEn: "Cheek filler",
    slug: "filler-cheek",
    aliases: ["\u0641\u06CC\u0644\u0631 \u06AF\u0648\u0646\u0647", "\u062D\u062C\u0645\u200C\u062F\u0647\u06CC \u06AF\u0648\u0646\u0647", "\u062A\u0632\u0631\u06CC\u0642 \u0698\u0644 \u06AF\u0648\u0646\u0647", "cheek filler"],
    category: ProcedureCategory.INJECTABLE,
    defaultDurationMinutes: 45,
    bufferMinutes: 15,
    defaultPriceRials: 7e7,
    // Deep, structural placement on bone with little movement — the longest-
    // lasting filler indication, commonly 12–18 months.
    retreatIntervalDays: 420,
    retreatIntervalLabel: "\u062D\u062F\u0648\u062F \u06F1\u06F4 \u0645\u0627\u0647",
    courseSessions: 1,
    courseIntervalDays: null,
    consumesUnits: true,
    unitLabel: "\u0633\u06CC\u200C\u0633\u06CC",
    materialUnit: MaterialUnit.ML,
    materialCategory: MaterialCategory.FILLER,
    typicalUnitsMilli: { min: 1e3, max: 4e3 },
    bodyMapType: BodyMapType.FACE_FRONT,
    requiresConsent: true,
    reminderRules: [
      aftercare("aftercare_filler"),
      followUp(14, "\u0628\u0631\u0631\u0633\u06CC \u0646\u062A\u06CC\u062C\u0647 \u0628\u0639\u062F \u0627\u0632 \u0641\u0631\u0648\u06A9\u0634\u200C\u06A9\u0631\u062F\u0646 \u062A\u0648\u0631\u0645\u060C \u06F2 \u0647\u0641\u062A\u0647 \u0628\u0639\u062F", "followup_filler"),
      review(14),
      nextSession(420, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062A\u0632\u0631\u06CC\u0642 \u0645\u062C\u062F\u062F \u0641\u06CC\u0644\u0631 \u06AF\u0648\u0646\u0647\u060C \u062D\u062F\u0648\u062F \u06F1\u06F4 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_filler")
    ],
    aftercareTemplateKey: "aftercare_filler",
    summary: "\u0641\u06CC\u0644\u0631 \u06AF\u0648\u0646\u0647 \u062D\u062C\u0645 \u0627\u0632 \u062F\u0633\u062A \u0631\u0641\u062A\u0647 \u0631\u0648 \u0628\u0631\u0645\u06CC\u200C\u06AF\u0631\u062F\u0648\u0646\u0647 \u0648 \u0628\u0631\u062C\u0633\u062A\u06AF\u06CC \u06AF\u0648\u0646\u0647 \u0648 \u0632\u0627\u0648\u06CC\u0647 \u0635\u0648\u0631\u062A \u0631\u0648 \u0628\u0627\u0632\u0633\u0627\u0632\u06CC \u0645\u06CC\u200C\u06A9\u0646\u0647. \u0686\u0648\u0646 \u062A\u0632\u0631\u06CC\u0642 \u0639\u0645\u06CC\u0642\u200C\u062A\u0631\u0647 \u0648 \u0627\u06CC\u0646 \u0646\u0627\u062D\u06CC\u0647 \u062D\u0631\u06A9\u062A \u06A9\u0645\u062A\u0631\u06CC \u062F\u0627\u0631\u0647\u060C \u0645\u0627\u0646\u062F\u06AF\u0627\u0631\u06CC \u0627\u0648\u0646 \u0627\u0632 \u0641\u06CC\u0644\u0631 \u0644\u0628 \u0628\u06CC\u0634\u062A\u0631\u0647.",
    priceBandRials: [5e7, 15e7],
    iconKey: "user",
    sortOrder: 40
  },
  {
    key: "filler_chin",
    name: "\u062A\u0632\u0631\u06CC\u0642 \u0641\u06CC\u0644\u0631 \u0686\u0627\u0646\u0647",
    nameEn: "Chin filler",
    slug: "filler-chin",
    aliases: ["\u0641\u06CC\u0644\u0631 \u0686\u0627\u0646\u0647", "\u062D\u062C\u0645\u200C\u062F\u0647\u06CC \u0686\u0627\u0646\u0647", "\u0627\u0635\u0644\u0627\u062D \u0686\u0627\u0646\u0647 \u0628\u062F\u0648\u0646 \u062C\u0631\u0627\u062D\u06CC", "chin filler"],
    category: ProcedureCategory.INJECTABLE,
    defaultDurationMinutes: 40,
    bufferMinutes: 15,
    defaultPriceRials: 65e6,
    retreatIntervalDays: 420,
    retreatIntervalLabel: "\u062D\u062F\u0648\u062F \u06F1\u06F4 \u0645\u0627\u0647",
    courseSessions: 1,
    courseIntervalDays: null,
    consumesUnits: true,
    unitLabel: "\u0633\u06CC\u200C\u0633\u06CC",
    materialUnit: MaterialUnit.ML,
    materialCategory: MaterialCategory.FILLER,
    typicalUnitsMilli: { min: 1e3, max: 3e3 },
    bodyMapType: BodyMapType.FACE_SIDE,
    requiresConsent: true,
    reminderRules: [
      aftercare("aftercare_filler"),
      followUp(14, "\u0628\u0631\u0631\u0633\u06CC \u0646\u062A\u06CC\u062C\u0647 \u0628\u0639\u062F \u0627\u0632 \u0641\u0631\u0648\u06A9\u0634\u200C\u06A9\u0631\u062F\u0646 \u062A\u0648\u0631\u0645\u060C \u06F2 \u0647\u0641\u062A\u0647 \u0628\u0639\u062F", "followup_filler"),
      nextSession(420, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062A\u0632\u0631\u06CC\u0642 \u0645\u062C\u062F\u062F \u0641\u06CC\u0644\u0631 \u0686\u0627\u0646\u0647\u060C \u062D\u062F\u0648\u062F \u06F1\u06F4 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_filler")
    ],
    aftercareTemplateKey: "aftercare_filler",
    summary: "\u0641\u06CC\u0644\u0631 \u0686\u0627\u0646\u0647 \u0637\u0648\u0644 \u0648 \u0628\u0631\u062C\u0633\u062A\u06AF\u06CC \u0686\u0627\u0646\u0647 \u0631\u0648 \u0627\u0635\u0644\u0627\u062D \u0645\u06CC\u200C\u06A9\u0646\u0647 \u0648 \u0646\u06CC\u0645\u200C\u0631\u062E \u0635\u0648\u0631\u062A \u0631\u0648 \u0645\u062A\u0639\u0627\u062F\u0644\u200C\u062A\u0631 \u0646\u0634\u0648\u0646 \u0645\u06CC\u200C\u062F\u0647. \u0627\u06CC\u0646 \u0631\u0648\u0634\u060C \u062C\u0627\u06CC\u06AF\u0632\u06CC\u0646\u06CC \u0628\u0627 \u062A\u0647\u0627\u062C\u0645 \u06A9\u0645\u062A\u0631 \u0628\u0631\u0627\u06CC \u062C\u0631\u0627\u062D\u06CC \u0627\u0635\u0644\u0627\u062D \u0686\u0627\u0646\u0647 (\u0698\u0646\u06CC\u0648\u067E\u0644\u0627\u0633\u062A\u06CC) \u0645\u062D\u0633\u0648\u0628 \u0645\u06CC\u200C\u0634\u0647.",
    priceBandRials: [45e6, 13e7],
    iconKey: "user",
    sortOrder: 50
  },
  {
    key: "filler_under_eye",
    name: "\u062A\u0632\u0631\u06CC\u0642 \u0641\u06CC\u0644\u0631 \u0632\u06CC\u0631\u0686\u0634\u0645",
    nameEn: "Tear trough filler",
    slug: "filler-under-eye",
    aliases: ["\u0641\u06CC\u0644\u0631 \u0632\u06CC\u0631 \u0686\u0634\u0645", "\u0641\u06CC\u0644\u0631 \u06AF\u0648\u062F\u06CC \u0632\u06CC\u0631 \u0686\u0634\u0645", "tear trough", "\u0631\u0641\u0639 \u06AF\u0648\u062F\u06CC \u0632\u06CC\u0631\u0686\u0634\u0645"],
    category: ProcedureCategory.INJECTABLE,
    defaultDurationMinutes: 45,
    bufferMinutes: 15,
    defaultPriceRials: 75e6,
    // The tear trough is a low-movement, low-metabolism area; the filler often
    // outlasts a year, but the follow-up window is longer because oedema here
    // settles slowly.
    retreatIntervalDays: 330,
    retreatIntervalLabel: "\u062D\u062F\u0648\u062F \u06F1\u06F1 \u0645\u0627\u0647",
    courseSessions: 1,
    courseIntervalDays: null,
    consumesUnits: true,
    unitLabel: "\u0633\u06CC\u200C\u0633\u06CC",
    materialUnit: MaterialUnit.ML,
    materialCategory: MaterialCategory.FILLER,
    typicalUnitsMilli: { min: 500, max: 2e3 },
    bodyMapType: BodyMapType.FACE_FRONT,
    requiresConsent: true,
    reminderRules: [
      aftercare("aftercare_filler"),
      // Periorbital swelling can take three weeks to settle — reviewing at two
      // would produce a needless top-up.
      followUp(21, "\u0628\u0631\u0631\u0633\u06CC \u0646\u062A\u06CC\u062C\u0647\u060C \u06F3 \u0647\u0641\u062A\u0647 \u0628\u0639\u062F \u0627\u0632 \u062A\u0632\u0631\u06CC\u0642", "followup_filler"),
      nextSession(330, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062A\u0632\u0631\u06CC\u0642 \u0645\u062C\u062F\u062F\u060C \u062D\u062F\u0648\u062F \u06F1\u06F1 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_filler")
    ],
    aftercareTemplateKey: "aftercare_filler",
    summary: "\u062A\u0632\u0631\u06CC\u0642 \u0641\u06CC\u0644\u0631 \u0632\u06CC\u0631 \u0686\u0634\u0645\u060C \u06AF\u0648\u062F\u06CC \u0632\u06CC\u0631 \u067E\u0644\u06A9 \u0631\u0648 \u067E\u0631 \u0645\u06CC\u200C\u06A9\u0646\u0647 \u0648 \u0633\u0627\u06CC\u0647 \u0648 \u0638\u0627\u0647\u0631 \u062E\u0633\u062A\u06AF\u06CC \u0635\u0648\u0631\u062A \u0631\u0648 \u06A9\u0645\u062A\u0631 \u0645\u06CC\u200C\u06A9\u0646\u0647. \u0632\u06CC\u0631 \u0686\u0634\u0645 \u0646\u0627\u062D\u06CC\u0647 \u062D\u0633\u0627\u0633\u06CC\u0647 \u0648 \u062A\u0648\u0631\u0645 \u0627\u0648\u0646 \u062F\u06CC\u0631\u062A\u0631 \u0627\u0632 \u0642\u0633\u0645\u062A\u200C\u0647\u0627\u06CC \u062F\u06CC\u06AF\u0647 \u06A9\u0645 \u0645\u06CC\u200C\u0634\u0647.",
    priceBandRials: [5e7, 14e7],
    iconKey: "eye",
    sortOrder: 60
  },
  {
    key: "gel_injection",
    name: "\u062A\u0632\u0631\u06CC\u0642 \u0698\u0644 \u0635\u0648\u0631\u062A",
    nameEn: "Dermal filler (general)",
    slug: "gel-injection",
    aliases: ["\u062A\u0632\u0631\u06CC\u0642 \u0698\u0644", "\u0698\u0644 \u0635\u0648\u0631\u062A", "\u0641\u06CC\u0644\u0631 \u0635\u0648\u0631\u062A", "dermal filler"],
    category: ProcedureCategory.INJECTABLE,
    defaultDurationMinutes: 45,
    bufferMinutes: 15,
    defaultPriceRials: 6e7,
    retreatIntervalDays: 365,
    retreatIntervalLabel: "\u062D\u062F\u0648\u062F \u06F1\u06F2 \u0645\u0627\u0647",
    courseSessions: 1,
    courseIntervalDays: null,
    consumesUnits: true,
    unitLabel: "\u0633\u06CC\u200C\u0633\u06CC",
    materialUnit: MaterialUnit.ML,
    materialCategory: MaterialCategory.FILLER,
    typicalUnitsMilli: { min: 1e3, max: 3e3 },
    bodyMapType: BodyMapType.FACE_FRONT,
    requiresConsent: true,
    reminderRules: [
      aftercare("aftercare_filler"),
      followUp(14, "\u0628\u0631\u0631\u0633\u06CC \u0646\u062A\u06CC\u062C\u0647 \u0628\u0639\u062F \u0627\u0632 \u0641\u0631\u0648\u06A9\u0634\u200C\u06A9\u0631\u062F\u0646 \u062A\u0648\u0631\u0645\u060C \u06F2 \u0647\u0641\u062A\u0647 \u0628\u0639\u062F", "followup_filler"),
      nextSession(365, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062A\u0632\u0631\u06CC\u0642 \u0645\u062C\u062F\u062F \u0698\u0644\u060C \u062D\u062F\u0648\u062F \u06F1\u06F2 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_filler")
    ],
    aftercareTemplateKey: "aftercare_filler",
    summary: "\u0698\u0644 \u0647\u06CC\u0627\u0644\u0648\u0631\u0648\u0646\u06CC\u06A9\u200C\u0627\u0633\u06CC\u062F \u0628\u0631\u0627\u06CC \u067E\u0631 \u06A9\u0631\u062F\u0646 \u0686\u06CC\u0646\u200C\u0647\u0627 \u0648 \u062E\u0637 \u062E\u0646\u062F\u0647 \u0648 \u0628\u0631\u06AF\u0631\u062F\u0648\u0646\u062F\u0646 \u062D\u062C\u0645 \u0642\u0633\u0645\u062A\u200C\u0647\u0627\u06CC \u0645\u062E\u062A\u0644\u0641 \u0635\u0648\u0631\u062A \u062A\u0632\u0631\u06CC\u0642 \u0645\u06CC\u200C\u0634\u0647. \u0645\u0627\u0646\u062F\u06AF\u0627\u0631\u06CC \u0627\u0648\u0646\u060C \u0628\u0633\u062A\u0647 \u0628\u0647 \u0646\u0627\u062D\u06CC\u0647 \u062A\u0632\u0631\u06CC\u0642 \u0648 \u0646\u0648\u0639 \u0698\u0644\u060C \u0628\u06CC\u0646 \u06F9 \u062A\u0627 \u06F1\u06F8 \u0645\u0627\u0647\u0647.",
    priceBandRials: [4e7, 13e7],
    iconKey: "syringe",
    sortOrder: 70
  },
  {
    key: "gel_rhinoplasty",
    name: "\u062A\u0632\u0631\u06CC\u0642 \u0698\u0644 \u0628\u06CC\u0646\u06CC",
    nameEn: "Non-surgical rhinoplasty",
    slug: "gel-rhinoplasty",
    aliases: ["\u0698\u0644 \u0628\u06CC\u0646\u06CC", "\u0631\u06CC\u0646\u0648\u067E\u0644\u0627\u0633\u062A\u06CC \u0628\u062F\u0648\u0646 \u062C\u0631\u0627\u062D\u06CC", "\u0627\u0635\u0644\u0627\u062D \u0642\u0648\u0632 \u0628\u06CC\u0646\u06CC \u0628\u0627 \u0698\u0644", "liquid nose job"],
    category: ProcedureCategory.INJECTABLE,
    defaultDurationMinutes: 40,
    bufferMinutes: 15,
    defaultPriceRials: 8e7,
    retreatIntervalDays: 365,
    retreatIntervalLabel: "\u062D\u062F\u0648\u062F \u06F1\u06F2 \u0645\u0627\u0647",
    courseSessions: 1,
    courseIntervalDays: null,
    consumesUnits: true,
    unitLabel: "\u0633\u06CC\u200C\u0633\u06CC",
    materialUnit: MaterialUnit.ML,
    materialCategory: MaterialCategory.FILLER,
    typicalUnitsMilli: { min: 300, max: 1e3 },
    bodyMapType: BodyMapType.FACE_SIDE,
    requiresConsent: true,
    reminderRules: [
      aftercare("aftercare_filler"),
      followUp(14, "\u0628\u0631\u0631\u0633\u06CC \u0646\u062A\u06CC\u062C\u0647\u060C \u06F2 \u0647\u0641\u062A\u0647 \u0628\u0639\u062F \u0627\u0632 \u062A\u0632\u0631\u06CC\u0642", "followup_filler"),
      nextSession(365, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062A\u0632\u0631\u06CC\u0642 \u0645\u062C\u062F\u062F\u060C \u062D\u062F\u0648\u062F \u06F1\u06F2 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_filler")
    ],
    aftercareTemplateKey: "aftercare_filler",
    summary: "\u0628\u0627 \u062A\u0632\u0631\u06CC\u0642 \u0645\u0642\u062F\u0627\u0631 \u06A9\u0645\u06CC \u0698\u0644 \u0645\u06CC\u200C\u0634\u0647 \u0642\u0648\u0632 \u0628\u06CC\u0646\u06CC \u0631\u0648 \u067E\u0648\u0634\u0648\u0646\u062F \u0648 \u0646\u0648\u06A9 \u0628\u06CC\u0646\u06CC \u0631\u0648 \u06A9\u0645\u06CC \u0628\u0627\u0644\u0627 \u0622\u0648\u0631\u062F. \u0627\u06CC\u0646 \u0631\u0648\u0634 \u0628\u0647 \u062C\u0631\u0627\u062D\u06CC \u0648 \u062F\u0648\u0631\u0647 \u0646\u0642\u0627\u0647\u062A \u0646\u06CC\u0627\u0632 \u0646\u062F\u0627\u0631\u0647 \u0648 \u0628\u0631\u0627\u06CC \u0627\u0635\u0644\u0627\u062D\u200C\u0647\u0627\u06CC \u062C\u0632\u0626\u06CC \u0645\u0646\u0627\u0633\u0628\u0647\u061B \u0628\u06CC\u0646\u06CC \u0631\u0648 \u06A9\u0648\u0686\u06A9 \u0646\u0645\u06CC\u200C\u06A9\u0646\u0647.",
    priceBandRials: [5e7, 15e7],
    iconKey: "triangle",
    sortOrder: 80
  },
  {
    key: "fat_transfer",
    name: "\u062A\u0632\u0631\u06CC\u0642 \u0686\u0631\u0628\u06CC \u0635\u0648\u0631\u062A",
    nameEn: "Facial fat transfer",
    slug: "fat-transfer",
    aliases: ["\u062A\u0632\u0631\u06CC\u0642 \u0686\u0631\u0628\u06CC", "\u0644\u06CC\u067E\u0648\u0641\u06CC\u0644\u06CC\u0646\u06AF", "\u0686\u0631\u0628\u06CC \u0635\u0648\u0631\u062A", "fat grafting"],
    category: ProcedureCategory.SURGICAL,
    defaultDurationMinutes: 120,
    bufferMinutes: 30,
    defaultPriceRials: 35e7,
    // Grafted fat that survives the first three months is permanent; the
    // "re-treatment" interval is really "assess whether a second graft is
    // needed", which is a year out.
    retreatIntervalDays: 365,
    retreatIntervalLabel: "\u062F\u0631 \u0635\u0648\u0631\u062A \u0646\u06CC\u0627\u0632\u060C \u0628\u0639\u062F \u0627\u0632 \u06CC\u06A9 \u0633\u0627\u0644",
    courseSessions: 1,
    courseIntervalDays: null,
    consumesUnits: false,
    unitLabel: "\u0633\u06CC\u200C\u0633\u06CC",
    materialUnit: MaterialUnit.ML,
    materialCategory: MaterialCategory.CONSUMABLE,
    typicalUnitsMilli: null,
    bodyMapType: BodyMapType.FACE_FRONT,
    requiresConsent: true,
    reminderRules: [
      aftercare("aftercare_fat_transfer"),
      followUp(7, "\u0648\u06CC\u0632\u06CC\u062A \u06A9\u0646\u062A\u0631\u0644\u060C \u06CC\u06A9 \u0647\u0641\u062A\u0647 \u0628\u0639\u062F \u0627\u0632 \u0639\u0645\u0644", "followup_postop"),
      // Graft take is only judgeable after the resorption phase ends, ~3 months.
      followUp(90, "\u0628\u0631\u0631\u0633\u06CC \u0645\u06CC\u0632\u0627\u0646 \u0645\u0627\u0646\u062F\u06AF\u0627\u0631\u06CC \u0686\u0631\u0628\u06CC\u060C \u06F3 \u0645\u0627\u0647 \u0628\u0639\u062F", "followup_fat_transfer"),
      review(30)
    ],
    aftercareTemplateKey: "aftercare_fat_transfer",
    summary: "\u062F\u0631 \u0627\u06CC\u0646 \u0631\u0648\u0634\u060C \u0686\u0631\u0628\u06CC \u0627\u0632 \u0646\u0627\u062D\u06CC\u0647\u200C\u0627\u06CC \u0645\u062B\u0644 \u0634\u06A9\u0645 \u06CC\u0627 \u067E\u0647\u0644\u0648 \u0628\u0631\u062F\u0627\u0634\u062A\u0647 \u0645\u06CC\u200C\u0634\u0647 \u0648 \u0628\u0639\u062F \u0627\u0632 \u0622\u0645\u0627\u062F\u0647\u200C\u0633\u0627\u0632\u06CC\u060C \u0628\u0647 \u0635\u0648\u0631\u062A \u062A\u0632\u0631\u06CC\u0642 \u0645\u06CC\u200C\u0634\u0647. \u0628\u062E\u0634\u06CC \u0627\u0632 \u0686\u0631\u0628\u06CC \u062F\u0631 \u0633\u0647 \u0645\u0627\u0647 \u0627\u0648\u0644 \u062C\u0630\u0628 \u0645\u06CC\u200C\u0634\u0647 \u0648 \u0628\u0627\u0642\u06CC \u0627\u0648\u0646 \u0645\u0627\u0646\u062F\u06AF\u0627\u0631\u0647.",
    priceBandRials: [2e8, 6e8],
    iconKey: "refresh-cw",
    sortOrder: 90
  },
  {
    key: "thread_lift",
    name: "\u0644\u06CC\u0641\u062A \u0628\u0627 \u0646\u062E",
    nameEn: "Thread lift",
    slug: "thread-lift",
    aliases: ["\u0646\u062E \u0644\u06CC\u0641\u062A", "\u0644\u06CC\u0641\u062A \u0635\u0648\u0631\u062A \u0628\u0627 \u0646\u062E", "\u0646\u062E PDO", "thread lift"],
    category: ProcedureCategory.INJECTABLE,
    defaultDurationMinutes: 75,
    bufferMinutes: 20,
    defaultPriceRials: 2e8,
    // PDO threads dissolve in about six months but the collagen scaffold they
    // trigger holds the result to roughly a year.
    retreatIntervalDays: 365,
    retreatIntervalLabel: "\u062D\u062F\u0648\u062F \u06F1\u06F2 \u0645\u0627\u0647",
    courseSessions: 1,
    courseIntervalDays: null,
    consumesUnits: true,
    unitLabel: "\u0639\u062F\u062F",
    materialUnit: MaterialUnit.PIECE,
    materialCategory: MaterialCategory.THREAD,
    typicalUnitsMilli: { min: 4e3, max: 16e3 },
    bodyMapType: BodyMapType.FACE_SIDE,
    requiresConsent: true,
    reminderRules: [
      aftercare("aftercare_thread"),
      followUp(7, "\u0648\u06CC\u0632\u06CC\u062A \u06A9\u0646\u062A\u0631\u0644\u060C \u06CC\u06A9 \u0647\u0641\u062A\u0647 \u0628\u0639\u062F \u0627\u0632 \u0644\u06CC\u0641\u062A", "followup_postop"),
      followUp(30, "\u0628\u0631\u0631\u0633\u06CC \u0646\u062A\u06CC\u062C\u0647 \u0646\u0647\u0627\u06CC\u06CC\u060C \u06CC\u06A9 \u0645\u0627\u0647 \u0628\u0639\u062F", "followup_thread"),
      nextSession(365, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062A\u06A9\u0631\u0627\u0631 \u0644\u06CC\u0641\u062A\u060C \u062D\u062F\u0648\u062F \u06F1\u06F2 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_thread")
    ],
    aftercareTemplateKey: "aftercare_thread",
    summary: "\u062F\u0631 \u0644\u06CC\u0641\u062A \u0628\u0627 \u0646\u062E\u060C \u0646\u062E\u200C\u0647\u0627\u06CC \u0642\u0627\u0628\u0644 \u062C\u0630\u0628 \u0632\u06CC\u0631 \u067E\u0648\u0633\u062A \u0642\u0631\u0627\u0631 \u0645\u06CC\u200C\u06AF\u06CC\u0631\u0646 \u0648 \u0628\u0627\u0641\u062A \u0627\u0641\u062A\u0627\u062F\u0647 \u06AF\u0648\u0646\u0647\u060C \u0641\u06A9 \u0648 \u0627\u0628\u0631\u0648 \u0631\u0648 \u0628\u0627\u0644\u0627 \u0645\u06CC\u200C\u06A9\u0634\u0646. \u0627\u06CC\u0646 \u0646\u062E\u200C\u0647\u0627\u060C \u0639\u0644\u0627\u0648\u0647 \u0628\u0631 \u0628\u0627\u0644\u0627 \u06A9\u0634\u06CC\u062F\u0646 \u0628\u0627\u0641\u062A\u060C \u062A\u0648\u0644\u06CC\u062F \u06A9\u0644\u0627\u0698\u0646 \u0631\u0648 \u0647\u0645 \u062A\u062D\u0631\u06CC\u06A9 \u0645\u06CC\u200C\u06A9\u0646\u0646.",
    priceBandRials: [12e7, 4e8],
    iconKey: "arrow-up",
    sortOrder: 100
  },
  {
    key: "prp_hair",
    name: "PRP \u0645\u0648",
    nameEn: "PRP for hair loss",
    slug: "prp-hair",
    aliases: ["\u067E\u06CC \u0622\u0631 \u067E\u06CC \u0645\u0648", "PRP \u0645\u0648", "\u062F\u0631\u0645\u0627\u0646 \u0631\u06CC\u0632\u0634 \u0645\u0648 \u0628\u0627 PRP", "\u067E\u0644\u0627\u0633\u0645\u0627\u06CC \u063A\u0646\u06CC \u0627\u0632 \u067E\u0644\u0627\u06A9\u062A"],
    category: ProcedureCategory.HAIR,
    defaultDurationMinutes: 60,
    bufferMinutes: 15,
    defaultPriceRials: 35e6,
    retreatIntervalDays: 180,
    retreatIntervalLabel: "\u062C\u0644\u0633\u0647 \u0646\u06AF\u0647\u062F\u0627\u0631\u0646\u062F\u0647 \u0647\u0631 \u06F6 \u0645\u0627\u0647",
    // Three to four sessions a month apart is the standard induction protocol;
    // the growth cycle needs that spacing to respond.
    courseSessions: 4,
    courseIntervalDays: 30,
    consumesUnits: false,
    unitLabel: null,
    materialUnit: MaterialUnit.VIAL,
    materialCategory: MaterialCategory.PRP_KIT,
    typicalUnitsMilli: { min: 1e3, max: 2e3 },
    bodyMapType: BodyMapType.SCALP,
    requiresConsent: true,
    reminderRules: [
      aftercare("aftercare_prp"),
      nextSession(30, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062C\u0644\u0633\u0647 \u0628\u0639\u062F\u06CC \u062F\u0648\u0631\u0647 PRP\u060C \u06CC\u06A9 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_course_session"),
      // Visible regrowth lags the injections by a full hair cycle.
      followUp(90, "\u0627\u0631\u0632\u06CC\u0627\u0628\u06CC \u0646\u062A\u06CC\u062C\u0647 \u062F\u0648\u0631\u0647\u060C \u06F3 \u0645\u0627\u0647 \u0628\u0639\u062F \u0627\u0632 \u0634\u0631\u0648\u0639", "followup_prp_hair"),
      nextSession(180, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062C\u0644\u0633\u0647 \u0646\u06AF\u0647\u062F\u0627\u0631\u0646\u062F\u0647\u060C \u06F6 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_prp_maintenance")
    ],
    aftercareTemplateKey: "aftercare_prp",
    summary: "\u062F\u0631 PRP \u0645\u0648\u060C \u067E\u0644\u0627\u0633\u0645\u0627\u06CC \u063A\u0646\u06CC \u0627\u0632 \u067E\u0644\u0627\u06A9\u062A \u0631\u0648 \u0627\u0632 \u062E\u0648\u0646 \u062E\u0648\u062F \u0641\u0631\u062F \u062C\u062F\u0627 \u0645\u06CC\u200C\u06A9\u0646\u0646 \u0648 \u0628\u0631\u0627\u06CC \u062A\u0642\u0648\u06CC\u062A \u0641\u0648\u0644\u06CC\u06A9\u0648\u0644\u200C\u0647\u0627\u06CC \u0636\u0639\u06CC\u0641 \u0645\u0648 \u0628\u0647 \u067E\u0648\u0633\u062A \u0633\u0631 \u062A\u0632\u0631\u06CC\u0642 \u0645\u06CC\u200C\u06A9\u0646\u0646. \u062F\u0648\u0631\u0647 \u0627\u0633\u062A\u0627\u0646\u062F\u0627\u0631\u062F\u060C \u0633\u0647 \u062A\u0627 \u0686\u0647\u0627\u0631 \u062C\u0644\u0633\u0647 \u0628\u0627 \u0641\u0627\u0635\u0644\u0647 \u06CC\u06A9 \u0645\u0627\u0647\u0647.",
    priceBandRials: [2e7, 6e7],
    iconKey: "sparkles",
    sortOrder: 110
  },
  {
    key: "prp_face",
    name: "PRP \u0635\u0648\u0631\u062A",
    nameEn: "PRP facial",
    slug: "prp-face",
    aliases: ["\u067E\u06CC \u0622\u0631 \u067E\u06CC \u0635\u0648\u0631\u062A", "PRP \u067E\u0648\u0633\u062A", "\u062C\u0648\u0627\u0646\u200C\u0633\u0627\u0632\u06CC \u0628\u0627 PRP", "vampire facial"],
    category: ProcedureCategory.SKIN,
    defaultDurationMinutes: 60,
    bufferMinutes: 15,
    defaultPriceRials: 35e6,
    retreatIntervalDays: 180,
    retreatIntervalLabel: "\u062C\u0644\u0633\u0647 \u0646\u06AF\u0647\u062F\u0627\u0631\u0646\u062F\u0647 \u0647\u0631 \u06F6 \u0645\u0627\u0647",
    courseSessions: 3,
    courseIntervalDays: 30,
    consumesUnits: false,
    unitLabel: null,
    materialUnit: MaterialUnit.VIAL,
    materialCategory: MaterialCategory.PRP_KIT,
    typicalUnitsMilli: { min: 1e3, max: 2e3 },
    bodyMapType: BodyMapType.FACE_FRONT,
    requiresConsent: true,
    reminderRules: [
      aftercare("aftercare_prp"),
      nextSession(30, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062C\u0644\u0633\u0647 \u0628\u0639\u062F\u06CC \u062F\u0648\u0631\u0647 PRP\u060C \u06CC\u06A9 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_course_session"),
      review(14),
      nextSession(180, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062C\u0644\u0633\u0647 \u0646\u06AF\u0647\u062F\u0627\u0631\u0646\u062F\u0647\u060C \u06F6 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_prp_maintenance")
    ],
    aftercareTemplateKey: "aftercare_prp",
    summary: "PRP \u0635\u0648\u0631\u062A \u0628\u0627 \u062A\u0632\u0631\u06CC\u0642 \u067E\u0644\u0627\u0633\u0645\u0627\u06CC \u062E\u0648\u062F\u0650 \u0641\u0631\u062F\u060C \u062A\u0648\u0644\u06CC\u062F \u06A9\u0644\u0627\u0698\u0646 \u0631\u0648 \u062A\u062D\u0631\u06CC\u06A9 \u0645\u06CC\u200C\u06A9\u0646\u0647 \u0648 \u0628\u0627\u0641\u062A \u0648 \u0631\u0648\u0634\u0646\u06CC \u067E\u0648\u0633\u062A \u0631\u0648 \u0628\u0647\u0628\u0648\u062F \u0645\u06CC\u200C\u062F\u0647. \u0645\u0639\u0645\u0648\u0644\u0627\u064B \u0633\u0647 \u062C\u0644\u0633\u0647 \u0628\u0627 \u0641\u0627\u0635\u0644\u0647 \u06CC\u06A9 \u0645\u0627\u0647 \u062A\u0648\u0635\u06CC\u0647 \u0645\u06CC\u200C\u0634\u0647.",
    priceBandRials: [2e7, 6e7],
    iconKey: "sparkles",
    sortOrder: 120
  },
  {
    key: "meso_hair",
    name: "\u0645\u0632\u0648\u062A\u0631\u0627\u067E\u06CC \u0645\u0648",
    nameEn: "Hair mesotherapy",
    slug: "meso-hair",
    aliases: ["\u0645\u0632\u0648\u062A\u0631\u0627\u067E\u06CC \u0645\u0648", "\u0645\u0632\u0648 \u0645\u0648", "\u062F\u0631\u0645\u0627\u0646 \u0631\u06CC\u0632\u0634 \u0645\u0648", "hair mesotherapy"],
    category: ProcedureCategory.HAIR,
    defaultDurationMinutes: 45,
    bufferMinutes: 10,
    defaultPriceRials: 2e7,
    retreatIntervalDays: 120,
    retreatIntervalLabel: "\u062F\u0648\u0631\u0647 \u0646\u06AF\u0647\u062F\u0627\u0631\u0646\u062F\u0647 \u0647\u0631 \u06F4 \u0645\u0627\u0647",
    // Mesotherapy cocktails are dosed more often and more lightly than PRP:
    // six sessions at two-week intervals is the common Iranian protocol.
    courseSessions: 6,
    courseIntervalDays: 14,
    consumesUnits: true,
    unitLabel: "\u0633\u06CC\u200C\u0633\u06CC",
    materialUnit: MaterialUnit.ML,
    materialCategory: MaterialCategory.MESO,
    typicalUnitsMilli: { min: 2e3, max: 5e3 },
    bodyMapType: BodyMapType.SCALP,
    requiresConsent: false,
    reminderRules: [
      aftercare("aftercare_meso"),
      nextSession(14, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062C\u0644\u0633\u0647 \u0628\u0639\u062F\u06CC \u0645\u0632\u0648\u062A\u0631\u0627\u067E\u06CC\u060C \u06F2 \u0647\u0641\u062A\u0647 \u0628\u0639\u062F", "next_course_session"),
      followUp(90, "\u0627\u0631\u0632\u06CC\u0627\u0628\u06CC \u0646\u062A\u06CC\u062C\u0647 \u062F\u0648\u0631\u0647\u060C \u06F3 \u0645\u0627\u0647 \u0628\u0639\u062F \u0627\u0632 \u0634\u0631\u0648\u0639", "followup_meso_hair"),
      nextSession(120, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062F\u0648\u0631\u0647 \u0646\u06AF\u0647\u062F\u0627\u0631\u0646\u062F\u0647\u060C \u06F4 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_meso_maintenance")
    ],
    aftercareTemplateKey: "aftercare_meso",
    summary: "\u062F\u0631 \u0645\u0632\u0648\u062A\u0631\u0627\u067E\u06CC \u0645\u0648\u060C \u062A\u0631\u06A9\u06CC\u0628\u06CC \u0627\u0632 \u0648\u06CC\u062A\u0627\u0645\u06CC\u0646\u200C\u0647\u0627\u060C \u0645\u0648\u0627\u062F \u0645\u0639\u062F\u0646\u06CC \u0648 \u067E\u067E\u062A\u06CC\u062F\u0647\u0627 \u0628\u0647\u200C\u0635\u0648\u0631\u062A \u0633\u0637\u062D\u06CC \u0628\u0647 \u067E\u0648\u0633\u062A \u0633\u0631 \u062A\u0632\u0631\u06CC\u0642 \u0645\u06CC\u200C\u0634\u0647 \u062A\u0627 \u0631\u06CC\u0632\u0634 \u0645\u0648 \u06A9\u0645\u062A\u0631 \u0648 \u062A\u0627\u0631 \u0645\u0648 \u0636\u062E\u06CC\u0645\u200C\u062A\u0631 \u0628\u0634\u0647. \u062F\u0648\u0631\u0647 \u0645\u0639\u0645\u0648\u0644\u060C \u0634\u0634 \u062C\u0644\u0633\u0647 \u0628\u0627 \u0641\u0627\u0635\u0644\u0647 \u062F\u0648 \u0647\u0641\u062A\u0647\u200C\u0633\u062A.",
    priceBandRials: [12e6, 35e6],
    iconKey: "sparkles",
    sortOrder: 130
  },
  {
    key: "meso_face",
    name: "\u0645\u0632\u0648\u062A\u0631\u0627\u067E\u06CC \u0635\u0648\u0631\u062A",
    nameEn: "Facial mesotherapy",
    slug: "meso-face",
    aliases: ["\u0645\u0632\u0648\u062A\u0631\u0627\u067E\u06CC \u0635\u0648\u0631\u062A", "\u0645\u0632\u0648 \u0635\u0648\u0631\u062A", "\u0622\u0628\u0631\u0633\u0627\u0646\u06CC \u0639\u0645\u0642\u06CC \u067E\u0648\u0633\u062A", "skin booster"],
    category: ProcedureCategory.SKIN,
    defaultDurationMinutes: 45,
    bufferMinutes: 10,
    defaultPriceRials: 25e6,
    retreatIntervalDays: 180,
    retreatIntervalLabel: "\u062F\u0648\u0631\u0647 \u0646\u06AF\u0647\u062F\u0627\u0631\u0646\u062F\u0647 \u0647\u0631 \u06F6 \u0645\u0627\u0647",
    courseSessions: 4,
    courseIntervalDays: 21,
    consumesUnits: true,
    unitLabel: "\u0633\u06CC\u200C\u0633\u06CC",
    materialUnit: MaterialUnit.ML,
    materialCategory: MaterialCategory.MESO,
    typicalUnitsMilli: { min: 2e3, max: 5e3 },
    bodyMapType: BodyMapType.FACE_FRONT,
    requiresConsent: false,
    reminderRules: [
      aftercare("aftercare_meso"),
      nextSession(21, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062C\u0644\u0633\u0647 \u0628\u0639\u062F\u06CC \u0645\u0632\u0648\u062A\u0631\u0627\u067E\u06CC\u060C \u06F3 \u0647\u0641\u062A\u0647 \u0628\u0639\u062F", "next_course_session"),
      review(21),
      nextSession(180, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062F\u0648\u0631\u0647 \u0646\u06AF\u0647\u062F\u0627\u0631\u0646\u062F\u0647\u060C \u06F6 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_meso_maintenance")
    ],
    aftercareTemplateKey: "aftercare_meso",
    summary: "\u062F\u0631 \u0645\u0632\u0648\u062A\u0631\u0627\u067E\u06CC \u0635\u0648\u0631\u062A\u060C \u062A\u0631\u06A9\u06CC\u0628\u06CC \u0627\u0632 \u0645\u0648\u0627\u062F \u0622\u0628\u0631\u0633\u0627\u0646 \u0648 \u0648\u06CC\u062A\u0627\u0645\u06CC\u0646\u200C\u0647\u0627 \u0628\u0647\u200C\u0635\u0648\u0631\u062A \u0633\u0637\u062D\u06CC \u062A\u0632\u0631\u06CC\u0642 \u0645\u06CC\u200C\u0634\u0647 \u062A\u0627 \u0634\u0627\u062F\u0627\u0628\u06CC\u060C \u0622\u0628\u0631\u0633\u0627\u0646\u06CC \u0648 \u06A9\u06CC\u0641\u06CC\u062A \u067E\u0648\u0633\u062A \u0628\u0647\u062A\u0631 \u0628\u0634\u0647. \u0645\u0639\u0645\u0648\u0644\u0627\u064B \u0686\u0647\u0627\u0631 \u062C\u0644\u0633\u0647 \u0628\u0627 \u0641\u0627\u0635\u0644\u0647 \u0633\u0647 \u0647\u0641\u062A\u0647 \u0627\u0646\u062C\u0627\u0645 \u0645\u06CC\u200C\u0634\u0647.",
    priceBandRials: [15e6, 45e6],
    iconKey: "droplet",
    sortOrder: 140
  },
  {
    key: "pdrn_salmon",
    name: "\u062A\u0632\u0631\u06CC\u0642 \u067E\u0644\u06CC\u200C\u062F\u06CC\u200C\u0627\u06A9\u0633\u06CC\u200C\u0631\u06CC\u0628\u0648\u0646\u0648\u06A9\u0644\u0626\u0648\u062A\u06CC\u062F (\u0627\u0633\u067E\u0627\u0631\u062A\u06CC\u0646 / \u0633\u0627\u0644\u0645\u0648\u0646)",
    nameEn: "PDRN / polynucleotide (salmon DNA) injection",
    slug: "pdrn-salmon",
    aliases: [
      "\u0627\u0633\u067E\u0627\u0631\u062A\u06CC\u0646",
      "\u0633\u0627\u0644\u0645\u0648\u0646",
      "\u062A\u0632\u0631\u06CC\u0642 \u0633\u0627\u0644\u0645\u0648\u0646",
      "\u067E\u0644\u06CC \u0646\u0648\u06A9\u0644\u0626\u0648\u062A\u06CC\u062F",
      "PDRN",
      "DNA \u0633\u0627\u0644\u0645\u0648\u0646",
      "\u0631\u062C\u0648\u0631\u0627\u0646"
    ],
    category: ProcedureCategory.SKIN,
    defaultDurationMinutes: 45,
    bufferMinutes: 15,
    defaultPriceRials: 6e7,
    retreatIntervalDays: 180,
    retreatIntervalLabel: "\u062F\u0648\u0631\u0647 \u0646\u06AF\u0647\u062F\u0627\u0631\u0646\u062F\u0647 \u0647\u0631 \u06F6 \u0645\u0627\u0647",
    // Polynucleotide protocols are three sessions at three-week intervals; the
    // regenerative effect builds across the course rather than per session.
    courseSessions: 3,
    courseIntervalDays: 21,
    consumesUnits: true,
    unitLabel: "\u0633\u06CC\u200C\u0633\u06CC",
    materialUnit: MaterialUnit.SYRINGE,
    materialCategory: MaterialCategory.MESO,
    typicalUnitsMilli: { min: 1e3, max: 3e3 },
    bodyMapType: BodyMapType.FACE_FRONT,
    requiresConsent: true,
    reminderRules: [
      aftercare("aftercare_meso"),
      nextSession(21, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062C\u0644\u0633\u0647 \u0628\u0639\u062F\u06CC \u062F\u0648\u0631\u0647\u060C \u06F3 \u0647\u0641\u062A\u0647 \u0628\u0639\u062F", "next_course_session"),
      followUp(60, "\u0627\u0631\u0632\u06CC\u0627\u0628\u06CC \u0646\u062A\u06CC\u062C\u0647 \u062F\u0648\u0631\u0647\u060C \u06F2 \u0645\u0627\u0647 \u0628\u0639\u062F \u0627\u0632 \u0634\u0631\u0648\u0639", "followup_pdrn"),
      nextSession(180, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062F\u0648\u0631\u0647 \u0646\u06AF\u0647\u062F\u0627\u0631\u0646\u062F\u0647\u060C \u06F6 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_pdrn_maintenance")
    ],
    aftercareTemplateKey: "aftercare_meso",
    summary: "\u067E\u0644\u06CC\u200C\u062F\u06CC\u200C\u0627\u06A9\u0633\u06CC\u200C\u0631\u06CC\u0628\u0648\u0646\u0648\u06A9\u0644\u0626\u0648\u062A\u06CC\u062F \u0628\u0627 \u0646\u0627\u0645\u200C\u0647\u0627\u06CC \u0627\u0633\u067E\u0627\u0631\u062A\u06CC\u0646 \u06CC\u0627 \u0633\u0627\u0644\u0645\u0648\u0646 \u0647\u0645 \u0634\u0646\u0627\u062E\u062A\u0647 \u0645\u06CC\u200C\u0634\u0647. \u0627\u06CC\u0646 \u0645\u0627\u062F\u0647 \u0628\u0627 \u062A\u0631\u0645\u06CC\u0645 \u0628\u0627\u0641\u062A \u0648 \u062A\u062D\u0631\u06CC\u06A9 \u06A9\u0644\u0627\u0698\u0646\u200C\u0633\u0627\u0632\u06CC\u060C \u06A9\u06CC\u0641\u06CC\u062A\u060C \u0636\u062E\u0627\u0645\u062A \u0648 \u0622\u0628\u0631\u0633\u0627\u0646\u06CC \u067E\u0648\u0633\u062A \u0631\u0648 \u0628\u0647\u062A\u0631 \u0645\u06CC\u200C\u06A9\u0646\u0647. \u062F\u0648\u0631\u0647 \u0627\u0633\u062A\u0627\u0646\u062F\u0627\u0631\u062F\u060C \u0633\u0647 \u062C\u0644\u0633\u0647 \u0628\u0627 \u0641\u0627\u0635\u0644\u0647 \u0633\u0647 \u0647\u0641\u062A\u0647\u200C\u0633\u062A.",
    priceBandRials: [4e7, 12e7],
    iconKey: "dna",
    sortOrder: 150
  },
  {
    key: "exosome_hair",
    name: "\u0627\u06AF\u0632\u0648\u0632\u0648\u0645 \u0645\u0648",
    nameEn: "Exosome therapy for hair",
    slug: "exosome-hair",
    aliases: ["\u0627\u06AF\u0632\u0648\u0632\u0648\u0645", "\u0627\u06AF\u0632\u0648\u0632\u0648\u0645 \u0645\u0648", "exosome"],
    category: ProcedureCategory.HAIR,
    defaultDurationMinutes: 45,
    bufferMinutes: 15,
    defaultPriceRials: 9e7,
    retreatIntervalDays: 180,
    retreatIntervalLabel: "\u062F\u0648\u0631\u0647 \u0646\u06AF\u0647\u062F\u0627\u0631\u0646\u062F\u0647 \u0647\u0631 \u06F6 \u0645\u0627\u0647",
    courseSessions: 3,
    courseIntervalDays: 30,
    consumesUnits: true,
    unitLabel: "\u0648\u06CC\u0627\u0644",
    materialUnit: MaterialUnit.VIAL,
    materialCategory: MaterialCategory.MESO,
    typicalUnitsMilli: { min: 1e3, max: 2e3 },
    bodyMapType: BodyMapType.SCALP,
    requiresConsent: true,
    reminderRules: [
      aftercare("aftercare_prp"),
      nextSession(30, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062C\u0644\u0633\u0647 \u0628\u0639\u062F\u06CC \u062F\u0648\u0631\u0647\u060C \u06CC\u06A9 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_course_session"),
      followUp(90, "\u0627\u0631\u0632\u06CC\u0627\u0628\u06CC \u0646\u062A\u06CC\u062C\u0647 \u062F\u0648\u0631\u0647\u060C \u06F3 \u0645\u0627\u0647 \u0628\u0639\u062F \u0627\u0632 \u0634\u0631\u0648\u0639", "followup_exosome")
    ],
    aftercareTemplateKey: "aftercare_prp",
    summary: "\u0627\u06AF\u0632\u0648\u0632\u0648\u0645\u200C\u0647\u0627 \u06A9\u06CC\u0633\u0647\u200C\u0647\u0627\u06CC \u0628\u0633\u06CC\u0627\u0631 \u0631\u06CC\u0632 \u062D\u0627\u0645\u0644 \u067E\u06CC\u0627\u0645\u200C\u0647\u0627\u06CC \u0633\u0644\u0648\u0644\u06CC \u0647\u0633\u062A\u0646 \u06A9\u0647 \u0628\u0631\u0627\u06CC \u062A\u062D\u0631\u06CC\u06A9 \u0641\u0648\u0644\u06CC\u06A9\u0648\u0644 \u0645\u0648 \u0628\u0647 \u067E\u0648\u0633\u062A \u0633\u0631 \u062A\u0632\u0631\u06CC\u0642 \u0645\u06CC\u200C\u0634\u0646. \u0627\u06CC\u0646 \u0631\u0648\u0634 \u0645\u0639\u0645\u0648\u0644\u0627\u064B \u0645\u06A9\u0645\u0644 \u06CC\u0627 \u062C\u0627\u06CC\u06AF\u0632\u06CC\u0646 PRP \u0645\u062D\u0633\u0648\u0628 \u0645\u06CC\u200C\u0634\u0647 \u0648 \u062F\u0631 \u0633\u0647 \u062C\u0644\u0633\u0647 \u0628\u0627 \u0641\u0627\u0635\u0644\u0647 \u06CC\u06A9 \u0645\u0627\u0647 \u0627\u0646\u062C\u0627\u0645 \u0645\u06CC\u200C\u0634\u0647.",
    priceBandRials: [6e7, 18e7],
    iconKey: "atom",
    sortOrder: 160
  },
  {
    key: "hifu",
    name: "\u0647\u0627\u06CC\u0641\u0648",
    nameEn: "HIFU (high-intensity focused ultrasound)",
    slug: "hifu",
    aliases: ["\u0647\u0627\u06CC\u0641\u0648", "\u0644\u06CC\u0641\u062A \u0628\u0627 \u0647\u0627\u06CC\u0641\u0648", "HIFU", "\u0627\u0648\u0644\u062A\u0631\u0627\u0641\u0631\u0645\u0631"],
    category: ProcedureCategory.SKIN,
    defaultDurationMinutes: 90,
    bufferMinutes: 20,
    defaultPriceRials: 15e7,
    // HIFU stimulates neocollagenesis; the result peaks at ~3 months and holds
    // for about a year, so a single session is repeated annually.
    retreatIntervalDays: 365,
    retreatIntervalLabel: "\u062D\u062F\u0648\u062F \u06F1\u06F2 \u0645\u0627\u0647",
    courseSessions: 1,
    courseIntervalDays: null,
    consumesUnits: false,
    unitLabel: "\u0634\u0627\u062A",
    materialUnit: MaterialUnit.PIECE,
    materialCategory: MaterialCategory.CONSUMABLE,
    typicalUnitsMilli: null,
    bodyMapType: BodyMapType.FACE_SIDE,
    requiresConsent: true,
    reminderRules: [
      aftercare("aftercare_energy_device"),
      // Judging HIFU before the collagen response completes leads to a needless
      // second session; 90 days is when the result is real.
      followUp(90, "\u0627\u0631\u0632\u06CC\u0627\u0628\u06CC \u0646\u062A\u06CC\u062C\u0647 \u0646\u0647\u0627\u06CC\u06CC\u060C \u06F3 \u0645\u0627\u0647 \u0628\u0639\u062F \u0627\u0632 \u062C\u0644\u0633\u0647", "followup_hifu"),
      review(30),
      nextSession(365, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062A\u06A9\u0631\u0627\u0631 \u0647\u0627\u06CC\u0641\u0648\u060C \u062D\u062F\u0648\u062F \u06F1\u06F2 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_hifu")
    ],
    aftercareTemplateKey: "aftercare_energy_device",
    summary: "\u0647\u0627\u06CC\u0641\u0648 \u0628\u0627 \u0627\u0645\u0648\u0627\u062C \u0627\u0648\u0644\u062A\u0631\u0627\u0633\u0648\u0646\u062F \u0645\u062A\u0645\u0631\u06A9\u0632\u060C \u0644\u0627\u06CC\u0647\u200C\u0647\u0627\u06CC \u0639\u0645\u0642\u06CC \u067E\u0648\u0633\u062A \u0631\u0648 \u06AF\u0631\u0645 \u0645\u06CC\u200C\u06A9\u0646\u0647 \u0648 \u06A9\u0644\u0627\u0698\u0646\u200C\u0633\u0627\u0632\u06CC \u0631\u0648 \u062A\u062D\u0631\u06CC\u06A9 \u0645\u06CC\u200C\u06A9\u0646\u0647. \u0646\u062A\u06CC\u062C\u0647 \u0628\u0647\u200C\u062A\u062F\u0631\u06CC\u062C \u0648 \u0637\u06CC \u062F\u0648 \u062A\u0627 \u0633\u0647 \u0645\u0627\u0647 \u0638\u0627\u0647\u0631 \u0645\u06CC\u200C\u0634\u0647 \u0648 \u062D\u062F\u0648\u062F \u06CC\u06A9 \u0633\u0627\u0644 \u0628\u0627\u0642\u06CC \u0645\u06CC\u200C\u0645\u0648\u0646\u0647.",
    priceBandRials: [8e7, 35e7],
    iconKey: "waves",
    sortOrder: 170
  },
  {
    key: "rf_microneedling",
    name: "\u0622\u0631\u200C\u0627\u0641 \u0641\u0631\u06A9\u0634\u0646\u0627\u0644 (\u0645\u06CC\u06A9\u0631\u0648\u0646\u06CC\u062F\u0644\u06CC\u0646\u06AF RF)",
    nameEn: "Fractional radiofrequency microneedling",
    slug: "rf-microneedling",
    aliases: ["RF \u0641\u0631\u06A9\u0634\u0646\u0627\u0644", "\u0622\u0631 \u0627\u0641 \u0641\u0631\u06A9\u0634\u0646\u0627\u0644", "\u0645\u06CC\u06A9\u0631\u0648\u0646\u06CC\u062F\u0644\u06CC\u0646\u06AF RF", "\u0645\u0648\u0631\u0641\u06CC\u0648\u0633", "RF microneedling"],
    category: ProcedureCategory.SKIN,
    defaultDurationMinutes: 75,
    bufferMinutes: 20,
    defaultPriceRials: 9e7,
    retreatIntervalDays: 365,
    retreatIntervalLabel: "\u062F\u0648\u0631\u0647 \u0646\u06AF\u0647\u062F\u0627\u0631\u0646\u062F\u0647 \u0633\u0627\u0644\u0627\u0646\u0647",
    courseSessions: 3,
    courseIntervalDays: 30,
    consumesUnits: true,
    unitLabel: "\u062A\u06CC\u067E",
    materialUnit: MaterialUnit.PIECE,
    materialCategory: MaterialCategory.CONSUMABLE,
    typicalUnitsMilli: { min: 1e3, max: 1e3 },
    bodyMapType: BodyMapType.FACE_FRONT,
    requiresConsent: true,
    reminderRules: [
      aftercare("aftercare_energy_device"),
      nextSession(30, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062C\u0644\u0633\u0647 \u0628\u0639\u062F\u06CC \u062F\u0648\u0631\u0647\u060C \u06CC\u06A9 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_course_session"),
      followUp(90, "\u0627\u0631\u0632\u06CC\u0627\u0628\u06CC \u0646\u062A\u06CC\u062C\u0647 \u062F\u0648\u0631\u0647\u060C \u06F3 \u0645\u0627\u0647 \u0628\u0639\u062F \u0627\u0632 \u0634\u0631\u0648\u0639", "followup_rf"),
      nextSession(365, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062C\u0644\u0633\u0647 \u0646\u06AF\u0647\u062F\u0627\u0631\u0646\u062F\u0647\u060C \u06CC\u06A9 \u0633\u0627\u0644 \u0628\u0639\u062F", "next_rf_maintenance")
    ],
    aftercareTemplateKey: "aftercare_energy_device",
    summary: "\u062F\u0631 \u0622\u0631\u200C\u0627\u0641 \u0641\u0631\u06A9\u0634\u0646\u0627\u0644\u060C \u0633\u0648\u0632\u0646\u200C\u0647\u0627\u06CC \u0631\u06CC\u0632 \u0627\u0645\u0648\u0627\u062C \u0631\u0627\u062F\u06CC\u0648\u06CC\u06CC \u0631\u0648 \u0628\u0647 \u0639\u0645\u0642 \u067E\u0648\u0633\u062A \u0645\u06CC\u200C\u0631\u0633\u0648\u0646\u0646. \u0622\u0633\u06CC\u0628 \u06A9\u0646\u062A\u0631\u0644\u200C\u0634\u062F\u0647\u200C\u0627\u06CC \u06A9\u0647 \u0627\u06CC\u062C\u0627\u062F \u0645\u06CC\u200C\u0634\u0647\u060C \u0628\u0647 \u0633\u0641\u062A\u200C\u062A\u0631 \u0634\u062F\u0646 \u067E\u0648\u0633\u062A \u0648 \u0628\u0647\u0628\u0648\u062F \u062C\u0627\u06CC \u062C\u0648\u0634 \u06A9\u0645\u06A9 \u0645\u06CC\u200C\u06A9\u0646\u0647. \u0645\u0639\u0645\u0648\u0644\u0627\u064B \u0633\u0647 \u062C\u0644\u0633\u0647 \u0628\u0627 \u0641\u0627\u0635\u0644\u0647 \u06CC\u06A9 \u0645\u0627\u0647 \u0644\u0627\u0632\u0645\u0647.",
    priceBandRials: [5e7, 2e8],
    iconKey: "zap",
    sortOrder: 180
  },
  {
    key: "microneedling",
    name: "\u0645\u06CC\u06A9\u0631\u0648\u0646\u06CC\u062F\u0644\u06CC\u0646\u06AF",
    nameEn: "Microneedling",
    slug: "microneedling",
    aliases: ["\u0645\u06CC\u06A9\u0631\u0648\u0646\u06CC\u062F\u0644\u06CC\u0646\u06AF", "\u062F\u0631\u0645\u0627\u0631\u0648\u0644\u0631", "\u0645\u06CC\u06A9\u0631\u0648\u0646\u06CC\u062F\u0644", "microneedling"],
    category: ProcedureCategory.SKIN,
    defaultDurationMinutes: 60,
    bufferMinutes: 15,
    defaultPriceRials: 3e7,
    retreatIntervalDays: 180,
    retreatIntervalLabel: "\u062F\u0648\u0631\u0647 \u0646\u06AF\u0647\u062F\u0627\u0631\u0646\u062F\u0647 \u0647\u0631 \u06F6 \u0645\u0627\u0647",
    // A four-week gap lets the epidermis fully re-form between passes.
    courseSessions: 4,
    courseIntervalDays: 28,
    consumesUnits: true,
    unitLabel: "\u06A9\u0627\u0631\u062A\u0631\u06CC\u062C",
    materialUnit: MaterialUnit.PIECE,
    materialCategory: MaterialCategory.CONSUMABLE,
    typicalUnitsMilli: { min: 1e3, max: 1e3 },
    bodyMapType: BodyMapType.FACE_FRONT,
    requiresConsent: false,
    reminderRules: [
      aftercare("aftercare_microneedling"),
      nextSession(28, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062C\u0644\u0633\u0647 \u0628\u0639\u062F\u06CC \u062F\u0648\u0631\u0647\u060C \u06F4 \u0647\u0641\u062A\u0647 \u0628\u0639\u062F", "next_course_session"),
      review(14),
      nextSession(180, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062F\u0648\u0631\u0647 \u0646\u06AF\u0647\u062F\u0627\u0631\u0646\u062F\u0647\u060C \u06F6 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_microneedling")
    ],
    aftercareTemplateKey: "aftercare_microneedling",
    summary: "\u062F\u0631 \u0645\u06CC\u06A9\u0631\u0648\u0646\u06CC\u062F\u0644\u06CC\u0646\u06AF\u060C \u0633\u0648\u0632\u0646\u200C\u0647\u0627\u06CC \u0628\u0633\u06CC\u0627\u0631 \u0631\u06CC\u0632 \u0633\u0648\u0631\u0627\u062E\u200C\u0647\u0627\u06CC \u0645\u06CC\u06A9\u0631\u0648\u0633\u06A9\u0648\u067E\u06CC \u062F\u0631 \u067E\u0648\u0633\u062A \u0627\u06CC\u062C\u0627\u062F \u0645\u06CC\u200C\u06A9\u0646\u0646 \u0648 \u0631\u0648\u0646\u062F \u062A\u0631\u0645\u06CC\u0645 \u0648 \u06A9\u0644\u0627\u0698\u0646\u200C\u0633\u0627\u0632\u06CC \u0631\u0648 \u0641\u0639\u0627\u0644 \u0645\u06CC\u200C\u06A9\u0646\u0646. \u0627\u06CC\u0646 \u0631\u0648\u0634 \u0628\u0631\u0627\u06CC \u062C\u0627\u06CC \u062C\u0648\u0634\u060C \u0645\u0646\u0627\u0641\u0630 \u0628\u0627\u0632 \u0648 \u0628\u0627\u0641\u062A \u0646\u0627\u0647\u0645\u0648\u0627\u0631 \u067E\u0648\u0633\u062A \u06A9\u0627\u0631\u0628\u0631\u062F \u062F\u0627\u0631\u0647.",
    priceBandRials: [18e6, 6e7],
    iconKey: "grid",
    sortOrder: 190
  },
  {
    key: "laser_hair_removal",
    name: "\u0644\u06CC\u0632\u0631 \u0645\u0648\u0647\u0627\u06CC \u0632\u0627\u0626\u062F",
    nameEn: "Laser hair removal",
    slug: "laser-hair-removal",
    aliases: ["\u0644\u06CC\u0632\u0631 \u0645\u0648\u0647\u0627\u06CC \u0632\u0627\u0626\u062F", "\u0644\u06CC\u0632\u0631 \u0645\u0648", "\u0644\u06CC\u0632\u0631 \u0627\u0644\u06A9\u0633", "\u062F\u0627\u06CC\u0648\u062F \u0644\u06CC\u0632\u0631", "laser hair removal"],
    category: ProcedureCategory.LASER,
    defaultDurationMinutes: 45,
    bufferMinutes: 10,
    defaultPriceRials: 25e6,
    retreatIntervalDays: 180,
    retreatIntervalLabel: "\u062C\u0644\u0633\u0647 \u0646\u06AF\u0647\u062F\u0627\u0631\u0646\u062F\u0647 \u0647\u0631 \u06F6 \u0645\u0627\u0647",
    // Only follicles in anagen respond, so sessions are spaced to catch the
    // next cohort: ~4 weeks on the face, ~6 on the body; 35 days is the middle.
    courseSessions: 8,
    courseIntervalDays: 35,
    consumesUnits: false,
    unitLabel: null,
    materialUnit: MaterialUnit.PIECE,
    materialCategory: MaterialCategory.CONSUMABLE,
    typicalUnitsMilli: null,
    bodyMapType: BodyMapType.FULL_BODY,
    requiresConsent: true,
    reminderRules: [
      aftercare("aftercare_laser"),
      nextSession(35, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062C\u0644\u0633\u0647 \u0628\u0639\u062F\u06CC \u0644\u06CC\u0632\u0631\u060C \u062D\u062F\u0648\u062F \u06F5 \u0647\u0641\u062A\u0647 \u0628\u0639\u062F", "next_course_session"),
      review(21),
      nextSession(180, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062C\u0644\u0633\u0647 \u0646\u06AF\u0647\u062F\u0627\u0631\u0646\u062F\u0647\u060C \u06F6 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_laser_maintenance")
    ],
    aftercareTemplateKey: "aftercare_laser",
    summary: "\u0644\u06CC\u0632\u0631 \u0645\u0648\u0647\u0627\u06CC \u0632\u0627\u0626\u062F\u060C \u0645\u0644\u0627\u0646\u06CC\u0646 \u06CC\u0627 \u0631\u0646\u06AF\u200C\u062F\u0627\u0646\u0647 \u0631\u06CC\u0634\u0647 \u0645\u0648 \u0631\u0648 \u0647\u062F\u0641 \u0645\u06CC\u200C\u06AF\u06CC\u0631\u0647 \u0648 \u0641\u0648\u0644\u06CC\u06A9\u0648\u0644 \u0631\u0648 \u063A\u06CC\u0631\u0641\u0639\u0627\u0644 \u0645\u06CC\u200C\u06A9\u0646\u0647. \u0641\u0642\u0637 \u0645\u0648\u0647\u0627\u06CC\u06CC \u06A9\u0647 \u062F\u0631 \u0645\u0631\u062D\u0644\u0647 \u0631\u0634\u062F \u0647\u0633\u062A\u0646 \u0628\u0647 \u0644\u06CC\u0632\u0631 \u067E\u0627\u0633\u062E \u0645\u06CC\u200C\u062F\u0646\u061B \u0628\u0631\u0627\u06CC \u0647\u0645\u06CC\u0646 \u0645\u0639\u0645\u0648\u0644\u0627\u064B \u0634\u0634 \u062A\u0627 \u062F\u0647 \u062C\u0644\u0633\u0647 \u0628\u0627 \u0641\u0627\u0635\u0644\u0647 \u0686\u0647\u0627\u0631 \u062A\u0627 \u0634\u0634 \u0647\u0641\u062A\u0647 \u0644\u0627\u0632\u0645\u0647.",
    priceBandRials: [8e6, 6e7],
    iconKey: "zap",
    sortOrder: 200
  },
  {
    key: "fractional_laser",
    name: "\u0644\u06CC\u0632\u0631 \u0641\u0631\u06A9\u0634\u0646\u0627\u0644",
    nameEn: "Fractional CO2 laser",
    slug: "fractional-laser",
    aliases: ["\u0644\u06CC\u0632\u0631 \u0641\u0631\u06A9\u0634\u0646\u0627\u0644", "\u0641\u0631\u06A9\u0634\u0646\u0627\u0644 CO2", "\u0644\u06CC\u0632\u0631 \u062C\u0627\u06CC \u062C\u0648\u0634", "fractional laser"],
    category: ProcedureCategory.LASER,
    defaultDurationMinutes: 60,
    bufferMinutes: 20,
    defaultPriceRials: 8e7,
    retreatIntervalDays: 365,
    retreatIntervalLabel: "\u062F\u0631 \u0635\u0648\u0631\u062A \u0646\u06CC\u0627\u0632\u060C \u0628\u0639\u062F \u0627\u0632 \u06CC\u06A9 \u0633\u0627\u0644",
    // Ablative resurfacing needs a full re-epithelialisation plus remodelling
    // cycle between passes — two months, not one.
    courseSessions: 3,
    courseIntervalDays: 60,
    consumesUnits: false,
    unitLabel: null,
    materialUnit: MaterialUnit.PIECE,
    materialCategory: MaterialCategory.CONSUMABLE,
    typicalUnitsMilli: null,
    bodyMapType: BodyMapType.FACE_FRONT,
    requiresConsent: true,
    reminderRules: [
      aftercare("aftercare_fractional_laser"),
      // The crusting phase is days 3-7 and is when patients panic; the check-in
      // is deliberately early.
      followUp(7, "\u067E\u06CC\u06AF\u06CC\u0631\u06CC \u0631\u0648\u0646\u062F \u062A\u0631\u0645\u06CC\u0645 \u067E\u0648\u0633\u062A\u060C \u06CC\u06A9 \u0647\u0641\u062A\u0647 \u0628\u0639\u062F \u0627\u0632 \u062C\u0644\u0633\u0647", "followup_fractional_laser"),
      nextSession(60, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062C\u0644\u0633\u0647 \u0628\u0639\u062F\u06CC \u062F\u0648\u0631\u0647\u060C \u06F2 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_course_session"),
      review(30)
    ],
    aftercareTemplateKey: "aftercare_fractional_laser",
    summary: "\u0644\u06CC\u0632\u0631 \u0641\u0631\u06A9\u0634\u0646\u0627\u0644\u060C \u0628\u062E\u0634\u200C\u0647\u0627\u06CC \u0628\u0633\u06CC\u0627\u0631 \u0631\u06CC\u0632\u06CC \u0627\u0632 \u067E\u0648\u0633\u062A \u0631\u0648 \u0628\u0647 \u0634\u06A9\u0644 \u0633\u062A\u0648\u0646\u200C\u0647\u0627\u06CC \u0645\u06CC\u06A9\u0631\u0648\u0633\u06A9\u0648\u067E\u06CC \u062A\u0628\u062E\u06CC\u0631 \u0645\u06CC\u200C\u06A9\u0646\u0647. \u0628\u0627 \u062A\u0631\u0645\u06CC\u0645 \u0627\u06CC\u0646 \u0628\u062E\u0634\u200C\u0647\u0627\u060C \u062C\u0627\u06CC \u062C\u0648\u0634\u060C \u0645\u0646\u0627\u0641\u0630 \u0628\u0627\u0632 \u0648 \u0686\u06CC\u0646\u200C\u0647\u0627\u06CC \u0631\u06CC\u0632 \u0628\u0647\u062A\u0631 \u0645\u06CC\u200C\u0634\u0646. \u062F\u0631 \u062F\u0648\u0631\u0647 \u0646\u0642\u0627\u0647\u062A\u060C \u067E\u0648\u0633\u062A \u0686\u0646\u062F \u0631\u0648\u0632 \u0642\u0631\u0645\u0632\u06CC \u0648 \u067E\u0648\u0633\u062A\u0647\u200C\u0631\u06CC\u0632\u06CC \u062F\u0627\u0631\u0647.",
    priceBandRials: [5e7, 2e8],
    iconKey: "scan",
    sortOrder: 210
  },
  {
    key: "chemical_peel",
    name: "\u067E\u06CC\u0644\u06CC\u0646\u06AF \u0634\u06CC\u0645\u06CC\u0627\u06CC\u06CC",
    nameEn: "Chemical peel",
    slug: "chemical-peel",
    aliases: ["\u067E\u06CC\u0644\u06CC\u0646\u06AF", "\u0644\u0627\u06CC\u0647\u200C\u0628\u0631\u062F\u0627\u0631\u06CC \u0634\u06CC\u0645\u06CC\u0627\u06CC\u06CC", "\u067E\u06CC\u0644\u06CC\u0646\u06AF \u0635\u0648\u0631\u062A", "chemical peel"],
    category: ProcedureCategory.SKIN,
    defaultDurationMinutes: 40,
    bufferMinutes: 10,
    defaultPriceRials: 2e7,
    retreatIntervalDays: 180,
    retreatIntervalLabel: "\u062F\u0648\u0631\u0647 \u0646\u06AF\u0647\u062F\u0627\u0631\u0646\u062F\u0647 \u0647\u0631 \u06F6 \u0645\u0627\u0647",
    courseSessions: 4,
    courseIntervalDays: 21,
    consumesUnits: true,
    unitLabel: "\u0633\u06CC\u200C\u0633\u06CC",
    materialUnit: MaterialUnit.ML,
    materialCategory: MaterialCategory.MEDICATION,
    typicalUnitsMilli: { min: 2e3, max: 5e3 },
    bodyMapType: BodyMapType.FACE_FRONT,
    requiresConsent: false,
    reminderRules: [
      aftercare("aftercare_peel"),
      nextSession(21, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062C\u0644\u0633\u0647 \u0628\u0639\u062F\u06CC \u067E\u06CC\u0644\u06CC\u0646\u06AF\u060C \u06F3 \u0647\u0641\u062A\u0647 \u0628\u0639\u062F", "next_course_session"),
      review(14),
      nextSession(180, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062F\u0648\u0631\u0647 \u0646\u06AF\u0647\u062F\u0627\u0631\u0646\u062F\u0647\u060C \u06F6 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_peel_maintenance")
    ],
    aftercareTemplateKey: "aftercare_peel",
    summary: "\u062F\u0631 \u067E\u06CC\u0644\u06CC\u0646\u06AF \u0634\u06CC\u0645\u06CC\u0627\u06CC\u06CC\u060C \u0645\u062D\u0644\u0648\u0644\u200C\u0647\u0627\u06CC \u0627\u0633\u06CC\u062F\u06CC \u0644\u0627\u06CC\u0647 \u0633\u0637\u062D\u06CC \u067E\u0648\u0633\u062A \u0631\u0648 \u0628\u0631\u0645\u06CC\u200C\u062F\u0627\u0631\u0646 \u062A\u0627 \u0644\u06A9\u060C \u062C\u0627\u06CC \u062C\u0648\u0634 \u0648 \u0646\u0627\u0647\u0645\u0648\u0627\u0631\u06CC \u067E\u0648\u0633\u062A \u06A9\u0645\u062A\u0631 \u0628\u0634\u0647. \u0639\u0645\u0642 \u0644\u0627\u06CC\u0647\u200C\u0628\u0631\u062F\u0627\u0631\u06CC \u0648 \u062A\u0639\u062F\u0627\u062F \u062C\u0644\u0633\u0627\u062A \u0628\u0647 \u0646\u0648\u0639 \u067E\u0648\u0633\u062A \u0648 \u0645\u0634\u06A9\u0644 \u0627\u0648\u0646 \u0628\u0633\u062A\u06AF\u06CC \u062F\u0627\u0631\u0647.",
    priceBandRials: [1e7, 45e6],
    iconKey: "layers",
    sortOrder: 220
  },
  {
    key: "carboxytherapy",
    name: "\u06A9\u0631\u0628\u0648\u06A9\u0633\u06CC\u200C\u062A\u0631\u0627\u067E\u06CC",
    nameEn: "Carboxytherapy",
    slug: "carboxytherapy",
    aliases: ["\u06A9\u0631\u0628\u0648\u06A9\u0633\u06CC \u062A\u0631\u0627\u067E\u06CC", "\u06A9\u0631\u0628\u0648\u06A9\u0633\u06CC", "\u062A\u0632\u0631\u06CC\u0642 \u06AF\u0627\u0632 CO2", "carboxytherapy"],
    category: ProcedureCategory.SKIN,
    defaultDurationMinutes: 30,
    bufferMinutes: 10,
    defaultPriceRials: 12e6,
    retreatIntervalDays: 180,
    retreatIntervalLabel: "\u062F\u0648\u0631\u0647 \u0646\u06AF\u0647\u062F\u0627\u0631\u0646\u062F\u0647 \u0647\u0631 \u06F6 \u0645\u0627\u0647",
    // Carboxytherapy is a high-frequency, low-intensity protocol: weekly for
    // eight to ten sessions.
    courseSessions: 8,
    courseIntervalDays: 7,
    consumesUnits: false,
    unitLabel: null,
    materialUnit: MaterialUnit.PIECE,
    materialCategory: MaterialCategory.CONSUMABLE,
    typicalUnitsMilli: null,
    bodyMapType: BodyMapType.FULL_BODY,
    requiresConsent: false,
    reminderRules: [
      aftercare("aftercare_carboxy"),
      nextSession(7, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062C\u0644\u0633\u0647 \u0628\u0639\u062F\u06CC \u06A9\u0631\u0628\u0648\u06A9\u0633\u06CC\u200C\u062A\u0631\u0627\u067E\u06CC\u060C \u06CC\u06A9 \u0647\u0641\u062A\u0647 \u0628\u0639\u062F", "next_course_session"),
      followUp(60, "\u0627\u0631\u0632\u06CC\u0627\u0628\u06CC \u0646\u062A\u06CC\u062C\u0647 \u062F\u0648\u0631\u0647\u060C \u06F2 \u0645\u0627\u0647 \u0628\u0639\u062F \u0627\u0632 \u0634\u0631\u0648\u0639", "followup_carboxy")
    ],
    aftercareTemplateKey: "aftercare_carboxy",
    summary: "\u062F\u0631 \u06A9\u0631\u0628\u0648\u06A9\u0633\u06CC\u200C\u062A\u0631\u0627\u067E\u06CC\u060C \u06AF\u0627\u0632 \u062F\u06CC\u200C\u0627\u06A9\u0633\u06CC\u062F \u06A9\u0631\u0628\u0646 \u067E\u0632\u0634\u06A9\u06CC \u0632\u06CC\u0631 \u067E\u0648\u0633\u062A \u062A\u0632\u0631\u06CC\u0642 \u0645\u06CC\u200C\u0634\u0647 \u062A\u0627 \u06AF\u0631\u062F\u0634 \u062E\u0648\u0646 \u0627\u0648\u0646 \u0646\u0627\u062D\u06CC\u0647 \u0648 \u0627\u06A9\u0633\u06CC\u0698\u0646\u200C\u0631\u0633\u0627\u0646\u06CC \u0628\u0647 \u0628\u0627\u0641\u062A \u0628\u06CC\u0634\u062A\u0631 \u0628\u0634\u0647. \u0627\u06CC\u0646 \u0631\u0648\u0634 \u0628\u0631\u0627\u06CC \u0633\u06CC\u0627\u0647\u06CC \u0632\u06CC\u0631 \u0686\u0634\u0645\u060C \u062A\u0631\u06A9 \u067E\u0648\u0633\u062A\u06CC \u0648 \u0633\u0644\u0648\u0644\u06CC\u062A \u0627\u0633\u062A\u0641\u0627\u062F\u0647 \u0645\u06CC\u200C\u0634\u0647.",
    priceBandRials: [6e6, 25e6],
    iconKey: "wind",
    sortOrder: 230
  },
  {
    key: "hydrafacial",
    name: "\u0647\u06CC\u062F\u0631\u0627\u0641\u06CC\u0634\u06CC\u0627\u0644",
    nameEn: "Hydrafacial",
    slug: "hydrafacial",
    aliases: ["\u0647\u06CC\u062F\u0631\u0627\u0641\u06CC\u0634\u06CC\u0627\u0644", "\u0641\u06CC\u0634\u06CC\u0627\u0644", "\u067E\u0627\u06A9\u0633\u0627\u0632\u06CC \u067E\u0648\u0633\u062A", "hydrafacial"],
    category: ProcedureCategory.SKIN,
    defaultDurationMinutes: 60,
    bufferMinutes: 10,
    defaultPriceRials: 25e6,
    retreatIntervalDays: 30,
    retreatIntervalLabel: "\u0645\u0627\u0647\u0627\u0646\u0647",
    courseSessions: 1,
    courseIntervalDays: null,
    consumesUnits: true,
    unitLabel: "\u0633\u062A",
    materialUnit: MaterialUnit.PIECE,
    materialCategory: MaterialCategory.CONSUMABLE,
    typicalUnitsMilli: { min: 1e3, max: 1e3 },
    bodyMapType: BodyMapType.NONE,
    requiresConsent: false,
    reminderRules: [
      aftercare("aftercare_hydrafacial"),
      nextSession(30, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u067E\u0627\u06A9\u0633\u0627\u0632\u06CC \u0645\u0627\u0647\u0627\u0646\u0647 \u067E\u0648\u0633\u062A\u060C \u06CC\u06A9 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_hydrafacial"),
      review(7)
    ],
    aftercareTemplateKey: "aftercare_hydrafacial",
    summary: "\u062F\u0631 \u0647\u06CC\u062F\u0631\u0627\u0641\u06CC\u0634\u06CC\u0627\u0644\u060C \u067E\u0648\u0633\u062A \u062F\u0631 \u06CC\u06A9 \u062C\u0644\u0633\u0647 \u0644\u0627\u06CC\u0647\u200C\u0628\u0631\u062F\u0627\u0631\u06CC \u0648 \u0622\u0628\u0631\u0633\u0627\u0646\u06CC \u0645\u06CC\u200C\u0634\u0647 \u0648 \u0645\u0646\u0627\u0641\u0630 \u0627\u0648\u0646 \u067E\u0627\u06A9\u200C\u0633\u0627\u0632\u06CC \u0645\u06CC\u200C\u0634\u0646. \u0627\u06CC\u0646 \u0631\u0648\u0634 \u062F\u0648\u0631\u0647 \u0646\u0642\u0627\u0647\u062A \u0646\u062F\u0627\u0631\u0647 \u0648 \u0628\u0631\u0627\u06CC \u062D\u0641\u0638 \u0646\u062A\u06CC\u062C\u0647 \u0645\u0639\u0645\u0648\u0644\u0627\u064B \u0645\u0627\u0647\u06CC \u06CC\u06A9\u200C\u0628\u0627\u0631 \u062A\u06A9\u0631\u0627\u0631 \u0645\u06CC\u200C\u0634\u0647.",
    priceBandRials: [12e6, 5e7],
    iconKey: "droplet",
    sortOrder: 240
  },
  {
    key: "cryolipolysis",
    name: "\u06A9\u0631\u0627\u06CC\u0648\u0644\u06CC\u067E\u0648\u0644\u06CC\u0632 (\u0644\u0627\u063A\u0631\u06CC \u0645\u0648\u0636\u0639\u06CC)",
    nameEn: "Cryolipolysis",
    slug: "cryolipolysis",
    aliases: ["\u06A9\u0631\u0627\u06CC\u0648\u0644\u06CC\u067E\u0648\u0644\u06CC\u0632", "\u0644\u0627\u063A\u0631\u06CC \u0645\u0648\u0636\u0639\u06CC", "\u06A9\u0648\u0644 \u0627\u0633\u06A9\u0627\u0644\u067E\u062A\u06CC\u0646\u06AF", "coolsculpting"],
    category: ProcedureCategory.OTHER,
    defaultDurationMinutes: 90,
    bufferMinutes: 15,
    defaultPriceRials: 6e7,
    // Fat cells are cleared over eight to twelve weeks, so a second session on
    // the same area before ~90 days cannot be judged.
    retreatIntervalDays: 90,
    retreatIntervalLabel: "\u062D\u062F\u0627\u0642\u0644 \u06F3 \u0645\u0627\u0647 \u0641\u0627\u0635\u0644\u0647 \u062A\u0627 \u062C\u0644\u0633\u0647 \u0628\u0639\u062F\u06CC",
    courseSessions: 2,
    courseIntervalDays: 90,
    consumesUnits: true,
    unitLabel: "\u0698\u0644\u200C\u067E\u062F",
    materialUnit: MaterialUnit.PIECE,
    materialCategory: MaterialCategory.CONSUMABLE,
    typicalUnitsMilli: { min: 1e3, max: 2e3 },
    bodyMapType: BodyMapType.LOWER_BODY,
    requiresConsent: true,
    reminderRules: [
      aftercare("aftercare_cryolipolysis"),
      followUp(60, "\u0627\u0631\u0632\u06CC\u0627\u0628\u06CC \u0646\u062A\u06CC\u062C\u0647\u060C \u06F2 \u0645\u0627\u0647 \u0628\u0639\u062F \u0627\u0632 \u062C\u0644\u0633\u0647", "followup_cryolipolysis"),
      nextSession(90, "\u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u062C\u0644\u0633\u0647 \u0628\u0639\u062F\u06CC \u062F\u0631 \u0635\u0648\u0631\u062A \u0646\u06CC\u0627\u0632\u060C \u06F3 \u0645\u0627\u0647 \u0628\u0639\u062F", "next_cryolipolysis")
    ],
    aftercareTemplateKey: "aftercare_cryolipolysis",
    summary: "\u06A9\u0631\u0627\u06CC\u0648\u0644\u06CC\u067E\u0648\u0644\u06CC\u0632 \u0628\u0627 \u0633\u0631\u0645\u0627\u06CC \u06A9\u0646\u062A\u0631\u0644\u200C\u0634\u062F\u0647\u060C \u0633\u0644\u0648\u0644\u200C\u0647\u0627\u06CC \u0686\u0631\u0628\u06CC \u0646\u0627\u062D\u06CC\u0647 \u0645\u0648\u0631\u062F\u0646\u0638\u0631 \u0631\u0648 \u0627\u0632 \u0628\u06CC\u0646 \u0645\u06CC\u200C\u0628\u0631\u0647 \u0648 \u0628\u062F\u0646 \u062F\u0631 \u062F\u0648 \u062A\u0627 \u0633\u0647 \u0645\u0627\u0647 \u0627\u0648\u0646\u200C\u0647\u0627 \u0631\u0648 \u062F\u0641\u0639 \u0645\u06CC\u200C\u06A9\u0646\u0647. \u0627\u06CC\u0646 \u0631\u0648\u0634 \u0628\u0631\u0627\u06CC \u0686\u0631\u0628\u06CC\u200C\u0647\u0627\u06CC \u0645\u0648\u0636\u0639\u06CC \u06A9\u0627\u0631\u0628\u0631\u062F \u062F\u0627\u0631\u0647 \u0648 \u062C\u0627\u06CC\u06AF\u0632\u06CC\u0646 \u0644\u06CC\u067E\u0648\u0633\u0627\u06A9\u0634\u0646 \u0646\u06CC\u0633\u062A.",
    priceBandRials: [3e7, 15e7],
    iconKey: "snowflake",
    sortOrder: 250
  }
]);
var PROCEDURE_KEYS = Object.freeze(
  PROCEDURE_CATALOG.map((procedure) => procedure.key)
);
var BY_KEY = new Map(
  PROCEDURE_CATALOG.map((procedure) => [procedure.key, procedure])
);
var BY_SLUG = new Map(
  PROCEDURE_CATALOG.map((procedure) => [procedure.slug, procedure])
);
function getProcedure(key) {
  return BY_KEY.get(key);
}
function getProcedureBySlug(slug) {
  return BY_SLUG.get(slug);
}
function proceduresByCategory(category) {
  return PROCEDURE_CATALOG.filter((procedure) => procedure.category === category);
}
var PROCEDURES_BY_CATEGORY = Object.freeze(
  Object.values(ProcedureCategory).reduce(
    (acc, category) => {
      acc[category] = Object.freeze(proceduresByCategory(category));
      return acc;
    },
    {}
  )
);
function searchProcedures(query) {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return [];
  const scored = [];
  for (const entry of PROCEDURE_CATALOG) {
    const haystacks = [entry.name, entry.nameEn, entry.slug, ...entry.aliases];
    let best = -1;
    for (const haystack of haystacks) {
      const index = haystack.toLowerCase().indexOf(needle);
      if (index !== -1 && (best === -1 || index < best)) best = index;
    }
    if (best !== -1) scored.push({ entry, score: best });
  }
  return scored.sort((a, b) => a.score - b.score || a.entry.sortOrder - b.entry.sortOrder).map((item) => item.entry);
}
var UNIT_CONSUMING_PROCEDURES = Object.freeze(
  PROCEDURE_CATALOG.filter((procedure) => procedure.consumesUnits)
);
function reminderRulesFor(key) {
  return getProcedure(key)?.reminderRules ?? [];
}
function nextRetreatmentDate(key, performedAt) {
  const procedure = getProcedure(key);
  if (!procedure || procedure.retreatIntervalDays === null) return null;
  return new Date(performedAt.getTime() + procedure.retreatIntervalDays * 864e5);
}

// src/geo.ts
var PROVINCE_SEED = [
  {
    slug: "tehran",
    name: "\u062A\u0647\u0631\u0627\u0646",
    code: "IR-23",
    lat: 35.6892,
    lng: 51.389,
    cities: [
      ["tehran", "\u062A\u0647\u0631\u0627\u0646", 8693706, 35.6892, 51.389],
      ["eslamshahr", "\u0627\u0633\u0644\u0627\u0645\u0634\u0647\u0631", 448129, 35.5522, 51.235],
      ["shahriar", "\u0634\u0647\u0631\u06CC\u0627\u0631", 309607, 35.6592, 51.0588],
      ["qods", "\u0642\u062F\u0633", 309605, 35.7211, 51.1094],
      ["malard", "\u0645\u0644\u0627\u0631\u062F", 281027, 35.6659, 50.9767],
      ["qarchak", "\u0642\u0631\u0686\u06A9", 231075, 35.4392, 51.5686],
      ["varamin", "\u0648\u0631\u0627\u0645\u06CC\u0646", 225628, 35.3242, 51.6456],
      ["pakdasht", "\u067E\u0627\u06A9\u062F\u0634\u062A", 132e3, 35.4796, 51.6803],
      ["robat-karim", "\u0631\u0628\u0627\u0637\u200C\u06A9\u0631\u06CC\u0645", 108e3, 35.4847, 51.0829],
      ["pardis", "\u067E\u0631\u062F\u06CC\u0633", 73e3, 35.7414, 51.7975],
      ["damavand", "\u062F\u0645\u0627\u0648\u0646\u062F", 6e4, 35.7175, 52.065],
      ["lavasan", "\u0644\u0648\u0627\u0633\u0627\u0646", 32e3, 35.8256, 51.6353]
    ]
  },
  {
    slug: "alborz",
    name: "\u0627\u0644\u0628\u0631\u0632",
    code: "IR-30",
    lat: 35.84,
    lng: 50.9391,
    cities: [
      ["karaj", "\u06A9\u0631\u062C", 1592492, 35.84, 50.9391],
      ["fardis", "\u0641\u0631\u062F\u06CC\u0633", 313222, 35.7261, 50.9758],
      ["nazarabad", "\u0646\u0638\u0631\u0622\u0628\u0627\u062F", 128e3, 35.9494, 50.6058],
      ["mohammadshahr", "\u0645\u062D\u0645\u062F\u0634\u0647\u0631", 12e4, 35.7594, 50.8697],
      ["kamalshahr", "\u06A9\u0645\u0627\u0644\u200C\u0634\u0647\u0631", 9e4, 35.8894, 50.8697],
      ["hashtgerd", "\u0647\u0634\u062A\u06AF\u0631\u062F", 6e4, 35.9622, 50.6803],
      ["mahdasht", "\u0645\u0627\u0647\u062F\u0634\u062A", 6e4, 35.7325, 50.8114],
      ["eshtehard", "\u0627\u0634\u062A\u0647\u0627\u0631\u062F", 4e4, 35.7239, 50.3697]
    ]
  },
  {
    slug: "khorasan-razavi",
    name: "\u062E\u0631\u0627\u0633\u0627\u0646 \u0631\u0636\u0648\u06CC",
    code: "IR-09",
    lat: 36.2605,
    lng: 59.6168,
    cities: [
      ["mashhad", "\u0645\u0634\u0647\u062F", 3001184, 36.2605, 59.6168],
      ["neyshabur", "\u0646\u06CC\u0634\u0627\u0628\u0648\u0631", 264375, 36.2133, 58.7958],
      ["sabzevar", "\u0633\u0628\u0632\u0648\u0627\u0631", 243700, 36.2126, 57.6819],
      ["torbat-heydarieh", "\u062A\u0631\u0628\u062A \u062D\u06CC\u062F\u0631\u06CC\u0647", 152e3, 35.2739, 59.2196],
      ["quchan", "\u0642\u0648\u0686\u0627\u0646", 101e3, 37.1061, 58.5097],
      ["kashmar", "\u06A9\u0627\u0634\u0645\u0631", 1e5, 35.2383, 58.4656],
      ["torbat-jam", "\u062A\u0631\u0628\u062A \u062C\u0627\u0645", 1e5, 35.2439, 60.6225],
      ["gonabad", "\u06AF\u0646\u0627\u0628\u0627\u062F", 45e3, 34.3528, 58.6836],
      ["chenaran", "\u0686\u0646\u0627\u0631\u0627\u0646", 43e3, 36.6456, 59.1219],
      ["sarakhs", "\u0633\u0631\u062E\u0633", 4e4, 36.5386, 61.1583]
    ]
  },
  {
    slug: "esfahan",
    name: "\u0627\u0635\u0641\u0647\u0627\u0646",
    code: "IR-10",
    lat: 32.6539,
    lng: 51.666,
    cities: [
      ["esfahan", "\u0627\u0635\u0641\u0647\u0627\u0646", 1961260, 32.6539, 51.666],
      ["kashan", "\u06A9\u0627\u0634\u0627\u0646", 364482, 33.9831, 51.4364],
      ["khomeinishahr", "\u062E\u0645\u06CC\u0646\u06CC\u200C\u0634\u0647\u0631", 248e3, 32.685, 51.5211],
      ["najafabad", "\u0646\u062C\u0641\u200C\u0622\u0628\u0627\u062F", 235e3, 32.6342, 51.3667],
      ["shahinshahr", "\u0634\u0627\u0647\u06CC\u0646\u200C\u0634\u0647\u0631", 173e3, 32.8656, 51.5522],
      ["shahreza", "\u0634\u0647\u0631\u0636\u0627", 141e3, 32.0086, 51.8664],
      ["fooladshahr", "\u0641\u0648\u0644\u0627\u062F\u0634\u0647\u0631", 92e3, 32.4906, 51.4144],
      ["mobarakeh", "\u0645\u0628\u0627\u0631\u06A9\u0647", 65e3, 32.3464, 51.5039],
      ["zarrinshahr", "\u0632\u0631\u06CC\u0646\u200C\u0634\u0647\u0631", 57e3, 32.3897, 51.3767],
      ["golpayegan", "\u06AF\u0644\u067E\u0627\u06CC\u06AF\u0627\u0646", 51e3, 33.4536, 50.2881],
      ["natanz", "\u0646\u0637\u0646\u0632", 15e3, 33.5089, 51.9147]
    ]
  },
  {
    slug: "fars",
    name: "\u0641\u0627\u0631\u0633",
    code: "IR-07",
    lat: 29.5918,
    lng: 52.5837,
    cities: [
      ["shiraz", "\u0634\u06CC\u0631\u0627\u0632", 1565572, 29.5918, 52.5837],
      ["marvdasht", "\u0645\u0631\u0648\u062F\u0634\u062A", 148858, 29.8742, 52.8025],
      ["jahrom", "\u062C\u0647\u0631\u0645", 141634, 28.5, 53.56],
      ["fasa", "\u0641\u0633\u0627", 110825, 28.9383, 53.6482],
      ["kazerun", "\u06A9\u0627\u0632\u0631\u0648\u0646", 96683, 29.6194, 51.6541],
      ["lar", "\u0644\u0627\u0631", 65e3, 27.6817, 54.3342],
      ["darab", "\u062F\u0627\u0631\u0627\u0628", 65e3, 28.7519, 54.5444],
      ["sadra", "\u0635\u062F\u0631\u0627", 6e4, 29.7714, 52.4386],
      ["abadeh", "\u0622\u0628\u0627\u062F\u0647", 6e4, 31.1608, 52.6506],
      ["neyriz", "\u0646\u06CC\u200C\u0631\u06CC\u0632", 55e3, 29.1978, 54.3286],
      ["eqlid", "\u0627\u0642\u0644\u06CC\u062F", 5e4, 30.8981, 52.6906]
    ]
  },
  {
    slug: "azarbaijan-sharghi",
    name: "\u0622\u0630\u0631\u0628\u0627\u06CC\u062C\u0627\u0646 \u0634\u0631\u0642\u06CC",
    code: "IR-03",
    lat: 38.08,
    lng: 46.2919,
    cities: [
      ["tabriz", "\u062A\u0628\u0631\u06CC\u0632", 1558693, 38.08, 46.2919],
      ["maragheh", "\u0645\u0631\u0627\u063A\u0647", 175255, 37.3892, 46.2381],
      ["marand", "\u0645\u0631\u0646\u062F", 132e3, 38.4328, 45.7739],
      ["ahar", "\u0627\u0647\u0631", 1e5, 38.4769, 47.0678],
      ["miyaneh", "\u0645\u06CC\u0627\u0646\u0647", 96e3, 37.4214, 47.7156],
      ["bonab", "\u0628\u0646\u0627\u0628", 82e3, 37.3406, 46.0561],
      ["sarab", "\u0633\u0631\u0627\u0628", 45e3, 37.9408, 47.5364],
      ["azarshahr", "\u0622\u0630\u0631\u0634\u0647\u0631", 4e4, 37.7592, 45.9772],
      ["shabestar", "\u0634\u0628\u0633\u062A\u0631", 4e4, 38.1794, 45.7025]
    ]
  },
  {
    slug: "azarbaijan-gharbi",
    name: "\u0622\u0630\u0631\u0628\u0627\u06CC\u062C\u0627\u0646 \u063A\u0631\u0628\u06CC",
    code: "IR-04",
    lat: 37.5527,
    lng: 45.0761,
    cities: [
      ["urmia", "\u0627\u0631\u0648\u0645\u06CC\u0647", 736224, 37.5527, 45.0761],
      ["khoy", "\u062E\u0648\u06CC", 200985, 38.5503, 44.9522],
      ["mahabad", "\u0645\u0647\u0627\u0628\u0627\u062F", 168e3, 36.7681, 45.7222],
      ["bukan", "\u0628\u0648\u06A9\u0627\u0646", 152e3, 36.5211, 46.2089],
      ["miandoab", "\u0645\u06CC\u0627\u0646\u062F\u0648\u0622\u0628", 134425, 36.9694, 46.1028],
      ["salmas", "\u0633\u0644\u0645\u0627\u0633", 92e3, 38.1961, 44.7653],
      ["piranshahr", "\u067E\u06CC\u0631\u0627\u0646\u0634\u0647\u0631", 9e4, 36.6994, 45.1417],
      ["naqadeh", "\u0646\u0642\u062F\u0647", 8e4, 36.9553, 45.3881],
      ["maku", "\u0645\u0627\u06A9\u0648", 45e3, 39.2953, 44.5153]
    ]
  },
  {
    slug: "khuzestan",
    name: "\u062E\u0648\u0632\u0633\u062A\u0627\u0646",
    code: "IR-06",
    lat: 31.3183,
    lng: 48.6706,
    cities: [
      ["ahvaz", "\u0627\u0647\u0648\u0627\u0632", 1184788, 31.3183, 48.6706],
      ["dezful", "\u062F\u0632\u0641\u0648\u0644", 264e3, 32.3814, 48.4058],
      ["abadan", "\u0622\u0628\u0627\u062F\u0627\u0646", 231e3, 30.3392, 48.3043],
      ["bandar-mahshahr", "\u0628\u0646\u062F\u0631 \u0645\u0627\u0647\u0634\u0647\u0631", 162e3, 30.5589, 49.1981],
      ["andimeshk", "\u0627\u0646\u062F\u06CC\u0645\u0634\u06A9", 155e3, 32.4603, 48.3592],
      ["khorramshahr", "\u062E\u0631\u0645\u0634\u0647\u0631", 133e3, 30.4392, 48.1664],
      ["behbahan", "\u0628\u0647\u0628\u0647\u0627\u0646", 122e3, 30.5959, 50.2417],
      ["izeh", "\u0627\u06CC\u0630\u0647", 119e3, 31.8339, 49.8672],
      ["shushtar", "\u0634\u0648\u0634\u062A\u0631", 101e3, 32.0456, 48.8567],
      ["masjed-soleyman", "\u0645\u0633\u062C\u062F\u0633\u0644\u06CC\u0645\u0627\u0646", 1e5, 31.9364, 49.3039],
      ["ramhormoz", "\u0631\u0627\u0645\u0647\u0631\u0645\u0632", 75e3, 31.28, 49.6031],
      ["shush", "\u0634\u0648\u0634", 65e3, 32.1942, 48.2436]
    ]
  },
  {
    slug: "kerman",
    name: "\u06A9\u0631\u0645\u0627\u0646",
    code: "IR-08",
    lat: 30.2839,
    lng: 57.0834,
    cities: [
      ["kerman", "\u06A9\u0631\u0645\u0627\u0646", 738724, 30.2839, 57.0834],
      ["sirjan", "\u0633\u06CC\u0631\u062C\u0627\u0646", 265e3, 29.4408, 55.6797],
      ["rafsanjan", "\u0631\u0641\u0633\u0646\u062C\u0627\u0646", 161909, 30.4067, 55.9939],
      ["jiroft", "\u062C\u06CC\u0631\u0641\u062A", 13e4, 28.6753, 57.7408],
      ["bam", "\u0628\u0645", 127e3, 29.106, 58.357],
      ["zarand", "\u0632\u0631\u0646\u062F", 71e3, 30.8128, 56.5644],
      ["shahr-e-babak", "\u0634\u0647\u0631\u0628\u0627\u0628\u06A9", 5e4, 30.1181, 55.1236],
      ["bardsir", "\u0628\u0631\u062F\u0633\u06CC\u0631", 3e4, 29.9283, 56.5719]
    ]
  },
  {
    slug: "mazandaran",
    name: "\u0645\u0627\u0632\u0646\u062F\u0631\u0627\u0646",
    code: "IR-02",
    lat: 36.5633,
    lng: 53.0601,
    cities: [
      ["sari", "\u0633\u0627\u0631\u06CC", 309820, 36.5633, 53.0601],
      ["babol", "\u0628\u0627\u0628\u0644", 250217, 36.5513, 52.6789],
      ["amol", "\u0622\u0645\u0644", 237528, 36.4696, 52.3507],
      ["qaemshahr", "\u0642\u0627\u0626\u0645\u200C\u0634\u0647\u0631", 204953, 36.4631, 52.86],
      ["behshahr", "\u0628\u0647\u0634\u0647\u0631", 94e3, 36.6919, 53.5525],
      ["tonekabon", "\u062A\u0646\u06A9\u0627\u0628\u0646", 56e3, 36.8163, 50.8739],
      ["babolsar", "\u0628\u0627\u0628\u0644\u0633\u0631", 55e3, 36.7025, 52.6575],
      ["chalus", "\u0686\u0627\u0644\u0648\u0633", 55e3, 36.6553, 51.4203],
      ["neka", "\u0646\u06A9\u0627", 5e4, 36.6511, 53.2989],
      ["nowshahr", "\u0646\u0648\u0634\u0647\u0631", 47e3, 36.6486, 51.4964],
      ["fereydunkenar", "\u0641\u0631\u06CC\u062F\u0648\u0646\u06A9\u0646\u0627\u0631", 4e4, 36.6858, 52.5236],
      ["ramsar", "\u0631\u0627\u0645\u0633\u0631", 35e3, 36.9186, 50.6583]
    ]
  },
  {
    slug: "gilan",
    name: "\u06AF\u06CC\u0644\u0627\u0646",
    code: "IR-01",
    lat: 37.2808,
    lng: 49.5832,
    cities: [
      ["rasht", "\u0631\u0634\u062A", 679995, 37.2808, 49.5832],
      ["bandar-anzali", "\u0628\u0646\u062F\u0631 \u0627\u0646\u0632\u0644\u06CC", 118564, 37.4722, 49.4622],
      ["lahijan", "\u0644\u0627\u0647\u06CC\u062C\u0627\u0646", 101073, 37.2072, 50.0036],
      ["langarud", "\u0644\u0646\u06AF\u0631\u0648\u062F", 79e3, 37.1953, 50.1547],
      ["someh-sara", "\u0635\u0648\u0645\u0639\u0647\u200C\u0633\u0631\u0627", 47e3, 37.3081, 49.3225],
      ["astara", "\u0622\u0633\u062A\u0627\u0631\u0627", 51e3, 38.4292, 48.8722],
      ["talesh", "\u062A\u0627\u0644\u0634", 45e3, 37.8006, 48.9067],
      ["astaneh-ashrafiyeh", "\u0622\u0633\u062A\u0627\u0646\u0647 \u0627\u0634\u0631\u0641\u06CC\u0647", 4e4, 37.2622, 49.9439],
      ["rudsar", "\u0631\u0648\u062F\u0633\u0631", 4e4, 37.1372, 50.2892],
      ["fuman", "\u0641\u0648\u0645\u0646", 3e4, 37.2242, 49.3125]
    ]
  },
  {
    slug: "qom",
    name: "\u0642\u0645",
    code: "IR-25",
    lat: 34.6416,
    lng: 50.8746,
    cities: [
      ["qom", "\u0642\u0645", 1201158, 34.6416, 50.8746],
      ["salafchegan", "\u0633\u0644\u0641\u0686\u06AF\u0627\u0646", 8e3, 34.4844, 50.4736],
      ["jafarieh", "\u062C\u0639\u0641\u0631\u06CC\u0647", 1e4, 34.7883, 50.5081]
    ]
  },
  {
    slug: "qazvin",
    name: "\u0642\u0632\u0648\u06CC\u0646",
    code: "IR-26",
    lat: 36.2688,
    lng: 50.0041,
    cities: [
      ["qazvin", "\u0642\u0632\u0648\u06CC\u0646", 402748, 36.2688, 50.0041],
      ["takestan", "\u062A\u0627\u06A9\u0633\u062A\u0627\u0646", 84e3, 36.0697, 49.6961],
      ["alvand", "\u0627\u0644\u0648\u0646\u062F", 72e3, 36.1911, 50.0672],
      ["abyek", "\u0622\u0628\u06CC\u06A9", 45e3, 36.0389, 50.5311],
      ["buin-zahra", "\u0628\u0648\u0626\u06CC\u0646\u200C\u0632\u0647\u0631\u0627", 25e3, 35.7672, 50.0578]
    ]
  },
  {
    slug: "ardabil",
    name: "\u0627\u0631\u062F\u0628\u06CC\u0644",
    code: "IR-24",
    lat: 38.2498,
    lng: 48.2933,
    cities: [
      ["ardabil", "\u0627\u0631\u062F\u0628\u06CC\u0644", 529374, 38.2498, 48.2933],
      ["parsabad", "\u067E\u0627\u0631\u0633\u200C\u0622\u0628\u0627\u062F", 1e5, 39.6483, 47.9186],
      ["meshgin-shahr", "\u0645\u0634\u06AF\u06CC\u0646\u200C\u0634\u0647\u0631", 78e3, 38.3986, 47.6819],
      ["khalkhal", "\u062E\u0644\u062E\u0627\u0644", 41e3, 37.6189, 48.5258],
      ["germi", "\u06AF\u0631\u0645\u06CC", 4e4, 39.0139, 48.0803]
    ]
  },
  {
    slug: "zanjan",
    name: "\u0632\u0646\u062C\u0627\u0646",
    code: "IR-19",
    lat: 36.6736,
    lng: 48.4787,
    cities: [
      ["zanjan", "\u0632\u0646\u062C\u0627\u0646", 430871, 36.6736, 48.4787],
      ["abhar", "\u0627\u0628\u0647\u0631", 101e3, 36.1469, 49.2181],
      ["khorramdarreh", "\u062E\u0631\u0645\u062F\u0631\u0647", 6e4, 36.2072, 49.1892],
      ["qeydar", "\u0642\u06CC\u062F\u0627\u0631", 3e4, 36.1225, 48.5906]
    ]
  },
  {
    slug: "semnan",
    name: "\u0633\u0645\u0646\u0627\u0646",
    code: "IR-20",
    lat: 35.5729,
    lng: 53.3971,
    cities: [
      ["semnan", "\u0633\u0645\u0646\u0627\u0646", 185129, 35.5729, 53.3971],
      ["shahrud", "\u0634\u0627\u0647\u0631\u0648\u062F", 154e3, 36.4181, 54.9764],
      ["damghan", "\u062F\u0627\u0645\u063A\u0627\u0646", 6e4, 36.1683, 54.3489],
      ["garmsar", "\u06AF\u0631\u0645\u0633\u0627\u0631", 55e3, 35.2181, 52.3406],
      ["mahdishahr", "\u0645\u0647\u062F\u06CC\u200C\u0634\u0647\u0631", 25e3, 35.7047, 53.3542]
    ]
  },
  {
    slug: "golestan",
    name: "\u06AF\u0644\u0633\u062A\u0627\u0646",
    code: "IR-27",
    lat: 36.8427,
    lng: 54.4436,
    cities: [
      ["gorgan", "\u06AF\u0631\u06AF\u0627\u0646", 350676, 36.8427, 54.4436],
      ["gonbad-kavus", "\u06AF\u0646\u0628\u062F \u06A9\u0627\u0648\u0648\u0633", 151910, 37.25, 55.1672],
      ["aliabad-katul", "\u0639\u0644\u06CC\u200C\u0622\u0628\u0627\u062F \u06A9\u062A\u0648\u0644", 6e4, 36.9075, 54.8697],
      ["bandar-torkaman", "\u0628\u0646\u062F\u0631 \u062A\u0631\u06A9\u0645\u0646", 55e3, 36.8992, 54.0703],
      ["azadshahr", "\u0622\u0632\u0627\u062F\u0634\u0647\u0631", 4e4, 37.0894, 55.1739],
      ["aq-qala", "\u0622\u0642\u200C\u0642\u0644\u0627", 4e4, 37.0158, 54.4553],
      ["kordkuy", "\u06A9\u0631\u062F\u06A9\u0648\u06CC", 34e3, 36.7967, 54.1122],
      ["minudasht", "\u0645\u06CC\u0646\u0648\u062F\u0634\u062A", 3e4, 37.2286, 55.3739]
    ]
  },
  {
    slug: "khorasan-shomali",
    name: "\u062E\u0631\u0627\u0633\u0627\u0646 \u0634\u0645\u0627\u0644\u06CC",
    code: "IR-28",
    lat: 37.4747,
    lng: 57.329,
    cities: [
      ["bojnurd", "\u0628\u062C\u0646\u0648\u0631\u062F", 228931, 37.4747, 57.329],
      ["shirvan", "\u0634\u06CC\u0631\u0648\u0627\u0646", 84e3, 37.3961, 57.9294],
      ["esfarayen", "\u0627\u0633\u0641\u0631\u0627\u06CC\u0646", 6e4, 37.0764, 57.5103],
      ["ashkhaneh", "\u0622\u0634\u062E\u0627\u0646\u0647", 3e4, 37.5719, 56.9169],
      ["jajarm", "\u062C\u0627\u062C\u0631\u0645", 2e4, 36.9506, 56.3803]
    ]
  },
  {
    slug: "khorasan-jonoubi",
    name: "\u062E\u0631\u0627\u0633\u0627\u0646 \u062C\u0646\u0648\u0628\u06CC",
    code: "IR-29",
    lat: 32.8663,
    lng: 59.2211,
    cities: [
      ["birjand", "\u0628\u06CC\u0631\u062C\u0646\u062F", 203636, 32.8663, 59.2211],
      ["tabas", "\u0637\u0628\u0633", 4e4, 33.5959, 56.9244],
      ["qaen", "\u0642\u0627\u0626\u0646", 4e4, 33.7261, 59.1839],
      ["ferdows", "\u0641\u0631\u062F\u0648\u0633", 3e4, 34.0189, 58.1739],
      ["nehbandan", "\u0646\u0647\u0628\u0646\u062F\u0627\u0646", 15e3, 31.5417, 60.0367]
    ]
  },
  {
    slug: "sistan-baluchestan",
    name: "\u0633\u06CC\u0633\u062A\u0627\u0646 \u0648 \u0628\u0644\u0648\u0686\u0633\u062A\u0627\u0646",
    code: "IR-11",
    lat: 29.4963,
    lng: 60.8629,
    cities: [
      ["zahedan", "\u0632\u0627\u0647\u062F\u0627\u0646", 587730, 29.4963, 60.8629],
      ["zabol", "\u0632\u0627\u0628\u0644", 148e3, 31.0286, 61.5011],
      ["iranshahr", "\u0627\u06CC\u0631\u0627\u0646\u0634\u0647\u0631", 11e4, 27.2025, 60.6847],
      ["chabahar", "\u0686\u0627\u0628\u0647\u0627\u0631", 106e3, 25.2919, 60.643],
      ["khash", "\u062E\u0627\u0634", 9e4, 28.2211, 61.2158],
      ["saravan", "\u0633\u0631\u0627\u0648\u0627\u0646", 7e4, 27.3708, 62.335],
      ["konarak", "\u06A9\u0646\u0627\u0631\u06A9", 3e4, 25.3606, 60.3956]
    ]
  },
  {
    slug: "hormozgan",
    name: "\u0647\u0631\u0645\u0632\u06AF\u0627\u0646",
    code: "IR-22",
    lat: 27.1832,
    lng: 56.2666,
    cities: [
      ["bandar-abbas", "\u0628\u0646\u062F\u0631\u0639\u0628\u0627\u0633", 526648, 27.1832, 56.2666],
      ["minab", "\u0645\u06CC\u0646\u0627\u0628", 75e3, 27.1467, 57.08],
      ["kish", "\u06A9\u06CC\u0634", 4e4, 26.5578, 53.9803],
      ["qeshm", "\u0642\u0634\u0645", 4e4, 26.9581, 56.2719],
      ["bandar-lengeh", "\u0628\u0646\u062F\u0631 \u0644\u0646\u06AF\u0647", 3e4, 26.5578, 54.8808],
      ["rudan", "\u0631\u0648\u062F\u0627\u0646", 25e3, 27.4406, 57.1867]
    ]
  },
  {
    slug: "bushehr",
    name: "\u0628\u0648\u0634\u0647\u0631",
    code: "IR-18",
    lat: 28.9234,
    lng: 50.8203,
    cities: [
      ["bushehr", "\u0628\u0648\u0634\u0647\u0631", 223504, 28.9234, 50.8203],
      ["borazjan", "\u0628\u0631\u0627\u0632\u062C\u0627\u0646", 11e4, 29.2694, 51.2158],
      ["bandar-genaveh", "\u0628\u0646\u062F\u0631 \u06AF\u0646\u0627\u0648\u0647", 8e4, 29.5794, 50.5203],
      ["kangan", "\u06A9\u0646\u06AF\u0627\u0646", 4e4, 27.8372, 52.0619],
      ["asaluyeh", "\u0639\u0633\u0644\u0648\u06CC\u0647", 2e4, 27.4756, 52.6069],
      ["jam", "\u062C\u0645", 2e4, 27.8264, 52.3269]
    ]
  },
  {
    slug: "kohgiluyeh-boyerahmad",
    name: "\u06A9\u0647\u06AF\u06CC\u0644\u0648\u06CC\u0647 \u0648 \u0628\u0648\u06CC\u0631\u0627\u062D\u0645\u062F",
    code: "IR-17",
    lat: 30.6682,
    lng: 51.588,
    cities: [
      ["yasuj", "\u06CC\u0627\u0633\u0648\u062C", 134e3, 30.6682, 51.588],
      ["dogonbadan", "\u062F\u0648\u06AF\u0646\u0628\u062F\u0627\u0646 (\u06AF\u0686\u0633\u0627\u0631\u0627\u0646)", 85e3, 30.3583, 50.7981],
      ["dehdasht", "\u062F\u0647\u062F\u0634\u062A", 45e3, 30.7947, 50.5661],
      ["likak", "\u0644\u06CC\u06A9\u06A9", 1e4, 30.8925, 50.1414]
    ]
  },
  {
    slug: "chaharmahal-bakhtiari",
    name: "\u0686\u0647\u0627\u0631\u0645\u062D\u0627\u0644 \u0648 \u0628\u062E\u062A\u06CC\u0627\u0631\u06CC",
    code: "IR-14",
    lat: 32.3256,
    lng: 50.8644,
    cities: [
      ["shahrekord", "\u0634\u0647\u0631\u06A9\u0631\u062F", 159e3, 32.3256, 50.8644],
      ["borujen", "\u0628\u0631\u0648\u062C\u0646", 65e3, 31.9653, 51.2872],
      ["farsan", "\u0641\u0627\u0631\u0633\u0627\u0646", 4e4, 32.2567, 50.5661],
      ["lordegan", "\u0644\u0631\u062F\u06AF\u0627\u0646", 3e4, 31.5108, 50.8283]
    ]
  },
  {
    slug: "lorestan",
    name: "\u0644\u0631\u0633\u062A\u0627\u0646",
    code: "IR-15",
    lat: 33.4878,
    lng: 48.3558,
    cities: [
      ["khorramabad", "\u062E\u0631\u0645\u200C\u0622\u0628\u0627\u062F", 373416, 33.4878, 48.3558],
      ["borujerd", "\u0628\u0631\u0648\u062C\u0631\u062F", 234997, 33.8973, 48.7516],
      ["dorud", "\u062F\u0648\u0631\u0648\u062F", 11e4, 33.4989, 49.0575],
      ["aligudarz", "\u0627\u0644\u06CC\u06AF\u0648\u062F\u0631\u0632", 9e4, 33.4006, 49.6947],
      ["kuhdasht", "\u06A9\u0648\u0647\u062F\u0634\u062A", 8e4, 33.5347, 47.6058],
      ["nurabad", "\u0646\u0648\u0631\u0622\u0628\u0627\u062F", 6e4, 34.0728, 47.9722],
      ["azna", "\u0627\u0632\u0646\u0627", 45e3, 33.4553, 49.4553]
    ]
  },
  {
    slug: "ilam",
    name: "\u0627\u06CC\u0644\u0627\u0645",
    code: "IR-16",
    lat: 33.6374,
    lng: 46.4227,
    cities: [
      ["ilam", "\u0627\u06CC\u0644\u0627\u0645", 194030, 33.6374, 46.4227],
      ["dehloran", "\u062F\u0647\u0644\u0631\u0627\u0646", 4e4, 32.6942, 47.2678],
      ["eyvan", "\u0627\u06CC\u0648\u0627\u0646", 35e3, 33.8261, 46.3086],
      ["abdanan", "\u0622\u0628\u062F\u0627\u0646\u0627\u0646", 25e3, 32.9925, 47.4192],
      ["mehran", "\u0645\u0647\u0631\u0627\u0646", 2e4, 33.1219, 46.1656]
    ]
  },
  {
    slug: "kermanshah",
    name: "\u06A9\u0631\u0645\u0627\u0646\u0634\u0627\u0647",
    code: "IR-05",
    lat: 34.3142,
    lng: 47.065,
    cities: [
      ["kermanshah", "\u06A9\u0631\u0645\u0627\u0646\u0634\u0627\u0647", 946651, 34.3142, 47.065],
      ["eslamabad-gharb", "\u0627\u0633\u0644\u0627\u0645\u200C\u0622\u0628\u0627\u062F \u063A\u0631\u0628", 1e5, 34.1097, 46.5275],
      ["javanrud", "\u062C\u0648\u0627\u0646\u0631\u0648\u062F", 6e4, 34.7986, 46.4914],
      ["harsin", "\u0647\u0631\u0633\u06CC\u0646", 55e3, 34.2722, 47.5867],
      ["kangavar", "\u06A9\u0646\u06AF\u0627\u0648\u0631", 5e4, 34.5042, 47.965],
      ["sarpol-zahab", "\u0633\u0631\u067E\u0644 \u0630\u0647\u0627\u0628", 45e3, 34.4611, 45.8642],
      ["sonqor", "\u0633\u0646\u0642\u0631", 4e4, 34.7828, 47.5992],
      ["paveh", "\u067E\u0627\u0648\u0647", 25e3, 35.0433, 46.3564]
    ]
  },
  {
    slug: "kordestan",
    name: "\u06A9\u0631\u062F\u0633\u062A\u0627\u0646",
    code: "IR-12",
    lat: 35.3219,
    lng: 46.9862,
    cities: [
      ["sanandaj", "\u0633\u0646\u0646\u062F\u062C", 412767, 35.3219, 46.9862],
      ["saqqez", "\u0633\u0642\u0632", 162e3, 36.2494, 46.2739],
      ["marivan", "\u0645\u0631\u06CC\u0648\u0627\u0646", 136e3, 35.5219, 46.1856],
      ["baneh", "\u0628\u0627\u0646\u0647", 108e3, 35.9975, 45.885],
      ["qorveh", "\u0642\u0631\u0648\u0647", 8e4, 35.1656, 47.805],
      ["bijar", "\u0628\u06CC\u062C\u0627\u0631", 5e4, 35.8722, 47.605],
      ["kamyaran", "\u06A9\u0627\u0645\u06CC\u0627\u0631\u0627\u0646", 4e4, 34.7953, 46.9353]
    ]
  },
  {
    slug: "hamedan",
    name: "\u0647\u0645\u062F\u0627\u0646",
    code: "IR-13",
    lat: 34.7983,
    lng: 48.5148,
    cities: [
      ["hamedan", "\u0647\u0645\u062F\u0627\u0646", 554406, 34.7983, 48.5148],
      ["malayer", "\u0645\u0644\u0627\u06CC\u0631", 17e4, 34.2972, 48.8239],
      ["nahavand", "\u0646\u0647\u0627\u0648\u0646\u062F", 78e3, 34.1889, 48.3767],
      ["asadabad", "\u0627\u0633\u062F\u0622\u0628\u0627\u062F", 55e3, 34.7822, 48.1181],
      ["tuyserkan", "\u062A\u0648\u06CC\u0633\u0631\u06A9\u0627\u0646", 45e3, 34.5453, 48.4453],
      ["bahar", "\u0628\u0647\u0627\u0631", 35e3, 34.9078, 48.4406],
      ["kabudarahang", "\u06A9\u0628\u0648\u062F\u0631\u0622\u0647\u0646\u06AF", 25e3, 35.2078, 48.7239]
    ]
  },
  {
    slug: "yazd",
    name: "\u06CC\u0632\u062F",
    code: "IR-21",
    lat: 31.8974,
    lng: 54.3569,
    cities: [
      ["yazd", "\u06CC\u0632\u062F", 529673, 31.8974, 54.3569],
      ["meybod", "\u0645\u06CC\u0628\u062F", 9e4, 32.2497, 54.0164],
      ["ardakan", "\u0627\u0631\u062F\u06A9\u0627\u0646", 8e4, 32.31, 54.0175],
      ["bafq", "\u0628\u0627\u0641\u0642", 4e4, 31.6058, 55.4142],
      ["mehriz", "\u0645\u0647\u0631\u06CC\u0632", 4e4, 31.5811, 54.4344],
      ["abarkuh", "\u0627\u0628\u0631\u06A9\u0648\u0647", 3e4, 31.1281, 53.28],
      ["taft", "\u062A\u0641\u062A", 15e3, 31.7472, 54.2069]
    ]
  },
  {
    slug: "markazi",
    name: "\u0645\u0631\u06A9\u0632\u06CC",
    code: "IR-00",
    lat: 34.0954,
    lng: 49.7013,
    cities: [
      ["arak", "\u0627\u0631\u0627\u06A9", 520944, 34.0954, 49.7013],
      ["saveh", "\u0633\u0627\u0648\u0647", 179e3, 35.0213, 50.3566],
      ["khomeyn", "\u062E\u0645\u06CC\u0646", 75e3, 33.6386, 50.0789],
      ["mahallat", "\u0645\u062D\u0644\u0627\u062A", 45e3, 33.9086, 50.4553],
      ["delijan", "\u062F\u0644\u06CC\u062C\u0627\u0646", 4e4, 33.9908, 50.6839],
      ["shazand", "\u0634\u0627\u0632\u0646\u062F", 4e4, 33.9269, 49.4108],
      ["tafresh", "\u062A\u0641\u0631\u0634", 15e3, 34.6919, 50.0122]
    ]
  }
];
var MAJOR_CITY_THRESHOLD = 3e5;
var PROVINCES = Object.freeze(
  PROVINCE_SEED.map((province) => ({
    slug: province.slug,
    name: province.name,
    code: province.code,
    capitalSlug: province.cities[0][0],
    lat: province.lat,
    lng: province.lng
  }))
);
var CITIES = Object.freeze(
  PROVINCE_SEED.flatMap(
    (province) => province.cities.map(([slug, name, population, lat, lng], index) => ({
      slug,
      name,
      provinceSlug: province.slug,
      populationWeight: population,
      isProvinceCapital: index === 0,
      isMajor: index === 0 || population >= MAJOR_CITY_THRESHOLD,
      lat: lat ?? null,
      lng: lng ?? null
    }))
  )
);
var M = "MUNICIPAL";
var N = "NEIGHBORHOOD";
var TEHRAN_DISTRICTS = [
  [
    "mantaghe-1",
    "\u0645\u0646\u0637\u0642\u0647 \u06F1",
    M,
    95,
    "\u0634\u0645\u06CC\u0631\u0627\u0646\u0627\u062A",
    "\u062A\u062C\u0631\u06CC\u0634",
    "\u0632\u0639\u0641\u0631\u0627\u0646\u06CC\u0647",
    "\u0648\u0644\u0646\u062C\u06A9",
    "\u0646\u06CC\u0627\u0648\u0631\u0627\u0646",
    "\u0641\u0631\u0645\u0627\u0646\u06CC\u0647",
    "\u0627\u0644\u0647\u06CC\u0647",
    "\u0642\u06CC\u0637\u0631\u06CC\u0647",
    "\u062F\u0631\u0628\u0646\u062F"
  ],
  ["mantaghe-2", "\u0645\u0646\u0637\u0642\u0647 \u06F2", M, 92, "\u0633\u0639\u0627\u062F\u062A\u200C\u0622\u0628\u0627\u062F", "\u0634\u0647\u0631\u06A9 \u063A\u0631\u0628", "\u0645\u0631\u0632\u062F\u0627\u0631\u0627\u0646", "\u0637\u0631\u0634\u062A", "\u0635\u0627\u062F\u0642\u06CC\u0647", "\u06AF\u06CC\u0634\u0627"],
  ["mantaghe-3", "\u0645\u0646\u0637\u0642\u0647 \u06F3", M, 94, "\u0648\u0646\u06A9", "\u062C\u0631\u062F\u0646", "\u0645\u06CC\u0631\u062F\u0627\u0645\u0627\u062F", "\u0638\u0641\u0631", "\u067E\u0627\u0633\u062F\u0627\u0631\u0627\u0646 \u062C\u0646\u0648\u0628\u06CC", "\u062F\u0627\u0648\u0648\u062F\u06CC\u0647"],
  ["mantaghe-4", "\u0645\u0646\u0637\u0642\u0647 \u06F4", M, 78, "\u062A\u0647\u0631\u0627\u0646\u067E\u0627\u0631\u0633", "\u067E\u0627\u0633\u062F\u0627\u0631\u0627\u0646", "\u062D\u06A9\u06CC\u0645\u06CC\u0647", "\u0645\u062C\u06CC\u062F\u06CC\u0647", "\u0634\u0645\u0633\u200C\u0622\u0628\u0627\u062F", "\u0647\u0631\u0648\u06CC"],
  ["mantaghe-5", "\u0645\u0646\u0637\u0642\u0647 \u06F5", M, 85, "\u067E\u0648\u0646\u06A9", "\u062C\u0646\u062A\u200C\u0622\u0628\u0627\u062F", "\u0634\u0647\u0631\u0627\u0646", "\u0628\u0644\u0648\u0627\u0631 \u0641\u0631\u062F\u0648\u0633", "\u0628\u0627\u063A \u0641\u06CC\u0636", "\u0627\u0646\u062F\u06CC\u0634\u0647"],
  [
    "mantaghe-6",
    "\u0645\u0646\u0637\u0642\u0647 \u06F6",
    M,
    88,
    "\u0627\u0645\u06CC\u0631\u0622\u0628\u0627\u062F",
    "\u06CC\u0648\u0633\u0641\u200C\u0622\u0628\u0627\u062F",
    "\u0641\u0627\u0637\u0645\u06CC",
    "\u06A9\u0631\u06CC\u0645\u062E\u0627\u0646",
    "\u0622\u0631\u0698\u0627\u0646\u062A\u06CC\u0646",
    "\u0648\u0644\u06CC\u0639\u0635\u0631"
  ],
  ["mantaghe-7", "\u0645\u0646\u0637\u0642\u0647 \u06F7", M, 74, "\u0628\u0647\u0634\u062A\u06CC", "\u0633\u0647\u0631\u0648\u0631\u062F\u06CC", "\u0646\u0638\u0627\u0645\u200C\u0622\u0628\u0627\u062F", "\u062F\u0628\u0633\u062A\u0627\u0646", "\u0627\u0645\u062C\u062F\u06CC\u0647"],
  ["mantaghe-8", "\u0645\u0646\u0637\u0642\u0647 \u06F8", M, 66, "\u0646\u0627\u0631\u0645\u06A9", "\u062A\u0647\u0631\u0627\u0646\u200C\u0646\u0648", "\u0645\u062C\u06CC\u062F\u06CC\u0647 \u0634\u0645\u0627\u0644\u06CC", "\u0648\u062D\u06CC\u062F\u06CC\u0647"],
  ["mantaghe-9", "\u0645\u0646\u0637\u0642\u0647 \u06F9", M, 42, "\u0645\u0647\u0631\u0622\u0628\u0627\u062F", "\u0627\u0633\u062A\u0627\u062F \u0645\u0639\u06CC\u0646", "\u062F\u06A9\u062A\u0631 \u0647\u0648\u0634\u06CC\u0627\u0631"],
  ["mantaghe-10", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F0", M, 45, "\u0633\u0644\u06CC\u0645\u0627\u0646\u06CC", "\u0647\u0627\u0634\u0645\u06CC", "\u0628\u0631\u06CC\u0627\u0646\u06A9", "\u0632\u0646\u062C\u0627\u0646 \u062C\u0646\u0648\u0628\u06CC"],
  ["mantaghe-11", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F1", M, 50, "\u062C\u0645\u0647\u0648\u0631\u06CC", "\u0627\u0646\u0642\u0644\u0627\u0628", "\u0641\u0644\u0633\u0637\u06CC\u0646", "\u062D\u0631", "\u0627\u0645\u06CC\u0631\u0628\u0647\u0627\u062F\u0631"],
  ["mantaghe-12", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F2", M, 48, "\u0628\u0627\u0632\u0627\u0631", "\u067E\u0627\u0646\u0632\u062F\u0647 \u062E\u0631\u062F\u0627\u062F", "\u0627\u0645\u0627\u0645 \u062E\u0645\u06CC\u0646\u06CC", "\u067E\u0627\u0645\u0646\u0627\u0631", "\u0645\u0648\u0644\u0648\u06CC"],
  ["mantaghe-13", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F3", M, 44, "\u067E\u06CC\u0631\u0648\u0632\u06CC", "\u0646\u06CC\u0631\u0648\u06CC \u0647\u0648\u0627\u06CC\u06CC", "\u062F\u0647\u0642\u0627\u0646", "\u0635\u0641\u0627"],
  ["mantaghe-14", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F4", M, 42, "\u0627\u0641\u0633\u0631\u06CC\u0647", "\u0634\u0647\u06CC\u062F \u0645\u062D\u0644\u0627\u062A\u06CC", "\u0627\u0628\u0648\u0630\u0631", "\u0645\u06CC\u0646\u0627\u06CC \u062C\u0646\u0648\u0628\u06CC"],
  ["mantaghe-15", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F5", M, 40, "\u0645\u0634\u06CC\u0631\u06CC\u0647", "\u062E\u0627\u0648\u0631\u0634\u0647\u0631", "\u0648\u0627\u0644\u0641\u062C\u0631", "\u0627\u062A\u0627\u0628\u06A9"],
  ["mantaghe-16", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F6", M, 38, "\u062C\u0648\u0627\u062F\u06CC\u0647", "\u0646\u0627\u0632\u06CC\u200C\u0622\u0628\u0627\u062F", "\u062E\u0632\u0627\u0646\u0647", "\u0631\u0627\u0647\u200C\u0622\u0647\u0646"],
  ["mantaghe-17", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F7", M, 34, "\u0632\u0645\u0632\u0645", "\u0627\u0645\u0627\u0645\u200C\u0632\u0627\u062F\u0647 \u062D\u0633\u0646", "\u0641\u0644\u0627\u062D", "\u06CC\u0627\u062E\u0686\u06CC\u200C\u0622\u0628\u0627\u062F"],
  ["mantaghe-18", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F8", M, 34, "\u06CC\u0627\u0641\u062A\u200C\u0622\u0628\u0627\u062F", "\u0634\u0627\u062F\u0622\u0628\u0627\u062F", "\u0635\u0627\u062D\u0628\u200C\u0627\u0644\u0632\u0645\u0627\u0646"],
  ["mantaghe-19", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F9", M, 32, "\u062E\u0627\u0646\u06CC\u200C\u0622\u0628\u0627\u062F \u0646\u0648", "\u0639\u0628\u062F\u0644\u200C\u0622\u0628\u0627\u062F", "\u0646\u0639\u0645\u062A\u200C\u0622\u0628\u0627\u062F", "\u0627\u0633\u0641\u0646\u062F\u06CC\u0627\u0631\u06CC"],
  ["mantaghe-20", "\u0645\u0646\u0637\u0642\u0647 \u06F2\u06F0", M, 40, "\u0634\u0647\u0631\u0631\u06CC", "\u062F\u0648\u0644\u062A\u200C\u0622\u0628\u0627\u062F", "\u062C\u0648\u0627\u0646\u0645\u0631\u062F \u0642\u0635\u0627\u0628", "\u0627\u0633\u062A\u062E\u0631"],
  ["mantaghe-21", "\u0645\u0646\u0637\u0642\u0647 \u06F2\u06F1", M, 40, "\u062A\u0647\u0631\u0627\u0646\u0633\u0631", "\u0634\u0647\u0631\u06A9 \u0627\u0633\u062A\u0642\u0644\u0627\u0644", "\u0634\u0647\u0631\u06A9 \u062F\u0627\u0646\u0634\u06AF\u0627\u0647", "\u0648\u0631\u062F\u0622\u0648\u0631\u062F"],
  ["mantaghe-22", "\u0645\u0646\u0637\u0642\u0647 \u06F2\u06F2", M, 70, "\u0634\u0647\u0631\u06A9 \u06AF\u0644\u0633\u062A\u0627\u0646", "\u062F\u0631\u06CC\u0627\u0686\u0647 \u0686\u06CC\u062A\u06AF\u0631", "\u0634\u0647\u0631\u06A9 \u0631\u0627\u0647\u200C\u0622\u0647\u0646", "\u0634\u0647\u06CC\u062F \u0628\u0627\u0642\u0631\u06CC"],
  ["saadat-abad", "\u0633\u0639\u0627\u062F\u062A\u200C\u0622\u0628\u0627\u062F", N, 100, "\u0633\u0639\u0627\u062F\u062A \u0622\u0628\u0627\u062F", "\u0633\u0631\u0648", "\u06A9\u0627\u062C"],
  ["shahrak-gharb", "\u0634\u0647\u0631\u06A9 \u063A\u0631\u0628", N, 96, "\u0634\u0647\u0631\u06A9 \u0642\u062F\u0633", "\u0627\u06CC\u0648\u0627\u0646\u06A9"],
  ["vanak", "\u0648\u0646\u06A9", N, 94, "\u0645\u06CC\u062F\u0627\u0646 \u0648\u0646\u06A9", "\u06AF\u0627\u0646\u062F\u06CC"],
  ["jordan", "\u062C\u0631\u062F\u0646", N, 92, "\u0622\u0641\u0631\u06CC\u0642\u0627", "\u0646\u0644\u0633\u0648\u0646 \u0645\u0627\u0646\u062F\u0644\u0627"],
  ["mirdamad", "\u0645\u06CC\u0631\u062F\u0627\u0645\u0627\u062F", N, 88, "\u0645\u06CC\u0631 \u062F\u0627\u0645\u0627\u062F", "\u0646\u0641\u062A"],
  ["pasdaran", "\u067E\u0627\u0633\u062F\u0627\u0631\u0627\u0646", N, 90, "\u0628\u0648\u0633\u062A\u0627\u0646", "\u06AF\u0644\u0633\u062A\u0627\u0646"],
  ["niavaran", "\u0646\u06CC\u0627\u0648\u0631\u0627\u0646", N, 86, "\u06A9\u0627\u0634\u0627\u0646\u06A9", "\u062C\u0645\u0627\u0631\u0627\u0646"],
  ["zafaraniyeh", "\u0632\u0639\u0641\u0631\u0627\u0646\u06CC\u0647", N, 88, "\u0632\u0639\u0641\u0631\u0627\u0646\u06CC\u0647", "\u0645\u0642\u062F\u0633 \u0627\u0631\u062F\u0628\u06CC\u0644\u06CC"],
  ["elahiyeh", "\u0627\u0644\u0647\u06CC\u0647", N, 90, "\u0641\u0631\u0634\u062A\u0647", "\u0645\u0631\u06CC\u0645"],
  ["fereshteh", "\u0641\u0631\u0634\u062A\u0647", N, 84, "\u062E\u06CC\u0627\u0628\u0627\u0646 \u0641\u0631\u0634\u062A\u0647", "\u0634\u0631\u06CC\u0641\u06CC\u200C\u0645\u0646\u0634"],
  ["tajrish", "\u062A\u062C\u0631\u06CC\u0634", N, 82, "\u0645\u06CC\u062F\u0627\u0646 \u062A\u062C\u0631\u06CC\u0634", "\u062F\u0631\u0628\u0646\u062F"],
  ["gheytarieh", "\u0642\u06CC\u0637\u0631\u06CC\u0647", N, 80, "\u0642\u06CC\u0637\u0631\u06CC\u0647", "\u06A9\u0627\u0645\u0631\u0627\u0646\u06CC\u0647"],
  ["farmanieh", "\u0641\u0631\u0645\u0627\u0646\u06CC\u0647", N, 78, "\u0644\u0648\u0627\u0633\u0627\u0646\u06CC", "\u062F\u06CC\u0628\u0627\u062C\u06CC"],
  ["darous", "\u062F\u0631\u0648\u0633", N, 78, "\u062F\u0631\u0648\u0633", "\u0647\u062F\u0627\u06CC\u062A"],
  ["aqdasiyeh", "\u0627\u0642\u062F\u0633\u06CC\u0647", N, 74, "\u0627\u0632\u06AF\u0644", "\u0633\u0648\u0647\u0627\u0646\u06A9"],
  ["velenjak", "\u0648\u0644\u0646\u062C\u06A9", N, 76, "\u0648\u0644\u0646\u062C\u06A9", "\u06CC\u0645\u0646"],
  ["heravi", "\u0647\u0631\u0648\u06CC", N, 76, "\u0647\u0631\u0648\u06CC", "\u0648\u0641\u0627\u0645\u0646\u0634"],
  ["tehranpars", "\u062A\u0647\u0631\u0627\u0646\u067E\u0627\u0631\u0633", N, 84, "\u062A\u0647\u0631\u0627\u0646 \u067E\u0627\u0631\u0633", "\u0641\u0644\u06A9\u0647 \u0627\u0648\u0644 \u062A\u0647\u0631\u0627\u0646\u067E\u0627\u0631\u0633"],
  ["narmak", "\u0646\u0627\u0631\u0645\u06A9", N, 80, "\u0646\u0627\u0631\u0645\u06A9", "\u0645\u06CC\u062F\u0627\u0646 \u0647\u0644\u0627\u0644 \u0627\u062D\u0645\u0631"],
  ["punak", "\u067E\u0648\u0646\u06A9", N, 84, "\u067E\u0648\u0646\u06A9", "\u0645\u06CC\u062F\u0627\u0646 \u067E\u0648\u0646\u06A9"],
  ["jannat-abad", "\u062C\u0646\u062A\u200C\u0622\u0628\u0627\u062F", N, 82, "\u062C\u0646\u062A \u0622\u0628\u0627\u062F", "\u0634\u0627\u0647\u06CC\u0646 \u0634\u0645\u0627\u0644\u06CC"],
  ["shahran", "\u0634\u0647\u0631\u0627\u0646", N, 76, "\u0634\u0647\u0631\u0627\u0646", "\u06A9\u0648\u0647\u0633\u0627\u0631"],
  ["sattarkhan", "\u0633\u062A\u0627\u0631\u062E\u0627\u0646", N, 78, "\u0633\u062A\u0627\u0631\u062E\u0627\u0646", "\u0634\u0627\u062F\u0645\u0647\u0631"],
  ["sadeghiyeh", "\u0635\u0627\u062F\u0642\u06CC\u0647", N, 80, "\u0635\u0627\u062F\u0642\u06CC\u0647", "\u0641\u0644\u06A9\u0647 \u062F\u0648\u0645 \u0635\u0627\u062F\u0642\u06CC\u0647", "\u0622\u0631\u06CC\u0627\u0634\u0647\u0631"],
  ["gisha", "\u06AF\u06CC\u0634\u0627", N, 72, "\u06AF\u06CC\u0634\u0627", "\u06A9\u0648\u06CC \u0646\u0635\u0631"],
  ["marzdaran", "\u0645\u0631\u0632\u062F\u0627\u0631\u0627\u0646", N, 72, "\u0645\u0631\u0632\u062F\u0627\u0631\u0627\u0646", "\u0622\u0631\u06CC\u0627\u0641\u0631"],
  ["amirabad", "\u0627\u0645\u06CC\u0631\u0622\u0628\u0627\u062F", N, 70, "\u0627\u0645\u06CC\u0631 \u0622\u0628\u0627\u062F", "\u06A9\u0627\u0631\u06AF\u0631 \u0634\u0645\u0627\u0644\u06CC"],
  ["yousef-abad", "\u06CC\u0648\u0633\u0641\u200C\u0622\u0628\u0627\u062F", N, 74, "\u06CC\u0648\u0633\u0641 \u0622\u0628\u0627\u062F", "\u0627\u0633\u062F\u0622\u0628\u0627\u062F\u06CC"],
  ["valiasr", "\u0648\u0644\u06CC\u0639\u0635\u0631", N, 86, "\u062E\u06CC\u0627\u0628\u0627\u0646 \u0648\u0644\u06CC\u0639\u0635\u0631", "\u0648\u0646\u06A9 \u062A\u0627 \u062A\u062C\u0631\u06CC\u0634"],
  ["boulevard-ferdows", "\u0628\u0644\u0648\u0627\u0631 \u0641\u0631\u062F\u0648\u0633", N, 78, "\u0641\u0631\u062F\u0648\u0633 \u063A\u0631\u0628", "\u0633\u0627\u0632\u0645\u0627\u0646 \u0628\u0631\u0646\u0627\u0645\u0647"],
  ["ekbatan", "\u0627\u06A9\u0628\u0627\u062A\u0627\u0646", N, 70, "\u0634\u0647\u0631\u06A9 \u0627\u06A9\u0628\u0627\u062A\u0627\u0646", "\u0628\u06CC\u0645\u0647"],
  ["chitgar", "\u0686\u06CC\u062A\u06AF\u0631", N, 72, "\u062F\u0631\u06CC\u0627\u0686\u0647 \u0686\u06CC\u062A\u06AF\u0631", "\u0634\u0647\u0631\u06A9 \u06AF\u0644\u0633\u062A\u0627\u0646"],
  ["seyed-khandan", "\u0633\u06CC\u062F\u062E\u0646\u062F\u0627\u0646", N, 70, "\u0633\u06CC\u062F \u062E\u0646\u062F\u0627\u0646", "\u062F\u0628\u0633\u062A\u0627\u0646"],
  ["haft-tir", "\u0647\u0641\u062A \u062A\u06CC\u0631", N, 68, "\u0645\u06CC\u062F\u0627\u0646 \u0647\u0641\u062A \u062A\u06CC\u0631", "\u0645\u0641\u062A\u062D"],
  ["shariati", "\u0634\u0631\u06CC\u0639\u062A\u06CC", N, 72, "\u062E\u06CC\u0627\u0628\u0627\u0646 \u0634\u0631\u06CC\u0639\u062A\u06CC", "\u0642\u0644\u0647\u06A9"],
  ["gholhak", "\u0642\u0644\u0647\u06A9", N, 74, "\u0642\u0644\u0647\u06A9", "\u062F\u0648\u0644\u062A"],
  ["shahr-e-ray", "\u0634\u0647\u0631\u0631\u06CC", N, 56, "\u0634\u0647\u0631 \u0631\u06CC", "\u062D\u0631\u0645 \u0639\u0628\u062F\u0627\u0644\u0639\u0638\u06CC\u0645"],
  ["afsariyeh", "\u0627\u0641\u0633\u0631\u06CC\u0647", N, 50, "\u0627\u0641\u0633\u0631\u06CC\u0647", "\u062E\u0627\u0648\u0631\u0627\u0646"],
  ["piroozi", "\u067E\u06CC\u0631\u0648\u0632\u06CC", N, 54, "\u062E\u06CC\u0627\u0628\u0627\u0646 \u067E\u06CC\u0631\u0648\u0632\u06CC", "\u0646\u0628\u0631\u062F"]
];
var MASHHAD_DISTRICTS = [
  ["mantaghe-1", "\u0645\u0646\u0637\u0642\u0647 \u06F1", M, 88, "\u0627\u062D\u0645\u062F\u0622\u0628\u0627\u062F", "\u06A9\u0648\u0647\u0633\u0646\u06AF\u06CC", "\u0645\u0644\u06A9\u200C\u0622\u0628\u0627\u062F", "\u0633\u0646\u0627\u0628\u0627\u062F"],
  ["mantaghe-2", "\u0645\u0646\u0637\u0642\u0647 \u06F2", M, 60, "\u0637\u0628\u0631\u0633\u06CC \u0634\u0645\u0627\u0644\u06CC", "\u0642\u0627\u0626\u0645", "\u0634\u0647\u06CC\u062F \u0642\u0631\u0628\u0627\u0646\u06CC"],
  ["mantaghe-3", "\u0645\u0646\u0637\u0642\u0647 \u06F3", M, 58, "\u0637\u0628\u0631\u0633\u06CC", "\u06AF\u0644\u0634\u0647\u0631", "\u062E\u0648\u0627\u062C\u0647 \u0631\u0628\u06CC\u0639"],
  ["mantaghe-4", "\u0645\u0646\u0637\u0642\u0647 \u06F4", M, 56, "\u0627\u0628\u0648\u0637\u0627\u0644\u0628", "\u0645\u0641\u062A\u062D", "\u0634\u0647\u06CC\u062F \u0647\u0627\u0634\u0645\u06CC\u200C\u0646\u0698\u0627\u062F"],
  ["mantaghe-5", "\u0645\u0646\u0637\u0642\u0647 \u06F5", M, 62, "\u0627\u0644\u062A\u06CC\u0645\u0648\u0631", "\u06A9\u0627\u0631\u06AF\u0631", "\u0631\u0627\u0647\u200C\u0622\u0647\u0646"],
  ["mantaghe-6", "\u0645\u0646\u0637\u0642\u0647 \u06F6", M, 60, "\u0647\u0641\u062A \u062A\u06CC\u0631", "\u0641\u0644\u0633\u0637\u06CC\u0646", "\u0633\u062C\u0627\u062F\u06CC\u0647"],
  ["mantaghe-7", "\u0645\u0646\u0637\u0642\u0647 \u06F7", M, 55, "\u0642\u0627\u0633\u0645\u200C\u0622\u0628\u0627\u062F \u0634\u0631\u0642\u06CC", "\u0634\u0647\u0631\u06A9 \u0634\u0647\u06CC\u062F \u0631\u062C\u0627\u06CC\u06CC"],
  ["mantaghe-8", "\u0645\u0646\u0637\u0642\u0647 \u06F8", M, 82, "\u0633\u062C\u0627\u062F", "\u0641\u0631\u0627\u0645\u0631\u0632 \u0639\u0628\u0627\u0633\u06CC", "\u0631\u0627\u0647\u0646\u0645\u0627\u06CC\u06CC", "\u0628\u0647\u0627\u0631\u0633\u062A\u0627\u0646"],
  ["mantaghe-9", "\u0645\u0646\u0637\u0642\u0647 \u06F9", M, 90, "\u0648\u06A9\u06CC\u0644\u200C\u0622\u0628\u0627\u062F", "\u0647\u0627\u0634\u0645\u06CC\u0647", "\u0635\u06CC\u0627\u062F \u0634\u06CC\u0631\u0627\u0632\u06CC", "\u062F\u0627\u0646\u0634\u062C\u0648"],
  ["mantaghe-10", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F0", M, 58, "\u0627\u0644\u0647\u06CC\u0647", "\u0627\u0645\u0627\u0645 \u0647\u0627\u062F\u06CC", "\u0634\u0647\u0631\u06A9 \u0627\u0628\u0648\u0630\u0631"],
  ["mantaghe-11", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F1", M, 72, "\u0642\u0627\u0633\u0645\u200C\u0622\u0628\u0627\u062F", "\u0627\u0646\u062F\u06CC\u0634\u0647", "\u0634\u0631\u06CC\u0639\u062A\u06CC"],
  ["mantaghe-12", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F2", M, 50, "\u0634\u0647\u0631\u06A9 \u0635\u0646\u0639\u062A\u06CC", "\u06A9\u0634\u0627\u0648\u0631\u0632", "\u0645\u06CC\u062B\u0645"],
  ["mantaghe-samen", "\u0645\u0646\u0637\u0642\u0647 \u062B\u0627\u0645\u0646", M, 70, "\u062D\u0631\u0645 \u0645\u0637\u0647\u0631", "\u0627\u0645\u0627\u0645 \u0631\u0636\u0627", "\u0646\u0648\u0627\u0628 \u0635\u0641\u0648\u06CC"],
  ["ahmadabad", "\u0627\u062D\u0645\u062F\u0622\u0628\u0627\u062F", N, 96, "\u0627\u062D\u0645\u062F \u0622\u0628\u0627\u062F", "\u062E\u06CC\u0627\u0628\u0627\u0646 \u0627\u062D\u0645\u062F\u0622\u0628\u0627\u062F"],
  ["vakilabad", "\u0648\u06A9\u06CC\u0644\u200C\u0622\u0628\u0627\u062F", N, 100, "\u0648\u06A9\u06CC\u0644 \u0622\u0628\u0627\u062F", "\u0628\u0644\u0648\u0627\u0631 \u0648\u06A9\u06CC\u0644\u200C\u0622\u0628\u0627\u062F"],
  ["hashemiyeh", "\u0647\u0627\u0634\u0645\u06CC\u0647", N, 92, "\u0647\u0627\u0634\u0645\u06CC\u0647", "\u0647\u0641\u062A \u062A\u06CC\u0631 \u0647\u0627\u0634\u0645\u06CC\u0647"],
  ["sajjad", "\u0633\u062C\u0627\u062F", N, 94, "\u0628\u0644\u0648\u0627\u0631 \u0633\u062C\u0627\u062F", "\u0628\u0632\u0631\u06AF\u0645\u0647\u0631"],
  ["kuhsangi", "\u06A9\u0648\u0647\u0633\u0646\u06AF\u06CC", N, 86, "\u06A9\u0648\u0647\u0633\u0646\u06AF\u06CC", "\u067E\u0627\u0631\u06A9 \u06A9\u0648\u0647\u0633\u0646\u06AF\u06CC"],
  ["ghasemabad", "\u0642\u0627\u0633\u0645\u200C\u0622\u0628\u0627\u062F", N, 78, "\u0642\u0627\u0633\u0645 \u0622\u0628\u0627\u062F", "\u0634\u0627\u0647\u062F"],
  ["elahiyeh-mashhad", "\u0627\u0644\u0647\u06CC\u0647", N, 70, "\u0627\u0644\u0647\u06CC\u0647 \u0645\u0634\u0647\u062F"],
  ["faramarz-abbasi", "\u0641\u0631\u0627\u0645\u0631\u0632 \u0639\u0628\u0627\u0633\u06CC", N, 82, "\u0641\u0631\u0627\u0645\u0631\u0632", "\u0628\u0644\u0648\u0627\u0631 \u0641\u0631\u0627\u0645\u0631\u0632 \u0639\u0628\u0627\u0633\u06CC"],
  ["moallem", "\u0628\u0644\u0648\u0627\u0631 \u0645\u0639\u0644\u0645", N, 80, "\u0645\u0639\u0644\u0645", "\u0628\u0644\u0648\u0627\u0631 \u0645\u0639\u0644\u0645 \u0645\u0634\u0647\u062F"],
  ["imam-reza", "\u0627\u0645\u0627\u0645 \u0631\u0636\u0627", N, 66, "\u062E\u06CC\u0627\u0628\u0627\u0646 \u0627\u0645\u0627\u0645 \u0631\u0636\u0627", "\u062D\u0631\u0645"],
  ["sayyad-shirazi", "\u0635\u06CC\u0627\u062F \u0634\u06CC\u0631\u0627\u0632\u06CC", N, 76, "\u0635\u06CC\u0627\u062F", "\u0628\u0644\u0648\u0627\u0631 \u0635\u06CC\u0627\u062F \u0634\u06CC\u0631\u0627\u0632\u06CC"],
  ["malek-abad", "\u0645\u0644\u06A9\u200C\u0622\u0628\u0627\u062F", N, 84, "\u0645\u0644\u06A9 \u0622\u0628\u0627\u062F", "\u0628\u0644\u0648\u0627\u0631 \u0645\u0644\u06A9\u200C\u0622\u0628\u0627\u062F"],
  ["abu-taleb", "\u0627\u0628\u0648\u0637\u0627\u0644\u0628", N, 58, "\u0627\u0628\u0648 \u0637\u0627\u0644\u0628", "\u0628\u0644\u0648\u0627\u0631 \u0627\u0628\u0648\u0637\u0627\u0644\u0628"],
  ["haft-tir-mashhad", "\u0647\u0641\u062A \u062A\u06CC\u0631", N, 62, "\u0647\u0641\u062A \u062A\u06CC\u0631 \u0645\u0634\u0647\u062F"]
];
var ESFAHAN_DISTRICTS = [
  ["mantaghe-1", "\u0645\u0646\u0637\u0642\u0647 \u06F1", M, 78, "\u0686\u0647\u0627\u0631\u0628\u0627\u063A", "\u0645\u06CC\u062F\u0627\u0646 \u0627\u0645\u0627\u0645", "\u0628\u0627\u0632\u0627\u0631"],
  ["mantaghe-2", "\u0645\u0646\u0637\u0642\u0647 \u06F2", M, 60, "\u062C\u0644\u0641\u0627", "\u062E\u0648\u0627\u062C\u0648", "\u0634\u06CC\u062E \u0635\u062F\u0648\u0642"],
  ["mantaghe-3", "\u0645\u0646\u0637\u0642\u0647 \u06F3", M, 66, "\u0622\u0645\u0627\u062F\u06AF\u0627\u0647", "\u0634\u0645\u0633\u200C\u0622\u0628\u0627\u062F\u06CC", "\u0633\u067E\u0647"],
  ["mantaghe-4", "\u0645\u0646\u0637\u0642\u0647 \u06F4", M, 58, "\u0631\u0648\u062F\u06A9\u06CC", "\u062E\u06CC\u0627\u0628\u0627\u0646 \u062C\u06CC", "\u0632\u06CC\u0646\u0628\u06CC\u0647"],
  ["mantaghe-5", "\u0645\u0646\u0637\u0642\u0647 \u06F5", M, 84, "\u0645\u0631\u062F\u0627\u0648\u06CC\u062C", "\u0633\u0639\u0627\u062F\u062A\u200C\u0622\u0628\u0627\u062F \u0627\u0635\u0641\u0647\u0627\u0646", "\u0647\u0632\u0627\u0631\u062C\u0631\u06CC\u0628"],
  ["mantaghe-6", "\u0645\u0646\u0637\u0642\u0647 \u06F6", M, 80, "\u0628\u0632\u0631\u06AF\u0645\u0647\u0631", "\u0627\u0631\u062A\u0634", "\u0645\u0634\u062A\u0627\u0642"],
  ["mantaghe-7", "\u0645\u0646\u0637\u0642\u0647 \u06F7", M, 62, "\u062E\u0627\u0646\u0647 \u0627\u0635\u0641\u0647\u0627\u0646", "\u06AF\u0644\u0633\u062A\u0627\u0646", "\u0634\u0627\u0647\u06CC\u0646\u200C\u0634\u0647\u0631 \u062C\u0646\u0648\u0628\u06CC"],
  ["mantaghe-8", "\u0645\u0646\u0637\u0642\u0647 \u06F8", M, 56, "\u0645\u0644\u06A9\u200C\u0634\u0647\u0631", "\u06A9\u0647\u0646\u062F\u0698"],
  ["mantaghe-9", "\u0645\u0646\u0637\u0642\u0647 \u06F9", M, 54, "\u0631\u0647\u0646\u0627\u0646", "\u06A9\u0648\u06CC \u0627\u0645\u0627\u0645"],
  ["mantaghe-10", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F0", M, 50, "\u062E\u0645\u06CC\u0646\u06CC\u200C\u0634\u0647\u0631 \u0645\u0631\u0632", "\u0648\u0644\u062F\u0627\u0646"],
  ["mantaghe-11", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F1", M, 52, "\u06A9\u0647\u0646\u062F\u0698", "\u062F\u0633\u062A\u06AF\u0631\u062F"],
  ["mantaghe-12", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F2", M, 48, "\u062E\u0648\u0631\u0627\u0633\u06AF\u0627\u0646", "\u0627\u0631\u063A\u0648\u0627\u0646\u06CC\u0647"],
  ["mantaghe-13", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F3", M, 46, "\u0632\u06CC\u0646\u0628\u06CC\u0647 \u0634\u0631\u0642\u06CC", "\u062D\u0635\u0647"],
  ["mantaghe-14", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F4", M, 50, "\u062E\u0648\u0631\u0627\u0633\u06AF\u0627\u0646", "\u0634\u0647\u0631\u06A9 \u0627\u0645\u06CC\u0631\u06CC\u0647"],
  ["mantaghe-15", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F5", M, 48, "\u062F\u0627\u0631\u06A9", "\u0627\u0628\u0631"],
  ["chaharbagh", "\u0686\u0647\u0627\u0631\u0628\u0627\u063A", N, 90, "\u0686\u0647\u0627\u0631 \u0628\u0627\u063A", "\u0686\u0647\u0627\u0631\u0628\u0627\u063A \u0628\u0627\u0644\u0627"],
  ["mardavij", "\u0645\u0631\u062F\u0627\u0648\u06CC\u062C", N, 94, "\u0645\u0631\u062F\u0627\u0648\u06CC\u062C \u0627\u0635\u0641\u0647\u0627\u0646", "\u0628\u0644\u0648\u0627\u0631 \u0645\u0631\u062F\u0627\u0648\u06CC\u062C"],
  ["sepahan-shahr", "\u0633\u067E\u0627\u0647\u0627\u0646\u200C\u0634\u0647\u0631", N, 86, "\u0633\u067E\u0627\u0647\u0627\u0646 \u0634\u0647\u0631", "\u0634\u0647\u0631\u06A9 \u0633\u067E\u0627\u0647\u0627\u0646"],
  ["hezar-jarib", "\u0647\u0632\u0627\u0631\u062C\u0631\u06CC\u0628", N, 82, "\u0647\u0632\u0627\u0631 \u062C\u0631\u06CC\u0628", "\u062F\u0627\u0646\u0634\u06AF\u0627\u0647 \u0627\u0635\u0641\u0647\u0627\u0646"],
  ["sheikh-sadooq", "\u0634\u06CC\u062E \u0635\u062F\u0648\u0642", N, 84, "\u0634\u06CC\u062E \u0635\u062F\u0648\u0642 \u0634\u0645\u0627\u0644\u06CC", "\u0634\u06CC\u062E \u0635\u062F\u0648\u0642 \u062C\u0646\u0648\u0628\u06CC"],
  ["bozorgmehr", "\u0628\u0632\u0631\u06AF\u0645\u0647\u0631", N, 80, "\u062E\u06CC\u0627\u0628\u0627\u0646 \u0628\u0632\u0631\u06AF\u0645\u0647\u0631"],
  ["jolfa", "\u062C\u0644\u0641\u0627", N, 72, "\u0645\u062D\u0644\u0647 \u062C\u0644\u0641\u0627", "\u0627\u0631\u0627\u0645\u0646\u0647"],
  ["amadegah", "\u0622\u0645\u0627\u062F\u06AF\u0627\u0647", N, 74, "\u062E\u06CC\u0627\u0628\u0627\u0646 \u0622\u0645\u0627\u062F\u06AF\u0627\u0647"],
  ["khane-esfahan", "\u062E\u0627\u0646\u0647 \u0627\u0635\u0641\u0647\u0627\u0646", N, 70, "\u062E\u0627\u0646\u0647 \u0627\u0635\u0641\u0647\u0627\u0646"],
  ["malekshahr", "\u0645\u0644\u06A9\u200C\u0634\u0647\u0631", N, 66, "\u0645\u0644\u06A9 \u0634\u0647\u0631"],
  ["kaveh", "\u06A9\u0627\u0648\u0647", N, 64, "\u062E\u06CC\u0627\u0628\u0627\u0646 \u06A9\u0627\u0648\u0647", "\u0628\u0644\u0648\u0627\u0631 \u06A9\u0627\u0648\u0647"],
  ["rahnan", "\u0631\u0647\u0646\u0627\u0646", N, 54, "\u0631\u0647\u0646\u0627\u0646 \u0627\u0635\u0641\u0647\u0627\u0646"]
];
var SHIRAZ_DISTRICTS = [
  ["mantaghe-1", "\u0645\u0646\u0637\u0642\u0647 \u06F1", M, 88, "\u0645\u0639\u0627\u0644\u06CC\u200C\u0622\u0628\u0627\u062F", "\u0642\u0635\u0631\u062F\u0634\u062A", "\u0633\u062A\u0627\u0631\u062E\u0627\u0646"],
  ["mantaghe-2", "\u0645\u0646\u0637\u0642\u0647 \u06F2", M, 66, "\u0632\u0646\u062F", "\u0627\u0631\u0645", "\u0642\u0635\u0631\u0627\u0644\u062F\u0634\u062A \u0634\u0631\u0642\u06CC"],
  ["mantaghe-3", "\u0645\u0646\u0637\u0642\u0647 \u06F3", M, 60, "\u0634\u0627\u0647\u0632\u0627\u062F\u0647 \u0642\u0627\u0633\u0645", "\u0633\u0639\u062F\u06CC"],
  ["mantaghe-4", "\u0645\u0646\u0637\u0642\u0647 \u06F4", M, 62, "\u06AF\u0644\u062F\u0634\u062A \u0645\u0639\u0627\u0644\u06CC\u200C\u0622\u0628\u0627\u062F", "\u0634\u0647\u0631\u06A9 \u06AF\u0644\u0633\u062A\u0627\u0646 \u0634\u06CC\u0631\u0627\u0632"],
  ["mantaghe-5", "\u0645\u0646\u0637\u0642\u0647 \u06F5", M, 58, "\u0634\u0647\u0631\u06A9 \u0648\u0627\u0644\u0641\u062C\u0631", "\u0628\u0644\u0648\u0627\u0631 \u0627\u0645\u06CC\u0631\u06A9\u0628\u06CC\u0631"],
  ["mantaghe-6", "\u0645\u0646\u0637\u0642\u0647 \u06F6", M, 72, "\u0639\u0641\u06CC\u0641\u200C\u0622\u0628\u0627\u062F", "\u0641\u0631\u0647\u0646\u06AF\u200C\u0634\u0647\u0631", "\u0686\u0645\u0631\u0627\u0646"],
  ["mantaghe-7", "\u0645\u0646\u0637\u0642\u0647 \u06F7", M, 54, "\u062F\u0631\u0648\u0627\u0632\u0647 \u06A9\u0627\u0632\u0631\u0648\u0646", "\u0628\u0627\u0632\u0627\u0631"],
  ["mantaghe-8", "\u0645\u0646\u0637\u0642\u0647 \u06F8", M, 76, "\u0642\u062F\u0648\u0633\u06CC \u063A\u0631\u0628\u06CC", "\u0628\u0644\u0648\u0627\u0631 \u067E\u0627\u0633\u062F\u0627\u0631\u0627\u0646 \u0634\u06CC\u0631\u0627\u0632"],
  ["mantaghe-9", "\u0645\u0646\u0637\u0642\u0647 \u06F9", M, 50, "\u0634\u0647\u0631\u06A9 \u0635\u062F\u0631\u0627 \u0645\u0631\u0632", "\u06A9\u0648\u06CC \u0632\u0647\u0631\u0627"],
  ["mantaghe-10", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F0", M, 48, "\u06AF\u0648\u06CC\u0645", "\u062F\u0631\u0648\u0627\u0632\u0647 \u0642\u0631\u0622\u0646"],
  ["mantaghe-11", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F1", M, 46, "\u0634\u0647\u0631\u06A9 \u0633\u0647\u06CC\u0644\u06CC", "\u06A9\u0641\u062A\u0631\u06A9"],
  ["maali-abad", "\u0645\u0639\u0627\u0644\u06CC\u200C\u0622\u0628\u0627\u062F", N, 100, "\u0645\u0639\u0627\u0644\u06CC \u0622\u0628\u0627\u062F", "\u0628\u0644\u0648\u0627\u0631 \u0645\u0639\u0627\u0644\u06CC\u200C\u0622\u0628\u0627\u062F"],
  ["ghasrodasht", "\u0642\u0635\u0631\u062F\u0634\u062A", N, 92, "\u0642\u0635\u0631 \u062F\u0634\u062A", "\u0642\u0635\u0631\u0627\u0644\u062F\u0634\u062A"],
  ["afif-abad", "\u0639\u0641\u06CC\u0641\u200C\u0622\u0628\u0627\u062F", N, 86, "\u0639\u0641\u06CC\u0641 \u0622\u0628\u0627\u062F", "\u0628\u0627\u063A \u0639\u0641\u06CC\u0641\u200C\u0622\u0628\u0627\u062F"],
  ["farhang-shahr", "\u0641\u0631\u0647\u0646\u06AF\u200C\u0634\u0647\u0631", N, 82, "\u0641\u0631\u0647\u0646\u06AF \u0634\u0647\u0631"],
  ["sattarkhan-shiraz", "\u0633\u062A\u0627\u0631\u062E\u0627\u0646", N, 80, "\u0628\u0644\u0648\u0627\u0631 \u0633\u062A\u0627\u0631\u062E\u0627\u0646 \u0634\u06CC\u0631\u0627\u0632"],
  ["zand", "\u0632\u0646\u062F", N, 74, "\u062E\u06CC\u0627\u0628\u0627\u0646 \u0632\u0646\u062F", "\u06A9\u0631\u06CC\u0645\u200C\u062E\u0627\u0646 \u0632\u0646\u062F"],
  ["eram", "\u0627\u0631\u0645", N, 70, "\u0628\u0644\u0648\u0627\u0631 \u0627\u0631\u0645", "\u0628\u0627\u063A \u0627\u0631\u0645"],
  ["ghodusi", "\u0642\u062F\u0648\u0633\u06CC", N, 76, "\u0642\u062F\u0648\u0633\u06CC \u063A\u0631\u0628\u06CC", "\u0642\u062F\u0648\u0633\u06CC \u0634\u0631\u0642\u06CC"],
  ["sadra", "\u0635\u062F\u0631\u0627", N, 62, "\u0634\u0647\u0631 \u062C\u062F\u06CC\u062F \u0635\u062F\u0631\u0627"],
  ["amir-kabir-shiraz", "\u0627\u0645\u06CC\u0631\u06A9\u0628\u06CC\u0631", N, 58, "\u0628\u0644\u0648\u0627\u0631 \u0627\u0645\u06CC\u0631\u06A9\u0628\u06CC\u0631 \u0634\u06CC\u0631\u0627\u0632"],
  ["chamran-shiraz", "\u0686\u0645\u0631\u0627\u0646", N, 78, "\u0628\u0644\u0648\u0627\u0631 \u0686\u0645\u0631\u0627\u0646 \u0634\u06CC\u0631\u0627\u0632"]
];
var TABRIZ_DISTRICTS = [
  ["mantaghe-1", "\u0645\u0646\u0637\u0642\u0647 \u06F1", M, 86, "\u0648\u0644\u06CC\u0639\u0635\u0631", "\u0622\u0628\u0631\u0633\u0627\u0646", "\u06AF\u0644\u067E\u0627\u0631\u06A9"],
  ["mantaghe-2", "\u0645\u0646\u0637\u0642\u0647 \u06F2", M, 78, "\u0627\u0626\u0644\u200C\u06AF\u0644\u06CC", "\u06CC\u0627\u063A\u0686\u06CC\u0627\u0646", "\u0631\u0634\u062F\u06CC\u0647"],
  ["mantaghe-3", "\u0645\u0646\u0637\u0642\u0647 \u06F3", M, 64, "\u0628\u0627\u063A\u0645\u06CC\u0634\u0647", "\u0632\u0639\u0641\u0631\u0627\u0646\u06CC\u0647 \u062A\u0628\u0631\u06CC\u0632"],
  ["mantaghe-4", "\u0645\u0646\u0637\u0642\u0647 \u06F4", M, 60, "\u0645\u0627\u0631\u0627\u0644\u0627\u0646", "\u062E\u06CC\u0627\u0628\u0627\u0646 \u0631\u0627\u0647\u200C\u0622\u0647\u0646"],
  ["mantaghe-5", "\u0645\u0646\u0637\u0642\u0647 \u06F5", M, 58, "\u0634\u0647\u0631\u06A9 \u0627\u0646\u062F\u06CC\u0634\u0647", "\u06A9\u0648\u06CC \u0641\u0631\u062F\u0648\u0633"],
  ["mantaghe-6", "\u0645\u0646\u0637\u0642\u0647 \u06F6", M, 56, "\u0645\u0646\u0638\u0631\u06CC\u0647", "\u0648\u0644\u06CC\u0639\u0635\u0631 \u062C\u0646\u0648\u0628\u06CC"],
  ["mantaghe-7", "\u0645\u0646\u0637\u0642\u0647 \u06F7", M, 52, "\u062E\u06CC\u0627\u0628\u0627\u0646 \u062A\u0631\u0628\u06CC\u062A", "\u0628\u0627\u0632\u0627\u0631 \u062A\u0628\u0631\u06CC\u0632"],
  ["mantaghe-8", "\u0645\u0646\u0637\u0642\u0647 \u06F8", M, 50, "\u0645\u0642\u0635\u0648\u062F\u06CC\u0647", "\u0627\u0631\u06A9"],
  ["mantaghe-9", "\u0645\u0646\u0637\u0642\u0647 \u06F9", M, 48, "\u0634\u0647\u0631\u06A9 \u067E\u0631\u0648\u0627\u0632", "\u0633\u0631\u062F\u0627\u0631\u0627\u0646"],
  ["mantaghe-10", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F0", M, 46, "\u062E\u0627\u0648\u0631\u0627\u0646", "\u06A9\u0648\u06CC \u0644\u0627\u0644\u0647"],
  ["valiasr-tabriz", "\u0648\u0644\u06CC\u0639\u0635\u0631", N, 100, "\u0648\u0644\u06CC\u0639\u0635\u0631 \u062A\u0628\u0631\u06CC\u0632", "\u0641\u0644\u06A9\u0647 \u0648\u0644\u06CC\u0639\u0635\u0631"],
  ["elgoli", "\u0627\u0626\u0644\u200C\u06AF\u0644\u06CC", N, 92, "\u0627\u06CC\u0644 \u06AF\u0644\u06CC", "\u0634\u0627\u0647 \u06AF\u0644\u06CC"],
  ["abresan", "\u0622\u0628\u0631\u0633\u0627\u0646", N, 86, "\u0686\u0647\u0627\u0631\u0631\u0627\u0647 \u0622\u0628\u0631\u0633\u0627\u0646"],
  ["baghmisheh", "\u0628\u0627\u063A\u0645\u06CC\u0634\u0647", N, 78, "\u0634\u0647\u0631\u06A9 \u0628\u0627\u063A\u0645\u06CC\u0634\u0647"],
  ["roshdiyeh", "\u0631\u0634\u062F\u06CC\u0647", N, 84, "\u0634\u0647\u0631\u06A9 \u0631\u0634\u062F\u06CC\u0647"],
  ["manzariyeh", "\u0645\u0646\u0638\u0631\u06CC\u0647", N, 70, "\u06A9\u0648\u06CC \u0645\u0646\u0638\u0631\u06CC\u0647"],
  ["maralan", "\u0645\u0627\u0631\u0627\u0644\u0627\u0646", N, 60, "\u0645\u062D\u0644\u0647 \u0645\u0627\u0631\u0627\u0644\u0627\u0646"],
  ["yaghchian", "\u06CC\u0627\u063A\u0686\u06CC\u0627\u0646", N, 66, "\u0634\u0647\u0631\u06A9 \u06CC\u0627\u063A\u0686\u06CC\u0627\u0646"]
];
var KARAJ_DISTRICTS = [
  ["mantaghe-1", "\u0645\u0646\u0637\u0642\u0647 \u06F1", M, 82, "\u0639\u0638\u06CC\u0645\u06CC\u0647", "\u062F\u0647\u0642\u0627\u0646 \u0648\u06CC\u0644\u0627"],
  ["mantaghe-2", "\u0645\u0646\u0637\u0642\u0647 \u06F2", M, 88, "\u06AF\u0648\u0647\u0631\u062F\u0634\u062A", "\u0631\u062C\u0627\u06CC\u06CC\u200C\u0634\u0647\u0631"],
  ["mantaghe-3", "\u0645\u0646\u0637\u0642\u0647 \u06F3", M, 74, "\u062C\u0647\u0627\u0646\u0634\u0647\u0631", "\u0634\u0647\u0631\u06A9 \u0627\u0648\u062C"],
  ["mantaghe-4", "\u0645\u0646\u0637\u0642\u0647 \u06F4", M, 66, "\u0645\u0647\u0631\u0648\u06CC\u0644\u0627", "\u06A9\u0648\u06CC \u06A9\u0627\u0631\u0645\u0646\u062F\u0627\u0646"],
  ["mantaghe-5", "\u0645\u0646\u0637\u0642\u0647 \u06F5", M, 62, "\u0628\u0627\u063A\u0633\u062A\u0627\u0646", "\u0634\u0647\u0631\u06A9 \u0628\u0646\u0641\u0634\u0647"],
  ["mantaghe-6", "\u0645\u0646\u0637\u0642\u0647 \u06F6", M, 58, "\u062D\u0635\u0627\u0631\u06A9", "\u06A9\u0648\u06CC \u0627\u0645\u0627\u0645 \u0631\u0636\u0627"],
  ["mantaghe-7", "\u0645\u0646\u0637\u0642\u0647 \u06F7", M, 56, "\u06A9\u06CC\u0627\u0646\u0645\u0647\u0631", "\u0634\u0647\u0631\u06A9 \u0634\u0647\u06CC\u062F \u0641\u0647\u0645\u06CC\u062F\u0647"],
  ["mantaghe-8", "\u0645\u0646\u0637\u0642\u0647 \u06F8", M, 54, "\u0645\u0647\u0631\u0634\u0647\u0631", "\u0628\u0644\u0648\u0627\u0631 \u0627\u0631\u0645"],
  ["mantaghe-9", "\u0645\u0646\u0637\u0642\u0647 \u06F9", M, 50, "\u06A9\u0644\u0627\u06A9", "\u062E\u0631\u0645\u062F\u0634\u062A"],
  ["mantaghe-10", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F0", M, 48, "\u06A9\u0645\u0627\u0644\u0634\u0647\u0631 \u0645\u0631\u0632", "\u0645\u0635\u0628\u0627\u062D"],
  ["mantaghe-11", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F1", M, 46, "\u0627\u0633\u0644\u0627\u0645\u200C\u0622\u0628\u0627\u062F", "\u062D\u0635\u0627\u0631"],
  ["mantaghe-12", "\u0645\u0646\u0637\u0642\u0647 \u06F1\u06F2", M, 44, "\u06A9\u0648\u06CC \u0627\u062A\u062D\u0627\u062F", "\u0634\u0627\u0647\u06CC\u0646 \u0648\u06CC\u0644\u0627"],
  ["gohardasht", "\u06AF\u0648\u0647\u0631\u062F\u0634\u062A", N, 100, "\u06AF\u0648\u0647\u0631 \u062F\u0634\u062A", "\u0631\u062C\u0627\u06CC\u06CC\u200C\u0634\u0647\u0631"],
  ["azimiyeh", "\u0639\u0638\u06CC\u0645\u06CC\u0647", N, 96, "\u0639\u0638\u06CC\u0645\u06CC\u0647 \u06A9\u0631\u062C", "\u0645\u06CC\u062F\u0627\u0646 \u0627\u0633\u062A\u0627\u0646\u062F\u0627\u0631\u062F"],
  ["mehrshahr", "\u0645\u0647\u0631\u0634\u0647\u0631", N, 88, "\u0645\u0647\u0631 \u0634\u0647\u0631", "\u0628\u0644\u0648\u0627\u0631 \u0627\u0631\u0645 \u0645\u0647\u0631\u0634\u0647\u0631"],
  ["jahanshahr", "\u062C\u0647\u0627\u0646\u0634\u0647\u0631", N, 86, "\u062C\u0647\u0627\u0646 \u0634\u0647\u0631", "\u0628\u0644\u0648\u0627\u0631 \u0637\u0627\u0644\u0642\u0627\u0646\u06CC"],
  ["baghestan", "\u0628\u0627\u063A\u0633\u062A\u0627\u0646", N, 74, "\u0628\u0627\u063A\u0633\u062A\u0627\u0646 \u063A\u0631\u0628\u06CC", "\u0628\u0627\u063A\u0633\u062A\u0627\u0646 \u0634\u0631\u0642\u06CC"],
  ["rajaee-shahr", "\u0631\u062C\u0627\u06CC\u06CC\u200C\u0634\u0647\u0631", N, 80, "\u0631\u062C\u0627\u06CC\u06CC \u0634\u0647\u0631", "\u06AF\u0648\u0647\u0631\u062F\u0634\u062A \u0634\u0645\u0627\u0644\u06CC"],
  ["hesarak", "\u062D\u0635\u0627\u0631\u06A9", N, 60, "\u062D\u0635\u0627\u0631\u06A9 \u0628\u0627\u0644\u0627", "\u062D\u0635\u0627\u0631\u06A9 \u067E\u0627\u06CC\u06CC\u0646"],
  ["shahrak-banafsheh", "\u0634\u0647\u0631\u06A9 \u0628\u0646\u0641\u0634\u0647", N, 58, "\u0628\u0646\u0641\u0634\u0647"],
  ["kianmehr", "\u06A9\u06CC\u0627\u0646\u0645\u0647\u0631", N, 62, "\u0634\u0647\u0631\u06A9 \u06A9\u06CC\u0627\u0646\u0645\u0647\u0631"],
  ["fardis-karaj", "\u0641\u0631\u062F\u06CC\u0633", N, 78, "\u0641\u0631\u062F\u06CC\u0633 \u06A9\u0631\u062C", "\u06A9\u0627\u0646\u0627\u0644 \u063A\u0631\u0628\u06CC"]
];
var DISTRICT_SEED = {
  tehran: TEHRAN_DISTRICTS,
  mashhad: MASHHAD_DISTRICTS,
  esfahan: ESFAHAN_DISTRICTS,
  shiraz: SHIRAZ_DISTRICTS,
  tabriz: TABRIZ_DISTRICTS,
  karaj: KARAJ_DISTRICTS
};
var SPANS_SEVERAL_MUNICIPAL_DISTRICTS = /* @__PURE__ */ new Set([
  // Valiasr Street runs from Rah-Ahan to Tajrish through five مناطق.
  "tehran/valiasr"
]);
function geoKey(value) {
  return value.replace(/\u200c/g, " ").replace(/\s+/g, " ").trim();
}
function deriveParentDistrictSlug(neighbourhood, municipal) {
  if (neighbourhood.kind !== "NEIGHBORHOOD") return null;
  if (SPANS_SEVERAL_MUNICIPAL_DISTRICTS.has(`${neighbourhood.citySlug}/${neighbourhood.slug}`)) {
    return null;
  }
  const name = geoKey(neighbourhood.name);
  const sameCity = municipal.filter(
    (row) => row.kind === "MUNICIPAL" && row.citySlug === neighbourhood.citySlug
  );
  const exact = sameCity.filter((row) => row.aliases.some((alias) => geoKey(alias) === name));
  if (exact.length !== 1) return null;
  const parent = exact[0];
  if (!parent) return null;
  const qualifiedElsewhere = sameCity.some(
    (row) => row.slug !== parent.slug && row.aliases.some((alias) => geoKey(alias).startsWith(`${name} `))
  );
  return qualifiedElsewhere ? null : parent.slug;
}
var DISTRICT_ROWS = Object.entries(DISTRICT_SEED).flatMap(
  ([citySlug, tuples]) => tuples.map(([slug, name, kind, populationWeight, ...aliases]) => ({
    slug,
    name,
    citySlug,
    kind,
    populationWeight,
    aliases: Object.freeze(aliases)
  }))
);
var DISTRICTS = Object.freeze(
  DISTRICT_ROWS.map((row) => ({
    ...row,
    parentDistrictSlug: deriveParentDistrictSlug(row, DISTRICT_ROWS)
  }))
);
var PROVINCE_BY_SLUG = new Map(PROVINCES.map((province) => [province.slug, province]));
var CITY_BY_SLUG = new Map(CITIES.map((city) => [city.slug, city]));
var CITIES_BY_PROVINCE = /* @__PURE__ */ new Map();
var DISTRICTS_BY_CITY = /* @__PURE__ */ new Map();
for (const city of CITIES) {
  const bucket = CITIES_BY_PROVINCE.get(city.provinceSlug);
  if (bucket) bucket.push(city);
  else CITIES_BY_PROVINCE.set(city.provinceSlug, [city]);
}
for (const district of DISTRICTS) {
  const bucket = DISTRICTS_BY_CITY.get(district.citySlug);
  if (bucket) bucket.push(district);
  else DISTRICTS_BY_CITY.set(district.citySlug, [district]);
}
function getProvince(slug) {
  return PROVINCE_BY_SLUG.get(slug);
}
function getCity(slug) {
  return CITY_BY_SLUG.get(slug);
}
function citiesOfProvince(provinceSlug) {
  return CITIES_BY_PROVINCE.get(provinceSlug) ?? [];
}
function districtsOfCity(citySlug) {
  return DISTRICTS_BY_CITY.get(citySlug) ?? [];
}
function getDistrict(citySlug, districtSlug) {
  return districtsOfCity(citySlug).find((district) => district.slug === districtSlug);
}
function childDistrictsOf(citySlug, municipalSlug) {
  return districtsOfCity(citySlug).filter(
    (district) => district.parentDistrictSlug === municipalSlug
  );
}
function provinceOfCity(citySlug) {
  const city = getCity(citySlug);
  return city ? getProvince(city.provinceSlug) : void 0;
}
var MAJOR_CITIES = Object.freeze(CITIES.filter((city) => city.isMajor));
var CITIES_WITH_DISTRICTS = Object.freeze(Object.keys(DISTRICT_SEED));
function topCities(count) {
  return [...CITIES].sort((a, b) => b.populationWeight - a.populationWeight).slice(0, count);
}
function cityGeoPoint(citySlug) {
  const city = getCity(citySlug);
  if (!city || city.lat === null || city.lng === null) return null;
  return { lat: city.lat, lng: city.lng };
}
function findCityByName(name) {
  const needle = name.trim();
  return CITIES.find((city) => city.name === needle || city.slug === needle.toLowerCase());
}
function findDistrictByName(citySlug, name) {
  const needle = name.trim();
  return districtsOfCity(citySlug).find(
    (district) => district.name === needle || district.aliases.includes(needle)
  );
}
function geoBreadcrumb(citySlug, districtSlug) {
  const city = getCity(citySlug);
  if (!city) return "";
  if (!districtSlug) return city.name;
  const district = getDistrict(citySlug, districtSlug);
  return district ? `${city.name} \u203A ${district.name}` : city.name;
}
var GEO_STATS = Object.freeze({
  provinces: PROVINCES.length,
  cities: CITIES.length,
  majorCities: MAJOR_CITIES.length,
  districts: DISTRICTS.length
});

// src/guidance.ts
var GUIDANCE_KINDS = ["WELCOME", "TOUR", "TIP"];
var GUIDANCE_STATUSES = [
  "OFFERED",
  "STARTED",
  "SNOOZED",
  "DISMISSED",
  "COMPLETED",
  "ACCEPTED",
  "NOT_RELEVANT"
];
var GUIDANCE_COHORTS = ["NEWCOMER", "VETERAN"];
var GUIDANCE_EVENT_TYPES = [
  "OFFERED",
  "OFFER_DECLINED",
  "STARTED",
  "STEP_VIEWED",
  "STEP_SKIPPED",
  "COMPLETED",
  "DISMISSED",
  "INTERRUPTED",
  "SNOOZED",
  "ACCEPTED",
  "GOAL_REACHED",
  "NOT_RELEVANT",
  "RESTORED",
  "HUB_OPENED"
];
var ANALYTICS_ONLY_EVENT_TYPES = ["STEP_VIEWED", "STEP_SKIPPED", "HUB_OPENED"];
var CLIENT_EVENT_TYPES = [
  "OFFERED",
  "OFFER_DECLINED",
  "STARTED",
  "STEP_VIEWED",
  "STEP_SKIPPED",
  "COMPLETED",
  "DISMISSED",
  "INTERRUPTED",
  "SNOOZED",
  "HUB_OPENED"
];
var CLIENT_EVENT_KINDS = ["WELCOME", "TOUR", "TIP"];
var RESETTABLE_KINDS = ["WELCOME", "TOUR"];
var GUIDANCE_SOURCES = [
  "auto",
  "welcome",
  "help-menu",
  "hub",
  "chip",
  "deeplink",
  "tip",
  "offer",
  "bell",
  "server",
  "dashboard",
  "inline",
  "struggle"
];
var GUIDANCE_SURFACES = ["DASHBOARD", "HUB", "INLINE", "BELL"];
var GUIDANCE_VIEWPORTS = ["WIDE", "COMPACT"];
var GUIDANCE_PERSONAS = [
  "owner",
  "doctor",
  "clinical",
  "front-desk",
  "finance",
  "staff"
];
var GUIDANCE_STEP_REASONS = [
  "anchor_missing",
  "anchor_lost",
  "route",
  "overlay",
  "anchors",
  "host-closed"
];
var GUIDANCE_KEY_PATTERN = /^[a-z0-9][a-z0-9._-]{0,63}$/;
var GUIDANCE_INSTANCE_PATTERN = /^[a-z0-9-]{0,40}$/;
var GUIDANCE_MAX_CLIENT_ITEMS_PER_STAFF = 200;
var GUIDANCE_MAX_EVENTS_PER_REQUEST = 20;
var WELCOME_SNOOZE_DAYS_DEFAULT = 1;
var WELCOME_MAX_SNOOZES = 3;
var DECLINES_BEFORE_PAUSE = 2;
var DECLINE_PAUSE_DAYS = 7;
var NEWCOMER_WINDOW_DAYS = 14;
var PROACTIVE_MIN_GAP_MS = 10 * 6e4;
var PROACTIVE_PER_DAY = 3;
var TIP_DISMISSALS_TO_NOT_RELEVANT = 2;
var TIP_NEW_PER_DAY = 2;
var TIP_BACKOFF = [
  { after: 2, days: 7 },
  { after: 4, days: 14 }
];
var MS_PER_DAY2 = 864e5;
var ANALYTICS_ONLY = new Set(ANALYTICS_ONLY_EVENT_TYPES);
var TIP_ONLY_EVENT_TYPES = /* @__PURE__ */ new Set([
  "ACCEPTED",
  "GOAL_REACHED",
  "NOT_RELEVANT",
  "RESTORED"
]);
var SPENDING_SOURCES = /* @__PURE__ */ new Set(["auto", "bell", "struggle"]);
function isHelpOffer(ev) {
  return ev.source === "struggle" && ev.kind !== "TIP" && (ev.type === "OFFERED" || ev.type === "OFFER_DECLINED");
}
var TIP_FIRST_IMPRESSION_SOURCES = /* @__PURE__ */ new Set(["dashboard", "inline"]);
function addDays(now, days) {
  return new Date(now.getTime() + days * MS_PER_DAY2).toISOString();
}
function isoMs(value) {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}
function baseItem(ev, status2) {
  return {
    kind: ev.kind,
    key: ev.key,
    instanceKey: ev.instanceKey ?? "",
    version: ev.version,
    status: status2,
    lastStep: null,
    stepCount: null,
    impressions: 0,
    startedCount: 0,
    completedCount: 0,
    snoozeCount: 0,
    dismissCount: 0,
    shownDays: 0,
    lastShownDayKey: null,
    firstOfferedAt: null,
    lastOfferedAt: null,
    lastStartedAt: null,
    completedAt: null,
    dismissedAt: null,
    snoozedUntil: null,
    acceptedAt: null,
    goalReachedAt: null
  };
}
function createdStatus(type) {
  switch (type) {
    case "OFFERED":
      return "OFFERED";
    case "OFFER_DECLINED":
    case "DISMISSED":
      return "DISMISSED";
    case "STARTED":
      return "STARTED";
    case "COMPLETED":
    case "GOAL_REACHED":
      return "COMPLETED";
    case "SNOOZED":
      return "SNOOZED";
    case "ACCEPTED":
      return "ACCEPTED";
    case "NOT_RELEVANT":
      return "NOT_RELEVANT";
    case "STEP_VIEWED":
    case "STEP_SKIPPED":
    case "HUB_OPENED":
    case "INTERRUPTED":
    case "RESTORED":
      return null;
  }
}
function applyGuidanceEvent(item, ev, now) {
  if (item === null && ANALYTICS_ONLY.has(ev.type)) return null;
  if (isHelpOffer(ev)) return item;
  const isTip = ev.kind === "TIP";
  if (TIP_ONLY_EVENT_TYPES.has(ev.type) && !isTip) return item;
  if (item === null) {
    const status2 = createdStatus(ev.type);
    if (status2 === null) return null;
    return transition(baseItem(ev, status2), ev, now, isTip);
  }
  return transition(item, ev, now, isTip);
}
function transition(item, ev, now, isTip) {
  const nowIso = now.toISOString();
  const next = { ...item };
  switch (ev.type) {
    case "OFFERED": {
      next.impressions += 1;
      next.firstOfferedAt ??= nowIso;
      next.lastOfferedAt = nowIso;
      const dayKey = guidanceDayKey(now);
      if (next.lastShownDayKey !== dayKey) {
        next.shownDays += 1;
        next.lastShownDayKey = dayKey;
      }
      if (!isTip && next.status === "COMPLETED" && ev.version > next.version) {
        next.status = "OFFERED";
        next.version = ev.version;
      } else if (next.status === "SNOOZED") {
        const until = isoMs(next.snoozedUntil);
        if (until === null || until <= now.getTime()) next.status = "OFFERED";
      }
      return next;
    }
    case "OFFER_DECLINED":
      next.status = "DISMISSED";
      next.dismissedAt = nowIso;
      next.dismissCount += 1;
      return next;
    case "STARTED":
      next.status = "STARTED";
      next.startedCount += 1;
      next.lastStartedAt = nowIso;
      next.lastStep = ev.step ?? 0;
      next.stepCount = ev.total ?? null;
      next.version = ev.version;
      return next;
    case "STEP_VIEWED":
      if (next.status !== "STARTED" || ev.step === void 0) return item;
      next.lastStep = ev.step;
      return next;
    case "COMPLETED":
      next.status = "COMPLETED";
      next.completedAt ??= nowIso;
      next.completedCount += 1;
      next.version = ev.version;
      if (!isTip) next.lastStep = (ev.total ?? 1) - 1;
      return next;
    case "DISMISSED":
      next.dismissedAt = nowIso;
      next.dismissCount += 1;
      if (!isTip) next.lastStep = ev.step ?? next.lastStep;
      if (isTip) {
        next.status = item.status === "NOT_RELEVANT" || next.dismissCount >= TIP_DISMISSALS_TO_NOT_RELEVANT ? "NOT_RELEVANT" : "DISMISSED";
      } else {
        next.status = "DISMISSED";
      }
      return next;
    case "INTERRUPTED":
      if (next.status !== "STARTED") return item;
      next.lastStep = ev.step ?? next.lastStep;
      return next;
    case "SNOOZED":
      next.snoozeCount += 1;
      next.snoozedUntil = addDays(now, ev.snoozeDays ?? WELCOME_SNOOZE_DAYS_DEFAULT);
      if (ev.kind === "WELCOME" && next.snoozeCount >= WELCOME_MAX_SNOOZES) {
        next.status = "DISMISSED";
        next.dismissedAt = nowIso;
      } else {
        next.status = "SNOOZED";
      }
      if (isTip) next.shownDays = 0;
      return next;
    case "ACCEPTED":
      next.status = "ACCEPTED";
      next.acceptedAt = nowIso;
      next.shownDays = 0;
      return next;
    case "GOAL_REACHED":
      next.goalReachedAt ??= nowIso;
      next.status = "COMPLETED";
      next.completedAt ??= nowIso;
      return next;
    case "NOT_RELEVANT":
      next.status = "NOT_RELEVANT";
      next.dismissedAt = nowIso;
      return next;
    case "RESTORED":
      next.status = "OFFERED";
      next.snoozedUntil = null;
      next.dismissedAt = null;
      next.dismissCount = 0;
      return next;
    case "STEP_SKIPPED":
    case "HUB_OPENED":
      return item;
  }
}
function tipBackoffDays(consecutive) {
  const last = TIP_BACKOFF[TIP_BACKOFF.length - 1];
  for (const tier of TIP_BACKOFF) {
    if (consecutive === tier.after) return tier.days;
  }
  if (last && consecutive > last.after) return last.days;
  return null;
}
function applyProfileEvent(profile, ev, now, prev) {
  const next = { ...profile };
  const nowIso = now.toISOString();
  const dayKey = guidanceDayKey(now);
  const isTip = ev.kind === "TIP";
  const firstTipImpression = isTip && ev.type === "OFFERED" && prev === null;
  if (ev.type === "OFFERED") {
    const source = ev.source;
    const spends = source !== void 0 && SPENDING_SOURCES.has(source) || firstTipImpression && source !== void 0 && TIP_FIRST_IMPRESSION_SOURCES.has(source);
    if (spends) {
      next.lastProactiveAt = nowIso;
      if (next.proactiveDayKey !== dayKey) {
        next.proactiveDayKey = dayKey;
        next.proactiveToday = 0;
      }
      next.proactiveToday += 1;
    }
    if (firstTipImpression) {
      if (next.newTipsDayKey !== dayKey) {
        next.newTipsDayKey = dayKey;
        next.newTipsToday = 0;
      }
      next.newTipsToday += 1;
    }
  }
  if (ev.kind === "TOUR" && ev.type === "OFFER_DECLINED" && !isHelpOffer(ev)) {
    next.consecutiveDeclines += 1;
    if (next.consecutiveDeclines >= DECLINES_BEFORE_PAUSE) {
      next.autoOfferPausedUntil = addDays(now, DECLINE_PAUSE_DAYS);
      next.consecutiveDeclines = 0;
    }
  }
  if (ev.type === "STARTED" || ev.type === "ACCEPTED") next.consecutiveDeclines = 0;
  if (isTip && (ev.type === "DISMISSED" || ev.type === "NOT_RELEVANT")) {
    next.consecutiveTipDismissals += 1;
    const days = tipBackoffDays(next.consecutiveTipDismissals);
    if (days !== null) {
      const until = addDays(now, days);
      const current = isoMs(next.tipBackoffUntil);
      if (current === null || Date.parse(until) > current) next.tipBackoffUntil = until;
    }
  }
  if (isTip && (ev.type === "ACCEPTED" || ev.type === "COMPLETED" || ev.type === "GOAL_REACHED")) {
    next.consecutiveTipDismissals = 0;
  }
  return next;
}
function canSpendProactive(profile, now, opts) {
  const server = isoMs(profile.lastProactiveAt);
  const local = isoMs(opts?.localLastAt);
  const last = server === null ? local : local === null ? server : Math.max(server, local);
  if (last !== null && now.getTime() - last < PROACTIVE_MIN_GAP_MS) return false;
  if (!opts?.exemptFromDailyCap && profile.proactiveDayKey === guidanceDayKey(now) && profile.proactiveToday >= PROACTIVE_PER_DAY) {
    return false;
  }
  return true;
}
function guidanceEventIsLogged(prev, ev, now) {
  if (ev.kind !== "TIP" || ev.type !== "OFFERED") return true;
  return prev?.lastShownDayKey !== guidanceDayKey(now);
}
function analyticsEventName(ev) {
  return `guidance.${ev.kind}.${ev.type}`.toLowerCase();
}
function guidanceDayKey(now) {
  return toJalaliDateKey(now);
}
function guidanceCohortFor(joinedAt, now) {
  return joinedAt.getTime() >= now.getTime() - NEWCOMER_WINDOW_DAYS * MS_PER_DAY2 ? "NEWCOMER" : "VETERAN";
}
function initialGuidanceProfile(cohort, now) {
  return {
    cohort,
    autoOfferTours: true,
    consecutiveDeclines: 0,
    autoOfferPausedUntil: null,
    lastProactiveAt: null,
    proactiveDayKey: null,
    proactiveToday: 0,
    tipsEnabled: true,
    tipsPausedUntil: null,
    mutedTipCategories: [],
    newTipsDayKey: null,
    newTipsToday: 0,
    consecutiveTipDismissals: 0,
    tipBackoffUntil: null,
    createdAt: now.toISOString()
  };
}

// src/guide.ts
var GUIDE_CATEGORIES = [
  "SETUP",
  "SECURITY",
  "GROWTH",
  "RETENTION",
  "CRM",
  "OCCASIONS",
  "OPERATIONS",
  "CLINICAL",
  "INVENTORY",
  "FINANCE"
];
var GUIDE_CATEGORY_LABELS_FA = Object.freeze({
  SETUP: "\u062A\u06A9\u0645\u06CC\u0644 \u0631\u0627\u0647\u200C\u0627\u0646\u062F\u0627\u0632\u06CC",
  SECURITY: "\u0627\u0645\u0646\u06CC\u062A \u062D\u0633\u0627\u0628",
  GROWTH: "\u062C\u0630\u0628 \u0628\u06CC\u0645\u0627\u0631 \u0648 \u0641\u0631\u0648\u0634",
  RETENTION: "\u0628\u0627\u0632\u06AF\u0634\u062A \u0628\u06CC\u0645\u0627\u0631\u0627\u0646",
  CRM: "\u06AF\u0631\u0648\u0647\u200C\u0647\u0627 \u0648 \u067E\u06CC\u0627\u0645\u06A9",
  OCCASIONS: "\u0645\u0646\u0627\u0633\u0628\u062A\u200C\u0647\u0627",
  OPERATIONS: "\u0646\u0648\u0628\u062A\u200C\u062F\u0647\u06CC \u0648 \u06A9\u0627\u0631 \u0631\u0648\u0632\u0627\u0646\u0647",
  CLINICAL: "\u062F\u0631\u0645\u0627\u0646 \u0648 \u067E\u0631\u0648\u0646\u062F\u0647",
  INVENTORY: "\u0627\u0646\u0628\u0627\u0631",
  FINANCE: "\u0627\u0645\u0648\u0631 \u0645\u0627\u0644\u06CC"
});
var GUIDE_CATEGORY_ICONS = Object.freeze({
  SETUP: "clinic",
  SECURITY: "shield",
  GROWTH: "trend-up",
  RETENTION: "refresh",
  CRM: "sms",
  OCCASIONS: "gift",
  OPERATIONS: "calendar",
  CLINICAL: "treatment-plan",
  INVENTORY: "inventory",
  FINANCE: "receipt"
});
var GUIDE_RULE_KEYS = [
  "setup.verification",
  "setup.branding",
  "setup.patient-import",
  "setup.invite-staff",
  "setup.rooms",
  "security.owner-2fa",
  "growth.online-booking",
  "growth.first-package",
  "growth.loyalty",
  "growth.referral-program",
  "growth.reply-reviews",
  "growth.patient-app",
  "growth.plan-upgrade",
  "growth.raas-crossover",
  "retention.winback-segment",
  "retention.winback-campaign",
  "retention.birthday-segment",
  "retention.birthday-campaign",
  "crm.first-segment",
  "crm.first-campaign",
  "crm.lead-followups",
  "crm.sms-credit",
  "crm.hello-imported",
  "occasion.mothers-day",
  "occasion.black-friday",
  "occasion.yalda",
  "occasion.fathers-day",
  "occasion.sepandarmazgan",
  "occasion.nowruz-rush",
  "occasion.autumn-laser",
  "occasion.bridal-season",
  "ops.expired-requests",
  "ops.no-show-deposit",
  "ops.waitlist",
  "ops.holiday-closure",
  "ops.doctor-hours",
  "clinical.record-consumption",
  "clinical.before-after-photos",
  "clinical.photo-comparison",
  "clinical.consent-required",
  "clinical.record-sessions",
  "inventory.low-stock-thresholds",
  "inventory.suppliers",
  "finance.record-payments",
  "finance.monthly-expenses",
  "finance.revenue-shares",
  "finance.monthly-pnl",
  "import.patients-photo",
  "import.price-list-photo",
  "import.invoice-photo",
  "import.trial-pages",
  "import.upgrade",
  "import.treatments-photo",
  "import.stock-count-photo",
  "feature.announce"
];
var GUIDE_RULE_CODE_RESERVATIONS = Object.freeze({
  m5: "import.upgrade",
  m6: "import.treatments-photo",
  m7: "import.stock-count-photo",
  k5: "clinical.record-sessions",
  c5: "crm.hello-imported",
  c6: "crm.save-filter",
  g8: "growth.plan-teaser",
  g9: "growth.package-upsell",
  a1: "feature.announce",
  /** RaaS → flat after two closed months at ≥ 80% of the cap (pricing build, plan-gates stream). */
  b1: "growth.raas-crossover"
});
var FEATURE_ANNOUNCE_RULE_KEY = "feature.announce";
var FEATURE_ANNOUNCEMENTS = [
  /**
   * Photo import itself («ثبت اطلاعات از روی عکس»), for the clinics the
   * onboarding import tips (m1–m4) do not reach — a clinic that has been
   * running for months when the feature is switched on.
   */
  "import-photo",
  /** AI import kind TREATMENTS («سابقهٔ درمان از روی عکس»). */
  "import-treatments",
  /** AI import kind STOCK_COUNT («موجودی اولیهٔ انبار از روی عکس»). */
  "import-stock-count",
  /** The PATIENTS kind's «ساختن گروه از این فهرست» option. */
  "import-group"
];
var GUIDE_INLINE_SLOTS = [
  "crm.segments",
  "crm.campaigns",
  "reviews.list",
  "calendar.requests",
  "settings.clinic",
  "patients.list",
  "calendar.board",
  "calendar.waitlist",
  "sessions.list",
  "photos.studio",
  "inventory.materials",
  "inventory.suppliers",
  "packages.bundles",
  "crm.leads",
  "crm.loyalty",
  "accounting.payments",
  "accounting.expenses",
  "accounting.revenue-shares",
  "staff.team"
];
var GUIDE_TIP_SURFACES = ["DASHBOARD", "HUB"];
var GUIDE_TIP_ACTIONS = [
  "ACCEPT",
  "SNOOZE_DAY",
  "SNOOZE_WEEK",
  "DISMISS",
  "NOT_RELEVANT",
  "MARK_DONE",
  "RESTORE"
];
var GUIDE_CTA_KINDS = ["NAVIGATE", "DRAFT", "COPY", "TOUR", "MARK_DONE"];
var GUIDE_ACTION_CTA_KINDS = ["NAVIGATE", "COPY", "TOUR", "MARK_DONE"];
var GUIDE_TIP_TONES = ["INFO", "TIMELY", "ATTENTION"];
var GUIDE_QUIET_REASONS = [
  "CLINIC_READ_ONLY",
  "FIRST_SESSION",
  "WELCOME_DAY",
  "PAUSED",
  "OFF",
  "BACKOFF",
  "CLINIC_DISABLED",
  "DISABLED"
];
function tipItemKey(ruleKey, instance) {
  return instance === "" ? ruleKey : `${ruleKey}.${instance}`;
}
function parseTipItemKey(itemKey) {
  if (typeof itemKey !== "string" || itemKey.length > 64 + 1 + 40) return null;
  let best = null;
  for (const key of GUIDE_RULE_KEYS) {
    if (itemKey !== key && !itemKey.startsWith(`${key}.`)) continue;
    if (best === null || key.length > best.length) best = key;
  }
  if (best === null) return null;
  if (itemKey === best) return { ruleKey: best, instance: "" };
  const instance = itemKey.slice(best.length + 1);
  if (!TIP_INSTANCE_PATTERN.test(instance)) return null;
  return { ruleKey: best, instance };
}
var TIP_INSTANCE_PATTERN = /^[a-z0-9][a-z0-9-]{0,39}$/;
function parseGuidePrefill(value) {
  if (!isRecord(value)) return null;
  switch (value.kind) {
    case "segment": {
      const name = text(value.name, PREFILL_NAME_MAX);
      if (name === null || !isRecord(value.filter)) return null;
      const description = optionalText(value.description, PREFILL_DESCRIPTION_MAX);
      if (description === false) return null;
      return {
        kind: "segment",
        name,
        ...description === void 0 ? {} : { description },
        filter: { ...value.filter }
      };
    }
    case "package": {
      const name = text(value.name, PREFILL_NAME_MAX);
      if (name === null || !Array.isArray(value.procedures)) return null;
      const description = optionalText(value.description, PREFILL_DESCRIPTION_MAX);
      if (description === false) return null;
      const procedures = [];
      for (const row of value.procedures) {
        if (!isRecord(row)) return null;
        const procedureId = text(row.procedureId, 64);
        const sessionCount = row.sessionCount;
        if (procedureId === null || !isIntIn(sessionCount, 1, 1e3)) return null;
        procedures.push({ procedureId, sessionCount });
      }
      const out = { kind: "package", name, procedures };
      if (description !== void 0) out.description = description;
      if (value.priceRials !== void 0) {
        if (value.priceRials !== null && !isIntIn(value.priceRials, 0, Number.MAX_SAFE_INTEGER)) return null;
        out.priceRials = value.priceRials;
      }
      if (value.validityDays !== void 0) {
        if (!isIntIn(value.validityDays, 1, 3650)) return null;
        out.validityDays = value.validityDays;
      }
      return out;
    }
    case "expense": {
      if (typeof value.category !== "string") return null;
      if (!GUIDE_EXPENSE_CATEGORIES.includes(value.category)) return null;
      const description = optionalText(value.description, PREFILL_DESCRIPTION_MAX);
      if (description === false) return null;
      return {
        kind: "expense",
        category: value.category,
        ...description === void 0 ? {} : { description }
      };
    }
    case "appointment":
      return value.revealDeposit === true ? { kind: "appointment", revealDeposit: true } : null;
    default:
      return null;
  }
}
var GUIDE_EXPENSE_CATEGORIES = [
  "RENT",
  "SALARY",
  "MARKETING",
  "UTILITIES",
  "EQUIPMENT",
  "MATERIAL_PURCHASE",
  "TAX",
  "OTHER"
];
var PREFILL_NAME_MAX = 120;
var PREFILL_DESCRIPTION_MAX = 500;
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function text(value, max) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed.length <= max ? trimmed : null;
}
function optionalText(value, max) {
  if (value === void 0) return void 0;
  return text(value, max) ?? false;
}
function isIntIn(value, min, max) {
  return typeof value === "number" && Number.isInteger(value) && value >= min && value <= max;
}
var GUIDE_CTA_ROUTES = {
  verification: "/settings?tab=verification",
  clinicProfile: "/settings?tab=clinic",
  rooms: "/settings?tab=rooms",
  sms: "/settings?tab=sms",
  subscription: "/settings?tab=subscription",
  /**
   * The plan cards with one plan highlighted for a feature (C13):
   * `?plan=<key>&for=<reason>` — the reason names the feature the card is
   * suggested for («پیشنهاد برای …»); photo import when not given.
   */
  subscriptionUpgrade: (planKey, reason = "ai-import") => `/settings?tab=subscription&plan=${encodeURIComponent(planKey)}&for=${reason}`,
  /** Settings → «شعبه‌ها» (کلینیک plan). */
  settingsBranches: "/settings?tab=branches",
  /** Settings → «اتصال‌ها (API)» (کلینیک plan). */
  integrations: "/settings?tab=integrations",
  security: "/settings/security",
  patientImport: "/patients/import",
  staff: "/staff",
  staffInvite: "/staff?invite=1",
  staffHours: (staffId) => `/staff/${staffId}/working-hours`,
  segmentsNew: "/crm?tab=segments&new=1",
  campaignEdit: (id) => `/crm?tab=campaigns&edit=${id}`,
  leads: "/crm?tab=leads",
  loyaltyActivate: "/crm?tab=loyalty&activate=1",
  loyalty: "/crm?tab=loyalty",
  reviews: (id) => id ? `/reviews?review=${id}` : "/reviews",
  packagesNew: "/packages?new=1",
  calendarRequests: "/calendar?tab=requests",
  calendarWaitlist: "/calendar?tab=waitlist",
  calendarSchedule: "/calendar?tab=schedule",
  calendarNew: "/calendar?new=1",
  sessions: "/sessions",
  photos: "/photos",
  photosPairs: "/photos?view=sets",
  procedures: "/procedures?tab=tariff",
  inventoryMaterials: "/inventory?tab=materials",
  inventorySuppliers: "/inventory?tab=suppliers",
  inventoryAlerts: "/inventory?tab=alerts",
  payments: "/accounting?tab=payments&new=1",
  expensesNew: "/accounting?tab=expenses&new=1",
  revenueShares: "/accounting?tab=revenue-shares",
  profitLoss: "/accounting?tab=profit-loss&period=last_month",
  aiImport: (kind) => `/import/photo?kind=${kind}`,
  /** A photographed client list straight into a new list group (the PATIENTS group option). */
  aiImportGroup: "/import/photo?kind=PATIENTS&group=new"
};
var CRM_SECTION = [
  Permission.CRM_VIEW_LEADS,
  Permission.CRM_SEND_CAMPAIGNS,
  Permission.LOYALTY_VIEW,
  Permission.PATIENT_VIEW
];
var ACCOUNTING_SECTION = [
  Permission.FINANCE_VIEW_REVENUE,
  Permission.FINANCE_VIEW_COSTS,
  Permission.FINANCE_VIEW_PROFIT
];
var GUIDE_CTA_ROUTE_REQUIRES = Object.freeze({
  verification: [Permission.SETTINGS_VIEW],
  clinicProfile: [Permission.SETTINGS_VIEW],
  rooms: [Permission.SETTINGS_VIEW],
  sms: [Permission.SETTINGS_VIEW],
  subscription: [Permission.SETTINGS_VIEW],
  subscriptionUpgrade: [Permission.SETTINGS_VIEW],
  settingsBranches: [Permission.SETTINGS_VIEW],
  integrations: [Permission.SETTINGS_VIEW],
  security: [],
  patientImport: [Permission.PATIENT_CREATE],
  staff: [Permission.STAFF_VIEW],
  staffInvite: [Permission.STAFF_VIEW],
  staffHours: [Permission.APPOINTMENT_VIEW, Permission.STAFF_VIEW],
  segmentsNew: CRM_SECTION,
  campaignEdit: CRM_SECTION,
  leads: CRM_SECTION,
  loyaltyActivate: CRM_SECTION,
  loyalty: CRM_SECTION,
  reviews: [Permission.DIRECTORY_MANAGE],
  packagesNew: [Permission.PACKAGE_VIEW],
  calendarRequests: [Permission.APPOINTMENT_VIEW],
  calendarWaitlist: [Permission.APPOINTMENT_VIEW],
  calendarSchedule: [Permission.APPOINTMENT_VIEW],
  calendarNew: [Permission.APPOINTMENT_VIEW],
  sessions: [Permission.SESSION_VIEW],
  photos: [Permission.PHOTO_VIEW],
  photosPairs: [Permission.PHOTO_VIEW],
  procedures: [Permission.PROCEDURE_VIEW],
  inventoryMaterials: [Permission.INVENTORY_VIEW],
  inventorySuppliers: [Permission.INVENTORY_VIEW],
  inventoryAlerts: [Permission.INVENTORY_VIEW],
  payments: ACCOUNTING_SECTION,
  expensesNew: ACCOUNTING_SECTION,
  revenueShares: ACCOUNTING_SECTION,
  profitLoss: ACCOUNTING_SECTION,
  aiImport: [Permission.IMPORT_AI],
  aiImportGroup: [Permission.IMPORT_AI]
});
var GUIDE_CTA_ROUTE_PLAN_FEATURE = Object.freeze({
  campaignEdit: "campaigns",
  loyaltyActivate: "loyalty",
  loyalty: "loyalty",
  expensesNew: "accounting",
  profitLoss: "accounting",
  revenueShares: "revenueShare",
  settingsBranches: "branches",
  integrations: "apiAccess"
});

// src/ai-import.ts
var AI_IMPORT_KINDS = [
  "PATIENTS",
  "APPOINTMENTS",
  "INVOICE",
  "PRICE_LIST",
  "EXPENSES",
  // Wave 2 (2026-09-30), registered everywhere but NOT enabled: every
  // per-kind table below has their entry, the server dispatches them to
  // per-kind modules (stubs until their stream lands), and the panel hides
  // them behind a per-kind ready flag. A kind ships by joining
  // `AI_IMPORT_KINDS_ENABLED` after its handwriting benchmark.
  "TREATMENTS",
  "STOCK_COUNT"
];
var AI_IMPORT_KINDS_ENABLED = Object.freeze([
  "PATIENTS",
  "EXPENSES",
  "PRICE_LIST",
  "INVOICE",
  "APPOINTMENTS",
  "TREATMENTS",
  "STOCK_COUNT"
]);
var AI_IMPORT_KIND_PLAN_FEATURE = Object.freeze({
  INVOICE: "lotInventory",
  STOCK_COUNT: "lotInventory",
  EXPENSES: "accounting"
});
function isAiImportKind(value) {
  return typeof value === "string" && AI_IMPORT_KINDS.includes(value);
}
var AI_IMPORT_PROVIDER_ABUSE_RETENTION_DAYS = 30;
var AI_IMPORT_JOB_STATUSES = [
  "DRAFT",
  "QUEUED",
  "PROCESSING",
  "READY",
  "FAILED",
  "COMMITTING",
  "COMMITTED",
  "DISCARDED",
  "EXPIRED"
];
var AI_IMPORT_PAGE_STATUSES = [
  "UPLOADED",
  "QUEUED",
  "PROCESSING",
  "READY",
  "EMPTY",
  "FAILED"
];
var AI_IMPORT_ROW_STATUSES = [
  "PENDING",
  "CLAIMED",
  "EXCLUDED",
  "COMMITTED",
  "FAILED",
  "SKIPPED",
  "UNDONE"
];
var AI_IMPORT_QUOTA_SOURCES = ["PLAN", "SUBSCRIPTION_TRIAL", "LIFETIME_TRIAL"];
var AI_IMPORT_CONSENT_VERSION = 1;
var AI_IMPORT_KIND_REQUIREMENTS = Object.freeze({
  PATIENTS: [Permission.IMPORT_AI, Permission.PATIENT_CREATE, Permission.PATIENT_VIEW_ALL],
  APPOINTMENTS: [Permission.IMPORT_AI, Permission.APPOINTMENT_CREATE],
  INVOICE: [Permission.IMPORT_AI, Permission.INVENTORY_RECEIVE],
  PRICE_LIST: [Permission.IMPORT_AI, Permission.PROCEDURE_MANAGE],
  EXPENSES: [Permission.IMPORT_AI, Permission.FINANCE_MANAGE_EXPENSES],
  // Past visits go through `SessionsService.recordHistorical`, the owner of
  // sessions; see also `AI_IMPORT_KIND_DOCTOR_ALTERNATIVE` below.
  TREATMENTS: [Permission.IMPORT_AI, Permission.SESSION_CREATE],
  // An opening balance is a receipt (owner decision 5): the same as INVOICE.
  // Changing a lot that already exists is the `countExistingLots` option.
  STOCK_COUNT: [Permission.IMPORT_AI, Permission.INVENTORY_RECEIVE]
});
var AI_IMPORT_KIND_DOCTOR_ALTERNATIVE = Object.freeze({
  TREATMENTS: Permission.SESSION_VIEW_ALL
});
var AI_IMPORT_KIND_NOT_DOCTOR_FA = "\u0628\u0631\u0627\u06CC \u062B\u0628\u062A \u0633\u0627\u0628\u0642\u0647 \u062F\u0631\u0645\u0627\u0646 \u0628\u0627\u06CC\u062F \u067E\u0632\u0634\u06A9 \u0628\u0627\u0634\u06CC\u062F \u06CC\u0627 \u0628\u0647 \u0647\u0645\u0647 \u062C\u0644\u0633\u0647\u200C\u0647\u0627\u06CC \u062F\u0631\u0645\u0627\u0646 \u062F\u0633\u062A\u0631\u0633\u06CC \u062F\u0627\u0634\u062A\u0647 \u0628\u0627\u0634\u06CC\u062F. \u0627\u0632 \u0645\u0627\u0644\u06A9 \u06A9\u0644\u06CC\u0646\u06CC\u06A9 \u0628\u062E\u0648\u0627\u06CC\u062F \u062F\u0633\u062A\u0631\u0633\u06CC \u0634\u0645\u0627 \u0631\u0648 \u0628\u0631\u0631\u0633\u06CC \u06A9\u0646\u0647.";
var AI_IMPORT_OPTION_REQUIREMENTS = {
  mergeExistingPatient: [Permission.PATIENT_EDIT],
  restoreArchivedPatient: [Permission.PATIENT_DELETE, Permission.PATIENT_EDIT],
  undoPatients: [Permission.PATIENT_DELETE],
  createPatientFromAppointment: [Permission.PATIENT_CREATE, Permission.PATIENT_VIEW_ALL],
  createMaterial: [Permission.INVENTORY_MANAGE_MATERIALS],
  createSupplier: [Permission.INVENTORY_MANAGE_MATERIALS],
  discardOthersJob: [Permission.SETTINGS_EDIT],
  listClinicJobs: [Permission.SETTINGS_VIEW],
  giveConsent: [Permission.SETTINGS_EDIT],
  // ── Wave 2 ──────────────────────────────────────────────────────────────
  /** PATIENTS «افزودن به گروه» (G1): a list group is a segment write + a tag on each file. One bundle, no half-state. */
  addToGroup: [Permission.CRM_VIEW_LEADS, Permission.CRM_MANAGE_LEADS, Permission.PATIENT_EDIT],
  /**
   * PATIENTS undo of a job that created NO patient (it only linked existing
   * files to a group): removing a tag is an edit, not a delete (owner
   * decision, 2026-09-30). A job that created patients still needs `undoPatients`.
   */
  undoPatientLinks: [Permission.PATIENT_EDIT],
  /** TREATMENTS (G3): a row whose patient has no file yet. */
  createPatientFromTreatment: [Permission.PATIENT_CREATE, Permission.PATIENT_VIEW_ALL],
  /** TREATMENTS: a row recorded under another doctor's name (else the doctor is forced to self). */
  recordForOtherDoctors: [Permission.SESSION_VIEW_ALL],
  /**
   * TREATMENTS undo soft-deletes the sessions — the power of `DELETE /sessions/:id`.
   * Patients the job created go to the recycle bin only with `undoPatients` too.
   */
  undoTreatments: [Permission.SESSION_DELETE],
  /** STOCK_COUNT (G2): a COUNT / «جمع کل این کالا» row changes a lot that already exists. */
  countExistingLots: [Permission.INVENTORY_ADJUST]
};
var AI_IMPORT_NO_BATCH_LABEL_FA = "\u0634\u0645\u0627\u0631\u0647\u0654 \u0633\u0631\u06CC \u0633\u0627\u062E\u062A \u0646\u062F\u0627\u0631\u062F";
var AI_IMPORT_UNDOABLE_KINDS = Object.freeze([
  "PATIENTS",
  "EXPENSES",
  "PRICE_LIST",
  "INVOICE",
  // Soft-deletes the sessions and their payments (sends nothing).
  "TREATMENTS",
  // A compensating ADJUSTMENT per movement, newest first, while it is still the lot's latest.
  "STOCK_COUNT"
]);
var AI_IMPORT_GROUP_KINDS = Object.freeze(["PATIENTS"]);
var AI_IMPORT_GROUP_NAME_MAX = 40;
var AI_IMPORT_LIMITS = {
  /** Mirrors the `AI_IMPORT_MAX_PAGES_PER_JOB` default; the server is authoritative. */
  maxPagesPerJob: 20,
  maxUploadBytes: 12 * 1024 * 1024,
  clientLongEdgePx: 2400,
  clientJpegQuality: 0.82,
  clientTargetBytes: 12e5,
  commitChunk: 40,
  rowsPerPageMax: 100
};
var AMOUNT_UNITS = ["rial", "toman", "thousand_toman", "million_toman"];
var AMOUNT_UNIT_LABELS_FA = Object.freeze({
  rial: "\u0631\u06CC\u0627\u0644",
  toman: "\u062A\u0648\u0645\u0627\u0646",
  thousand_toman: "\u0647\u0632\u0627\u0631 \u062A\u0648\u0645\u0627\u0646",
  million_toman: "\u0645\u06CC\u0644\u06CC\u0648\u0646 \u062A\u0648\u0645\u0627\u0646"
});
var AI_IMPORT_FIELD_TYPES = Object.freeze([
  "text",
  "longText",
  "phone",
  "nationalId",
  "jalaliDate",
  "time",
  "minutes",
  "amount",
  "quantity",
  "enum",
  "tags",
  "ref"
]);
var field = (key, label, type, opts = {}) => {
  const required = opts.required ?? false;
  const def = {
    key,
    label,
    type,
    required,
    // A required field is always visible: the reviewer must be able to fix it.
    primary: required || (opts.primary ?? false),
    digits: opts.digits ?? false
  };
  if (opts.ref) def.ref = opts.ref;
  if (opts.enumValues) def.enumValues = opts.enumValues;
  return def;
};
var AI_IMPORT_FIELDS = Object.freeze({
  PATIENTS: [
    field("firstName", "\u0646\u0627\u0645", "text", { required: true }),
    field("lastName", "\u0646\u0627\u0645 \u062E\u0627\u0646\u0648\u0627\u062F\u06AF\u06CC", "text", { required: true }),
    field("phone", "\u0634\u0645\u0627\u0631\u0647 \u0645\u0648\u0628\u0627\u06CC\u0644", "phone", { required: true, digits: true }),
    field("nationalId", "\u06A9\u062F \u0645\u0644\u06CC", "nationalId", { primary: true, digits: true }),
    field("birthDate", "\u062A\u0627\u0631\u06CC\u062E \u062A\u0648\u0644\u062F", "jalaliDate", { primary: true, digits: true }),
    field("gender", "\u062C\u0646\u0633\u06CC\u062A", "enum", { primary: true, enumValues: Object.values(Gender) }),
    field("city", "\u0634\u0647\u0631", "text"),
    field("occupation", "\u0634\u063A\u0644", "text"),
    field("acquisitionSource", "\u0646\u062D\u0648\u0647\u0654 \u0622\u0634\u0646\u0627\u06CC\u06CC", "enum", {
      enumValues: Object.values(AcquisitionSource)
    }),
    field("tags", "\u0628\u0631\u0686\u0633\u0628\u200C\u0647\u0627", "tags"),
    field("allergies", "\u062D\u0633\u0627\u0633\u06CC\u062A\u200C\u0647\u0627", "longText"),
    field("medicalHistory", "\u0633\u0627\u0628\u0642\u0647\u0654 \u067E\u0632\u0634\u06A9\u06CC", "longText"),
    field("notes", "\u06CC\u0627\u062F\u062F\u0627\u0634\u062A", "longText"),
    field("code", "\u0634\u0645\u0627\u0631\u0647\u0654 \u067E\u0631\u0648\u0646\u062F\u0647", "text", { primary: true })
  ],
  EXPENSES: [
    field("description", "\u0634\u0631\u062D", "text", { required: true }),
    field("vendorName", "\u0641\u0631\u0648\u0634\u0646\u062F\u0647/\u0637\u0631\u0641 \u062D\u0633\u0627\u0628", "text", { primary: true }),
    field("category", "\u062F\u0633\u062A\u0647\u200C\u0628\u0646\u062F\u06CC", "enum", {
      required: true,
      enumValues: Object.values(ExpenseCategory)
    }),
    field("amount", "\u0645\u0628\u0644\u063A", "amount", { required: true, digits: true }),
    field("spentAt", "\u062A\u0627\u0631\u06CC\u062E", "jalaliDate", { required: true, digits: true }),
    field("isPaid", "\u067E\u0631\u062F\u0627\u062E\u062A \u0634\u062F\u0647\u061F", "enum", { enumValues: ["YES", "NO"] })
  ],
  PRICE_LIST: [
    field("serviceName", "\u062E\u062F\u0645\u062A", "ref", { required: true, ref: "procedure" }),
    field("variant", "\u062C\u0632\u0626\u06CC\u0627\u062A/\u0646\u0627\u062D\u06CC\u0647", "text", { primary: true }),
    field("price", "\u0642\u06CC\u0645\u062A", "amount", { required: true, digits: true }),
    field("priceMax", "\u062D\u062F\u0627\u06A9\u062B\u0631 \u0642\u06CC\u0645\u062A", "amount", { primary: true, digits: true }),
    field("durationMinutes", "\u0645\u062F\u062A", "minutes")
  ],
  INVOICE: [
    field("itemName", "\u0646\u0627\u0645 \u06A9\u0627\u0644\u0627", "ref", { required: true, ref: "material" }),
    field("brand", "\u0628\u0631\u0646\u062F", "text", { primary: true }),
    field("quantity", "\u062A\u0639\u062F\u0627\u062F", "quantity", { required: true, digits: true }),
    field("unitText", "\u0648\u0627\u062D\u062F (\u0646\u0648\u0634\u062A\u0647\u200C\u0634\u062F\u0647)", "text", { primary: true }),
    field("unitPrice", "\u0642\u06CC\u0645\u062A \u0648\u0627\u062D\u062F", "amount", { primary: true, digits: true }),
    field("lineTotal", "\u062C\u0645\u0639 \u0631\u062F\u06CC\u0641", "amount", { digits: true }),
    field("batchNo", "\u0634\u0645\u0627\u0631\u0647\u0654 \u0633\u0631\u06CC \u0633\u0627\u062E\u062A", "text", { required: true }),
    field("expiryDate", "\u062A\u0627\u0631\u06CC\u062E \u0627\u0646\u0642\u0636\u0627", "jalaliDate", { primary: true, digits: true })
  ],
  APPOINTMENTS: [
    field("date", "\u062A\u0627\u0631\u06CC\u062E", "jalaliDate", { required: true, digits: true }),
    field("time", "\u0633\u0627\u0639\u062A", "time", { required: true, digits: true }),
    field("durationMinutes", "\u0645\u062F\u062A", "minutes"),
    field("patientName", "\u0646\u0627\u0645 \u0628\u06CC\u0645\u0627\u0631", "text", { required: true }),
    field("phone", "\u0634\u0645\u0627\u0631\u0647 \u0645\u0648\u0628\u0627\u06CC\u0644", "phone", { primary: true, digits: true }),
    field("procedure", "\u062E\u062F\u0645\u062A", "ref", { primary: true, ref: "procedure" }),
    field("doctor", "\u067E\u0632\u0634\u06A9", "ref", { primary: true, ref: "staff" }),
    field("statusMark", "\u0648\u0636\u0639\u06CC\u062A \u062F\u0631 \u062F\u0641\u062A\u0631", "text"),
    field("notes", "\u06CC\u0627\u062F\u062F\u0627\u0634\u062A", "longText")
  ],
  // G3. `date` is required but may come from the page's day heading; the
  // card header's patientName / phone / code are copied into its rows.
  TREATMENTS: [
    field("date", "\u062A\u0627\u0631\u06CC\u062E", "jalaliDate", { required: true, digits: true }),
    field("patientName", "\u0646\u0627\u0645 \u0628\u06CC\u0645\u0627\u0631", "text", { primary: true }),
    field("phone", "\u0634\u0645\u0627\u0631\u0647 \u0645\u0648\u0628\u0627\u06CC\u0644", "phone", { primary: true, digits: true }),
    field("code", "\u0634\u0645\u0627\u0631\u0647\u0654 \u067E\u0631\u0648\u0646\u062F\u0647", "text"),
    field("procedure", "\u062E\u062F\u0645\u062A", "ref", { primary: true, ref: "procedure" }),
    field("area", "\u0646\u0627\u062D\u06CC\u0647", "text"),
    field("units", "\u0645\u0642\u062F\u0627\u0631 (\u0648\u0627\u062D\u062F/\u0633\u06CC\u200C\u0633\u06CC/\u0634\u0627\u062A)", "quantity", { digits: true }),
    field("materials", "\u0645\u0648\u0627\u062F \u0648 \u062F\u0627\u0631\u0648\u06CC \u0645\u0635\u0631\u0641\u06CC", "longText", { primary: true }),
    field("doctor", "\u067E\u0632\u0634\u06A9", "ref", { primary: true, ref: "staff" }),
    field("price", "\u0645\u0628\u0644\u063A \u062C\u0644\u0633\u0647", "amount", { primary: true, digits: true }),
    field("paid", "\u0645\u0628\u0644\u063A \u067E\u0631\u062F\u0627\u062E\u062A\u200C\u0634\u062F\u0647", "amount", { digits: true }),
    field("paymentMethod", "\u0631\u0648\u0634 \u067E\u0631\u062F\u0627\u062E\u062A", "enum", { enumValues: ["CASH", "CARD", "TRANSFER"] }),
    field("notes", "\u06CC\u0627\u062F\u062F\u0627\u0634\u062A", "longText")
  ],
  // G2. The count key is `quantity` (as INVOICE) so the quantity helpers and
  // the panel's cell formatting work unchanged; a count of 0 is valid here.
  STOCK_COUNT: [
    field("itemName", "\u0646\u0627\u0645 \u06A9\u0627\u0644\u0627", "ref", { required: true, ref: "material" }),
    field("brand", "\u0628\u0631\u0646\u062F", "text", { primary: true }),
    field("batchNo", "\u0634\u0645\u0627\u0631\u0647\u0654 \u0633\u0631\u06CC \u0633\u0627\u062E\u062A", "text", { primary: true }),
    field("expiryDate", "\u062A\u0627\u0631\u06CC\u062E \u0627\u0646\u0642\u0636\u0627", "jalaliDate", { primary: true, digits: true }),
    field("quantity", "\u062A\u0639\u062F\u0627\u062F \u0634\u0645\u0631\u062F\u0647\u200C\u0634\u062F\u0647", "quantity", { required: true, digits: true }),
    field("unitText", "\u0648\u0627\u062D\u062F (\u0646\u0648\u0634\u062A\u0647\u200C\u0634\u062F\u0647)", "text", { primary: true }),
    field("unitCost", "\u0642\u06CC\u0645\u062A \u0648\u0627\u062D\u062F", "amount", { digits: true }),
    field("location", "\u0645\u062D\u0644 \u0646\u06AF\u0647\u062F\u0627\u0631\u06CC", "text")
  ]
});
function aiImportField(kind, key) {
  return AI_IMPORT_FIELDS[kind].find((f) => f.key === key);
}
var AI_IMPORT_KIND_LABEL_FA = Object.freeze({
  PATIENTS: {
    title: "\u0641\u0647\u0631\u0633\u062A \u0628\u06CC\u0645\u0627\u0631\u0627\u0646",
    description: "\u062F\u0641\u062A\u0631 \u0645\u0634\u062A\u0631\u06CC\u0627\u0646\u060C \u0644\u06CC\u0633\u062A \u062A\u0644\u0641\u0646\u200C\u0647\u0627 \u06CC\u0627 \u06A9\u0627\u0631\u062A \u067E\u0631\u0648\u0646\u062F\u0647\u200C\u0647\u0627",
    noun: "\u0628\u06CC\u0645\u0627\u0631"
  },
  EXPENSES: {
    title: "\u0631\u0633\u06CC\u062F \u0648 \u0642\u0628\u0636 \u0647\u0632\u06CC\u0646\u0647",
    description: "\u0642\u0628\u0636\u060C \u0631\u0633\u06CC\u062F \u062E\u0631\u06CC\u062F \u06CC\u0627 \u0641\u0627\u06A9\u062A\u0648\u0631 \u0647\u0632\u06CC\u0646\u0647\u200C\u0647\u0627\u06CC \u062C\u0627\u0631\u06CC \u06A9\u0644\u06CC\u0646\u06CC\u06A9",
    noun: "\u0647\u0632\u06CC\u0646\u0647"
  },
  PRICE_LIST: {
    title: "\u0644\u06CC\u0633\u062A \u0642\u06CC\u0645\u062A \u062E\u062F\u0645\u0627\u062A",
    description: "\u062A\u0639\u0631\u0641\u0647\u0654 \u062E\u062F\u0645\u0627\u062A \u0631\u0648\u06CC \u06A9\u0627\u063A\u0630\u060C \u062A\u0627\u0628\u0644\u0648 \u06CC\u0627 \u0645\u0646\u0648",
    noun: "\u062E\u062F\u0645\u062A"
  },
  INVOICE: {
    title: "\u0641\u0627\u06A9\u062A\u0648\u0631 \u062E\u0631\u06CC\u062F \u0645\u0648\u0627\u062F \u0648 \u062F\u0627\u0631\u0648",
    description: "\u0641\u0627\u06A9\u062A\u0648\u0631 \u062A\u0623\u0645\u06CC\u0646\u200C\u06A9\u0646\u0646\u062F\u0647 \u06CC\u0627 \u0644\u06CC\u0633\u062A \u0645\u0648\u0627\u062F\u061B \u0628\u0647 \u0627\u0646\u0628\u0627\u0631 \u0627\u0636\u0627\u0641\u0647 \u0645\u06CC\u200C\u0634\u0647",
    noun: "\u0642\u0644\u0645 \u06A9\u0627\u0644\u0627"
  },
  APPOINTMENTS: {
    title: "\u062F\u0641\u062A\u0631 \u0646\u0648\u0628\u062A\u200C\u0647\u0627",
    description: "\u0635\u0641\u062D\u0647\u0654 \u0631\u0648\u0632\u0627\u0646\u0647\u0654 \u062F\u0641\u062A\u0631 \u0646\u0648\u0628\u062A \u06CC\u0627 \u0628\u0631\u0646\u0627\u0645\u0647\u0654 \u0647\u0641\u062A\u0647",
    noun: "\u0646\u0648\u0628\u062A"
  },
  TREATMENTS: {
    title: "\u067E\u0631\u0648\u0646\u062F\u0647 \u0648 \u0633\u0627\u0628\u0642\u0647\u0654 \u062F\u0631\u0645\u0627\u0646",
    description: "\u06A9\u0627\u0631\u062A \u062F\u0631\u0645\u0627\u0646 \u0628\u06CC\u0645\u0627\u0631 \u06CC\u0627 \u062F\u0641\u062A\u0631 \u0631\u0648\u0632\u0627\u0646\u0647\u0654 \u062C\u0644\u0633\u0647\u200C\u0647\u0627\u061B \u0628\u0647 \u0633\u0648\u0627\u0628\u0642 \u0628\u06CC\u0645\u0627\u0631\u0627\u0646 \u0627\u0636\u0627\u0641\u0647 \u0645\u06CC\u200C\u0634\u0647",
    noun: "\u062C\u0644\u0633\u0647"
  },
  STOCK_COUNT: {
    title: "\u0634\u0645\u0627\u0631\u0634 \u0627\u0646\u0628\u0627\u0631",
    description: "\u0644\u06CC\u0633\u062A \u0645\u0648\u062C\u0648\u062F\u06CC \u0642\u0641\u0633\u0647 \u0648 \u06CC\u062E\u0686\u0627\u0644 \u06CC\u0627 \u0628\u0631\u06AF\u0647\u0654 \u0634\u0645\u0627\u0631\u0634\u061B \u0645\u0648\u062C\u0648\u062F\u06CC \u0627\u0648\u0644\u06CC\u0647 \u062B\u0628\u062A \u0648 \u0627\u062E\u062A\u0644\u0627\u0641\u200C\u0647\u0627 \u0627\u0635\u0644\u0627\u062D \u0645\u06CC\u200C\u0634\u0647",
    noun: "\u0642\u0644\u0645"
  }
});
var AI_IMPORT_RESOLUTION_ACTIONS = [
  "CREATE",
  "SKIP",
  "UPDATE_EXISTING",
  "LINK_EXISTING",
  "RESTORE_AND_FILL",
  "TIME_ONLY"
];
var AI_IMPORT_PAGE_ERROR_CODES = [
  "BLURRY",
  "NO_TABLE",
  "WRONG_KIND",
  "TOO_DENSE",
  "RELAY_UNAVAILABLE",
  "TIMEOUT",
  "MODEL_REFUSED",
  "TOO_LARGE",
  "INTERNAL",
  "DISCARDED"
];
var AI_IMPORT_TRANSIENT_PAGE_ERROR_CODES = Object.freeze(
  ["RELAY_UNAVAILABLE", "TIMEOUT", "INTERNAL"]
);
var AI_IMPORT_PAGE_ERROR_COPY_FA = Object.freeze({
  BLURRY: {
    title: "\u0627\u06CC\u0646 \u0635\u0641\u062D\u0647 \u062E\u0648\u0627\u0646\u0627 \u0646\u0628\u0648\u062F",
    hint: "\u0639\u06A9\u0633 \u0631\u0648 \u062F\u0631 \u0646\u0648\u0631 \u0628\u06CC\u0634\u062A\u0631\u060C \u0628\u062F\u0648\u0646 \u0644\u0631\u0632\u0634 \u0648 \u0633\u0627\u06CC\u0647\u060C \u062F\u0648\u0628\u0627\u0631\u0647 \u0628\u06AF\u06CC\u0631\u06CC\u062F."
  },
  NO_TABLE: {
    title: "\u062F\u0631 \u0627\u06CC\u0646 \u0639\u06A9\u0633 \u0641\u0647\u0631\u0633\u062A\u06CC \u067E\u06CC\u062F\u0627 \u0646\u0634\u062F",
    hint: "\u0627\u0632 \u0628\u0631\u06AF\u0647 \u0645\u0648\u0631\u062F\u0646\u0638\u0631 \u0639\u06A9\u0633 \u0628\u06AF\u06CC\u0631\u06CC\u062F \u0648 \u0645\u0637\u0645\u0626\u0646 \u0628\u0634\u06CC\u062F \u062A\u0645\u0627\u0645 \u0628\u0631\u06AF\u0647 \u062F\u0627\u062E\u0644 \u06A9\u0627\u062F\u0631\u0647."
  },
  WRONG_KIND: {
    title: "\u0646\u0648\u0639 \u0628\u0631\u06AF\u0647 \u0628\u0627 \u0646\u0648\u0639 \u0627\u0637\u0644\u0627\u0639\u0627\u062A \u0627\u0646\u062A\u062E\u0627\u0628\u200C\u0634\u062F\u0647 \u0641\u0631\u0642 \u062F\u0627\u0631\u0647",
    hint: "\u0646\u0648\u0639 \u0627\u0637\u0644\u0627\u0639\u0627\u062A \u0631\u0648 \u062A\u063A\u06CC\u06CC\u0631 \u0628\u062F\u06CC\u062F \u062A\u0627 \u0628\u0631\u06AF\u0647 \u062F\u0631\u0633\u062A \u062E\u0648\u0627\u0646\u062F\u0647 \u0628\u0634\u0647. \u0647\u0632\u06CC\u0646\u0647\u200C\u0627\u06CC \u06A9\u0645 \u0646\u0645\u06CC\u200C\u0634\u0647."
  },
  TOO_DENSE: {
    title: "\u062A\u0639\u062F\u0627\u062F \u0631\u062F\u06CC\u0641\u200C\u0647\u0627\u06CC \u0627\u06CC\u0646 \u0635\u0641\u062D\u0647 \u0627\u0632 \u062D\u062F \u0645\u062C\u0627\u0632 \u0628\u06CC\u0634\u062A\u0631\u0647",
    hint: "\u0646\u06CC\u0645\u0647\u0654 \u0628\u0627\u0644\u0627 \u0648 \u067E\u0627\u06CC\u06CC\u0646 \u0628\u0631\u06AF\u0647 \u0631\u0648 \u062C\u062F\u0627\u06AF\u0627\u0646\u0647 \u0639\u06A9\u0633 \u0628\u06AF\u06CC\u0631\u06CC\u062F."
  },
  RELAY_UNAVAILABLE: {
    title: "\u0627\u0631\u062A\u0628\u0627\u0637 \u0628\u0627 \u0633\u0631\u0648\u06CC\u0633 \u062E\u0648\u0627\u0646\u062F\u0646 \u0639\u06A9\u0633 \u0628\u0631\u0642\u0631\u0627\u0631 \u0646\u0634\u062F",
    hint: "\u0686\u0646\u062F \u062F\u0642\u06CC\u0642\u0647\u0654 \u062F\u06CC\u06AF\u0647 \xAB\u062A\u0644\u0627\u0634 \u062F\u0648\u0628\u0627\u0631\u0647\xBB \u0631\u0648 \u0628\u0632\u0646\u06CC\u062F. \u0627\u0632 \u0633\u0647\u0645\u06CC\u0647\u0654 \u0634\u0645\u0627 \u06A9\u0645 \u0646\u0634\u062F\u0647."
  },
  TIMEOUT: {
    title: "\u062E\u0648\u0627\u0646\u062F\u0646 \u0627\u06CC\u0646 \u0635\u0641\u062D\u0647 \u0628\u06CC\u0634 \u0627\u0632 \u062D\u062F \u0637\u0648\u0644 \u06A9\u0634\u06CC\u062F",
    hint: "\xAB\u062A\u0644\u0627\u0634 \u062F\u0648\u0628\u0627\u0631\u0647\xBB \u0631\u0648 \u0628\u0632\u0646\u06CC\u062F. \u0627\u0632 \u0633\u0647\u0645\u06CC\u0647\u0654 \u0634\u0645\u0627 \u06A9\u0645 \u0646\u0634\u062F\u0647."
  },
  MODEL_REFUSED: {
    title: "\u0627\u06CC\u0646 \u0639\u06A9\u0633 \u067E\u0631\u062F\u0627\u0632\u0634 \u0646\u0634\u062F",
    hint: "\u0639\u06A9\u0633 \u062F\u06CC\u06AF\u0631\u06CC \u0627\u0632 \u0647\u0645\u06CC\u0646 \u0628\u0631\u06AF\u0647 \u0628\u06AF\u06CC\u0631\u06CC\u062F \u06CC\u0627 \u0627\u0637\u0644\u0627\u0639\u0627\u062A \u0631\u0648 \u062F\u0633\u062A\u06CC \u0648\u0627\u0631\u062F \u06A9\u0646\u06CC\u062F."
  },
  TOO_LARGE: {
    title: "\u062D\u062C\u0645 \u0627\u06CC\u0646 \u0639\u06A9\u0633 \u0628\u0631\u0627\u06CC \u0627\u0631\u0633\u0627\u0644 \u0628\u06CC\u0634 \u0627\u0632 \u062D\u062F \u0645\u062C\u0627\u0632\u0647",
    hint: "\u0639\u06A9\u0633 \u0631\u0648 \u062F\u0648\u0628\u0627\u0631\u0647 \u0628\u06AF\u06CC\u0631\u06CC\u062F\u061B \u0627\u06AF\u0631 \u062A\u06A9\u0631\u0627\u0631 \u0634\u062F \u0628\u0627 \u067E\u0634\u062A\u06CC\u0628\u0627\u0646\u06CC \u062A\u0645\u0627\u0633 \u0628\u06AF\u06CC\u0631\u06CC\u062F."
  },
  INTERNAL: {
    title: "\u062E\u0648\u0627\u0646\u062F\u0646 \u0627\u06CC\u0646 \u0635\u0641\u062D\u0647 \u0628\u0647 \u0645\u0634\u06A9\u0644 \u062E\u0648\u0631\u062F",
    hint: "\xAB\u062A\u0644\u0627\u0634 \u062F\u0648\u0628\u0627\u0631\u0647\xBB \u0631\u0648 \u0628\u0632\u0646\u06CC\u062F\u061B \u0627\u06AF\u0631 \u062A\u06A9\u0631\u0627\u0631 \u0634\u062F \u0628\u0627 \u067E\u0634\u062A\u06CC\u0628\u0627\u0646\u06CC \u062A\u0645\u0627\u0633 \u0628\u06AF\u06CC\u0631\u06CC\u062F."
  },
  DISCARDED: {
    title: "\u062B\u0628\u062A \u0644\u063A\u0648 \u0634\u062F \u0648 \u0627\u0637\u0644\u0627\u0639\u0627\u062A \u0645\u0648\u0642\u062A \u067E\u0627\u06A9 \u0634\u062F",
    hint: "\u0628\u0631\u0627\u06CC \u062B\u0628\u062A \u0627\u06CC\u0646 \u0628\u0631\u06AF\u0647\u060C \u062F\u0648\u0628\u0627\u0631\u0647 \u0627\u0632 \u0631\u0648\u06CC \u0639\u06A9\u0633 \u0634\u0631\u0648\u0639 \u06A9\u0646\u06CC\u062F."
  }
});
var AI_IMPORT_ISSUE_CODES = [
  "UNCLEAR",
  "DIGITS_DISAGREE",
  "DIGITS_UNCONFIRMED",
  "DITTO",
  "REQUIRED",
  "LANDLINE",
  "PHONE_INVALID",
  "PHONE_UNVERIFIED_FUTURE",
  "NID_INVALID",
  "DATE_INVALID",
  "INFERRED_YEAR",
  "GREGORIAN_CONVERTED",
  "TIME_INVALID",
  "INFERRED_PM",
  "UNIT_UNCONFIRMED",
  "UNIT_MIXED",
  "AMOUNT_INVALID",
  "QUANTITY_INVALID",
  "CATEGORY_SUGGESTED",
  "FUZZY_MATCH",
  "NO_MATCH",
  "NEEDS_PATIENT",
  "DUPLICATE_IN_CLINIC",
  "DUPLICATE_ARCHIVED",
  "ARCHIVED_BY_UNDO",
  "DUPLICATE_IN_JOB",
  "LOOKS_LIKE_MATERIAL_PURCHASE",
  "STRUCK",
  "PRICE_RANGE",
  "PRICE_UNCHANGED",
  "SAME_PROCEDURE_IN_JOB",
  "BATCH_MISSING",
  "BATCH_EXISTS",
  "UNIT_MAPPED",
  "EXPIRY_MISSING",
  "SLOT_TAKEN",
  "ROOM_UNAVAILABLE",
  "DOCTOR_ON_LEAVE",
  "PAST_APPOINTMENT_SKIP",
  "PAST_APPOINTMENT_DONE",
  "PAST_APPOINTMENT_NO_SHOW",
  "APPOINTMENT_CANCELLED",
  "NOTES_REDACTED",
  "COMMIT_UNCERTAIN",
  "VALUE_INVALID",
  // ── TREATMENTS (G3) ─────────────────────────────────────────────────────
  "SESSION_DATE_FUTURE",
  "SESSION_EXISTS",
  "DOCTOR_NOT_YOU",
  "DOCTOR_INACTIVE",
  "PROCEDURE_DISABLED",
  "PAID_OVER_PRICE",
  "PRICE_MISSING",
  "SESSION_DEBT",
  "IDENTITY_NAME_MISMATCH",
  // ── STOCK_COUNT (G2) ────────────────────────────────────────────────────
  "LOT_AMBIGUOUS",
  "NEW_LOT",
  "COUNT_DENIED",
  "COUNT_DIFF_LARGE",
  "NEGATIVE_AFTER_COUNT",
  "EXPIRY_DIFFERS",
  "LOT_EXPIRED",
  "NEW_LOT_EXPIRED",
  "ZERO_NEW_LOT",
  "SAME_LOT_IN_JOB",
  "MATERIAL_TOTAL_CONFLICT",
  "BATCH_FUZZY",
  "COST_ESTIMATED",
  "COST_MISSING",
  "SURPLUS_NEEDS_LOT"
];
var AI_IMPORT_ISSUE_SEVERITY = Object.freeze({
  UNCLEAR: "check",
  DIGITS_DISAGREE: "check",
  DIGITS_UNCONFIRMED: "check",
  DITTO: "check",
  REQUIRED: "invalid",
  LANDLINE: "invalid",
  PHONE_INVALID: "invalid",
  PHONE_UNVERIFIED_FUTURE: "invalid",
  NID_INVALID: "invalid",
  DATE_INVALID: "invalid",
  INFERRED_YEAR: "check",
  GREGORIAN_CONVERTED: "check",
  TIME_INVALID: "invalid",
  INFERRED_PM: "check",
  UNIT_UNCONFIRMED: "check",
  UNIT_MIXED: "check",
  AMOUNT_INVALID: "invalid",
  QUANTITY_INVALID: "invalid",
  CATEGORY_SUGGESTED: "check",
  FUZZY_MATCH: "check",
  NO_MATCH: "invalid",
  NEEDS_PATIENT: "invalid",
  DUPLICATE_IN_CLINIC: "check",
  DUPLICATE_ARCHIVED: "invalid",
  ARCHIVED_BY_UNDO: "check",
  DUPLICATE_IN_JOB: "check",
  LOOKS_LIKE_MATERIAL_PURCHASE: "check",
  STRUCK: "check",
  PRICE_RANGE: "check",
  PRICE_UNCHANGED: "check",
  SAME_PROCEDURE_IN_JOB: "invalid",
  BATCH_MISSING: "invalid",
  BATCH_EXISTS: "invalid",
  UNIT_MAPPED: "check",
  EXPIRY_MISSING: "check",
  SLOT_TAKEN: "invalid",
  ROOM_UNAVAILABLE: "invalid",
  DOCTOR_ON_LEAVE: "invalid",
  PAST_APPOINTMENT_SKIP: "check",
  PAST_APPOINTMENT_DONE: "check",
  PAST_APPOINTMENT_NO_SHOW: "check",
  APPOINTMENT_CANCELLED: "check",
  NOTES_REDACTED: "check",
  COMMIT_UNCERTAIN: "invalid",
  VALUE_INVALID: "invalid",
  SESSION_DATE_FUTURE: "invalid",
  SESSION_EXISTS: "check",
  DOCTOR_NOT_YOU: "invalid",
  DOCTOR_INACTIVE: "check",
  PROCEDURE_DISABLED: "check",
  PAID_OVER_PRICE: "check",
  PRICE_MISSING: "check",
  SESSION_DEBT: "check",
  IDENTITY_NAME_MISMATCH: "check",
  LOT_AMBIGUOUS: "invalid",
  NEW_LOT: "check",
  COUNT_DENIED: "invalid",
  COUNT_DIFF_LARGE: "check",
  NEGATIVE_AFTER_COUNT: "invalid",
  EXPIRY_DIFFERS: "check",
  LOT_EXPIRED: "check",
  NEW_LOT_EXPIRED: "check",
  ZERO_NEW_LOT: "check",
  SAME_LOT_IN_JOB: "check",
  MATERIAL_TOTAL_CONFLICT: "invalid",
  BATCH_FUZZY: "check",
  COST_ESTIMATED: "check",
  COST_MISSING: "check",
  SURPLUS_NEEDS_LOT: "invalid"
});
var AI_IMPORT_ISSUE_COPY_FA = Object.freeze({
  UNCLEAR: "\u0645\u062A\u0646 \u0627\u06CC\u0646 \u062E\u0627\u0646\u0647 \u0628\u0647\u200C\u062F\u0631\u0633\u062A\u06CC \u062E\u0648\u0627\u0646\u062F\u0647 \u0646\u0634\u062F\u061B \u0627\u0648\u0646 \u0631\u0648 \u0628\u0627 \u0639\u06A9\u0633 \u0645\u0642\u0627\u06CC\u0633\u0647 \u06A9\u0646\u06CC\u062F.",
  DIGITS_DISAGREE: "\u0627\u06CC\u0646 \u0639\u062F\u062F \u062F\u0631 \u062F\u0648 \u0628\u0627\u0631 \u0628\u0631\u0631\u0633\u06CC \u0645\u062A\u0641\u0627\u0648\u062A \u062E\u0648\u0627\u0646\u062F\u0647 \u0634\u062F\u0647 ({alt}). \u0628\u0627 \u0639\u06A9\u0633 \u0645\u0642\u0627\u06CC\u0633\u0647\u200C\u0627\u0634 \u06A9\u0646\u06CC\u062F.",
  DIGITS_UNCONFIRMED: "\u0627\u06CC\u0646 \u0639\u062F\u062F \u0641\u0642\u0637 \u06CC\u06A9 \u0628\u0627\u0631 \u062E\u0648\u0627\u0646\u062F\u0647 \u0634\u062F\u0647 \u0648 \u0647\u0646\u0648\u0632 \u0627\u0632 \u062F\u0631\u0633\u062A \u0628\u0648\u062F\u0646\u0634 \u0645\u0637\u0645\u0626\u0646 \u0646\u06CC\u0633\u062A\u06CC\u0645. \u0628\u0627 \u0639\u06A9\u0633 \u0645\u0642\u0627\u06CC\u0633\u0647\u200C\u0627\u0634 \u06A9\u0646\u06CC\u062F.",
  DITTO: "\u062F\u0631 \u0628\u0631\u06AF\u0647 \u0639\u0644\u0627\u0645\u062A \u062A\u06A9\u0631\u0627\u0631 \xAB\u3003\xBB \u0646\u0648\u0634\u062A\u0647 \u0634\u062F\u0647\u060C \u0628\u0631\u0627\u06CC \u0647\u0645\u06CC\u0646 \u0645\u0642\u062F\u0627\u0631 \u0631\u062F\u06CC\u0641 \u0628\u0627\u0644\u0627 \u062F\u0631 \u0627\u06CC\u0646 \u062E\u0627\u0646\u0647 \u06AF\u0630\u0627\u0634\u062A\u0647 \u0634\u062F\u0647.",
  REQUIRED: "\u0628\u0631\u0627\u06CC \u062B\u0628\u062A \u0627\u06CC\u0646 \u0631\u062F\u06CC\u0641\u060C \u0627\u06CC\u0646 \u062E\u0627\u0646\u0647 \u0631\u0648 \u067E\u0631 \u06A9\u0646\u06CC\u062F.",
  LANDLINE: "\u0627\u06CC\u0646 \u0634\u0645\u0627\u0631\u0647 \u062A\u0644\u0641\u0646 \u062B\u0627\u0628\u062A\u0647. \u0634\u0645\u0627\u0631\u0647 \u0645\u0648\u0628\u0627\u06CC\u0644 \u0628\u06CC\u0645\u0627\u0631 \u0631\u0648 \u0648\u0627\u0631\u062F \u06A9\u0646\u06CC\u062F.",
  PHONE_INVALID: "\u0634\u0645\u0627\u0631\u0647 \u0645\u0648\u0628\u0627\u06CC\u0644 \u0628\u0627\u06CC\u062F \u06F1\u06F1 \u0631\u0642\u0645 \u062F\u0627\u0634\u062A\u0647 \u0628\u0627\u0634\u0647 \u0648 \u0628\u0627 \u06F0\u06F9 \u0634\u0631\u0648\u0639 \u0628\u0634\u0647.",
  PHONE_UNVERIFIED_FUTURE: "\u0628\u0631\u0627\u06CC \u0627\u06CC\u0646 \u0646\u0648\u0628\u062A \u067E\u06CC\u0627\u0645\u06A9 \u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u0627\u0631\u0633\u0627\u0644 \u0645\u06CC\u200C\u0634\u0647\u061B \u0634\u0645\u0627\u0631\u0647 \u0631\u0648 \u0628\u0627 \u0639\u06A9\u0633 \u0645\u0642\u0627\u06CC\u0633\u0647 \u0648 \u062A\u0623\u06CC\u06CC\u062F \u06A9\u0646\u06CC\u062F.",
  NID_INVALID: "\u06A9\u062F \u0645\u0644\u06CC \u0645\u0639\u062A\u0628\u0631 \u0646\u06CC\u0633\u062A\u061B \u0631\u0642\u0645\u200C\u0647\u0627 \u0631\u0648 \u0628\u0627 \u0639\u06A9\u0633 \u0645\u0642\u0627\u06CC\u0633\u0647 \u0648 \u0627\u0635\u0644\u0627\u062D \u06A9\u0646\u06CC\u062F.",
  DATE_INVALID: "\u0627\u06CC\u0646 \u062A\u0627\u0631\u06CC\u062E \u0645\u0639\u062A\u0628\u0631 \u0646\u06CC\u0633\u062A.",
  INFERRED_YEAR: "\u0633\u0627\u0644 \u062F\u0631 \u0628\u0631\u06AF\u0647 \u0646\u0628\u0648\u062F\u061B {year} \u062F\u0631 \u0646\u0638\u0631 \u06AF\u0631\u0641\u062A\u0647 \u0634\u062F.",
  GREGORIAN_CONVERTED: "\u062A\u0627\u0631\u06CC\u062E \u0645\u06CC\u0644\u0627\u062F\u06CC \u0628\u0648\u062F \u0648 \u0628\u0647 \u0634\u0645\u0633\u06CC \u062A\u0628\u062F\u06CC\u0644 \u0634\u062F.",
  TIME_INVALID: "\u0633\u0627\u0639\u062A \u0645\u0639\u062A\u0628\u0631 \u0646\u06CC\u0633\u062A.",
  INFERRED_PM: "\u0633\u0627\u0639\u062A \xAB{raw}\xBB {time} \u062F\u0631 \u0646\u0638\u0631 \u06AF\u0631\u0641\u062A\u0647 \u0634\u062F.",
  UNIT_UNCONFIRMED: "\u0648\u0627\u062D\u062F \u0645\u0628\u0644\u063A \u0645\u0634\u062E\u0635 \u0646\u06CC\u0633\u062A\u061B \u0648\u0627\u062D\u062F \u0627\u06CC\u0646 \u0628\u0631\u06AF\u0647 \u0631\u0648 \u0628\u0627\u0644\u0627\u06CC \u062C\u062F\u0648\u0644 \u062A\u0623\u06CC\u06CC\u062F \u06A9\u0646\u06CC\u062F.",
  UNIT_MIXED: "\u0628\u0647 \u0646\u0638\u0631 \u0645\u06CC\u200C\u0631\u0633\u0647 \u0648\u0627\u062D\u062F \u0627\u06CC\u0646 \u0645\u0628\u0644\u063A {unit} \u0628\u0627\u0634\u0647. \u0628\u0627 \u0628\u0631\u06AF\u0647 \u0645\u0642\u0627\u06CC\u0633\u0647 \u06A9\u0646\u06CC\u062F.",
  AMOUNT_INVALID: "\u0627\u06CC\u0646 \u0645\u0628\u0644\u063A \u062E\u0648\u0627\u0646\u062F\u0647 \u0646\u0634\u062F.",
  QUANTITY_INVALID: "\u062A\u0639\u062F\u0627\u062F \u0628\u0627\u06CC\u062F \u0639\u062F\u062F\u06CC \u0628\u0632\u0631\u06AF\u200C\u062A\u0631 \u0627\u0632 \u0635\u0641\u0631 \u0628\u0627\u0634\u0647.",
  CATEGORY_SUGGESTED: "\u0627\u06CC\u0646 \u062F\u0633\u062A\u0647\u200C\u0628\u0646\u062F\u06CC \u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u0647. \u062A\u0623\u06CC\u06CC\u062F\u0634 \u06A9\u0646\u06CC\u062F \u06CC\u0627 \u062A\u063A\u06CC\u06CC\u0631\u0634 \u0628\u062F\u06CC\u062F.",
  FUZZY_MATCH: "\u06CC\u06A9 \u06AF\u0632\u06CC\u0646\u0647\u0654 \u0645\u0634\u0627\u0628\u0647 \u0627\u0646\u062A\u062E\u0627\u0628 \u0634\u062F\u0647. \u0628\u0631\u0631\u0633\u06CC \u06A9\u0646\u06CC\u062F \u0647\u0645\u0648\u0646 \u0645\u0648\u0631\u062F\u06CC \u0628\u0627\u0634\u0647 \u06A9\u0647 \u062F\u0631 \u0628\u0631\u06AF\u0647 \u0646\u0648\u0634\u062A\u0647 \u0634\u062F\u0647.",
  NO_MATCH: "\u0627\u06CC\u0646 \u0645\u0648\u0631\u062F \u062F\u0631 \u0641\u0647\u0631\u0633\u062A \u067E\u06CC\u062F\u0627 \u0646\u0634\u062F\u061B \u06AF\u0632\u06CC\u0646\u0647\u200C\u0627\u06CC \u0631\u0648 \u0627\u0646\u062A\u062E\u0627\u0628 \u06A9\u0646\u06CC\u062F \u06CC\u0627 \u0645\u0648\u0631\u062F \u062C\u062F\u06CC\u062F\u06CC \u0628\u0633\u0627\u0632\u06CC\u062F.",
  NEEDS_PATIENT: "\u0628\u06CC\u0645\u0627\u0631 \u0631\u0648 \u0627\u0646\u062A\u062E\u0627\u0628 \u06A9\u0646\u06CC\u062F\u060C \u0634\u0645\u0627\u0631\u0647 \u0645\u0648\u0628\u0627\u06CC\u0644 \u0648\u0627\u0631\u062F \u06A9\u0646\u06CC\u062F\u060C \u06CC\u0627 \xAB\u0641\u0642\u0637 \u0632\u0645\u0627\u0646 \u0631\u0632\u0631\u0648 \u0634\u0648\u062F (\u0628\u062F\u0648\u0646 \u0646\u0627\u0645)\xBB \u0631\u0648 \u0627\u0646\u062A\u062E\u0627\u0628 \u06A9\u0646\u06CC\u062F.",
  DUPLICATE_IN_CLINIC: "\u0627\u06CC\u0646 \u0645\u0648\u0631\u062F \u0627\u0632 \u0642\u0628\u0644 \u062B\u0628\u062A \u0634\u062F\u0647 \u0627\u0633\u062A.",
  DUPLICATE_ARCHIVED: "\u067E\u0631\u0648\u0646\u062F\u0647\u0654 \u0627\u06CC\u0646 \u0628\u06CC\u0645\u0627\u0631 \u062F\u0631 \u0633\u0637\u0644 \u0628\u0627\u0632\u06CC\u0627\u0641\u062A\u0647. \u0627\u0648\u0644 \u067E\u0631\u0648\u0646\u062F\u0647 \u0631\u0648 \u0628\u0631\u06AF\u0631\u062F\u0648\u0646\u06CC\u062F.",
  ARCHIVED_BY_UNDO: "\u062B\u0628\u062A \u0642\u0628\u0644\u06CC \u0627\u0632 \u0631\u0648\u06CC \u0639\u06A9\u0633 \u0644\u063A\u0648 \u0634\u062F\u0647 \u0648 \u067E\u0631\u0648\u0646\u062F\u0647\u0654 \u0627\u06CC\u0646 \u0628\u06CC\u0645\u0627\u0631 \u0628\u0647 \u0633\u0637\u0644 \u0628\u0627\u0632\u06CC\u0627\u0641\u062A \u0631\u0641\u062A\u0647. \u0645\u06CC\u200C\u062A\u0648\u0646\u06CC\u062F \u067E\u0631\u0648\u0646\u062F\u0647 \u0631\u0648 \u0628\u0631\u06AF\u0631\u062F\u0648\u0646\u06CC\u062F \u0648 \u06A9\u0627\u0645\u0644 \u06A9\u0646\u06CC\u062F.",
  DUPLICATE_IN_JOB: "\u0647\u0645\u06CC\u0646 \u0645\u0648\u0631\u062F \u062F\u0631 \u0631\u062F\u06CC\u0641 {line} \u0647\u0645 \u0647\u0633\u062A.",
  LOOKS_LIKE_MATERIAL_PURCHASE: "\u0627\u06CC\u0646 \u0631\u062F\u06CC\u0641 \u0638\u0627\u0647\u0631\u0627\u064B \u062E\u0631\u06CC\u062F \u0645\u0648\u0627\u062F\u0647. \u0627\u06CC\u0646\u062C\u0627 \u0641\u0642\u0637 \u0647\u0632\u06CC\u0646\u0647\u200C\u0627\u0634 \u062B\u0628\u062A \u0645\u06CC\u200C\u0634\u0647 \u0648 \u0645\u0648\u062C\u0648\u062F\u06CC \u0627\u0646\u0628\u0627\u0631 \u062A\u063A\u06CC\u06CC\u0631 \u0646\u0645\u06CC\u200C\u06A9\u0646\u0647.",
  STRUCK: "\u0627\u06CC\u0646 \u0631\u062F\u06CC\u0641 \u062F\u0631 \u0628\u0631\u06AF\u0647 \u062E\u0637 \u062E\u0648\u0631\u062F\u0647.",
  PRICE_RANGE: "\u062F\u0631 \u0628\u0631\u06AF\u0647 \u0628\u0631\u0627\u06CC \u0627\u06CC\u0646 \u062E\u062F\u0645\u062A \u0628\u0627\u0632\u0647 \u0642\u06CC\u0645\u062A \u0646\u0648\u0634\u062A\u0647 \u0634\u062F\u0647. \u0628\u0631\u0627\u06CC \u0647\u0631 \u062D\u0627\u0644\u062A\u060C \u062E\u062F\u0645\u062A \u062C\u062F\u0627\u06AF\u0627\u0646\u0647\u200C\u0627\u06CC \u0628\u0633\u0627\u0632\u06CC\u062F\u061B \u0648\u06AF\u0631\u0646\u0647 \u06A9\u0645\u062A\u0631\u06CC\u0646 \u0645\u0628\u0644\u063A \u062B\u0628\u062A \u0645\u06CC\u200C\u0634\u0647.",
  PRICE_UNCHANGED: "\u0642\u06CC\u0645\u062A \u0641\u0639\u0644\u06CC \u0647\u0645\u06CC\u0646 \u0645\u0642\u062F\u0627\u0631\u0647. \u062A\u063A\u06CC\u06CC\u0631\u06CC \u062B\u0628\u062A \u0646\u0645\u06CC\u200C\u0634\u0647.",
  SAME_PROCEDURE_IN_JOB: "\u0631\u062F\u06CC\u0641 {line} \u0647\u0645 \u0628\u0631\u0627\u06CC \u0647\u0645\u06CC\u0646 \u062E\u062F\u0645\u062A\u0647. \u06CC\u06A9\u06CC \u0627\u0632 \u0631\u062F\u06CC\u0641\u200C\u0647\u0627 \u0631\u0648 \u0646\u06AF\u0647 \u062F\u0627\u0631\u06CC\u062F \u06CC\u0627 \u0628\u0631\u0627\u06CC \u0627\u06CC\u0646 \u0631\u062F\u06CC\u0641 \u062E\u062F\u0645\u062A \u062C\u062F\u06CC\u062F\u06CC \u062A\u0639\u0631\u06CC\u0641 \u06A9\u0646\u06CC\u062F.",
  BATCH_MISSING: `\u0634\u0645\u0627\u0631\u0647\u0654 \u0633\u0631\u06CC \u0633\u0627\u062E\u062A \u062F\u0631 \u0641\u0627\u06A9\u062A\u0648\u0631 \u0646\u06CC\u0633\u062A\u061B \u0648\u0627\u0631\u062F \u06A9\u0646\u06CC\u062F \u06CC\u0627 \xAB${AI_IMPORT_NO_BATCH_LABEL_FA}\xBB \u0631\u0648 \u0628\u0632\u0646\u06CC\u062F.`,
  BATCH_EXISTS: "\u0627\u06CC\u0646 \u0634\u0645\u0627\u0631\u0647\u0654 \u0633\u0631\u06CC \u0633\u0627\u062E\u062A \u0628\u0631\u0627\u06CC \u0627\u06CC\u0646 \u06A9\u0627\u0644\u0627 \u0642\u0628\u0644\u0627\u064B \u062B\u0628\u062A \u0634\u062F\u0647 \u0627\u0633\u062A.",
  UNIT_MAPPED: "\xAB{unitText}\xBB \u0628\u0647\u200C\u0635\u0648\u0631\u062A {mapped} \u062D\u0633\u0627\u0628 \u0634\u062F\u061B \u0645\u0642\u062F\u0627\u0631 \u0648\u0631\u0648\u062F\u06CC \u0628\u0647 \u0627\u0646\u0628\u0627\u0631 \u0631\u0648 \u0628\u0631\u0631\u0633\u06CC \u06A9\u0646\u06CC\u062F.",
  EXPIRY_MISSING: "\u062A\u0627\u0631\u06CC\u062E \u0627\u0646\u0642\u0636\u0627 \u062E\u0648\u0627\u0646\u062F\u0647 \u0646\u0634\u062F. \u0628\u062F\u0648\u0646 \u0627\u06CC\u0646 \u062A\u0627\u0631\u06CC\u062E\u060C \u0647\u0634\u062F\u0627\u0631 \u0627\u0646\u0642\u0636\u0627 \u0641\u0639\u0627\u0644 \u0646\u0645\u06CC\u200C\u0634\u0647.",
  SLOT_TAKEN: "\u0627\u06CC\u0646 \u0633\u0627\u0639\u062A \u0628\u0631\u0627\u06CC {doctor} \u067E\u0631 \u0634\u062F\u0647.",
  ROOM_UNAVAILABLE: "\u0627\u062A\u0627\u0642 \u062F\u0631 \u0627\u06CC\u0646 \u0633\u0627\u0639\u062A \u0622\u0632\u0627\u062F \u0646\u06CC\u0633\u062A.",
  DOCTOR_ON_LEAVE: "{doctor} \u062F\u0631 \u0627\u06CC\u0646 \u0631\u0648\u0632 \u0645\u0631\u062E\u0635\u06CC\u0647.",
  PAST_APPOINTMENT_SKIP: "\u0632\u0645\u0627\u0646 \u0627\u06CC\u0646 \u0646\u0648\u0628\u062A \u06AF\u0630\u0634\u062A\u0647. \u0637\u0628\u0642 \u062A\u0646\u0638\u06CC\u0645 \u0628\u0627\u0644\u0627\u06CC \u062C\u062F\u0648\u0644\u060C \u062B\u0628\u062A \u0646\u0645\u06CC\u200C\u0634\u0647.",
  PAST_APPOINTMENT_DONE: "\u0632\u0645\u0627\u0646 \u0627\u06CC\u0646 \u0646\u0648\u0628\u062A \u06AF\u0630\u0634\u062A\u0647. \u0628\u0627 \u0648\u0636\u0639\u06CC\u062A \xAB\u0627\u0646\u062C\u0627\u0645\u200C\u0634\u062F\u0647\xBB \u062B\u0628\u062A \u0645\u06CC\u200C\u0634\u0647.",
  PAST_APPOINTMENT_NO_SHOW: "\u0632\u0645\u0627\u0646 \u0627\u06CC\u0646 \u0646\u0648\u0628\u062A \u06AF\u0630\u0634\u062A\u0647 \u0648 \u062F\u0631 \u062F\u0641\u062A\u0631 \xAB\u0646\u06CC\u0627\u0645\u062F\xBB \u0646\u0648\u0634\u062A\u0647 \u0634\u062F\u0647. \u0628\u0627 \u0648\u0636\u0639\u06CC\u062A \xAB\u0639\u062F\u0645 \u0645\u0631\u0627\u062C\u0639\u0647\xBB \u062B\u0628\u062A \u0645\u06CC\u200C\u0634\u0647.",
  APPOINTMENT_CANCELLED: "\u0627\u06CC\u0646 \u0646\u0648\u0628\u062A \u062F\u0631 \u062F\u0641\u062A\u0631 \u0644\u063A\u0648 \u0634\u062F\u0647. \u062B\u0628\u062A \u0646\u0645\u06CC\u200C\u0634\u0647.",
  NOTES_REDACTED: "\u0634\u0645\u0627\u0631\u0647\u200C\u0647\u0627\u06CC \u062F\u0627\u062E\u0644 \u06CC\u0627\u062F\u062F\u0627\u0634\u062A \u062D\u0630\u0641 \u0634\u062F\u061B \u06CC\u0627\u062F\u062F\u0627\u0634\u062A \u0646\u0648\u0628\u062A \u0631\u0645\u0632\u06AF\u0630\u0627\u0631\u06CC \u0646\u0645\u06CC\u200C\u0634\u0647.",
  COMMIT_UNCERTAIN: "\u0645\u0645\u06A9\u0646\u0647 \u0627\u06CC\u0646 \u0631\u062F\u06CC\u0641 \u0642\u0628\u0644 \u0627\u0632 \u0642\u0637\u0639 \u0627\u0631\u062A\u0628\u0627\u0637 \u062B\u0628\u062A \u0634\u062F\u0647 \u0628\u0627\u0634\u0647. \u0642\u0628\u0644 \u0627\u0632 \u062B\u0628\u062A \u062F\u0648\u0628\u0627\u0631\u0647\u060C \u0641\u0647\u0631\u0633\u062A \u0631\u0648 \u0628\u0631\u0631\u0633\u06CC \u06A9\u0646\u06CC\u062F.",
  // Generic: the issue's own `message` carries the field's specific Persian
  // complaint (e.g. «جنسیت: مقدار باید …») from the same parser the CSV import uses.
  VALUE_INVALID: "\u0627\u06CC\u0646 \u0645\u0642\u062F\u0627\u0631 \u0642\u0627\u0628\u0644 \u062B\u0628\u062A \u0646\u06CC\u0633\u062A\u061B \u0628\u0627 \u0639\u06A9\u0633 \u0645\u0642\u0627\u06CC\u0633\u0647 \u0648 \u0627\u0635\u0644\u0627\u062D \u06A9\u0646\u06CC\u062F.",
  // TREATMENTS
  SESSION_DATE_FUTURE: "\u0627\u06CC\u0646 \u062A\u0627\u0631\u06CC\u062E \u0647\u0646\u0648\u0632 \u0646\u0631\u0633\u06CC\u062F\u0647. \u0646\u0648\u0628\u062A\u200C\u0647\u0627\u06CC \u0622\u06CC\u0646\u062F\u0647 \u0631\u0648 \u0627\u0632 \xAB\u062F\u0641\u062A\u0631 \u0646\u0648\u0628\u062A\u200C\u0647\u0627\xBB \u0648\u0627\u0631\u062F \u06A9\u0646\u06CC\u062F.",
  SESSION_EXISTS: "\u0627\u06CC\u0646 \u062C\u0644\u0633\u0647 \u0642\u0628\u0644\u0627\u064B \u062F\u0631 \u067E\u0631\u0648\u0646\u062F\u0647 \u0628\u06CC\u0645\u0627\u0631 \u062B\u0628\u062A \u0634\u062F\u0647\u060C \u0628\u0631\u0627\u06CC \u0647\u0645\u06CC\u0646 \u062F\u0648\u0628\u0627\u0631\u0647 \u062B\u0628\u062A \u0646\u0645\u06CC\u200C\u0634\u0647. \u0627\u06AF\u0631 \u062C\u0644\u0633\u0647 \u062C\u062F\u0627\u06AF\u0627\u0646\u0647\u200C\u0627\u06CC\u0647\u060C \xAB\u062B\u0628\u062A \u0634\u0648\u062F\xBB \u0631\u0648 \u0628\u0632\u0646\u06CC\u062F.",
  DOCTOR_NOT_YOU: "\u0641\u0642\u0637 \u062C\u0644\u0633\u0647\u200C\u0647\u0627\u06CC \u062E\u0648\u062F\u062A\u0648\u0646 \u0631\u0648 \u0645\u06CC\u200C\u062A\u0648\u0646\u06CC\u062F \u062B\u0628\u062A \u06A9\u0646\u06CC\u062F. \u0627\u06CC\u0646 \u0631\u062F\u06CC\u0641 \u0631\u0648 \u06A9\u0646\u0627\u0631 \u0628\u06AF\u0630\u0627\u0631\u06CC\u062F \u06CC\u0627 \u0627\u0632 \u0645\u062F\u06CC\u0631 \u06A9\u0644\u06CC\u0646\u06CC\u06A9 \u0628\u062E\u0648\u0627\u06CC\u062F \u062B\u0628\u062A\u0634 \u06A9\u0646\u0647.",
  DOCTOR_INACTIVE: "\u0627\u06CC\u0646 \u067E\u0632\u0634\u06A9 \u062F\u06CC\u06AF\u0647 \u062F\u0631 \u06A9\u0644\u06CC\u0646\u06CC\u06A9 \u0641\u0639\u0627\u0644 \u0646\u06CC\u0633\u062A\u060C \u0648\u0644\u06CC \u062C\u0644\u0633\u0647 \u0628\u0647 \u0646\u0627\u0645 \u0647\u0645\u06CC\u0646 \u067E\u0632\u0634\u06A9 \u062B\u0628\u062A \u0645\u06CC\u200C\u0634\u0647.",
  PROCEDURE_DISABLED: "\u0627\u06CC\u0646 \u062E\u062F\u0645\u062A \u062F\u0631 \u062A\u0639\u0631\u0641\u0647\u0654 \u0641\u0639\u0644\u06CC \u06A9\u0644\u06CC\u0646\u06CC\u06A9 \u0641\u0639\u0627\u0644 \u0646\u06CC\u0633\u062A\u061B \u0641\u0642\u0637 \u0628\u0647\u200C\u0639\u0646\u0648\u0627\u0646 \u0633\u0627\u0628\u0642\u0647 \u062B\u0628\u062A \u0645\u06CC\u200C\u0634\u0647.",
  PAID_OVER_PRICE: "\u0645\u0628\u0644\u063A \u067E\u0631\u062F\u0627\u062E\u062A\u200C\u0634\u062F\u0647 \u0627\u0632 \u0645\u0628\u0644\u063A \u062C\u0644\u0633\u0647 \u0628\u06CC\u0634\u062A\u0631\u0647. \u0628\u0627 \u0639\u06A9\u0633 \u0645\u0642\u0627\u06CC\u0633\u0647 \u06A9\u0646\u06CC\u062F.",
  PRICE_MISSING: "\u0645\u0628\u0644\u063A \u062F\u0631 \u0628\u0631\u06AF\u0647 \u0646\u06CC\u0633\u062A\u061B \u062C\u0644\u0633\u0647 \u0628\u062F\u0648\u0646 \u0645\u0628\u0644\u063A \u062B\u0628\u062A \u0645\u06CC\u200C\u0634\u0647.",
  // `{amount}` is rendered through `Money` by the panel.
  SESSION_DEBT: "\u0628\u0631\u0627\u06CC \u0627\u06CC\u0646 \u062C\u0644\u0633\u0647 {amount} \u0628\u062F\u0647\u06CC \u062F\u0631 \u067E\u0631\u0648\u0646\u062F\u0647 \u0628\u06CC\u0645\u0627\u0631 \u062B\u0628\u062A \u0645\u06CC\u200C\u0634\u0647.",
  IDENTITY_NAME_MISMATCH: "\u0634\u0645\u0627\u0631\u0647\u0654 \u0628\u0627\u0644\u0627\u06CC \u06A9\u0627\u0631\u062A \u0628\u0631\u0627\u06CC \u06CC\u06A9 \u067E\u0631\u0648\u0646\u062F\u0647\u0654 \u062F\u06CC\u06AF\u0647 \u0627\u0633\u062A \u0648 \u0646\u0627\u0645 \u0628\u06CC\u0645\u0627\u0631 \u0647\u0645 \u06CC\u06A9\u06CC \u0646\u06CC\u0633\u062A. \u0628\u06CC\u0645\u0627\u0631 \u0631\u0648 \u062E\u0648\u062F\u062A\u0648\u0646 \u0627\u0646\u062A\u062E\u0627\u0628 \u06A9\u0646\u06CC\u062F.",
  // STOCK_COUNT
  LOT_AMBIGUOUS: "\u0627\u06CC\u0646 \u06A9\u0627\u0644\u0627 \u0686\u0646\u062F \u0633\u0631\u06CC \u0633\u0627\u062E\u062A \u062F\u0631 \u0627\u0646\u0628\u0627\u0631 \u062F\u0627\u0631\u0647\u060C \u0648\u0644\u06CC \u0634\u0645\u0627\u0631\u0647 \u0633\u0631\u06CC \u0633\u0627\u062E\u062A \u062F\u0631 \u0628\u0631\u06AF\u0647 \u0646\u06CC\u0633\u062A. \u0633\u0631\u06CC \u0633\u0627\u062E\u062A \u0631\u0648 \u0627\u0646\u062A\u062E\u0627\u0628 \u06A9\u0646\u06CC\u062F \u06CC\u0627 \xAB\u062C\u0645\u0639 \u06A9\u0644 \u0627\u06CC\u0646 \u06A9\u0627\u0644\u0627\xBB \u0631\u0648 \u0628\u0632\u0646\u06CC\u062F.",
  NEW_LOT: "\u0627\u06CC\u0646 \u0633\u0631\u06CC \u062F\u0631 \u0627\u0646\u0628\u0627\u0631 \u0646\u06CC\u0633\u062A\u061B \u0628\u0647\u200C\u0639\u0646\u0648\u0627\u0646 \u0645\u0648\u062C\u0648\u062F\u06CC \u0627\u0648\u0644\u06CC\u0647 (\u0633\u0631\u06CC \u062C\u062F\u06CC\u062F) \u062B\u0628\u062A \u0645\u06CC\u200C\u0634\u0647.",
  COUNT_DENIED: "\u0628\u0631\u0627\u06CC \u0627\u0635\u0644\u0627\u062D \u0645\u0648\u062C\u0648\u062F\u06CC \u0633\u0631\u06CC\u200C\u0647\u0627\u06CC \u062B\u0628\u062A\u200C\u0634\u062F\u0647\u060C \u062F\u0633\u062A\u0631\u0633\u06CC \xAB\u0627\u0635\u0644\u0627\u062D \u0645\u0648\u062C\u0648\u062F\u06CC\xBB \u0644\u0627\u0632\u0645\u0647. \u0628\u0631\u0627\u06CC \u0627\u06CC\u0646 \u0631\u062F\u06CC\u0641 \xAB\u062B\u0628\u062A \u0646\u0634\u0648\u062F\xBB \u0631\u0648 \u0627\u0646\u062A\u062E\u0627\u0628 \u06A9\u0646\u06CC\u062F \u06CC\u0627 \u0627\u0632 \u0645\u062F\u06CC\u0631 \u06A9\u0644\u06CC\u0646\u06CC\u06A9 \u0628\u062E\u0648\u0627\u06CC\u062F \u0645\u0648\u062C\u0648\u062F\u06CC \u0631\u0648 \u0627\u0635\u0644\u0627\u062D \u06A9\u0646\u0647.",
  COUNT_DIFF_LARGE: "\u0627\u062E\u062A\u0644\u0627\u0641 \u0628\u0627 \u0645\u0648\u062C\u0648\u062F\u06CC \u062B\u0628\u062A\u200C\u0634\u062F\u0647 \u0632\u06CC\u0627\u062F\u0647. \u0639\u062F\u062F \u0631\u0648 \u0628\u0627 \u0639\u06A9\u0633 \u0645\u0642\u0627\u06CC\u0633\u0647 \u06A9\u0646\u06CC\u062F.",
  NEGATIVE_AFTER_COUNT: "\u0628\u0627 \u0627\u06CC\u0646 \u0634\u0645\u0627\u0631\u0634\u060C \u0645\u0648\u062C\u0648\u062F\u06CC \u0641\u0639\u0644\u06CC \u0645\u0646\u0641\u06CC \u0645\u06CC\u200C\u0634\u0647. \u0645\u0635\u0631\u0641 \u062B\u0628\u062A\u200C\u0634\u062F\u0647 \u0628\u0639\u062F \u0627\u0632 \u0632\u0645\u0627\u0646 \u0634\u0645\u0627\u0631\u0634\u060C \u0627\u0632 \u062A\u0639\u062F\u0627\u062F \u0634\u0645\u0631\u062F\u0647\u200C\u0634\u062F\u0647 \u0628\u06CC\u0634\u062A\u0631\u0647. \u0639\u062F\u062F \u06CC\u0627 \xAB\u0632\u0645\u0627\u0646 \u0634\u0645\u0627\u0631\u0634\xBB \u0631\u0648 \u0628\u0631\u0631\u0633\u06CC \u06A9\u0646\u06CC\u062F.",
  EXPIRY_DIFFERS: "\u062A\u0627\u0631\u06CC\u062E \u0627\u0646\u0642\u0636\u0627 \u062F\u0631 \u0628\u0631\u06AF\u0647 \u0628\u0627 \u062A\u0627\u0631\u06CC\u062E \u0633\u0631\u06CC \u0633\u0627\u062E\u062A \u062B\u0628\u062A\u200C\u0634\u062F\u0647 \u0641\u0631\u0642 \u062F\u0627\u0631\u0647. \u062A\u0627\u0631\u06CC\u062E \u062B\u0628\u062A\u200C\u0634\u062F\u0647 \u062A\u063A\u06CC\u06CC\u0631 \u0646\u0645\u06CC\u200C\u06A9\u0646\u0647.",
  LOT_EXPIRED: "\u062A\u0627\u0631\u06CC\u062E \u0627\u0646\u0642\u0636\u0627\u06CC \u0627\u06CC\u0646 \u0633\u0631\u06CC \u0633\u0627\u062E\u062A \u06AF\u0630\u0634\u062A\u0647 \u0648 \u0627\u0632 \u0645\u0648\u062C\u0648\u062F\u06CC \u062E\u0627\u0631\u062C \u0634\u062F\u0647. \u0627\u0648\u0646 \u0631\u0648 \u0627\u0632 \u0642\u0641\u0633\u0647 \u0628\u0631\u062F\u0627\u0631\u06CC\u062F.",
  NEW_LOT_EXPIRED: "\u062A\u0627\u0631\u06CC\u062E \u0627\u0646\u0642\u0636\u0627\u06CC \u0627\u06CC\u0646 \u06A9\u0627\u0644\u0627 \u06AF\u0630\u0634\u062A\u0647. \u0628\u0647 \u0627\u0646\u0628\u0627\u0631 \u0627\u0636\u0627\u0641\u0647 \u0646\u0645\u06CC\u200C\u0634\u0647.",
  ZERO_NEW_LOT: "\u0645\u0648\u062C\u0648\u062F\u06CC \u0627\u06CC\u0646 \u0633\u0631\u06CC \u0633\u0627\u062E\u062A \u062C\u062F\u06CC\u062F \u0635\u0641\u0631 \u0627\u0633\u062A \u0648 \u062B\u0628\u062A \u0646\u0645\u06CC\u200C\u0634\u0647.",
  SAME_LOT_IN_JOB: "\u0627\u06CC\u0646 \u0633\u0631\u06CC \u062F\u0631 \u0631\u062F\u06CC\u0641 {line} \u0647\u0645 \u0622\u0645\u062F\u0647\u061B \u062A\u0639\u062F\u0627\u062F\u0647\u0627 \u0628\u0627 \u0647\u0645 \u062C\u0645\u0639 \u0645\u06CC\u200C\u0634\u0646.",
  MATERIAL_TOTAL_CONFLICT: "\u0627\u06CC\u0646 \u06A9\u0627\u0644\u0627 \u062F\u0631 \u0631\u062F\u06CC\u0641 {line} \u0647\u0645 \u0647\u0633\u062A. \u06AF\u0632\u06CC\u0646\u0647 \xAB\u062C\u0645\u0639 \u06A9\u0644 \u0627\u06CC\u0646 \u06A9\u0627\u0644\u0627\xBB \u0641\u0642\u0637 \u0648\u0642\u062A\u06CC \u0642\u0627\u0628\u0644 \u0627\u0633\u062A\u0641\u0627\u062F\u0647\u200C\u0633\u062A \u06A9\u0647 \u06A9\u0627\u0644\u0627 \u06CC\u06A9 \u0628\u0627\u0631 \u062F\u0631 \u0628\u0631\u06AF\u0647 \u0646\u0648\u0634\u062A\u0647 \u0634\u062F\u0647 \u0628\u0627\u0634\u0647.",
  BATCH_FUZZY: "\u0634\u0645\u0627\u0631\u0647\u0654 \u0633\u0631\u06CC \u0633\u0627\u062E\u062A \u0628\u0627 \u06CC\u06A9\u06CC \u0627\u0632 \u0633\u0631\u06CC\u200C\u0647\u0627\u06CC \u0627\u0646\u0628\u0627\u0631 \u062A\u0642\u0631\u06CC\u0628\u0627\u064B \u06CC\u06A9\u06CC \u0627\u0633\u062A\u061B \u0633\u0631\u06CC \u0627\u0646\u062A\u062E\u0627\u0628\u200C\u0634\u062F\u0647 \u0631\u0648 \u0628\u0631\u0631\u0633\u06CC \u06A9\u0646\u06CC\u062F.",
  COST_ESTIMATED: "\u0642\u06CC\u0645\u062A \u062F\u0631 \u0628\u0631\u06AF\u0647 \u0646\u0648\u0634\u062A\u0647 \u0646\u0634\u062F\u0647. \u0627\u0632 \u0642\u06CC\u0645\u062A \u0622\u062E\u0631\u06CC\u0646 \u062E\u0631\u06CC\u062F \u0647\u0645\u06CC\u0646 \u06A9\u0627\u0644\u0627 \u0627\u0633\u062A\u0641\u0627\u062F\u0647 \u0634\u062F\u0647.",
  COST_MISSING: "\u0642\u06CC\u0645\u062A \u062E\u0631\u06CC\u062F \u0645\u0634\u062E\u0635 \u0646\u06CC\u0633\u062A\u061B \u0647\u0632\u06CC\u0646\u0647 \u0645\u0635\u0631\u0641 \u0627\u06CC\u0646 \u0633\u0631\u06CC \u0633\u0627\u062E\u062A \u0635\u0641\u0631 \u0645\u062D\u0627\u0633\u0628\u0647 \u0645\u06CC\u200C\u0634\u0647. \u0627\u06AF\u0631 \u0642\u06CC\u0645\u062A \u0631\u0648 \u0645\u06CC\u200C\u062F\u0648\u0646\u06CC\u062F\u060C \u0627\u0648\u0646 \u0631\u0648 \u0648\u0627\u0631\u062F \u06A9\u0646\u06CC\u062F.",
  SURPLUS_NEEDS_LOT: "\u062A\u0639\u062F\u0627\u062F \u0634\u0645\u0631\u062F\u0647\u200C\u0634\u062F\u0647 \u0627\u0632 \u0645\u0648\u062C\u0648\u062F\u06CC \u06A9\u0644 \u0628\u06CC\u0634\u062A\u0631\u0647. \u0633\u0631\u06CC \u0633\u0627\u062E\u062A \u0631\u0648 \u0627\u0646\u062A\u062E\u0627\u0628 \u06A9\u0646\u06CC\u062F \u062A\u0627 \u0645\u0648\u062C\u0648\u062F\u06CC \u0627\u0636\u0627\u0641\u0647 \u0628\u0631\u0627\u06CC \u0647\u0645\u0648\u0646 \u0633\u0631\u06CC \u062B\u0628\u062A \u0628\u0634\u0647."
});
var AI_IMPORT_LOOKS_LIKE_PURCHASE_WITH_INVOICE_FA = "\u062E\u0631\u06CC\u062F \u0645\u0648\u0627\u062F \u0631\u0648 \u0627\u0632 \xAB\u0641\u0627\u06A9\u062A\u0648\u0631 \u062E\u0631\u06CC\u062F \u0645\u0648\u0627\u062F\xBB \u0648\u0627\u0631\u062F \u06A9\u0646\u06CC\u062F \u062A\u0627 \u062F\u0631 \u0627\u0646\u0628\u0627\u0631 \u0647\u0645 \u062B\u0628\u062A \u0628\u0634\u0647.";

// src/plan-features.ts
var PLAN_FEATURE_KEYS = [
  "campaigns",
  "loyalty",
  "inventory",
  "lotInventory",
  "accounting",
  "payroll",
  "revenueShare",
  "apiAccess",
  "branches",
  "publicBooking",
  "directoryListing",
  "photos",
  "consents",
  "patients",
  "aiImport"
];
var PLAN_FEATURE_LABELS_FA = Object.freeze({
  campaigns: "\u067E\u06CC\u0627\u0645\u06A9 \u06AF\u0631\u0648\u0647\u06CC \u0648 \u0645\u0646\u0627\u0633\u0628\u062A\u200C\u0647\u0627",
  loyalty: "\u0628\u0627\u0634\u06AF\u0627\u0647 \u0645\u0634\u062A\u0631\u06CC\u0627\u0646",
  inventory: "\u0627\u0646\u0628\u0627\u0631 \u0648 \u0645\u0635\u0631\u0641 \u0645\u0648\u0627\u062F",
  lotInventory: "\u0627\u0646\u0628\u0627\u0631 \u0628\u0627 \u0633\u0631\u06CC \u0633\u0627\u062E\u062A \u0648 \u062A\u0627\u0631\u06CC\u062E \u0627\u0646\u0642\u0636\u0627",
  accounting: "\u062D\u0633\u0627\u0628\u062F\u0627\u0631\u06CC \u0648 \u0633\u0648\u062F \u0648 \u0632\u06CC\u0627\u0646",
  payroll: "\u062D\u0642\u0648\u0642 \u0648 \u062F\u0633\u062A\u0645\u0632\u062F",
  revenueShare: "\u0633\u0647\u0645 \u067E\u0632\u0634\u06A9\u0627\u0646",
  apiAccess: "\u062F\u0633\u062A\u0631\u0633\u06CC API \u0648 \u0648\u0628\u200C\u0647\u0648\u06A9",
  branches: "\u0686\u0646\u062F \u0634\u0639\u0628\u0647",
  publicBooking: "\u0646\u0648\u0628\u062A \u0622\u0646\u0644\u0627\u06CC\u0646",
  directoryListing: "\u0635\u0641\u062D\u0647\u0654 \u0639\u0645\u0648\u0645\u06CC \u06A9\u0644\u06CC\u0646\u06CC\u06A9",
  photos: "\u0639\u06A9\u0633\u200C\u0647\u0627\u06CC \u0642\u0628\u0644 \u0648 \u0628\u0639\u062F",
  consents: "\u0631\u0636\u0627\u06CC\u062A\u200C\u0646\u0627\u0645\u0647",
  patients: "\u067E\u0631\u0648\u0646\u062F\u0647 \u0628\u06CC\u0645\u0627\u0631\u0627\u0646",
  aiImport: "\u062B\u0628\u062A \u0627\u0637\u0644\u0627\u0639\u0627\u062A \u0627\u0632 \u0631\u0648\u06CC \u0639\u06A9\u0633"
});
var ENFORCED_PLAN_FEATURES = Object.freeze([
  "aiImport",
  "accounting",
  "lotInventory",
  "campaigns",
  "loyalty",
  "payroll",
  "revenueShare",
  "apiAccess",
  "branches"
]);
function isEnforcedPlanFeature(value) {
  return typeof value === "string" && ENFORCED_PLAN_FEATURES.includes(value);
}
var DERIVED_PLAN_FEATURES = Object.freeze(["branches"]);
function isPlanFeatureKey(value) {
  return typeof value === "string" && PLAN_FEATURE_KEYS.includes(value);
}
function effectivePlanFeatures(plan2) {
  const out = {};
  for (const [key, value] of Object.entries(plan2.features ?? {})) {
    if (typeof value === "boolean") out[key] = value;
  }
  out.branches = plan2.maxLocations === -1 || plan2.maxLocations >= 2;
  return out;
}
var POOL_BATCH_NO = "POOL";
var POOL_BATCH_RE = /^POOL(-\d+)?$/i;
function isPoolBatchNo(batchNo) {
  return typeof batchNo === "string" && POOL_BATCH_RE.test(batchNo.trim());
}
function aiImportPagesOf(plan2) {
  if (!plan2 || plan2.features.aiImport !== true) return 0;
  return plan2.aiImportPagesPerMonth < 0 ? -1 : plan2.aiImportPagesPerMonth;
}
function pickAiImportUpgrade(plans, currentPages) {
  if (currentPages < 0) return null;
  let best = null;
  let bestPages = 0;
  for (const plan2 of plans) {
    const pages = aiImportPagesOf(plan2);
    if (pages === 0) continue;
    if (pages !== -1 && pages <= currentPages) continue;
    const better = !best || plan2.priceRials < best.priceRials || plan2.priceRials === best.priceRials && (plan2.sortOrder < best.sortOrder || plan2.sortOrder === best.sortOrder && morePages(pages, bestPages));
    if (better) {
      best = plan2;
      bestPages = pages;
    }
  }
  return best ? { key: best.key, name: best.name, pagesPerMonth: bestPages } : null;
}
function morePages(a, b) {
  if (a === -1) return b !== -1;
  if (b === -1) return false;
  return a > b;
}
var PLAN_LINK_REASONS = [
  "ai-import",
  "accounting",
  "lot-inventory",
  "campaigns",
  "loyalty",
  "payroll",
  "revenue-share",
  "api-access",
  "branches"
];
var PLAN_FEATURE_LINK_REASON = Object.freeze({
  aiImport: "ai-import",
  accounting: "accounting",
  lotInventory: "lot-inventory",
  campaigns: "campaigns",
  loyalty: "loyalty",
  payroll: "payroll",
  revenueShare: "revenue-share",
  apiAccess: "api-access",
  branches: "branches"
});
function planLinkReasonFor(feature) {
  return PLAN_FEATURE_LINK_REASON[feature];
}
function planFeatureForLinkReason(reason) {
  const entry = Object.entries(PLAN_FEATURE_LINK_REASON).find(([, r]) => r === reason);
  return entry[0];
}
var PLAN_LINK_MODELS = ["flat", "raas"];
var PLAN_LINK_TERMS = [1, 6, 12];
var SUBSCRIPTION_PATH = "/settings/subscription";
function subscriptionPlanPath(planKey, reason, opts) {
  const params = new URLSearchParams();
  if (planKey) params.set("plan", planKey);
  else params.set("section", "plans");
  if (reason) params.set("for", reason);
  if (opts?.model) params.set("model", opts.model);
  if (opts?.term) params.set("term", String(opts.term));
  return `${SUBSCRIPTION_PATH}?${params.toString()}`;
}
var PLAN_KEY_SHAPE = /^[a-z0-9][a-z0-9_-]{0,63}$/i;
function readPlanLink(search2) {
  const params = typeof search2 === "string" ? new URLSearchParams(search2) : search2;
  const rawKey = params.get("plan")?.trim() ?? "";
  const planKey = PLAN_KEY_SHAPE.test(rawKey) ? rawKey : null;
  const rawReason = params.get("for") ?? "";
  const reason = PLAN_LINK_REASONS.includes(rawReason) ? rawReason : null;
  const rawModel = params.get("model") ?? "";
  const model = PLAN_LINK_MODELS.includes(rawModel) ? rawModel : null;
  const rawTerm = Number(params.get("term") ?? "");
  const term = PLAN_LINK_TERMS.includes(rawTerm) ? rawTerm : null;
  return {
    showPlans: planKey !== null || reason !== null || model !== null || params.get("section") === "plans",
    planKey,
    reason,
    model,
    term
  };
}

// src/blog-queues.ts
var BLOG_POST_QUEUES = [
  "needs_review",
  "ready_to_publish",
  "drafts",
  "generating",
  "published",
  "archived"
];
var DEFAULT_BLOG_POST_QUEUE = "needs_review";
var BLOG_POST_QUEUE_LABELS_FA = Object.freeze({
  needs_review: "\u0646\u06CC\u0627\u0632 \u0628\u0647 \u0628\u0627\u0632\u0628\u06CC\u0646\u06CC",
  ready_to_publish: "\u0622\u0645\u0627\u062F\u0647 \u0627\u0646\u062A\u0634\u0627\u0631",
  drafts: "\u067E\u06CC\u0634\u200C\u0646\u0648\u06CC\u0633\u200C\u0647\u0627",
  generating: "\u062F\u0631 \u062D\u0627\u0644 \u062A\u0648\u0644\u06CC\u062F",
  published: "\u0645\u0646\u062A\u0634\u0631\u0634\u062F\u0647",
  archived: "\u0628\u0627\u06CC\u06AF\u0627\u0646\u06CC"
});
var BLOG_POST_QUEUE_HINTS_FA = Object.freeze({
  needs_review: "\u0627\u06CC\u0646\u062C\u0627 \u067E\u06CC\u0634\u200C\u0646\u0648\u06CC\u0633\u200C\u0647\u0627\u06CC \u062F\u0627\u0631\u0627\u06CC \u0627\u06CC\u0631\u0627\u062F\u060C \u0645\u0637\u0627\u0644\u0628 \u0641\u0631\u0633\u062A\u0627\u062F\u0647\u200C\u0634\u062F\u0647 \u0628\u0631\u0627\u06CC \u0628\u0627\u0632\u0628\u06CC\u0646\u06CC\u060C \u0646\u0633\u062E\u0647\u200C\u0647\u0627\u06CC \u062C\u062F\u06CC\u062F \u0645\u0646\u062A\u0638\u0631 \u062A\u0623\u06CC\u06CC\u062F \u0648 \u0645\u0637\u0627\u0644\u0628\u06CC \u0631\u0648 \u0645\u06CC\u200C\u0628\u06CC\u0646\u06CC\u062F \u06A9\u0647 \u0628\u0627 \u0648\u062C\u0648\u062F \u0627\u06CC\u0631\u0627\u062F \u062E\u0648\u062F\u06A9\u0627\u0631 \u0645\u0646\u062A\u0634\u0631 \u0634\u062F\u0647\u200C\u0627\u0646\u062F. \u0647\u0631 \u0645\u0637\u0644\u0628 \u0631\u0648 \u0628\u0631\u0631\u0633\u06CC \u06A9\u0646\u06CC\u062F \u0648 \u0628\u0633\u062A\u0647 \u0628\u0647 \u0648\u0636\u0639\u06CC\u062A\u0634\u060C \u0627\u0635\u0644\u0627\u062D \u06CC\u0627 \u0645\u0646\u062A\u0634\u0631 \u06A9\u0646\u06CC\u062F \u06CC\u0627 \u0628\u0627\u0632\u0628\u06CC\u0646\u06CC \u0627\u0648\u0646 \u0631\u0648 \u062A\u0623\u06CC\u06CC\u062F \u06A9\u0646\u06CC\u062F.",
  ready_to_publish: "\u0645\u0637\u0627\u0644\u0628\u06CC \u06A9\u0647 \u0628\u0631\u0631\u0633\u06CC\u200C\u0647\u0627 \u0631\u0648 \u06AF\u0630\u0631\u0648\u0646\u062F\u0647\u200C\u0627\u0646\u062F \u0648 \u0622\u0645\u0627\u062F\u0647 \u0627\u0646\u062A\u0634\u0627\u0631\u0646\u062F.",
  drafts: "\u0645\u0637\u0627\u0644\u0628\u06CC \u06A9\u0647 \u0647\u0646\u0648\u0632 \u062F\u0631 \u062D\u0627\u0644 \u0646\u0648\u0634\u062A\u0646\u200C\u0627\u0646\u062F \u0648 \u0628\u0631\u0627\u06CC \u0628\u0627\u0632\u0628\u06CC\u0646\u06CC \u0641\u0631\u0633\u062A\u0627\u062F\u0647 \u0646\u0634\u062F\u0647\u200C\u0627\u0646\u062F.",
  generating: "\u0645\u0637\u0627\u0644\u0628\u06CC \u06A9\u0647 \u0633\u06CC\u0633\u062A\u0645 \u062F\u0631 \u062D\u0627\u0644 \u0646\u0648\u0634\u062A\u0646 \u0627\u0648\u0646\u200C\u0647\u0627\u0633\u062A. \u0686\u0646\u062F \u062F\u0642\u06CC\u0642\u0647 \u0628\u0639\u062F \u0628\u0647 \u0635\u0641 \u0645\u0646\u0627\u0633\u0628 \u0645\u0646\u062A\u0642\u0644 \u0645\u06CC\u200C\u0634\u0646.",
  published: "\u0645\u0637\u0627\u0644\u0628\u06CC \u06A9\u0647 \u0627\u0644\u0627\u0646 \u0631\u0648\u06CC \u0633\u0627\u06CC\u062A \u0645\u0646\u062A\u0634\u0631 \u0634\u062F\u0647\u200C\u0627\u0646\u062F.",
  archived: "\u0645\u0637\u0627\u0644\u0628\u06CC \u06A9\u0647 \u0627\u0632 \u0633\u0627\u06CC\u062A \u0628\u0631\u062F\u0627\u0634\u062A\u0647 \u0634\u062F\u0647\u200C\u0627\u0646\u062F \u0648 \u062F\u06CC\u06AF\u0647 \u0646\u0645\u0627\u06CC\u0634 \u062F\u0627\u062F\u0647 \u0646\u0645\u06CC\u200C\u0634\u0646."
});
function blogPostQueue(post) {
  switch (post.status) {
    case "QUEUED":
    case "GENERATING":
      return "generating";
    case "ARCHIVED":
      return "archived";
    case "PUBLISHED":
      return post.hasPendingRevision || post.awaitingPostPublishReview ? "needs_review" : "published";
    case "IN_REVIEW":
      return "needs_review";
    case "DRAFT":
      if (!post.generated) return "drafts";
      return post.autoPublishBlockerCount > 0 ? "needs_review" : "ready_to_publish";
  }
}
var BLOG_AUTO_PUBLISH_MODES = ["off", "clean_only", "always"];
var BLOG_AUTO_PUBLISH_MODE_LABELS_FA = Object.freeze({
  off: "\u062E\u0627\u0645\u0648\u0634",
  clean_only: "\u0641\u0642\u0637 \u0645\u0637\u0627\u0644\u0628 \u0628\u062F\u0648\u0646 \u0627\u06CC\u0631\u0627\u062F",
  always: "\u0647\u0645\u0647\u0654 \u0645\u0637\u0627\u0644\u0628"
});
var BLOG_AUTO_PUBLISH_MODE_HINTS_FA = Object.freeze({
  off: "\u0647\u06CC\u0686 \u0645\u0637\u0644\u0628\u06CC \u062E\u0648\u062F\u06A9\u0627\u0631 \u0645\u0646\u062A\u0634\u0631 \u0646\u0645\u06CC\u200C\u0634\u0647\u061B \u0647\u0645\u0647\u0654 \u0645\u0637\u0627\u0644\u0628 \u062A\u0648\u0644\u06CC\u062F\u0634\u062F\u0647 \u0645\u0646\u062A\u0638\u0631 \u0627\u0646\u062A\u0634\u0627\u0631 \u0634\u0645\u0627 \u0645\u06CC\u200C\u0645\u0648\u0646\u0646.",
  clean_only: "\u0645\u0637\u0627\u0644\u0628\u06CC \u06A9\u0647 \u0647\u0645\u0647 \u0628\u0631\u0631\u0633\u06CC\u200C\u0647\u0627\u06CC \u06A9\u06CC\u0641\u06CC \u0631\u0648 \u0628\u06AF\u0630\u0631\u0648\u0646\u0646\u060C \u062E\u0648\u062F\u06A9\u0627\u0631 \u0645\u0646\u062A\u0634\u0631 \u0645\u06CC\u200C\u0634\u0646. \u0645\u0637\u0627\u0644\u0628 \u062F\u0627\u0631\u0627\u06CC \u0627\u06CC\u0631\u0627\u062F \u0628\u0631\u0627\u06CC \u0628\u0627\u0632\u0628\u06CC\u0646\u06CC \u0646\u06AF\u0647 \u062F\u0627\u0634\u062A\u0647 \u0645\u06CC\u200C\u0634\u0646.",
  always: "\u0647\u0645\u0647 \u0645\u0637\u0627\u0644\u0628 \u062A\u0648\u0644\u06CC\u062F\u0634\u062F\u0647\u060C \u062D\u062A\u06CC \u0645\u0637\u0627\u0644\u0628 \u062F\u0627\u0631\u0627\u06CC \u0627\u06CC\u0631\u0627\u062F\u060C \u0628\u0644\u0627\u0641\u0627\u0635\u0644\u0647 \u0645\u0646\u062A\u0634\u0631 \u0645\u06CC\u200C\u0634\u0646. \u0645\u0637\u0627\u0644\u0628 \u062F\u0627\u0631\u0627\u06CC \u0627\u06CC\u0631\u0627\u062F \u0628\u0627 \u0628\u0631\u0686\u0633\u0628 \xAB\u0645\u0646\u062A\u0634\u0631\u0634\u062F\u0647\u060C \u0645\u0646\u062A\u0638\u0631 \u0628\u0627\u0632\u0628\u06CC\u0646\u06CC\xBB \u062F\u0631 \u0635\u0641 \xAB\u0646\u06CC\u0627\u0632 \u0628\u0647 \u0628\u0627\u0632\u0628\u06CC\u0646\u06CC\xBB \u0645\u06CC\u200C\u0645\u0648\u0646\u0646 \u062A\u0627 \u062A\u06CC\u0645 \u0627\u0648\u0646\u200C\u0647\u0627 \u0631\u0648 \u0628\u0631\u0631\u0633\u06CC \u06A9\u0646\u0647."
});

// src/inline-links.ts
var LINK = /\[([^\[\]\n]{1,200})\]\((\/[^\s()]{0,300})\)/g;
function isSiteRelativeHref(href) {
  return href.startsWith("/") && !href.startsWith("//") && !/\s/.test(href);
}
function parseInlineLinks(input) {
  const out = [];
  let last = 0;
  for (const m of input.matchAll(LINK)) {
    const whole = m[0];
    const text2 = m[1] ?? "";
    const href = m[2] ?? "";
    const at = m.index ?? 0;
    if (!isSiteRelativeHref(href) || text2.trim() === "") continue;
    if (at > last) out.push({ kind: "text", text: input.slice(last, at) });
    out.push({ kind: "link", text: text2, href });
    last = at + whole.length;
  }
  if (last < input.length) out.push({ kind: "text", text: input.slice(last) });
  return out;
}
function stripInlineLinks(input) {
  return parseInlineLinks(input).map((s) => s.text).join("");
}
function inlineLinkHrefs(input) {
  return parseInlineLinks(input).flatMap((s) => s.kind === "link" ? [s.href] : []);
}

// src/blog-blocks.ts
var BLOG_BLOCK_KINDS = [
  "callout",
  "steps",
  "compare",
  "quote",
  "procedure",
  "price"
];
var CONTAINER_BLOCK_KINDS = ["callout", "steps", "compare", "quote"];
var LEAF_BLOCK_KINDS = ["procedure", "price"];
var CALLOUT_TONES = ["tip", "warning", "note"];
var OPEN = /^:::([A-Za-z][\w-]*)\s*(?:\{(.*)\})?$/;
var LEAF = /^::([A-Za-z][\w-]*)\s*(?:\{(.*)\})?$/;
var CLOSE = /^:::$/;
var DIRECTIVE_LIKE = /^:{2,3}[A-Za-z]/;
var FENCE = /^(`{3,}|~{3,})/;
var SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
var ATTRIBUTE = /([A-Za-z][\w-]*)=(?:"([^"\n]*)"|&quot;((?:(?!&quot;)[^\n])*)&quot;)/y;
var BLOCK_DIRECTIVE_LINE = /^[ \t]*:{2,3}(?:[A-Za-z][\w-]*[ \t]*(?:\{[^\n]*\})?)?[ \t]*$/gm;
var ALLOWED_ATTRIBUTES = {
  callout: ["tone"],
  steps: [],
  compare: ["title"],
  quote: ["cite"],
  procedure: ["slug"],
  price: ["procedure", "city"]
};
var MESSAGE = {
  badSyntax: '\u0648\u06CC\u0698\u06AF\u06CC\u200C\u0647\u0627 \u062E\u0648\u0627\u0646\u0627 \u0646\u06CC\u0633\u062A\u0646\u062F\u061B \u0647\u0631 \u0648\u06CC\u0698\u06AF\u06CC \u0631\u0648 \u0628\u0647 \u0634\u06A9\u0644 name="value" \u0648 \u0628\u0627 \u06AF\u06CC\u0648\u0645\u0647\u0654 \u0627\u0646\u06AF\u0644\u06CC\u0633\u06CC \u0628\u0646\u0648\u06CC\u0633\u06CC\u062F.',
  malformedLine: '\u062E\u0637 \u0628\u0644\u0648\u06A9 \u062F\u0631\u0633\u062A \u0646\u0648\u0634\u062A\u0647 \u0646\u0634\u062F\u0647 \u0627\u0633\u062A\u061B \u0634\u06A9\u0644 \u062F\u0631\u0633\u062A ::name{attr="value"} \u06CC\u0627 :::name{attr="value"} \u0627\u0633\u062A.',
  strayClose: "\u062E\u0637 \xAB:::\xBB \u0628\u0644\u0648\u06A9\u06CC \u0631\u0648 \u0646\u0645\u06CC\u200C\u0628\u0646\u062F\u062F\u061B \u0642\u0628\u0644 \u0627\u0632 \u0622\u0646 \u0628\u0644\u0648\u06A9 \u0628\u0627\u0632\u06CC \u0646\u06CC\u0633\u062A.",
  nested: "\u0628\u0644\u0648\u06A9\u200C\u0647\u0627 \u062A\u0648\u062F\u0631\u062A\u0648 \u0646\u0645\u06CC\u200C\u0634\u0646\u061B \u0627\u06CC\u0646 \u0628\u0644\u0648\u06A9 \u0631\u0648 \u0628\u06CC\u0631\u0648\u0646 \u0627\u0632 \u0628\u0644\u0648\u06A9 \u0642\u0628\u0644\u06CC \u0628\u06AF\u0630\u0627\u0631\u06CC\u062F."
};
function isContainerKind(name) {
  return CONTAINER_BLOCK_KINDS.includes(name);
}
function isLeafKind(name) {
  return LEAF_BLOCK_KINDS.includes(name);
}
function isTone(value) {
  return CALLOUT_TONES.includes(value);
}
function unknownName(name) {
  return `\u0628\u0644\u0648\u06A9 \xAB${name}\xBB \u062A\u0639\u0631\u06CC\u0641 \u0646\u0634\u062F\u0647 \u0627\u0633\u062A\u061B \u0628\u0644\u0648\u06A9\u200C\u0647\u0627\u06CC \u0645\u062C\u0627\u0632: ${BLOG_BLOCK_KINDS.join("\u060C ")}.`;
}
function parseAttributes(raw) {
  const attrs = /* @__PURE__ */ new Map();
  if (raw === void 0) return attrs;
  const text2 = raw.trim();
  let at = 0;
  while (at < text2.length) {
    while (text2[at] === " " || text2[at] === "	") at += 1;
    if (at >= text2.length) break;
    ATTRIBUTE.lastIndex = at;
    const match = ATTRIBUTE.exec(text2);
    if (!match) return null;
    const name = match[1] ?? "";
    if (attrs.has(name)) return null;
    attrs.set(name, match[2] ?? match[3] ?? "");
    at = ATTRIBUTE.lastIndex;
  }
  return attrs;
}
function unknownAttribute(kind, attrs) {
  for (const name of attrs.keys()) {
    if (!ALLOWED_ATTRIBUTES[kind].includes(name)) {
      return `\u0648\u06CC\u0698\u06AF\u06CC \xAB${name}\xBB \u0628\u0631\u0627\u06CC \u0628\u0644\u0648\u06A9 ${kind} \u062A\u0639\u0631\u06CC\u0641 \u0646\u0634\u062F\u0647 \u0627\u0633\u062A.`;
    }
  }
  return null;
}
function textOrNull(value) {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : null;
}
function leafSegment(kind, attrs) {
  const unknown = unknownAttribute(kind, attrs);
  if (unknown) return unknown;
  if (kind === "procedure") {
    const slug = attrs.get("slug") ?? "";
    if (!SLUG.test(slug)) return '\u0628\u0644\u0648\u06A9 ::procedure \u0628\u0647 slug \u0645\u0639\u062A\u0628\u0631 \u0646\u06CC\u0627\u0632 \u062F\u0627\u0631\u062F\u060C \u0645\u062B\u0644 slug="botox".';
    return { kind, slug };
  }
  const procedure = attrs.get("procedure") ?? "";
  if (!SLUG.test(procedure)) {
    return '\u0628\u0644\u0648\u06A9 ::price \u0628\u0647 procedure \u0645\u0639\u062A\u0628\u0631 \u0646\u06CC\u0627\u0632 \u062F\u0627\u0631\u062F\u060C \u0645\u062B\u0644 procedure="botox".';
  }
  const city = attrs.get("city");
  if (city !== void 0 && !SLUG.test(city)) {
    return 'city \u062F\u0631 ::price \u0628\u0627\u06CC\u062F \u0627\u0633\u0644\u0627\u06AF \u0634\u0647\u0631 \u0628\u0627\u0634\u062F\u060C \u0645\u062B\u0644 city="tehran".';
  }
  return { kind, procedure, city: city ?? null };
}
function containerSegment(kind, attrs, body) {
  const unknown = unknownAttribute(kind, attrs);
  if (unknown) return unknown;
  switch (kind) {
    case "callout": {
      const tone = attrs.get("tone") ?? "note";
      return isTone(tone) ? { kind, tone, body } : "tone \u062F\u0631 :::callout \u0641\u0642\u0637 tip\u060C warning \u06CC\u0627 note \u0627\u0633\u062A.";
    }
    case "steps":
      return { kind, body };
    case "compare":
      return { kind, title: textOrNull(attrs.get("title")), body };
    case "quote":
      return { kind, cite: textOrNull(attrs.get("cite")), body };
  }
}
function trimBlankEdges(lines) {
  let start = 0;
  let end = lines.length;
  while (start < end && (lines[start] ?? "").trim() === "") start += 1;
  while (end > start && (lines[end - 1] ?? "").trim() === "") end -= 1;
  return lines.slice(start, end);
}
function findClose(lines, from) {
  let depth = 0;
  let nested = -1;
  let fence = null;
  for (let j = from; j < lines.length; j += 1) {
    const trimmed = (lines[j] ?? "").trim();
    if (fence) {
      if (trimmed.startsWith(fence)) fence = null;
      continue;
    }
    const opensFence = FENCE.exec(trimmed);
    if (opensFence) {
      fence = opensFence[1] ?? null;
      continue;
    }
    if (CLOSE.test(trimmed)) {
      if (depth === 0) return { close: j, nested };
      depth -= 1;
      continue;
    }
    if (OPEN.test(trimmed)) {
      depth += 1;
      if (nested < 0) nested = j;
      continue;
    }
    if (LEAF.test(trimmed) && nested < 0) nested = j;
  }
  return { close: -1, nested };
}
function scan(source) {
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  const segments = [];
  const issues = [];
  const leaves = [];
  let markdown = [];
  let fence = null;
  const flush = () => {
    if (markdown.length === 0) return;
    segments.push({ kind: "markdown", text: markdown.join("\n") });
    markdown = [];
  };
  const issue = (index, code, message) => {
    issues.push({ line: index + 1, code, message });
  };
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i] ?? "";
    const trimmed = line.trim();
    if (fence) {
      markdown.push(line);
      if (trimmed.startsWith(fence)) fence = null;
      continue;
    }
    const opensFence = FENCE.exec(trimmed);
    if (opensFence) {
      fence = opensFence[1] ?? null;
      markdown.push(line);
      continue;
    }
    if (CLOSE.test(trimmed)) {
      issue(i, "unknown_block", MESSAGE.strayClose);
      markdown.push(line);
      continue;
    }
    const leaf = LEAF.exec(trimmed);
    if (leaf) {
      const name = leaf[1] ?? "";
      if (!isLeafKind(name)) {
        issue(
          i,
          "unknown_block",
          isContainerKind(name) ? `${name} \u0628\u0644\u0648\u06A9 \u0686\u0646\u062F\u062E\u0637\u06CC \u0627\u0633\u062A\u061B \u0627\u0648\u0646 \u0631\u0648 \u0628\u0627 \xAB:::${name}\xBB \u0628\u0627\u0632 \u0648 \u0628\u0627 \xAB:::\xBB \u0628\u0628\u0646\u062F\u06CC\u062F.` : unknownName(name)
        );
        markdown.push(line);
        continue;
      }
      const attrs = parseAttributes(leaf[2]);
      const built = attrs ? leafSegment(name, attrs) : MESSAGE.badSyntax;
      if (typeof built === "string") {
        issue(i, "bad_attribute", built);
        markdown.push(line);
        continue;
      }
      flush();
      segments.push(built);
      leaves.push({ line: i + 1, segment: built });
      continue;
    }
    const open = OPEN.exec(trimmed);
    if (open) {
      const name = open[1] ?? "";
      const { close, nested } = findClose(lines, i + 1);
      if (close < 0) {
        issue(i, "unclosed_block", `\u0628\u0644\u0648\u06A9 ${name} \u0628\u0627 \u062E\u0637\u06CC \u06A9\u0647 \u0641\u0642\u0637 \xAB:::\xBB \u062F\u0627\u0631\u062F \u0628\u0633\u062A\u0647 \u0646\u0634\u062F\u0647 \u0627\u0633\u062A.`);
        markdown.push(line);
        continue;
      }
      const whole = lines.slice(i, close + 1);
      let built;
      let at = i;
      let code = "bad_attribute";
      if (!isContainerKind(name)) {
        code = "unknown_block";
        built = isLeafKind(name) ? `${name} \u0628\u0644\u0648\u06A9 \u062A\u06A9\u200C\u062E\u0637\u06CC \u0627\u0633\u062A: \xAB::${name}{\u2026}\xBB.` : unknownName(name);
      } else if (nested >= 0) {
        code = "nested_block";
        at = nested;
        built = MESSAGE.nested;
      } else {
        const attrs = parseAttributes(open[2]);
        built = attrs ? containerSegment(name, attrs, trimBlankEdges(lines.slice(i + 1, close)).join("\n")) : MESSAGE.badSyntax;
      }
      if (typeof built === "string") {
        issue(at, code, built);
        markdown.push(...whole);
      } else {
        flush();
        segments.push(built);
      }
      i = close;
      continue;
    }
    if (DIRECTIVE_LIKE.test(trimmed)) issue(i, "bad_attribute", MESSAGE.malformedLine);
    markdown.push(line);
  }
  flush();
  return { segments, issues, leaves };
}
function leafProcedure(segment) {
  return segment.kind === "procedure" ? segment.slug : segment.procedure;
}
function parseBlocks(source) {
  return scan(source).segments;
}
function validateBlocks(source, opts = {}) {
  const { issues, leaves } = scan(source);
  const out = [...issues];
  const known = opts.knownProcedureSlugs;
  if (known) {
    for (const { line, segment } of leaves) {
      const slug = leafProcedure(segment);
      if (!known.has(slug)) {
        out.push({
          line,
          code: "unknown_procedure",
          message: `\u062E\u062F\u0645\u062A \xAB${slug}\xBB \u062F\u0631 \u062F\u0627\u0646\u0634\u0646\u0627\u0645\u0647\u0654 \u0645\u0646\u062A\u0634\u0631\u0634\u062F\u0647 \u0646\u06CC\u0633\u062A.`
        });
      }
    }
  }
  return out.sort((a, b) => a.line - b.line);
}
function stripBlocks(source) {
  if (!source.includes("::")) return source;
  return scan(source).segments.flatMap((segment) => {
    if (segment.kind === "markdown") return [segment.text];
    return "body" in segment ? [segment.body] : [];
  }).join("\n");
}
function removeUnknownProcedureBlocks(source, known) {
  const drop = new Set(
    scan(source).leaves.filter(({ segment }) => !known.has(leafProcedure(segment))).map(({ line }) => line - 1)
  );
  if (drop.size === 0) return source;
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  const out = [];
  for (let i = 0; i < lines.length; i += 1) {
    if (drop.has(i)) {
      if ((out.at(-1) ?? "").trim() === "" && (lines[i + 1] ?? "").trim() === "") i += 1;
      continue;
    }
    out.push(lines[i] ?? "");
  }
  return out.join("\n");
}
function blockSignature(source) {
  return parseBlocks(source).flatMap((segment) => {
    switch (segment.kind) {
      case "markdown":
        return [];
      case "callout":
        return [`callout:${segment.tone}`];
      case "procedure":
        return [`procedure:${segment.slug}`];
      case "price":
        return [`price:${segment.procedure}|${segment.city ?? ""}`];
      default:
        return [segment.kind];
    }
  });
}
function articleWordCount(source) {
  return countWordsFa(stripBlocks(source));
}
function articleReadingMinutes(source, wordsPerMinute = 200) {
  return readingTimeFa(stripBlocks(source), wordsPerMinute);
}

// src/occasions/catalog.ts
var DEFAULT_LEAD_DAYS = 7;
var OCCASIONS = Object.freeze([
  // ── Solar Hijri (Jalali) ─────────────────────────────────────────────────
  {
    key: "nowruz",
    nameFa: "\u0646\u0648\u0631\u0648\u0632",
    kind: "CELEBRATION",
    date: { cal: "jalali", m: 1, d: 1, days: 4 },
    officialHoliday: true,
    promo: "OK",
    campaignLead: { from: -34, to: -15 },
    leadDays: 15
  },
  {
    key: "farvardin-12",
    nameFa: "\u0631\u0648\u0632 \u062C\u0645\u0647\u0648\u0631\u06CC \u0627\u0633\u0644\u0627\u0645\u06CC",
    kind: "CELEBRATION",
    date: { cal: "jalali", m: 1, d: 12 },
    officialHoliday: true,
    promo: "OK",
    campaignLead: null
  },
  {
    key: "sizdah",
    nameFa: "\u0633\u06CC\u0632\u062F\u0647\u200C\u0628\u0647\u200C\u062F\u0631",
    kind: "CELEBRATION",
    date: { cal: "jalali", m: 1, d: 13 },
    officialHoliday: true,
    promo: "OK",
    campaignLead: null
  },
  {
    key: "teachers-day",
    nameFa: "\u0631\u0648\u0632 \u0645\u0639\u0644\u0645",
    kind: "PROFESSIONAL",
    date: { cal: "jalali", m: 2, d: 12 },
    officialHoliday: false,
    promo: "OK",
    campaignLead: null
  },
  {
    key: "khordad-14",
    nameFa: "\u0631\u062D\u0644\u062A \u0627\u0645\u0627\u0645 \u062E\u0645\u06CC\u0646\u06CC",
    kind: "MOURNING",
    date: { cal: "jalali", m: 3, d: 14 },
    officialHoliday: true,
    promo: "HARD",
    campaignLead: null
  },
  {
    key: "doctors-day",
    nameFa: "\u0631\u0648\u0632 \u067E\u0632\u0634\u06A9",
    kind: "PROFESSIONAL",
    date: { cal: "jalali", m: 6, d: 1 },
    officialHoliday: false,
    promo: "OK",
    campaignLead: null
  },
  {
    key: "autumn-start",
    nameFa: "\u0622\u063A\u0627\u0632 \u067E\u0627\u06CC\u06CC\u0632 \u0648 \u0628\u0627\u0632\u06AF\u0634\u0627\u06CC\u06CC \u0645\u062F\u0627\u0631\u0633",
    kind: "SEASONAL",
    date: { cal: "jalali", m: 7, d: 1 },
    officialHoliday: false,
    promo: "OK",
    campaignLead: { from: -12, to: 14 },
    leadDays: 0
  },
  {
    key: "yalda",
    nameFa: "\u0634\u0628 \u06CC\u0644\u062F\u0627",
    kind: "CELEBRATION",
    date: { cal: "jalali", m: 9, d: 30 },
    officialHoliday: false,
    promo: "OK",
    campaignLead: { from: -18, to: -3 },
    leadDays: 5
  },
  {
    key: "bahman-22",
    nameFa: "\u067E\u06CC\u0631\u0648\u0632\u06CC \u0627\u0646\u0642\u0644\u0627\u0628 \u0627\u0633\u0644\u0627\u0645\u06CC",
    kind: "CELEBRATION",
    date: { cal: "jalali", m: 11, d: 22 },
    officialHoliday: true,
    promo: "OK",
    campaignLead: null
  },
  {
    key: "sepandarmazgan",
    nameFa: "\u0633\u067E\u0646\u062F\u0627\u0631\u0645\u0630\u06AF\u0627\u0646",
    kind: "CELEBRATION",
    date: { cal: "jalali", m: 11, d: 29 },
    officialHoliday: false,
    promo: "OK",
    campaignLead: { from: -14, to: -3 },
    leadDays: 5
  },
  {
    key: "esfand-29",
    nameFa: "\u0645\u0644\u06CC \u0634\u062F\u0646 \u0635\u0646\u0639\u062A \u0646\u0641\u062A",
    kind: "CELEBRATION",
    date: { cal: "jalali", m: 12, d: 29 },
    officialHoliday: true,
    promo: "OK",
    campaignLead: null
  },
  // ── Gregorian ────────────────────────────────────────────────────────────
  {
    key: "valentine",
    nameFa: "\u0648\u0644\u0646\u062A\u0627\u06CC\u0646",
    kind: "COMMERCIAL",
    date: { cal: "gregorian", m: 2, d: 14 },
    officialHoliday: false,
    promo: "OK",
    campaignLead: null
  },
  {
    key: "black-friday",
    nameFa: "\u062C\u0645\u0639\u0647\u0654 \u0633\u06CC\u0627\u0647",
    kind: "COMMERCIAL",
    date: { cal: "black_friday" },
    officialHoliday: false,
    promo: "OK",
    campaignLead: { from: -14, to: -2 },
    leadDays: 3
  },
  // ── Lunar Hijri ──────────────────────────────────────────────────────────
  {
    key: "tasua-ashura",
    nameFa: "\u062A\u0627\u0633\u0648\u0639\u0627 \u0648 \u0639\u0627\u0634\u0648\u0631\u0627",
    kind: "MOURNING",
    date: { cal: "hijri", m: 1, d: 9, days: 2 },
    officialHoliday: true,
    promo: "HARD",
    campaignLead: null
  },
  {
    key: "arbaeen",
    nameFa: "\u0627\u0631\u0628\u0639\u06CC\u0646",
    kind: "MOURNING",
    date: { cal: "hijri", m: 2, d: 20 },
    officialHoliday: true,
    promo: "HARD",
    campaignLead: null
  },
  {
    key: "safar-28",
    nameFa: "\u0631\u062D\u0644\u062A \u067E\u06CC\u0627\u0645\u0628\u0631 \u0648 \u0634\u0647\u0627\u062F\u062A \u0627\u0645\u0627\u0645 \u062D\u0633\u0646",
    kind: "MOURNING",
    date: { cal: "hijri", m: 2, d: 28 },
    officialHoliday: true,
    promo: "HARD",
    campaignLead: null
  },
  {
    key: "safar-last",
    nameFa: "\u0634\u0647\u0627\u062F\u062A \u0627\u0645\u0627\u0645 \u0631\u0636\u0627",
    kind: "MOURNING",
    date: { cal: "hijri", m: 2, d: "last" },
    officialHoliday: true,
    promo: "HARD",
    campaignLead: null
  },
  {
    key: "rabi1-08",
    nameFa: "\u0634\u0647\u0627\u062F\u062A \u0627\u0645\u0627\u0645 \u062D\u0633\u0646 \u0639\u0633\u06A9\u0631\u06CC",
    kind: "MOURNING",
    date: { cal: "hijri", m: 3, d: 8 },
    officialHoliday: true,
    promo: "HARD",
    campaignLead: null
  },
  {
    key: "bridal-after-safar",
    nameFa: "\u0622\u063A\u0627\u0632 \u0641\u0635\u0644 \u0639\u0631\u0648\u0633\u06CC (\u067E\u0633 \u0627\u0632 \u0645\u0627\u0647\u200C\u0647\u0627\u06CC \u0633\u0648\u06AF\u0648\u0627\u0631\u06CC)",
    kind: "SEASONAL",
    date: { cal: "hijri", m: 3, d: 9 },
    officialHoliday: false,
    promo: "OK",
    campaignLead: { from: 0, to: 30 },
    leadDays: 0
  },
  {
    key: "mawlid",
    nameFa: "\u0645\u06CC\u0644\u0627\u062F \u067E\u06CC\u0627\u0645\u0628\u0631 \u0648 \u0627\u0645\u0627\u0645 \u0635\u0627\u062F\u0642",
    kind: "RELIGIOUS",
    date: { cal: "hijri", m: 3, d: 17 },
    officialHoliday: true,
    promo: "OK",
    campaignLead: null
  },
  {
    key: "nurses-day",
    nameFa: "\u0631\u0648\u0632 \u067E\u0631\u0633\u062A\u0627\u0631",
    kind: "PROFESSIONAL",
    date: { cal: "hijri", m: 5, d: 5 },
    officialHoliday: false,
    promo: "OK",
    campaignLead: null
  },
  {
    key: "fatimiyya-first",
    nameFa: "\u0634\u0647\u0627\u062F\u062A \u062D\u0636\u0631\u062A \u0641\u0627\u0637\u0645\u0647 (\u0631\u0648\u0627\u06CC\u062A \u0627\u0648\u0644)",
    kind: "MOURNING",
    date: { cal: "hijri", m: 5, d: 13 },
    officialHoliday: false,
    promo: "SOFT",
    campaignLead: null
  },
  {
    key: "fatimiyya-days",
    nameFa: "\u0627\u06CC\u0627\u0645 \u0641\u0627\u0637\u0645\u06CC\u0647",
    kind: "MOURNING",
    date: { cal: "hijri", m: 6, d: 1, days: 2 },
    officialHoliday: false,
    promo: "SOFT",
    campaignLead: null
  },
  {
    key: "fatimiyya",
    nameFa: "\u0634\u0647\u0627\u062F\u062A \u062D\u0636\u0631\u062A \u0641\u0627\u0637\u0645\u0647",
    kind: "MOURNING",
    date: { cal: "hijri", m: 6, d: 3 },
    officialHoliday: true,
    promo: "HARD",
    campaignLead: null
  },
  {
    key: "mothers-day",
    nameFa: "\u0631\u0648\u0632 \u0632\u0646 \u0648 \u0631\u0648\u0632 \u0645\u0627\u062F\u0631",
    kind: "CELEBRATION",
    date: { cal: "hijri", m: 6, d: 20 },
    officialHoliday: false,
    promo: "OK",
    campaignLead: { from: -21, to: -4 },
    leadDays: 7
  },
  {
    key: "fathers-day",
    nameFa: "\u0631\u0648\u0632 \u067E\u062F\u0631 \u0648 \u0631\u0648\u0632 \u0645\u0631\u062F",
    kind: "CELEBRATION",
    date: { cal: "hijri", m: 7, d: 13 },
    officialHoliday: true,
    promo: "OK",
    campaignLead: { from: -18, to: -3 },
    leadDays: 5
  },
  {
    key: "rajab-25",
    nameFa: "\u0634\u0647\u0627\u062F\u062A \u0627\u0645\u0627\u0645 \u06A9\u0627\u0638\u0645",
    kind: "MOURNING",
    date: { cal: "hijri", m: 7, d: 25 },
    officialHoliday: false,
    promo: "SOFT",
    campaignLead: null
  },
  {
    key: "mabath",
    nameFa: "\u0639\u06CC\u062F \u0645\u0628\u0639\u062B",
    kind: "RELIGIOUS",
    date: { cal: "hijri", m: 7, d: 27 },
    officialHoliday: true,
    promo: "OK",
    campaignLead: null
  },
  {
    key: "shaban-15",
    nameFa: "\u0646\u06CC\u0645\u0647\u0654 \u0634\u0639\u0628\u0627\u0646",
    kind: "RELIGIOUS",
    date: { cal: "hijri", m: 8, d: 15 },
    officialHoliday: true,
    promo: "OK",
    campaignLead: null
  },
  {
    key: "ramadan",
    nameFa: "\u0645\u0627\u0647 \u0631\u0645\u0636\u0627\u0646",
    kind: "RELIGIOUS",
    date: { cal: "hijri", m: 9, d: 1, days: "month" },
    officialHoliday: false,
    promo: "SOFT",
    campaignLead: null
  },
  {
    key: "qadr-19",
    nameFa: "\u0634\u0628 \u0642\u062F\u0631",
    kind: "MOURNING",
    date: { cal: "hijri", m: 9, d: 19 },
    officialHoliday: false,
    promo: "HARD",
    campaignLead: null
  },
  {
    key: "ali-21",
    nameFa: "\u0634\u0647\u0627\u062F\u062A \u0627\u0645\u0627\u0645 \u0639\u0644\u06CC",
    kind: "MOURNING",
    date: { cal: "hijri", m: 9, d: 21 },
    officialHoliday: true,
    promo: "HARD",
    campaignLead: null
  },
  {
    key: "qadr-23",
    nameFa: "\u0634\u0628 \u0642\u062F\u0631",
    kind: "MOURNING",
    date: { cal: "hijri", m: 9, d: 23 },
    officialHoliday: false,
    promo: "HARD",
    campaignLead: null
  },
  {
    key: "eid-fitr",
    nameFa: "\u0639\u06CC\u062F \u0641\u0637\u0631",
    kind: "RELIGIOUS",
    date: { cal: "hijri", m: 10, d: 1, days: 2 },
    officialHoliday: true,
    promo: "OK",
    campaignLead: null
  },
  {
    key: "sadiq-25",
    nameFa: "\u0634\u0647\u0627\u062F\u062A \u0627\u0645\u0627\u0645 \u0635\u0627\u062F\u0642",
    kind: "MOURNING",
    date: { cal: "hijri", m: 10, d: 25 },
    officialHoliday: true,
    promo: "HARD",
    campaignLead: null
  },
  {
    key: "girls-day",
    nameFa: "\u0631\u0648\u0632 \u062F\u062E\u062A\u0631",
    kind: "CELEBRATION",
    date: { cal: "hijri", m: 11, d: 1 },
    officialHoliday: false,
    promo: "OK",
    campaignLead: null
  },
  {
    key: "eid-adha",
    nameFa: "\u0639\u06CC\u062F \u0642\u0631\u0628\u0627\u0646",
    kind: "RELIGIOUS",
    date: { cal: "hijri", m: 12, d: 10 },
    officialHoliday: true,
    promo: "OK",
    campaignLead: null
  },
  {
    key: "eid-ghadir",
    nameFa: "\u0639\u06CC\u062F \u063A\u062F\u06CC\u0631",
    kind: "RELIGIOUS",
    date: { cal: "hijri", m: 12, d: 18 },
    officialHoliday: true,
    promo: "OK",
    campaignLead: null
  }
]);
var OCCASION_BY_KEY = new Map(
  OCCASIONS.map((def) => [def.key, def])
);

// src/occasions/overrides.ts
var HIJRI_MONTH_START_IR = Object.freeze({
  "1447-10": "2026-03-21",
  // Eid al-Fitr 1447 = 1 Farvardin 1405 (Umm al-Qura: 2026-03-20)
  "1448-01": "2026-06-16",
  // checked: Tasua/Ashura 1405 = 3–4 Tir
  "1448-02": "2026-07-15",
  // checked: Arbaeen 1405 = 12 Mordad
  "1448-03": "2026-08-14",
  // checked: 8 Rabi' al-Awwal 1405 = 30 Mordad
  "1448-06": "2026-11-11"
  // checked: روز مادر 1405 = 9 Azar
});
function hijriOverrideKey(hy, hm) {
  return `${String(hy)}-${String(hm).padStart(2, "0")}`;
}

// src/occasions/iso.ts
var MS_PER_DAY3 = 864e5;
function pad(n, width = 2) {
  return String(n).padStart(width, "0");
}
function isoOf(gy, gm, gd) {
  return `${pad(gy, 4)}-${pad(gm)}-${pad(gd)}`;
}
function isoToUtcMs(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}
function utcMsToIso(ms) {
  const date = new Date(ms);
  return isoOf(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}
function isoAddDays(iso, days) {
  return utcMsToIso(isoToUtcMs(iso) + days * MS_PER_DAY3);
}
function isoDaysBetween(a, b) {
  return Math.round((isoToUtcMs(b) - isoToUtcMs(a)) / MS_PER_DAY3);
}
function isoMin(a, b) {
  return a <= b ? a : b;
}
function isoMax(a, b) {
  return a >= b ? a : b;
}
function tehranIsoDay(now) {
  const { jy, jm, jd } = toJalali(now);
  const { gy, gm, gd } = jalaliToGregorian(jy, jm, jd);
  return isoOf(gy, gm, gd);
}
function isoToJalali(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return gregorianToJalali(y, m, d);
}
function isoToJalaliKey(iso) {
  const { jy, jm, jd } = isoToJalali(iso);
  return `${pad(jy, 4)}-${pad(jm)}-${pad(jd)}`;
}
function jalaliToIso(jy, jm, jd) {
  const { gy, gm, gd } = jalaliToGregorian(jy, jm, jd);
  return isoOf(gy, gm, gd);
}
function isoToTehranNoon(iso) {
  return new Date(isoToUtcMs(iso) + 8.5 * 36e5);
}

// src/occasions/hijri.ts
var MS_PER_DAY4 = 864e5;
var MEAN_LUNAR_MONTH_DAYS = 29.530588853;
var HIJRI_EPOCH_MS = Date.UTC(622, 6, 16);
var formatter = null;
function umalqura() {
  formatter ??= new Intl.DateTimeFormat("en-u-ca-islamic-umalqura-nu-latn", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    timeZone: "UTC"
  });
  return formatter;
}
var partsCache = /* @__PURE__ */ new Map();
function umqAt(utcMidnightMs) {
  const hit = partsCache.get(utcMidnightMs);
  if (hit) return hit;
  const parts = umalqura().formatToParts(new Date(utcMidnightMs + 12 * 36e5));
  const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? NaN);
  const value = { hy: get("year"), hm: get("month"), hd: get("day") };
  partsCache.set(utcMidnightMs, value);
  return value;
}
function isoToHijri(iso) {
  return umqAt(isoToUtcMs(iso));
}
function gregorianToHijri(date) {
  return isoToHijri(tehranIsoDay(date));
}
var umqCache = /* @__PURE__ */ new Map();
function umqToIso(hy, hm, hd) {
  const key = `${String(hy)}-${String(hm)}-${String(hd)}`;
  if (umqCache.has(key)) return umqCache.get(key) ?? null;
  const estimate = HIJRI_EPOCH_MS + Math.round(((hy - 1) * 12 + (hm - 1)) * MEAN_LUNAR_MONTH_DAYS + (hd - 1)) * MS_PER_DAY4;
  let found = null;
  for (let offset = -5; offset <= 5 && found === null; offset += 1) {
    const ms = estimate + offset * MS_PER_DAY4;
    const h = umqAt(ms);
    if (h.hy === hy && h.hm === hm && h.hd === hd) found = utcMsToIso(ms);
  }
  umqCache.set(key, found);
  return found;
}
function nextMonth(hy, hm) {
  return hm === 12 ? [hy + 1, 1] : [hy, hm + 1];
}
function umqMonthLength(hy, hm) {
  return umqToIso(hy, hm, 30) === null ? 29 : 30;
}
function hijriMonthStart(hy, hm) {
  const override = HIJRI_MONTH_START_IR[hijriOverrideKey(hy, hm)];
  if (override) return { iso: override, confidence: "OFFICIAL" };
  const iso = umqToIso(hy, hm, 1);
  if (iso === null) throw new RangeError(`lunar Hijri month out of range: ${String(hy)}/${String(hm)}`);
  return { iso, confidence: "COMPUTED" };
}
function hijriMonthLength(hy, hm) {
  const start = HIJRI_MONTH_START_IR[hijriOverrideKey(hy, hm)];
  if (start) {
    const [ny, nm] = nextMonth(hy, hm);
    const next = HIJRI_MONTH_START_IR[hijriOverrideKey(ny, nm)];
    if (next) return isoDaysBetween(start, next);
  }
  return umqMonthLength(hy, hm);
}
function hijriToGregorianDay(hy, hm, hd) {
  const override = HIJRI_MONTH_START_IR[hijriOverrideKey(hy, hm)];
  if (override) {
    const length = hijriMonthLength(hy, hm);
    const day = hd === "last" ? length : hd;
    if (day < 1 || day > length) return null;
    return { iso: isoAddDays(override, day - 1), confidence: "OFFICIAL" };
  }
  if (hd === "last") {
    const iso2 = umqToIso(hy, hm, 30) ?? umqToIso(hy, hm, 29);
    return iso2 === null ? null : { iso: iso2, confidence: "COMPUTED" };
  }
  if (hd < 1 || hd > 30) return null;
  const iso = umqToIso(hy, hm, hd);
  return iso === null ? null : { iso, confidence: "COMPUTED" };
}

// src/occasions/calendar.ts
var MUHARRAM_HARD_DAYS = 12;
var MUHARRAM_REASON_FA = "\u062F\u0647\u0647\u0654 \u0627\u0648\u0644 \u0645\u062D\u0631\u0645";
var MOURNING_SEASON_REASON_FA = "\u0645\u0627\u0647\u200C\u0647\u0627\u06CC \u0645\u062D\u0631\u0645 \u0648 \u0635\u0641\u0631";
var RAMADAN_REASON_FA = "\u0645\u0627\u0647 \u0631\u0645\u0636\u0627\u0646";
var SEARCH_LIMIT_DAYS = 400;
function makeInstance(def, date, days, confidence) {
  const jalali = isoToJalali(date);
  return {
    key: def.key,
    nameFa: def.nameFa,
    kind: def.kind,
    officialHoliday: def.officialHoliday,
    promo: def.promo,
    date,
    endDate: days > 1 ? isoAddDays(date, days - 1) : null,
    days,
    jalaliDate: isoToJalaliKey(date),
    jalaliYear: jalali.jy,
    confidence,
    campaignLead: def.campaignLead,
    leadDays: def.leadDays ?? DEFAULT_LEAD_DAYS
  };
}
function blackFriday(gy) {
  const firstWeekday = new Date(Date.UTC(gy, 10, 1)).getUTCDay();
  const firstThursday = 1 + (4 - firstWeekday + 7) % 7;
  return isoOf(gy, 11, firstThursday + 21 + 1);
}
function instancesOfDefInYear(def, gy) {
  const out = [];
  const inYear = (iso) => iso.startsWith(`${String(gy).padStart(4, "0")}-`);
  const rule = def.date;
  switch (rule.cal) {
    case "jalali": {
      const firstJy = isoToJalali(isoOf(gy, 1, 1)).jy;
      for (const jy of [firstJy, firstJy + 1]) {
        const iso = jalaliToIso(jy, rule.m, rule.d);
        if (inYear(iso)) out.push(makeInstance(def, iso, rule.days ?? 1, "OFFICIAL"));
      }
      break;
    }
    case "gregorian": {
      out.push(makeInstance(def, isoOf(gy, rule.m, rule.d), 1, "OFFICIAL"));
      break;
    }
    case "black_friday": {
      out.push(makeInstance(def, blackFriday(gy), 1, "OFFICIAL"));
      break;
    }
    case "hijri": {
      const firstHy = isoToHijri(isoOf(gy, 1, 1)).hy;
      for (let hy = firstHy - 1; hy <= firstHy + 2; hy += 1) {
        const day = hijriToGregorianDay(hy, rule.m, rule.d);
        if (!day || !inYear(day.iso)) continue;
        const days = rule.days === "month" ? hijriMonthLength(hy, rule.m) : rule.days ?? 1;
        out.push(makeInstance(def, day.iso, days, day.confidence));
      }
      break;
    }
  }
  return out;
}
var yearCache = /* @__PURE__ */ new Map();
function instancesInYear(gy) {
  const hit = yearCache.get(gy);
  if (hit) return hit;
  const list = OCCASIONS.flatMap((def) => instancesOfDefInYear(def, gy)).sort(compareInstances);
  yearCache.set(gy, list);
  return list;
}
function compareInstances(a, b) {
  return a.date === b.date ? a.key.localeCompare(b.key) : a.date < b.date ? -1 : 1;
}
function lastDay(instance) {
  return instance.endDate ?? instance.date;
}
function occasionsBetween(fromIso, toIso) {
  if (fromIso > toIso) return [];
  const fromYear = Number(fromIso.slice(0, 4));
  const toYear = Number(toIso.slice(0, 4));
  const out = [];
  for (let gy = fromYear - 1; gy <= toYear; gy += 1) {
    for (const instance of instancesInYear(gy)) {
      if (instance.date <= toIso && lastDay(instance) >= fromIso) out.push(instance);
    }
  }
  return out.sort(compareInstances);
}
function nextOccasion(key, fromIso) {
  return occasionsBetween(fromIso, isoAddDays(fromIso, 400)).find((o) => o.key === key) ?? null;
}
function campaignWindow(instance) {
  if (!instance.campaignLead) return null;
  return {
    from: isoAddDays(instance.date, instance.campaignLead.from),
    to: isoAddDays(instance.date, instance.campaignLead.to)
  };
}
var promoCache = /* @__PURE__ */ new Map();
function status(level, softKind, reasonFa, occasionKey) {
  return { level, softKind, reasonFa, occasionKey };
}
function within(iso, from, to) {
  return iso >= from && iso <= to;
}
function promoStatus(iso) {
  const hit = promoCache.get(iso);
  if (hit) return hit;
  const value = computePromoStatus(iso);
  promoCache.set(iso, value);
  return value;
}
function computePromoStatus(iso) {
  const nearby = occasionsBetween(isoAddDays(iso, -1), isoAddDays(iso, 1));
  for (const o of nearby) {
    if (o.promo !== "HARD") continue;
    const margin = o.confidence === "COMPUTED" ? 1 : 0;
    if (within(iso, isoAddDays(o.date, -margin), isoAddDays(lastDay(o), margin))) {
      return status("HARD", null, o.nameFa, o.key);
    }
  }
  const hy = isoToHijri(iso).hy;
  const lunarYears = [hy - 1, hy, hy + 1];
  for (const year of lunarYears) {
    const start = hijriMonthStart(year, 1);
    const margin = start.confidence === "COMPUTED" ? 1 : 0;
    const from = isoAddDays(start.iso, -margin);
    const to = isoAddDays(start.iso, MUHARRAM_HARD_DAYS - 1 + margin);
    if (within(iso, from, to)) return status("HARD", null, MUHARRAM_REASON_FA, null);
  }
  for (const o of nearby) {
    if (o.promo === "SOFT" && o.kind === "MOURNING" && within(iso, o.date, lastDay(o))) {
      return status("SOFT", "MOURNING", o.nameFa, o.key);
    }
  }
  for (const year of lunarYears) {
    const start = hijriMonthStart(year, 1).iso;
    const end = hijriToGregorianDay(year, 3, 8)?.iso;
    if (end && within(iso, isoAddDays(start, MUHARRAM_HARD_DAYS), end)) {
      return status("SOFT", "MOURNING", MOURNING_SEASON_REASON_FA, null);
    }
  }
  for (const year of lunarYears) {
    const from = hijriMonthStart(year, 9).iso;
    const to = isoAddDays(hijriMonthStart(year, 10).iso, -1);
    if (within(iso, from, to)) return status("SOFT", "RAMADAN", RAMADAN_REASON_FA, "ramadan");
  }
  return status("OK", null, null, null);
}
function isNotHard(iso) {
  return promoStatus(iso).level !== "HARD";
}
function isSendDay(iso) {
  const s = promoStatus(iso);
  return s.level === "OK" || s.level === "SOFT" && s.softKind === "RAMADAN";
}
function search(iso, step, accept) {
  let day = iso;
  for (let i = 0; i < SEARCH_LIMIT_DAYS; i += 1) {
    if (accept(day)) return day;
    day = isoAddDays(day, step);
  }
  return iso;
}
function nextOkDay(iso) {
  return search(iso, 1, isNotHard);
}
function prevOkDay(iso) {
  return search(iso, -1, isNotHard);
}
function nextSendDay(iso) {
  return search(iso, 1, isSendDay);
}
function prevSendDay(iso) {
  return search(iso, -1, isSendDay);
}
function sendBy(instance, todayIso) {
  const window = campaignWindow(instance);
  const target = isoAddDays(instance.date, -instance.leadDays);
  const latest = prevSendDay(window ? isoMin(window.to, target) : target);
  return isoMax(nextSendDay(isoAddDays(todayIso, 1)), latest);
}
function blackoutsBetween(fromIso, toIso) {
  const out = [];
  const span = isoDaysBetween(fromIso, toIso);
  let current = null;
  for (let i = 0; i <= span; i += 1) {
    const day = isoAddDays(fromIso, i);
    const s = promoStatus(day);
    if (s.level === "OK") {
      current = null;
      continue;
    }
    const reasonFa = s.reasonFa ?? "";
    if (current && current.level === s.level && current.reasonFa === reasonFa) {
      current.to = day;
    } else {
      current = { from: day, to: day, level: s.level, reasonFa };
      out.push(current);
    }
  }
  return out;
}
function nowruzIso(jy) {
  const { gy, gm, gd } = jalaliToGregorian(jy, 1, 1);
  return isoOf(gy, gm, gd);
}

// src/pricing/guard.ts
function wholeRials(value, label) {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`${label} must be a non-negative safe integer, received ${String(value)}`);
  }
  return value;
}
var wholeCount = wholeRials;

// src/pricing/raas.ts
function resolve(rate2, opts = {}) {
  const fee = opts.feeWaived ? 0 : wholeRials(rate2.platformFeeRials, "platformFeeRials");
  const included = opts.includedOverride !== void 0 && opts.includedOverride !== null ? wholeCount(opts.includedOverride, "includedOverride") : wholeCount(rate2.includedQty, "includedQty");
  const unit = wholeRials(rate2.unitPriceRials, "unitPriceRials");
  const cap = wholeRials(rate2.capRials, "capRials");
  return { fee, included, unit, cap, usageCap: Math.max(0, cap - fee) };
}
function raasUsageRials(rate2, n, opts) {
  const r = resolve(rate2, opts);
  const count = wholeCount(n, "treated");
  const billable = Math.max(0, count - r.included);
  if (billable === 0 || r.unit === 0) return 0;
  if (billable >= Math.ceil(r.usageCap / r.unit)) return r.usageCap;
  return Math.min(r.usageCap, billable * r.unit);
}
function raasMarginalRials(rate2, k, opts) {
  if (k <= 0) return 0;
  return raasUsageRials(rate2, k, opts) - raasUsageRials(rate2, k - 1, opts);
}
function raasBillRials(rate2, n, opts) {
  return resolve(rate2, opts).fee + raasUsageRials(rate2, n, opts);
}
function raasCapThreshold(rate2, opts) {
  const r = resolve(rate2, opts);
  if (r.usageCap === 0) return 0;
  if (r.unit === 0) return null;
  return r.included + Math.ceil(r.usageCap / r.unit);
}
function raasCapPercentBp(rate2, n, opts) {
  const r = resolve(rate2, opts);
  if (r.cap === 0) return 0;
  return mulDiv(raasBillRials(rate2, n, opts), BASIS_POINTS_SCALE, r.cap, "floor");
}
function raasChargeKindAt(rate2, k, opts = {}) {
  const r = resolve(rate2, opts);
  wholeCount(k, "position");
  if (k <= r.included) return opts.trial ? "TRIAL_FREE" : "INCLUDED";
  return raasMarginalRials(rate2, k, opts) > 0 ? "CHARGED" : "CAPPED_FREE";
}
function raasStatementBreakdown(rate2, n, opts = {}) {
  const r = resolve(rate2, opts);
  const count = wholeCount(n, "treated");
  const usage = raasUsageRials(rate2, count, opts);
  const threshold = raasCapThreshold(rate2, opts);
  const freeCount = Math.min(count, r.included);
  const chargedCount = r.unit === 0 || r.usageCap === 0 ? 0 : Math.max(0, Math.min(count, threshold ?? count) - r.included);
  const cappedFreeCount = Math.max(0, count - freeCount - chargedCount);
  const freeKind = opts.trial ? "TRIAL_FREE" : "INCLUDED";
  const lines = [
    { kind: "RAAS_PLATFORM_FEE", quantity: 1, unitPriceRials: r.fee, amountRials: r.fee }
  ];
  if (freeCount > 0) {
    lines.push({
      kind: freeKind === "TRIAL_FREE" ? "RAAS_TRIAL_FREE" : "RAAS_INCLUDED",
      quantity: freeCount,
      unitPriceRials: 0,
      amountRials: 0
    });
  }
  if (chargedCount > 0) {
    lines.push({
      kind: "RAAS_CHARGED",
      quantity: chargedCount,
      unitPriceRials: r.unit,
      amountRials: usage
    });
  }
  if (cappedFreeCount > 0) {
    lines.push({
      kind: "RAAS_CAPPED_FREE",
      quantity: cappedFreeCount,
      unitPriceRials: 0,
      amountRials: 0
    });
  }
  return {
    feeRials: r.fee,
    feeWaived: Boolean(opts.feeWaived),
    freeCount,
    freeKind,
    chargedCount,
    cappedFreeCount,
    usageRials: usage,
    totalRials: r.fee + usage,
    lines
  };
}
function raasForecast(countSoFar, elapsedDays, monthDays) {
  const count = wholeCount(countSoFar, "countSoFar");
  const days = wholeCount(monthDays, "monthDays");
  if (days === 0) return count;
  const elapsed = Math.min(days, Math.max(1, wholeCount(elapsedDays, "elapsedDays")));
  if (elapsed >= days) return count;
  return mulDiv(count, days, elapsed, "round");
}
function raasProrate(amountRials, prorationBp) {
  const amount = wholeRials(amountRials, "amount");
  const bp = wholeCount(prorationBp, "prorationBp");
  return mulDiv(amount, Math.min(bp, BASIS_POINTS_SCALE), BASIS_POINTS_SCALE, "floor");
}
function comparePlansForVolume(input) {
  const treated = wholeCount(input.treated, "treated");
  const raasRials = raasBillRials(input.raas, treated, input.trial);
  const rows = input.flat.map((p) => ({
    key: p.key,
    name: p.name,
    rials: wholeRials(p.monthlyRials, `${p.key}.monthlyRials`)
  }));
  if (rows.length === 0) return { raasRials, rows, cheapestKey: "raas", verdict: null };
  const cheapest = rows.reduce((a, b) => b.rials < a.rials ? b : a);
  let verdict;
  if (raasRials < cheapest.rials) {
    verdict = {
      kind: "raas-cheaper",
      savingRials: cheapest.rials - raasRials,
      vsKey: cheapest.key
    };
  } else if (raasRials === cheapest.rials) {
    verdict = { kind: "equal", vsKey: cheapest.key };
  } else {
    const r = resolve(input.raas, input.trial);
    const headroom = cheapest.rials - r.fee;
    const fromTreated = headroom < 0 || r.unit === 0 ? 0 : r.included + Math.floor(headroom / r.unit) + 1;
    verdict = { kind: "flat-cheaper", fromTreated, vsKey: cheapest.key };
  }
  return {
    raasRials,
    rows,
    cheapestKey: raasRials < cheapest.rials ? "raas" : cheapest.key,
    verdict
  };
}
function revenueShareBp(billRials, treated, avgServiceRials) {
  const bill = wholeRials(billRials, "billRials");
  const count = wholeCount(treated, "treated");
  const avg = wholeRials(avgServiceRials, "avgServiceRials");
  if (count === 0 || avg === 0) return null;
  const revenue = count * avg;
  if (!Number.isSafeInteger(revenue)) throw new RangeError("revenue exceeds the safe range");
  return mulDiv(bill, BASIS_POINTS_SCALE, revenue, "round");
}
function isRateIncrease(previous, next) {
  return next.platformFeeRials > previous.platformFeeRials || next.unitPriceRials > previous.unitPriceRials || next.capRials > previous.capRials || next.includedQty < previous.includedQty;
}
function raasTrialOffered(rate2) {
  return rate2.trialFreeQty > 0 || rate2.trialFeeWaivedMonths > 0;
}
var RAAS_UPSELL_DEFAULTS = Object.freeze({ thresholdBp: 8e3, months: 2 });
function raasUpsellDecision(closedPeriods, opts = {}) {
  const threshold = opts.thresholdBp ?? RAAS_UPSELL_DEFAULTS.thresholdBp;
  const months = opts.months ?? RAAS_UPSELL_DEFAULTS.months;
  let consecutive = 0;
  for (let i = closedPeriods.length - 1; i >= 0; i -= 1) {
    if ((closedPeriods[i]?.capPercentBp ?? 0) >= threshold) consecutive += 1;
    else break;
  }
  return { upsell: months > 0 && consecutive >= months, consecutive };
}

// src/pricing/terms.ts
var BILLING_TERMS = Object.freeze([1, 6, 12]);
function isBillingTerm(value) {
  return typeof value === "number" && BILLING_TERMS.includes(value);
}
function termPriceRials(plan2, months) {
  switch (months) {
    case 1:
      return wholeRials(plan2.priceRials, "priceRials");
    case 6:
      return plan2.sixMonthPriceRials === null || plan2.sixMonthPriceRials === void 0 ? null : wholeRials(plan2.sixMonthPriceRials, "sixMonthPriceRials");
    case 12:
      return plan2.twelveMonthPriceRials === null || plan2.twelveMonthPriceRials === void 0 ? null : wholeRials(plan2.twelveMonthPriceRials, "twelveMonthPriceRials");
    default:
      return null;
  }
}
function termsOf(plan2) {
  const out = [];
  for (const months of BILLING_TERMS) {
    const price = termPriceRials(plan2, months);
    if (price === null) continue;
    out.push({
      months,
      priceRials: price,
      monthlyEquivalentRials: perMonthEquivalentRials(price, months)
    });
  }
  return out;
}
function perMonthEquivalentRials(priceRials, months) {
  const price = wholeRials(priceRials, "priceRials");
  const m = wholeCount(months, "months");
  if (m === 0) throw new RangeError("months must be at least 1");
  return Math.floor(price / m);
}
function termSavingRials(monthlyRials, priceRials, months) {
  const monthly = wholeRials(monthlyRials, "monthlyRials");
  const price = wholeRials(priceRials, "priceRials");
  const m = wholeCount(months, "months");
  const list = monthly * m;
  if (!Number.isSafeInteger(list)) throw new RangeError("monthly \xD7 months exceeds the safe range");
  return list - price;
}
function termDiscountBp(monthlyRials, priceRials, months) {
  const saving = termSavingRials(monthlyRials, priceRials, months);
  const list = monthlyRials * months;
  if (list === 0) return 0;
  const sign = saving < 0 ? -1 : 1;
  return sign * mulDiv(Math.abs(saving), BASIS_POINTS_SCALE, list, "round");
}
function monthsFreeInTerm(monthlyRials, priceRials, months) {
  const monthly = wholeRials(monthlyRials, "monthlyRials");
  if (monthly === 0) return 0;
  const saving = termSavingRials(monthly, priceRials, months);
  return saving <= 0 ? 0 : Math.floor(saving / monthly);
}
var TERM_PRICE_STEP_RIALS = 1e5;
function termPriceFromPolicy(monthlyRials, months, policy) {
  const monthly = wholeRials(monthlyRials, "monthlyRials");
  switch (months) {
    case 1:
      return monthly;
    case 6: {
      const bp = wholeCount(policy.sixMonthDiscountBp, "sixMonthDiscountBp");
      if (bp > BASIS_POINTS_SCALE) throw new RangeError("sixMonthDiscountBp above 100%");
      const discounted = mulDiv(monthly * 6, BASIS_POINTS_SCALE - bp, BASIS_POINTS_SCALE, "floor");
      return roundRialTo(discounted, TERM_PRICE_STEP_RIALS, "floor");
    }
    case 12: {
      const charged = wholeCount(policy.yearlyMonthsCharged, "yearlyMonthsCharged");
      if (charged < 1 || charged > 12) throw new RangeError("yearlyMonthsCharged must be 1..12");
      return monthly * charged;
    }
    default:
      throw new RangeError(`Not a billing term: ${String(months)}`);
  }
}

// src/pricing/offers.ts
var OFFER_DURATIONS = Object.freeze(["ONCE", "CYCLES", "FOREVER"]);
var OFFER_AUDIENCES = Object.freeze(["PUBLIC", "CODE_ONLY", "RULE", "MANUAL"]);
var OFFER_DISCOUNT_STEP_RIALS = 10;
var DAYS_PER_MONTH = 30;
function hasPriceDiscount(b) {
  return (b.percentOffBp ?? 0) > 0 || (b.amountOffRials ?? 0) > 0;
}
function hasExtras(b) {
  return (b.freeMonths ?? 0) > 0 || (b.trialDays ?? 0) > 0 || (b.smsCreditRials ?? 0) > 0;
}
function offerDiscountRials(priceRials, b) {
  wholeRials(priceRials, "priceRials");
  let discount = 0;
  if ((b.percentOffBp ?? 0) > 0) {
    const raw = mulDiv(priceRials, b.percentOffBp, BASIS_POINTS_SCALE, "floor");
    discount = roundRialTo(raw, OFFER_DISCOUNT_STEP_RIALS, "floor");
    if (b.capRials !== null && b.capRials !== void 0) discount = Math.min(discount, b.capRials);
  } else if ((b.amountOffRials ?? 0) > 0) {
    discount = b.amountOffRials;
  }
  return Math.max(0, Math.min(discount, priceRials));
}
function offerEligibility(c, ctx) {
  const t = c.terms;
  if (!c.active) return { ok: false, reason: "INACTIVE" };
  if (c.expiresAt && c.expiresAt.getTime() <= ctx.now.getTime()) return { ok: false, reason: "EXPIRED" };
  if (c.capReached) return { ok: false, reason: "CAP" };
  if (hasPriceDiscount(t)) {
    if (ctx.billingModel === "RAAS") {
      if (!hasExtras(t)) return { ok: false, reason: "RAAS" };
    } else if (ctx.negotiated) {
      return { ok: false, reason: "NEGOTIATED" };
    }
  }
  if (t.firstPaidOnly && ctx.hasPaidBefore) return { ok: false, reason: "ALREADY_PAID" };
  if (t.planKeys.length > 0 && !t.planKeys.includes(ctx.planKey)) return { ok: false, reason: "PLAN" };
  if (t.termMonths.length > 0 && !t.termMonths.includes(ctx.termMonths)) return { ok: false, reason: "TERM" };
  return { ok: true };
}
function offerValueRials(b, ctx) {
  const discount = ctx.billingModel === "RAAS" ? 0 : offerDiscountRials(ctx.listPriceRials, b);
  const months = (b.freeMonths ?? 0) * ctx.monthlyRials;
  const days = Math.floor((b.trialDays ?? 0) * ctx.monthlyRials / DAYS_PER_MONTH);
  return discount + months + days + (b.smsCreditRials ?? 0);
}
function bestOffer(cands, ctx) {
  const ranked = cands.filter((c) => offerEligibility(c, ctx).ok).map((c) => ({
    candidate: c,
    discountRials: ctx.billingModel === "RAAS" ? 0 : offerDiscountRials(ctx.listPriceRials, c.terms),
    valueRials: offerValueRials(c.terms, ctx)
  })).filter((r) => r.valueRials > 0);
  ranked.sort((a, b) => {
    if (b.valueRials !== a.valueRials) return b.valueRials - a.valueRials;
    const ea = a.candidate.expiresAt?.getTime() ?? Number.POSITIVE_INFINITY;
    const eb = b.candidate.expiresAt?.getTime() ?? Number.POSITIVE_INFINITY;
    if (ea !== eb) return ea - eb;
    if (a.candidate.viaCode !== b.candidate.viaCode) return a.candidate.viaCode ? -1 : 1;
    return a.candidate.offerId < b.candidate.offerId ? -1 : a.candidate.offerId > b.candidate.offerId ? 1 : 0;
  });
  return ranked[0] ?? null;
}
function cyclesAfterFirstPayment(duration, cycles) {
  if (duration === "FOREVER") return null;
  if (duration === "CYCLES") return Math.max(0, (cycles ?? 1) - 1);
  return 0;
}
function appliesAtRenewal(terms, at, cyclesRemaining) {
  if (!hasPriceDiscount(terms) || at.billingModel === "RAAS") return false;
  if (cyclesRemaining !== null && cyclesRemaining <= 0) return false;
  if (terms.planKeys.length > 0 && !terms.planKeys.includes(at.planKey)) return false;
  if (terms.termMonths.length > 0 && !terms.termMonths.includes(at.termMonths)) return false;
  return true;
}
var HOUR_MS = 36e5;
var DAY_MS = 24 * HOUR_MS;
function grantExpiresAt(offer, firstSeenAt) {
  const windowEnd = offer.personalWindowHours && firstSeenAt ? new Date(firstSeenAt.getTime() + offer.personalWindowHours * HOUR_MS) : null;
  if (offer.personalWindowHours && !firstSeenAt) return null;
  if (windowEnd && offer.endsAt) return windowEnd < offer.endsAt ? windowEnd : offer.endsAt;
  return windowEnd ?? offer.endsAt;
}
function signupGrantExpiresAt(offer, trialEndsAt, graceDays) {
  const reserved = new Date(trialEndsAt.getTime() + graceDays * DAY_MS);
  return offer.endsAt && offer.endsAt > reserved ? offer.endsAt : reserved;
}
var CODE_PATTERN = /^[A-Z0-9][A-Z0-9-]{2,31}$/;
function normaliseOfferCode(input) {
  if (!input) return null;
  const code = toEnglishDigits(input).trim().toUpperCase();
  return CODE_PATTERN.test(code) ? code : null;
}

// src/pricing/catalogue.ts
var Reject = class extends Error {
};
function obj(v, at) {
  if (typeof v !== "object" || v === null || Array.isArray(v))
    throw new Reject(`${at}: not an object`);
  return v;
}
function has(o, key, at) {
  if (!(key in o)) throw new Reject(`${at}.${key}: missing`);
  return o[key];
}
function int(v, at, { allowUnlimited = false } = {}) {
  if (typeof v !== "number" || !Number.isSafeInteger(v))
    throw new Reject(`${at}: not a safe integer`);
  if (v < 0 && !(allowUnlimited && v === -1)) throw new Reject(`${at}: negative`);
  return v;
}
function str(v, at) {
  if (typeof v !== "string") throw new Reject(`${at}: not a string`);
  return v;
}
function bool(v, at) {
  if (typeof v !== "boolean") throw new Reject(`${at}: not a boolean`);
  return v;
}
function isoDate(v, at) {
  const s = str(v, at);
  if (Number.isNaN(Date.parse(s))) throw new Reject(`${at}: not a date`);
  return s;
}
function arr(v, at) {
  if (!Array.isArray(v)) throw new Reject(`${at}: not an array`);
  return v;
}
function limits(v, at) {
  const o = obj(v, at);
  const u = { allowUnlimited: true };
  return {
    seats: int(has(o, "seats", at), `${at}.seats`, u),
    files: int(has(o, "files", at), `${at}.files`, u),
    storageMb: int(has(o, "storageMb", at), `${at}.storageMb`, u),
    smsPerMonth: int(has(o, "smsPerMonth", at), `${at}.smsPerMonth`, u),
    importPagesPerMonth: int(has(o, "importPagesPerMonth", at), `${at}.importPagesPerMonth`, u),
    branches: int(has(o, "branches", at), `${at}.branches`, u)
  };
}
function features(v, at) {
  return arr(v, at).filter((k) => isPlanFeatureKey(k));
}
function rate(v, at) {
  const o = obj(v, at);
  const r = {
    feeRials: int(has(o, "feeRials", at), `${at}.feeRials`),
    includedQty: int(has(o, "includedQty", at), `${at}.includedQty`),
    unitRials: int(has(o, "unitRials", at), `${at}.unitRials`),
    capRials: int(has(o, "capRials", at), `${at}.capRials`),
    trialFreeQty: int(has(o, "trialFreeQty", at), `${at}.trialFreeQty`),
    trialFeeWaivedMonths: int(has(o, "trialFeeWaivedMonths", at), `${at}.trialFeeWaivedMonths`),
    voidWindowHours: int(has(o, "voidWindowHours", at), `${at}.voidWindowHours`),
    disputeWindowDays: int(has(o, "disputeWindowDays", at), `${at}.disputeWindowDays`),
    backdateMaxDays: int(has(o, "backdateMaxDays", at), `${at}.backdateMaxDays`),
    effectiveFrom: isoDate(has(o, "effectiveFrom", at), `${at}.effectiveFrom`)
  };
  return r;
}
function plan(v, at) {
  const o = obj(v, at);
  const terms = arr(has(o, "terms", at), `${at}.terms`).map((t, i) => {
    const to = obj(t, `${at}.terms[${String(i)}]`);
    const months = int(has(to, "months", at), `${at}.terms[${String(i)}].months`);
    if (!BILLING_TERMS.includes(months)) {
      throw new Reject(`${at}.terms[${String(i)}].months: unknown term ${String(months)}`);
    }
    return {
      months,
      priceRials: int(has(to, "priceRials", at), `${at}.terms[${String(i)}].priceRials`)
    };
  });
  if (!terms.some((t) => t.months === 1)) throw new Reject(`${at}.terms: no monthly term`);
  const description = has(o, "description", at);
  return {
    key: str(has(o, "key", at), `${at}.key`),
    name: str(has(o, "name", at), `${at}.name`),
    description: description === null ? null : str(description, `${at}.description`),
    isFeatured: bool(has(o, "isFeatured", at), `${at}.isFeatured`),
    sortOrder: int(has(o, "sortOrder", at), `${at}.sortOrder`),
    terms,
    limits: limits(has(o, "limits", at), `${at}.limits`),
    enabledFeatures: features(has(o, "enabledFeatures", at), `${at}.enabledFeatures`)
  };
}
var nullableInt = (v) => v === null ? null : typeof v === "number" && Number.isSafeInteger(v) && v >= 0 ? v : void 0;
function parsePublicOffer(input, options = {}) {
  if (!input || typeof input !== "object") return null;
  const o = input;
  const ints = ["percentOffBp", "capRials", "amountOffRials", "freeMonths", "trialDays", "smsCreditRials", "cycles"];
  const parsed = {};
  for (const k of ints) {
    const v = nullableInt(o[k]);
    if (v === void 0) return null;
    parsed[k] = v;
  }
  if (typeof o.key !== "string" || typeof o.headline !== "string" || typeof o.body !== "string") return null;
  if (!OFFER_DURATIONS.includes(o.duration)) return null;
  if (!Array.isArray(o.planKeys) || !o.planKeys.every((k) => typeof k === "string")) return null;
  if (!Array.isArray(o.termMonths) || !o.termMonths.every((k) => Number.isInteger(k))) return null;
  const openEnd = o.endsAt === null && options.allowOpenEnd === true;
  if (!openEnd && (typeof o.endsAt !== "string" || Number.isNaN(Date.parse(o.endsAt)))) return null;
  return {
    key: o.key,
    headline: o.headline,
    body: o.body,
    percentOffBp: parsed.percentOffBp,
    capRials: parsed.capRials,
    amountOffRials: parsed.amountOffRials,
    freeMonths: parsed.freeMonths,
    trialDays: parsed.trialDays,
    smsCreditRials: parsed.smsCreditRials,
    duration: o.duration,
    cycles: parsed.cycles,
    planKeys: [...o.planKeys],
    termMonths: [...o.termMonths],
    endsAt: openEnd ? null : o.endsAt
  };
}
function parseStrict(input) {
  const o = obj(input, "pricing");
  if (has(o, "currency", "pricing") !== "IRR") throw new Reject("pricing.currency: not IRR");
  if (has(o, "vatInclusive", "pricing") !== true)
    throw new Reject("pricing.vatInclusive: not true");
  const caps = obj(has(o, "capabilities", "pricing"), "pricing.capabilities");
  const ai = obj(has(caps, "aiImport", "capabilities"), "capabilities.aiImport");
  const kinds = arr(has(ai, "kinds", "aiImport"), "aiImport.kinds").map((k, i) => {
    if (typeof k !== "string" || !AI_IMPORT_KINDS.includes(k)) {
      throw new Reject(`aiImport.kinds[${String(i)}]: unknown kind`);
    }
    return k;
  });
  const rb = has(o, "resultBased", "pricing");
  let resultBased = null;
  if (rb !== null) {
    const r = obj(rb, "resultBased");
    const next = has(r, "nextRate", "resultBased");
    resultBased = {
      key: str(has(r, "key", "resultBased"), "resultBased.key"),
      name: str(has(r, "name", "resultBased"), "resultBased.name"),
      limits: limits(has(r, "limits", "resultBased"), "resultBased.limits"),
      enabledFeatures: features(
        has(r, "enabledFeatures", "resultBased"),
        "resultBased.enabledFeatures"
      ),
      rate: rate(has(r, "rate", "resultBased"), "resultBased.rate"),
      nextRate: next === null ? null : rate(next, "resultBased.nextRate")
    };
  }
  const ent = has(o, "enterprise", "pricing");
  return {
    version: str(has(o, "version", "pricing"), "pricing.version"),
    generatedAt: isoDate(has(o, "generatedAt", "pricing"), "pricing.generatedAt"),
    currency: "IRR",
    vatInclusive: true,
    vatRateBp: int(has(o, "vatRateBp", "pricing"), "pricing.vatRateBp"),
    trialDays: int(has(o, "trialDays", "pricing"), "pricing.trialDays"),
    moneyBackDays: int(has(o, "moneyBackDays", "pricing"), "pricing.moneyBackDays"),
    priceChangeNoticeDays: int(
      has(o, "priceChangeNoticeDays", "pricing"),
      "pricing.priceChangeNoticeDays"
    ),
    smsPricePerSegmentRials: int(
      has(o, "smsPricePerSegmentRials", "pricing"),
      "pricing.smsPricePerSegmentRials"
    ),
    capabilities: {
      aiImport: {
        enabled: bool(has(ai, "enabled", "aiImport"), "aiImport.enabled"),
        kinds,
        undoHours: int(has(ai, "undoHours", "aiImport"), "aiImport.undoHours")
      }
    },
    plans: arr(has(o, "plans", "pricing"), "pricing.plans").map(
      (p, i) => plan(p, `pricing.plans[${String(i)}]`)
    ),
    resultBased,
    offer: parsePublicOffer(o.offer),
    nextOfferStartsAt: typeof o.nextOfferStartsAt === "string" && !Number.isNaN(Date.parse(o.nextOfferStartsAt)) ? o.nextOfferStartsAt : null,
    enterprise: ent === null ? null : {
      fromRials: int(
        has(obj(ent, "enterprise"), "fromRials", "enterprise"),
        "enterprise.fromRials"
      )
    }
  };
}
function parsePublicPricing(input) {
  try {
    return parseStrict(input);
  } catch (err) {
    if (err instanceof Reject) return null;
    throw err;
  }
}
function publicPricingProblem(input) {
  try {
    parseStrict(input);
    return null;
  } catch (err) {
    if (err instanceof Reject) return err.message;
    throw err;
  }
}
function rateAt(resultBased, now = /* @__PURE__ */ new Date()) {
  if (!resultBased) return null;
  const next = resultBased.nextRate;
  if (next && Date.parse(next.effectiveFrom) <= now.getTime()) return next;
  return resultBased.rate;
}
function publicTermPrice(plan2, months) {
  return plan2.terms.find((t) => t.months === months)?.priceRials ?? null;
}

// src/pricing/policy.ts
var DEFAULT_MONEY_BACK_DAYS = 60;
var DEFAULT_PRICE_CHANGE_NOTICE_DAYS = 30;
var DEFAULT_TRIAL_DAYS = 14;
var CALCULATOR_MAX_PATIENTS = 300;
var CALCULATOR_DEFAULT_PATIENTS = 40;
var DEFAULT_OFFER_GRACE_DAYS = 3;

// src/pricing/launch-catalogue.ts
var EVERY_PLAN = Object.freeze({
  appointments: true,
  patients: true,
  sessions: true,
  consents: true,
  reminders: true,
  photos: true,
  directoryListing: true,
  publicBooking: true,
  /** SIMPLE stock (stock + consumption, no lots/FEFO) is on every plan (D6). */
  inventory: true
});
var BASIC_FEATURES = Object.freeze({
  ...EVERY_PLAN,
  lotInventory: false,
  accounting: false,
  campaigns: false,
  loyalty: false,
  aiImport: false,
  payroll: false,
  revenueShare: false,
  apiAccess: false
});
var PRO_FEATURES = Object.freeze({
  ...BASIC_FEATURES,
  lotInventory: true,
  accounting: true,
  campaigns: true,
  loyalty: true,
  aiImport: true
});
var CLINIC_FEATURES = Object.freeze({
  ...PRO_FEATURES,
  payroll: true,
  revenueShare: true,
  apiAccess: true
});
var LAUNCH_PRICING = Object.freeze({
  version: "1405-08",
  plans: Object.freeze([
    Object.freeze({
      key: "basic",
      name: "\u0645\u0637\u0628",
      description: "\u0628\u0631\u0627\u06CC \u06A9\u0627\u0631\u0647\u0627\u06CC \u0631\u0648\u0632\u0645\u0631\u0647\u0654 \u0645\u0637\u0628\u200C\u0647\u0627\u06CC \u06A9\u0648\u0686\u06A9 \u0648 \u062A\u06A9\u200C\u067E\u0632\u0634\u06A9: \u0646\u0648\u0628\u062A\u200C\u062F\u0647\u06CC\u060C \u067E\u0631\u0648\u0646\u062F\u0647 \u0628\u06CC\u0645\u0627\u0631\u060C \u062B\u0628\u062A \u062C\u0644\u0633\u0647 \u062F\u0631\u0645\u0627\u0646\u060C \u06CC\u0627\u062F\u0622\u0648\u0631\u06CC \u067E\u06CC\u0627\u0645\u06A9\u06CC \u0648 \u0627\u0646\u0628\u0627\u0631 \u0633\u0627\u062F\u0647.",
      billingModel: "FLAT",
      interval: "MONTHLY",
      visibility: "PUBLIC",
      priceRials: 199e5,
      sixMonthPriceRials: 1134e5,
      twelveMonthPriceRials: 2189e5,
      trialDays: DEFAULT_TRIAL_DAYS,
      maxStaffSeats: 3,
      maxPatients: 1500,
      maxStorageMb: 5120,
      includedSms: 400,
      maxLocations: 1,
      aiImportPagesPerMonth: 0,
      features: BASIC_FEATURES,
      sortOrder: 10,
      isFeatured: false
    }),
    Object.freeze({
      key: "pro",
      name: "\u062D\u0631\u0641\u0647\u200C\u0627\u06CC",
      description: "\u0647\u0645\u0647\u0654 \u0627\u0645\u06A9\u0627\u0646\u0627\u062A \u067E\u0644\u0646 \u0645\u0637\u0628\u060C \u0647\u0645\u0631\u0627\u0647 \u0628\u0627 \u062D\u0633\u0627\u0628\u062F\u0627\u0631\u06CC\u060C \u06AF\u0632\u0627\u0631\u0634 \u0633\u0648\u062F \u0648 \u0632\u06CC\u0627\u0646\u060C \u067E\u06CC\u06AF\u06CC\u0631\u06CC \u0633\u0631\u06CC \u0633\u0627\u062E\u062A \u0648 \u0627\u0646\u0642\u0636\u0627\u06CC \u0645\u0648\u0627\u062F\u060C \u067E\u06CC\u0627\u0645\u06A9 \u06AF\u0631\u0648\u0647\u06CC\u060C \u0628\u0627\u0634\u06AF\u0627\u0647 \u0645\u0634\u062A\u0631\u06CC\u0627\u0646 \u0648 \u062B\u0628\u062A \u0627\u0637\u0644\u0627\u0639\u0627\u062A \u0627\u0632 \u0631\u0648\u06CC \u0639\u06A9\u0633.",
      billingModel: "FLAT",
      interval: "MONTHLY",
      visibility: "PUBLIC",
      priceRials: 9999e4,
      sixMonthPriceRials: 5699e5,
      twelveMonthPriceRials: 109989e4,
      trialDays: DEFAULT_TRIAL_DAYS,
      maxStaffSeats: 8,
      maxPatients: 1e4,
      maxStorageMb: 15360,
      includedSms: 1500,
      maxLocations: 1,
      aiImportPagesPerMonth: 100,
      features: PRO_FEATURES,
      sortOrder: 20,
      isFeatured: true
    }),
    Object.freeze({
      key: "clinic",
      name: "\u06A9\u0644\u06CC\u0646\u06CC\u06A9",
      description: "\u0628\u0631\u0627\u06CC \u06A9\u0644\u06CC\u0646\u06CC\u06A9\u200C\u0647\u0627\u06CC\u06CC \u0628\u0627 \u0686\u0646\u062F \u067E\u0632\u0634\u06A9: \u062A\u0639\u062F\u0627\u062F \u0646\u0627\u0645\u062D\u062F\u0648\u062F \u0628\u06CC\u0645\u0627\u0631 \u0648 \u06A9\u0627\u0631\u0628\u0631\u060C \u0645\u062D\u0627\u0633\u0628\u0647\u0654 \u062D\u0642\u0648\u0642 \u0648 \u0633\u0647\u0645 \u067E\u0632\u0634\u06A9\u0627\u0646\u060C \u0645\u062F\u06CC\u0631\u06CC\u062A \u0634\u0639\u0628\u0647\u200C\u0647\u0627 \u0648 \u0627\u062A\u0635\u0627\u0644 \u0627\u0632 \u0637\u0631\u06CC\u0642 API \u0648 \u0648\u0628\u200C\u0647\u0648\u06A9.",
      billingModel: "FLAT",
      interval: "MONTHLY",
      visibility: "PUBLIC",
      priceRials: 19999e4,
      sixMonthPriceRials: 11399e5,
      twelveMonthPriceRials: 219989e4,
      trialDays: DEFAULT_TRIAL_DAYS,
      maxStaffSeats: -1,
      maxPatients: -1,
      maxStorageMb: 61440,
      includedSms: 4e3,
      maxLocations: 3,
      aiImportPagesPerMonth: 300,
      features: CLINIC_FEATURES,
      sortOrder: 30,
      isFeatured: false
    }),
    Object.freeze({
      key: "raas",
      name: "\u0646\u062A\u06CC\u062C\u0647\u200C\u0645\u062D\u0648\u0631",
      description: "\u062A\u0639\u062F\u0627\u062F \u0645\u0634\u062E\u0635\u06CC \u0628\u06CC\u0645\u0627\u0631 \u062F\u0631 \u0647\u0632\u06CC\u0646\u0647\u0654 \u062B\u0627\u0628\u062A \u0645\u0627\u0647\u0627\u0646\u0647 \u062D\u0633\u0627\u0628 \u0645\u06CC\u200C\u0634\u0646. \u0628\u0639\u062F \u0627\u0632 \u0627\u0648\u0646\u060C \u0628\u0647\u200C\u0627\u0632\u0627\u06CC \u0647\u0631 \u0628\u06CC\u0645\u0627\u0631 \u062F\u0631\u0645\u0627\u0646\u200C\u0634\u062F\u0647 \u0647\u0632\u06CC\u0646\u0647 \u0645\u06CC\u200C\u062F\u06CC\u062F\u061B \u0645\u062C\u0645\u0648\u0639 \u0647\u0632\u06CC\u0646\u0647 \u0627\u0632 \u0633\u0642\u0641 \u0645\u0627\u0647\u0627\u0646\u0647 \u0628\u0627\u0644\u0627\u062A\u0631 \u0646\u0645\u06CC\u200C\u0631\u0647.",
      billingModel: "RAAS",
      interval: "MONTHLY",
      visibility: "PUBLIC",
      /** Never read: a RaaS month is priced by its ResultRate. */
      priceRials: 0,
      sixMonthPriceRials: null,
      twelveMonthPriceRials: null,
      trialDays: DEFAULT_TRIAL_DAYS,
      maxStaffSeats: 3,
      maxPatients: -1,
      maxStorageMb: 3072,
      includedSms: 200,
      maxLocations: 1,
      aiImportPagesPerMonth: 0,
      features: BASIC_FEATURES,
      sortOrder: 40,
      isFeatured: false
    })
  ]),
  resultRate: Object.freeze({
    planKey: "raas",
    platformFeeRials: 69e5,
    includedQty: 10,
    unitPriceRials: 49e4,
    capRials: 199e5,
    trialFreeQty: 0,
    trialFeeWaivedMonths: 0,
    voidWindowHours: 72,
    disputeWindowDays: 7,
    backdateMaxDays: 30,
    reviewGapBp: 1e3
  }),
  policy: Object.freeze({
    sixMonthDiscountBp: 500,
    yearlyMonthsCharged: 11,
    moneyBackDays: DEFAULT_MONEY_BACK_DAYS,
    priceChangeNoticeDays: DEFAULT_PRICE_CHANGE_NOTICE_DAYS,
    trialDays: DEFAULT_TRIAL_DAYS,
    enterpriseFromRials: null,
    defaultTrialPlanKey: "pro",
    offerGraceDays: DEFAULT_OFFER_GRACE_DAYS
  })
});
function launchPlan(key) {
  const plan2 = LAUNCH_PRICING.plans.find((p) => p.key === key);
  if (!plan2) throw new Error(`No launch plan "${key}"`);
  return plan2;
}
function publicLimits(plan2) {
  return {
    seats: plan2.maxStaffSeats,
    files: plan2.maxPatients,
    storageMb: plan2.maxStorageMb,
    smsPerMonth: plan2.includedSms,
    importPagesPerMonth: plan2.features.aiImport === true ? plan2.aiImportPagesPerMonth : 0,
    branches: plan2.maxLocations
  };
}
function enabled(plan2) {
  return Object.entries(effectivePlanFeatures(plan2)).filter(([key, on]) => on && isPlanFeatureKey(key)).map(([key]) => key);
}
var LAUNCH_RATE_EFFECTIVE_FROM = jalaliMonthRangeOfKey(
  LAUNCH_PRICING.version
).start.toISOString();
function launchPublicPricing(opts = {}) {
  const policy = LAUNCH_PRICING.policy;
  const flat = LAUNCH_PRICING.plans.filter((p) => p.billingModel === "FLAT").slice().sort((a, b) => a.sortOrder - b.sortOrder);
  const plans = flat.map((p) => ({
    key: p.key,
    name: p.name,
    description: p.description,
    isFeatured: p.isFeatured,
    sortOrder: p.sortOrder,
    terms: termsOf(p).map((t) => ({ months: t.months, priceRials: t.priceRials })),
    limits: publicLimits(p),
    enabledFeatures: enabled(p)
  }));
  const raas = LAUNCH_PRICING.plans.find((p) => p.key === LAUNCH_PRICING.resultRate.planKey);
  const r = LAUNCH_PRICING.resultRate;
  const rate2 = {
    feeRials: r.platformFeeRials,
    includedQty: r.includedQty,
    unitRials: r.unitPriceRials,
    capRials: r.capRials,
    trialFreeQty: r.trialFreeQty,
    trialFeeWaivedMonths: r.trialFeeWaivedMonths,
    voidWindowHours: r.voidWindowHours,
    disputeWindowDays: r.disputeWindowDays,
    backdateMaxDays: r.backdateMaxDays,
    effectiveFrom: LAUNCH_RATE_EFFECTIVE_FROM
  };
  return {
    version: `launch-${LAUNCH_PRICING.version}`,
    generatedAt: (opts.generatedAt ?? new Date(LAUNCH_RATE_EFFECTIVE_FROM)).toISOString(),
    currency: "IRR",
    vatInclusive: true,
    vatRateBp: opts.vatRateBp ?? 0,
    trialDays: policy.trialDays,
    moneyBackDays: policy.moneyBackDays,
    priceChangeNoticeDays: policy.priceChangeNoticeDays,
    smsPricePerSegmentRials: opts.smsPricePerSegmentRials ?? 5e3,
    capabilities: {
      aiImport: opts.aiImport ?? {
        enabled: true,
        kinds: [...AI_IMPORT_KINDS_ENABLED],
        undoHours: 24
      }
    },
    plans,
    resultBased: raas ? {
      key: raas.key,
      name: raas.name,
      limits: publicLimits(raas),
      enabledFeatures: enabled(raas),
      rate: rate2,
      nextRate: null
    } : null,
    offer: null,
    nextOfferStartsAt: null,
    enterprise: policy.enterpriseFromRials === null ? null : { fromRials: policy.enterpriseFromRials }
  };
}

// src/clinic-api.ts
var CLINIC_API_KEY_PREFIX = "bk_live_";
var PUBLIC_ID_RE = /^[a-z2-7]{16}$/;
var SECRET_RE = /^[A-Za-z0-9_-]{43}$/;
function parseClinicApiKey(raw) {
  if (typeof raw !== "string" || !raw.startsWith(CLINIC_API_KEY_PREFIX)) return null;
  const rest = raw.slice(CLINIC_API_KEY_PREFIX.length);
  const cut = rest.indexOf("_");
  if (cut !== 16) return null;
  const publicId = rest.slice(0, cut);
  const secret = rest.slice(cut + 1);
  if (!PUBLIC_ID_RE.test(publicId) || !SECRET_RE.test(secret)) return null;
  return { publicId, secret };
}
function clinicApiKeyDisplayPrefix(publicId) {
  return `${CLINIC_API_KEY_PREFIX}${publicId}`;
}
var ClinicApiScope = {
  CATALOG_READ: "catalog.read",
  AVAILABILITY_READ: "availability.read",
  APPOINTMENTS_READ: "appointments.read",
  APPOINTMENTS_WRITE: "appointments.write",
  PATIENTS_READ: "patients.read",
  PATIENTS_WRITE: "patients.write"
};
var CLINIC_API_SCOPES = Object.freeze([
  {
    key: ClinicApiScope.CATALOG_READ,
    labelFa: "\u062E\u062F\u0645\u0627\u062A\u060C \u0642\u06CC\u0645\u062A\u200C\u0647\u0627 \u0648 \u067E\u0632\u0634\u06A9\u0627\u0646",
    labelEn: "Services, prices and doctors",
    descriptionFa: "\u0641\u0647\u0631\u0633\u062A \u062E\u062F\u0645\u0627\u062A \u0641\u0639\u0627\u0644 \u0628\u0627 \u0645\u062F\u062A \u0648 \u0642\u06CC\u0645\u062A\u060C \u0648 \u067E\u0632\u0634\u06A9\u0627\u0646 \u0641\u0639\u0627\u0644 \u06A9\u0644\u06CC\u0646\u06CC\u06A9.",
    impliedPermissions: [Permission.PROCEDURE_VIEW],
    requires: [],
    write: false
  },
  {
    key: ClinicApiScope.AVAILABILITY_READ,
    labelFa: "\u0632\u0645\u0627\u0646\u200C\u0647\u0627\u06CC \u0622\u0632\u0627\u062F",
    labelEn: "Availability",
    descriptionFa: "\u0632\u0645\u0627\u0646\u200C\u0647\u0627\u06CC \u0622\u0632\u0627\u062F \u0628\u0631\u0627\u06CC \u0646\u0648\u0628\u062A\u200C\u062F\u0647\u06CC\u060C \u0628\u0631 \u0627\u0633\u0627\u0633 \u0633\u0627\u0639\u0627\u062A \u06A9\u0627\u0631\u06CC \u0648 \u0646\u0648\u0628\u062A\u200C\u0647\u0627\u06CC \u062B\u0628\u062A\u200C\u0634\u062F\u0647.",
    impliedPermissions: [Permission.APPOINTMENT_VIEW],
    requires: [],
    write: false
  },
  {
    key: ClinicApiScope.APPOINTMENTS_READ,
    labelFa: "\u0645\u0634\u0627\u0647\u062F\u0647 \u0646\u0648\u0628\u062A\u200C\u0647\u0627",
    labelEn: "Read appointments",
    descriptionFa: "\u0646\u0648\u0628\u062A\u200C\u0647\u0627\u06CC \u06A9\u0644\u06CC\u0646\u06CC\u06A9 \u0628\u0627 \u0632\u0645\u0627\u0646\u060C \u0648\u0636\u0639\u06CC\u062A\u060C \u067E\u0632\u0634\u06A9 \u0648 \u062E\u062F\u0645\u062A\u061B \u0628\u062F\u0648\u0646 \u06CC\u0627\u062F\u062F\u0627\u0634\u062A \u0648 \u0645\u0628\u0644\u063A.",
    impliedPermissions: [Permission.APPOINTMENT_VIEW, Permission.APPOINTMENT_VIEW_ALL],
    requires: [],
    write: false
  },
  {
    key: ClinicApiScope.APPOINTMENTS_WRITE,
    labelFa: "\u062B\u0628\u062A\u060C \u0644\u063A\u0648 \u0648 \u062C\u0627\u0628\u0647\u200C\u062C\u0627\u06CC\u06CC \u0646\u0648\u0628\u062A",
    labelEn: "Book, cancel and reschedule",
    descriptionFa: "\u062B\u0628\u062A \u0646\u0648\u0628\u062A \u062C\u062F\u06CC\u062F \u062F\u0631 \u0632\u0645\u0627\u0646 \u062E\u0627\u0644\u06CC\u060C \u0644\u063A\u0648 \u0648 \u062C\u0627\u0628\u0647\u200C\u062C\u0627\u06CC\u06CC \u0646\u0648\u0628\u062A.",
    impliedPermissions: [
      Permission.APPOINTMENT_CREATE,
      Permission.APPOINTMENT_EDIT,
      Permission.APPOINTMENT_CANCEL
    ],
    requires: [ClinicApiScope.APPOINTMENTS_READ, ClinicApiScope.AVAILABILITY_READ],
    write: true
  },
  {
    key: ClinicApiScope.PATIENTS_READ,
    labelFa: "\u062C\u0633\u062A\u200C\u0648\u062C\u0648 \u0648 \u0645\u0634\u0627\u0647\u062F\u0647 \u0645\u0634\u062E\u0635\u0627\u062A \u067E\u0627\u06CC\u0647 \u0628\u06CC\u0645\u0627\u0631",
    labelEn: "Find and read basic patient details",
    descriptionFa: "\u062C\u0633\u062A\u200C\u0648\u062C\u0648\u06CC \u0628\u06CC\u0645\u0627\u0631 \u0628\u0627 \u062A\u0637\u0628\u06CC\u0642 \u06A9\u0627\u0645\u0644 \u0634\u0645\u0627\u0631\u0647 \u0645\u0648\u0628\u0627\u06CC\u0644 \u06CC\u0627 \u06A9\u062F \u0645\u0644\u06CC \u0648 \u0645\u0634\u0627\u0647\u062F\u0647 \u0646\u0627\u0645 \u0648 \u0634\u0645\u0627\u0631\u0647 \u062A\u0645\u0627\u0633\u061B \u0628\u062F\u0648\u0646 \u0633\u0648\u0627\u0628\u0642 \u067E\u0632\u0634\u06A9\u06CC.",
    impliedPermissions: [Permission.PATIENT_VIEW, Permission.PATIENT_VIEW_ALL],
    requires: [],
    write: false
  },
  {
    key: ClinicApiScope.PATIENTS_WRITE,
    labelFa: "\u062B\u0628\u062A \u0628\u06CC\u0645\u0627\u0631 \u062C\u062F\u06CC\u062F",
    labelEn: "Create patients",
    descriptionFa: "\u062B\u0628\u062A \u067E\u0631\u0648\u0646\u062F\u0647 \u062C\u062F\u06CC\u062F \u0628\u0627 \u0646\u0627\u0645 \u0648 \u0634\u0645\u0627\u0631\u0647 \u0645\u0648\u0628\u0627\u06CC\u0644.",
    impliedPermissions: [Permission.PATIENT_CREATE],
    requires: [ClinicApiScope.PATIENTS_READ],
    write: true
  }
]);
var SCOPE_BY_KEY = new Map(
  CLINIC_API_SCOPES.map((s) => [s.key, s])
);
function isClinicApiScope(value) {
  return typeof value === "string" && SCOPE_BY_KEY.has(value);
}
function sanitizeClinicApiScopes(values) {
  const wanted = /* @__PURE__ */ new Set();
  const visit = (scope) => {
    if (wanted.has(scope)) return;
    wanted.add(scope);
    for (const req of SCOPE_BY_KEY.get(scope)?.requires ?? []) visit(req);
  };
  for (const value of values ?? []) if (isClinicApiScope(value)) visit(value);
  return CLINIC_API_SCOPES.map((s) => s.key).filter((k) => wanted.has(k));
}
function impliedPermissionsFor(scopes) {
  const out = [];
  for (const scope of sanitizeClinicApiScopes(scopes)) {
    for (const p of SCOPE_BY_KEY.get(scope)?.impliedPermissions ?? []) out.push(p);
  }
  return sanitizePermissions(out);
}
var WEBHOOK_EVENT_TYPES = Object.freeze([
  "appointment.created",
  "appointment.confirmed",
  "appointment.rescheduled",
  "appointment.cancelled",
  "appointment.no_show",
  "appointment_request.created"
]);
function isWebhookEventType(value) {
  return typeof value === "string" && WEBHOOK_EVENT_TYPES.includes(value);
}
var WEBHOOK_EVENT_LABELS_FA = Object.freeze({
  "appointment.created": "\u062B\u0628\u062A \u0646\u0648\u0628\u062A",
  "appointment.confirmed": "\u062A\u0623\u06CC\u06CC\u062F \u0646\u0648\u0628\u062A",
  "appointment.rescheduled": "\u062C\u0627\u0628\u0647\u200C\u062C\u0627\u06CC\u06CC \u0646\u0648\u0628\u062A",
  "appointment.cancelled": "\u0644\u063A\u0648 \u0646\u0648\u0628\u062A",
  "appointment.no_show": "\u0639\u062F\u0645 \u0645\u0631\u0627\u062C\u0639\u0647 \u0628\u06CC\u0645\u0627\u0631",
  "appointment_request.created": "\u062F\u0631\u062E\u0648\u0627\u0633\u062A \u0646\u0648\u0628\u062A \u062C\u062F\u06CC\u062F"
});
var WEBHOOK_EVENTS_IMPLIED_PERMISSIONS = Object.freeze([
  Permission.APPOINTMENT_VIEW,
  Permission.APPOINTMENT_VIEW_ALL
]);
var CLINIC_API_VERSION = "2026-10-01";
var CLINIC_API_DEFAULT_LIMITS = Object.freeze({
  perKeyPerMinute: 120,
  perClinicPerMinute: 300,
  writesPerKeyPerMinute: 30,
  lookupsPerKeyPerMinute: 30,
  maxActiveKeysPerClinic: 10,
  maxWebhookEndpointsPerClinic: 5
});

// src/ui-terms.ts
var UI_TERMS = Object.freeze({
  appointment: "\u0646\u0648\u0628\u062A",
  appointmentRequest: "\u062F\u0631\u062E\u0648\u0627\u0633\u062A \u0646\u0648\u0628\u062A",
  waitlist: "\u0641\u0647\u0631\u0633\u062A \u0627\u0646\u062A\u0638\u0627\u0631",
  noShow: "\u0639\u062F\u0645 \u0645\u0631\u0627\u062C\u0639\u0647",
  treatmentSession: "\u062C\u0644\u0633\u0647 \u062F\u0631\u0645\u0627\u0646",
  patient: "\u0628\u06CC\u0645\u0627\u0631",
  patients: "\u0628\u06CC\u0645\u0627\u0631\u0627\u0646",
  patientFile: "\u067E\u0631\u0648\u0646\u062F\u0647",
  patientFileLong: "\u067E\u0631\u0648\u0646\u062F\u0647 \u0628\u06CC\u0645\u0627\u0631",
  patientApp: "\u0627\u067E \u0645\u0631\u0627\u062C\u0639\u0627\u0646",
  clinic: "\u06A9\u0644\u06CC\u0646\u06CC\u06A9",
  staff: "\u06A9\u0627\u0631\u06A9\u0646\u0627\u0646",
  permissions: "\u0633\u0637\u062D \u062F\u0633\u062A\u0631\u0633\u06CC",
  loginCode: "\u06A9\u062F \u0648\u0631\u0648\u062F",
  twoFactorAuth: "\u062A\u0623\u06CC\u06CC\u062F \u062F\u0648\u0645\u0631\u062D\u0644\u0647\u200C\u0627\u06CC",
  review: "\u0646\u0638\u0631",
  reviews: "\u0646\u0638\u0631\u0647\u0627",
  photo: "\u0639\u06A9\u0633",
  beforeAfterPhotos: "\u0639\u06A9\u0633\u200C\u0647\u0627\u06CC \u0642\u0628\u0644 \u0648 \u0628\u0639\u062F",
  link: "\u0644\u06CC\u0646\u06A9",
  onlineBooking: "\u0646\u0648\u0628\u062A\u200C\u062F\u0647\u06CC \u0622\u0646\u0644\u0627\u06CC\u0646",
  subscription: "\u0627\u0634\u062A\u0631\u0627\u06A9",
  plan: "\u067E\u0644\u0646",
  fixedMonthlyFee: "\u0647\u0632\u06CC\u0646\u0647 \u062B\u0627\u0628\u062A \u0645\u0627\u0647\u0627\u0646\u0647",
  smsCredit: "\u0627\u0639\u062A\u0628\u0627\u0631 \u067E\u06CC\u0627\u0645\u06A9",
  subscriptionWallet: "\u06A9\u06CC\u0641 \u067E\u0648\u0644 \u0627\u0634\u062A\u0631\u0627\u06A9",
  subscriptionInvoice: "\u0635\u0648\u0631\u062A\u200C\u062D\u0633\u0627\u0628",
  purchaseInvoice: "\u0641\u0627\u06A9\u062A\u0648\u0631 \u062E\u0631\u06CC\u062F",
  package: "\u0628\u0633\u062A\u0647 \u062E\u062F\u0645\u0627\u062A",
  packages: "\u0628\u0633\u062A\u0647\u200C\u0647\u0627\u06CC \u062E\u062F\u0645\u0627\u062A",
  batch: "\u0633\u0631\u06CC \u0633\u0627\u062E\u062A",
  batchNumber: "\u0634\u0645\u0627\u0631\u0647\u0654 \u0633\u0631\u06CC \u0633\u0627\u062E\u062A",
  segment: "\u06AF\u0631\u0648\u0647",
  segmentLong: "\u06AF\u0631\u0648\u0647 \u0628\u06CC\u0645\u0627\u0631\u0627\u0646",
  campaign: "\u067E\u06CC\u0627\u0645\u06A9 \u06AF\u0631\u0648\u0647\u06CC",
  lead: "\u062F\u0631\u062E\u0648\u0627\u0633\u062A \u0645\u0634\u0627\u0648\u0631\u0647",
  blacklisted: "\u0645\u062D\u062F\u0648\u062F\u0634\u062F\u0647",
  archived: "\u0628\u0627\u06CC\u06AF\u0627\u0646\u06CC\u200C\u0634\u062F\u0647",
  recycleBin: "\u0633\u0637\u0644 \u0628\u0627\u0632\u06CC\u0627\u0641\u062A",
  customProcedure: "\u062E\u062F\u0645\u062A \u0627\u062E\u062A\u0635\u0627\u0635\u06CC",
  photoImport: "\u062B\u0628\u062A \u0627\u0637\u0644\u0627\u0639\u0627\u062A \u0627\u0632 \u0631\u0648\u06CC \u0639\u06A9\u0633",
  suggestions: "\u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u0647\u0627",
  help: "\u0631\u0627\u0647\u0646\u0645\u0627",
  press: "\u0628\u0632\u0646\u06CC\u062F"
});

// src/offers/segments.ts
var OFFER_SEGMENTS = Object.freeze([
  "TRIAL_DAY_AT_LEAST",
  "TRIAL_ENDING_WITHIN",
  "TRIAL_EXPIRED_UNPAID_WITHIN",
  "CHURNED_WITHIN",
  "PAST_DUE",
  "ON_PLAN_FOR"
]);
var DAY_MS2 = 864e5;
var MONTH_MS = 30 * DAY_MS2;
var NEEDS_DAYS = /* @__PURE__ */ new Set([
  "TRIAL_DAY_AT_LEAST",
  "TRIAL_ENDING_WITHIN",
  "TRIAL_EXPIRED_UNPAID_WITHIN",
  "CHURNED_WITHIN"
]);
var posInt = (v, max) => typeof v === "number" && Number.isInteger(v) && v >= 1 && v <= max ? v : null;
function parseAudienceRule(input) {
  if (!input || typeof input !== "object") return null;
  const o = input;
  if (!OFFER_SEGMENTS.includes(o.segment)) return null;
  const segment = o.segment;
  if (NEEDS_DAYS.has(segment)) {
    const days = posInt(o.days, 365);
    return days === null ? null : { segment, days };
  }
  if (segment === "ON_PLAN_FOR") {
    const months = posInt(o.months, 60);
    const planKeys = Array.isArray(o.planKeys) && o.planKeys.every((k) => typeof k === "string") ? o.planKeys : null;
    if (months === null || !planKeys || planKeys.length === 0) return null;
    return { segment, months, planKeys };
  }
  return { segment };
}
function segmentStatuses(rule) {
  switch (rule.segment) {
    case "TRIAL_DAY_AT_LEAST":
    case "TRIAL_ENDING_WITHIN":
      return ["TRIALING"];
    case "TRIAL_EXPIRED_UNPAID_WITHIN":
      return ["EXPIRED", "CANCELED"];
    case "CHURNED_WITHIN":
      return ["CANCELED", "EXPIRED"];
    case "PAST_DUE":
      return ["PAST_DUE"];
    case "ON_PLAN_FOR":
      return ["ACTIVE"];
  }
}
function segmentMatches(rule, s, now) {
  if (!segmentStatuses(rule).includes(s.status)) return false;
  const t = now.getTime();
  const days = (rule.days ?? 0) * DAY_MS2;
  switch (rule.segment) {
    case "TRIAL_DAY_AT_LEAST":
      return !!s.trialStartedAt && t - s.trialStartedAt.getTime() >= days;
    case "TRIAL_ENDING_WITHIN":
      return !!s.trialEndsAt && s.trialEndsAt.getTime() > t && s.trialEndsAt.getTime() - t <= days;
    case "TRIAL_EXPIRED_UNPAID_WITHIN": {
      const ended = s.endedAt ?? s.trialEndsAt;
      return !s.hasPaid && !!ended && ended.getTime() <= t && t - ended.getTime() <= days;
    }
    case "CHURNED_WITHIN":
      return s.hasPaid && !!s.endedAt && t - s.endedAt.getTime() <= days;
    case "PAST_DUE":
      return true;
    case "ON_PLAN_FOR":
      return !!s.planKey && (rule.planKeys ?? []).includes(s.planKey) && !!s.paidSince && t - s.paidSince.getTime() >= (rule.months ?? 0) * MONTH_MS;
  }
}

// src/offers/copy.ts
var OFFER_COPY_TOKENS = Object.freeze([
  "percent",
  "amount",
  "cap",
  "freeMonths",
  "trialDays",
  "smsCredit",
  "endsAt",
  "cycles"
]);
var ANY_DIGIT = /[0-9۰-۹٠-٩]/;
var TOKEN = /\{([a-zA-Z]+)\}/g;
function offerCopyProblem(text2) {
  if (ANY_DIGIT.test(text2)) return "DIGITS";
  for (const m of text2.matchAll(TOKEN)) {
    if (!OFFER_COPY_TOKENS.includes(m[1])) return "UNKNOWN_TOKEN";
  }
  return null;
}
function splitOfferCopy(text2) {
  const out = [];
  let last = 0;
  for (const m of text2.matchAll(TOKEN)) {
    const at = m.index ?? 0;
    if (at > last) out.push({ kind: "text", value: text2.slice(last, at) });
    if (OFFER_COPY_TOKENS.includes(m[1])) out.push({ kind: "token", token: m[1] });
    else out.push({ kind: "text", value: m[0] });
    last = at + m[0].length;
  }
  if (last < text2.length) out.push({ kind: "text", value: text2.slice(last) });
  return out;
}

export { ACQUISITION_SOURCE_LABELS_FA, ACTIVE_APPOINTMENT_STATUSES, AI_IMPORT_CONSENT_VERSION, AI_IMPORT_FIELDS, AI_IMPORT_FIELD_TYPES, AI_IMPORT_GROUP_KINDS, AI_IMPORT_GROUP_NAME_MAX, AI_IMPORT_ISSUE_CODES, AI_IMPORT_ISSUE_COPY_FA, AI_IMPORT_ISSUE_SEVERITY, AI_IMPORT_JOB_STATUSES, AI_IMPORT_KINDS, AI_IMPORT_KINDS_ENABLED, AI_IMPORT_KIND_DOCTOR_ALTERNATIVE, AI_IMPORT_KIND_LABEL_FA, AI_IMPORT_KIND_NOT_DOCTOR_FA, AI_IMPORT_KIND_PLAN_FEATURE, AI_IMPORT_KIND_REQUIREMENTS, AI_IMPORT_LIMITS, AI_IMPORT_LOOKS_LIKE_PURCHASE_WITH_INVOICE_FA, AI_IMPORT_NO_BATCH_LABEL_FA, AI_IMPORT_OPTION_REQUIREMENTS, AI_IMPORT_PAGE_ERROR_CODES, AI_IMPORT_PAGE_ERROR_COPY_FA, AI_IMPORT_PAGE_STATUSES, AI_IMPORT_PROVIDER_ABUSE_RETENTION_DAYS, AI_IMPORT_QUOTA_SOURCES, AI_IMPORT_RESOLUTION_ACTIONS, AI_IMPORT_ROW_STATUSES, AI_IMPORT_TRANSIENT_PAGE_ERROR_CODES, AI_IMPORT_UNDOABLE_KINDS, ALL_PERMISSIONS, AMOUNT_UNITS, AMOUNT_UNIT_LABELS_FA, ANALYTICS_ONLY_EVENT_TYPES, APPOINTMENT_SOURCE_LABELS_FA, APPOINTMENT_STATUS_LABELS_FA, AcquisitionSource, AnnouncementAudience, AnnouncementSeverity, AppointmentRequestStatus, AppointmentSource, AppointmentStatus, BASIS_POINTS_SCALE, BILLING_TERMS, BLOCK_DIRECTIVE_LINE, BLOG_AUTO_PUBLISH_MODES, BLOG_AUTO_PUBLISH_MODE_HINTS_FA, BLOG_AUTO_PUBLISH_MODE_LABELS_FA, BLOG_BLOCK_KINDS, BLOG_POST_QUEUES, BLOG_POST_QUEUE_HINTS_FA, BLOG_POST_QUEUE_LABELS_FA, BLOG_POST_STATUS_LABELS_FA, BODY_MAP_TYPE_LABELS_FA, BlogPostStatus, BlogTopicStatus, BodyMapType, CALCULATOR_DEFAULT_PATIENTS, CALCULATOR_MAX_PATIENTS, CALLOUT_TONES, CAMPAIGN_STATUS_LABELS_FA, CITIES, CITIES_WITH_DISTRICTS, CLIENT_EVENT_KINDS, CLIENT_EVENT_TYPES, CLINIC_API_DEFAULT_LIMITS, CLINIC_API_KEY_PREFIX, CLINIC_API_SCOPES, CLINIC_API_VERSION, CLINIC_STATUS_LABELS_FA, CLINIC_TYPE_LABELS_FA, CONSENT_METHOD_LABELS_FA, CONTAINER_BLOCK_KINDS, CampaignStatus, ClinicApiScope, ClinicStatus, ClinicType, ConsentMethod, ConsentStatus, ContactMessageStatus, DECIMAL_SEPARATOR, DECLINES_BEFORE_PAUSE, DECLINE_PAUSE_DAYS, DEFAULT_BLOG_POST_QUEUE, DEFAULT_LEAD_DAYS, DEFAULT_MONEY_BACK_DAYS, DEFAULT_OFFER_GRACE_DAYS, DEFAULT_PRICE_CHANGE_NOTICE_DAYS, DEFAULT_TRIAL_DAYS, DERIVED_PLAN_FEATURES, DISTRICTS, DataExportScope, DataExportStatus, ENFORCED_PLAN_FEATURES, EXPENSE_CATEGORY_LABELS_FA, ErasureRequestStatus, ExpenseCategory, FEATURE_ANNOUNCEMENTS, FEATURE_ANNOUNCE_RULE_KEY, FITZPATRICK_LABELS_FA, FinancialPeriodType, FitzpatrickType, GENDER_LABELS_FA, GEO_STATS, GUIDANCE_COHORTS, GUIDANCE_EVENT_TYPES, GUIDANCE_INSTANCE_PATTERN, GUIDANCE_KEY_PATTERN, GUIDANCE_KINDS, GUIDANCE_MAX_CLIENT_ITEMS_PER_STAFF, GUIDANCE_MAX_EVENTS_PER_REQUEST, GUIDANCE_PERSONAS, GUIDANCE_SOURCES, GUIDANCE_STATUSES, GUIDANCE_STEP_REASONS, GUIDANCE_SURFACES, GUIDANCE_VIEWPORTS, GUIDE_ACTION_CTA_KINDS, GUIDE_CATEGORIES, GUIDE_CATEGORY_ICONS, GUIDE_CATEGORY_LABELS_FA, GUIDE_CTA_KINDS, GUIDE_CTA_ROUTES, GUIDE_CTA_ROUTE_PLAN_FEATURE, GUIDE_CTA_ROUTE_REQUIRES, GUIDE_EXPENSE_CATEGORIES, GUIDE_INLINE_SLOTS, GUIDE_QUIET_REASONS, GUIDE_RULE_CODE_RESERVATIONS, GUIDE_RULE_KEYS, GUIDE_TIP_ACTIONS, GUIDE_TIP_SURFACES, GUIDE_TIP_TONES, Gender, GenerationStatus, HIJRI_MONTH_START_IR, INVENTORY_MOVEMENT_TYPE_LABELS_FA, INVOICE_STATUS_LABELS_FA, IRAN_BANK_CODES, IRAN_MOBILE_OPERATORS, InventoryMovementType, InvoiceStatus, JALALI_MONTH_NAMES, JALALI_SEASON_NAMES, LAUNCH_PRICING, LAUNCH_RATE_EFFECTIVE_FROM, LEAD_ACTIVITY_TYPE_LABELS_FA, LEAD_STATUS_LABELS_FA, LEAF_BLOCK_KINDS, LOT_STATUS_LABELS_FA, LOYALTY_ENTRY_TYPE_LABELS_FA, LeadActivityType, LeadStatus, LotStatus, LoyaltyEntryType, MAJOR_CITIES, MATERIAL_CATEGORY_LABELS_FA, MATERIAL_UNIT_LABELS_FA, MAX_JALALI_YEAR, MAX_SAFE_BIGINT_RIAL, MIN_JALALI_YEAR, MODERATION_STATUS_LABELS_FA, MaterialCategory, MaterialUnit, ModerationStatus, NEWCOMER_WINDOW_DAYS, NOTE_VISIBILITY_LABELS_FA, NOTIFICATION_CHANNEL_LABELS_FA, NOTIFICATION_STATUS_LABELS_FA, NoteVisibility, NotificationChannel, NotificationStatus, OCCASIONS, OCCASION_BY_KEY, OFFER_AUDIENCES, OFFER_COPY_TOKENS, OFFER_DISCOUNT_STEP_RIALS, OFFER_DURATIONS, OFFER_SEGMENTS, OtpPurpose, PATIENT_PACKAGE_STATUS_LABELS_FA, PAYMENT_METHOD_LABELS_FA, PAYMENT_STATUS_LABELS_FA, PAYROLL_STATUS_LABELS_FA, PERMISSION_GROUPS, PERMISSION_META, PERSIAN_WEEKDAY_NAMES, PERSIAN_WEEKDAY_SHORT, PHOTO_ANGLE_LABELS_FA, PHOTO_KIND_LABELS_FA, PLAN_FEATURE_KEYS, PLAN_FEATURE_LABELS_FA, PLAN_FEATURE_LINK_REASON, PLAN_INTERVAL_LABELS_FA, PLAN_LINK_MODELS, PLAN_LINK_REASONS, PLAN_LINK_TERMS, POOL_BATCH_NO, POOL_BATCH_RE, PROACTIVE_MIN_GAP_MS, PROACTIVE_PER_DAY, PROCEDURES_BY_CATEGORY, PROCEDURE_CATALOG, PROCEDURE_CATEGORY_LABELS_FA, PROCEDURE_KEYS, PROVINCES, PatientPackageStatus, PaymentMethod, PaymentProvider, PaymentPurpose, PaymentStatus, PayrollStatus, Permission, PhotoAccessAction, PhotoAngle, PhotoKind, PlanInterval, PlanVisibility, ProcedureCategory, RAAS_UPSELL_DEFAULTS, REFERRAL_STATUS_LABELS_FA, REMINDER_KIND_LABELS_FA, REMINDER_STATUS_LABELS_FA, REPORTING_PERIODS, REPORTING_PERIOD_LABELS, RESETTABLE_KINDS, REVENUE_SHARE_BASIS_LABELS_FA, RIALS_PER_TOMAN, ReferralStatus, ReminderKind, ReminderStatus, RevenueShareBasis, SESSION_ORIGIN_LABELS_FA, SESSION_STATUS_LABELS_FA, STAFF_INVITE_STATUS_LABELS_FA, STAFF_NOTIFICATION_TYPE_LABELS_FA, SUBSCRIPTION_PATH, SUBSCRIPTION_STATUS_LABELS_FA, SURVEY_STATUS_LABELS_FA, SYSTEM_ROLE_LIST, SYSTEM_ROLE_PRESETS, SessionOrigin, SessionStatus, SitemapType, StaffInviteStatus, StaffNotificationType, SubscriptionStatus, SurveyStatus, SystemRoleKey, TATWEEL, TEHRAN_TIME_ZONE, TERM_PRICE_STEP_RIALS, THOUSANDS_SEPARATOR, TIP_BACKOFF, TIP_DISMISSALS_TO_NOT_RELEVANT, TIP_INSTANCE_PATTERN, TIP_NEW_PER_DAY, UI_TERMS, UNIT_CONSUMING_PROCEDURES, USER_STATUS_LABELS_FA, UserStatus, VERIFICATION_DOCUMENT_KIND_LABELS_FA, VERIFICATION_STATUS_LABELS_FA, VerificationDocumentKind, VerificationStatus, WEBHOOK_EVENTS_IMPLIED_PERMISSIONS, WEBHOOK_EVENT_LABELS_FA, WEBHOOK_EVENT_TYPES, WEEKDAY_LABELS_FA, WEEKDAY_ORDER, WELCOME_MAX_SNOOZES, WELCOME_SNOOZE_DAYS_DEFAULT, WaitListStatus, WalletTxType, Weekday, ZWJ, ZWNJ, addDaysTehran, addJalaliDays, addJalaliMonths, aiImportField, aiImportPagesOf, analyticsEventName, appliesAtRenewal, applyBasisPoints, applyFixedDiscount, applyGuidanceEvent, applyPercentDiscount, applyProfileEvent, articleReadingMinutes, articleWordCount, basisPointsToPercent, bestOffer, blackoutsBetween, blockSignature, blogPostQueue, campaignWindow, canSpendProactive, childDistrictsOf, citiesOfProvince, cityGeoPoint, clampRial, clinicApiKeyDisplayPrefix, comparePlansForVolume, containsPersian, countWordsFa, cyclesAfterFirstPayment, deriveParentDistrictSlug, discountAmount, districtsOfCity, effectivePlanFeatures, expandPermissions, findCityByName, findDistrictByName, formatGroupedNumber, formatIban, formatJalali, formatJalaliDate, formatJalaliDateTime, formatJalaliFull, formatJalaliLong, formatMobile, formatPostalCode, formatRelativeFa, formatRial, formatTehranTime, formatToman, formatTomanRange, formatTomanShort, fromJalali, geoBreadcrumb, getCity, getDistrict, getProcedure, getProcedureBySlug, getProvince, grantExpiresAt, gregorianToHijri, gregorianToJalali, guidanceCohortFor, guidanceDayKey, guidanceEventIsLogged, hasAllPermissions, hasAnyPermission, hasExtras, hasNonAsciiDigits, hasPermission, hasPriceDiscount, hijriMonthLength, hijriMonthStart, hijriOverrideKey, hijriToGregorianDay, ibanBankCode, ibanBankName, impliedPermissionsFor, initialGuidanceProfile, initialsFa, inlineLinkHrefs, isAiImportKind, isBillingTerm, isClinicApiScope, isEnforcedPlanFeature, isHelpOffer, isJalaliLeapYear, isPermission, isPersianText, isPlanFeatureKey, isPoolBatchNo, isRateIncrease, isSameTehranDay, isScopedToOwnRecords, isSendDay, isSiteRelativeHref, isSystemRoleKey, isValidBankCard, isValidEmail, isValidIranIban, isValidJalaliDate, isValidLandline, isValidMedicalLicenseNo, isValidMobile, isValidNationalId, isValidPostalCode, isValidSlug, isWebhookEventType, isWholeToman, isoAddDays, isoDaysBetween, isoMax, isoMin, isoOf, isoToHijri, isoToJalali, isoToJalaliKey, isoToTehranNoon, isoToUtcMs, jalaliAge, jalaliDayRange, jalaliDaysBetween, jalaliMonthKey, jalaliMonthKeyOf, jalaliMonthLabelFa, jalaliMonthLength, jalaliMonthName, jalaliMonthRange, jalaliMonthRangeOfKey, jalaliQuarterRange, jalaliSeasonName, jalaliToGregorian, jalaliToIso, jalaliWeekRange, jalaliWeekday, jalaliWeekdayName, jalaliYearRange, joinFa, joinSlugPath, lastJalaliMonths, launchPlan, launchPublicPricing, maskBankCard, maskMobile, maskNationalId, mobileOperator, mobileOperatorName, monthsFreeInTerm, mulDiv, nextJalaliMonthKey, nextOccasion, nextOkDay, nextRetreatmentDate, nextSendDay, normaliseOfferCode, normalizeForSearch, normalizeIban, normalizeLandline, normalizeMedicalLicenseNo, normalizeMobile, normalizeNationalId, normalizeOtpCode, normalizePersian, normalizePostalCode, normalizeZwnj, nowruzIso, occasionsBetween, offerCopyProblem, offerDiscountRials, offerEligibility, offerValueRials, parseAmountPhrase, parseAudienceRule, parseBlocks, parseClinicApiKey, parseGuidePrefill, parseInlineLinks, parseJalaliDateKey, parseJalaliMonthKey, parseMoneyInput, parsePublicOffer, parsePublicPricing, parseTipItemKey, parseTomanOrZero, perMonthEquivalentRials, percentChange, percentOf, percentToBasisPoints, permissionGroupOf, permissionLabel, pickAiImportUpgrade, planFeatureForLinkReason, planLinkReasonFor, pluralizeFa, prevOkDay, prevSendDay, previousJalaliMonthKey, proceduresByCategory, promoStatus, provinceOfCity, publicPricingProblem, publicTermPrice, raasBillRials, raasCapPercentBp, raasCapThreshold, raasChargeKindAt, raasForecast, raasMarginalRials, raasProrate, raasStatementBreakdown, raasTrialOffered, raasUpsellDecision, raasUsageRials, rateAt, readPlanLink, readingTimeFa, readingTimeLabelFa, reminderRulesFor, removeUnknownProcedureBlocks, removeZwnj, reportingPeriodRange, resolvePermissions, revenueShareBp, rialToToman, rialToTomanExact, rials, roundRialTo, roundTomanTo, sanitizeClinicApiScopes, sanitizePermissions, searchProcedures, segmentMatches, segmentStatuses, sendBy, signupGrantExpiresAt, slugifyFa, slugifyLatin, sortPermissions, splitByPercent, splitOfferCopy, stripBlocks, stripInlineLinks, subscriptionPlanPath, sumRials, tehranDayDelta, tehranIsoDay, termDiscountBp, termPriceFromPolicy, termPriceRials, termSavingRials, termsOf, tipItemKey, toE164Mobile, toE164Phone, toEnglishDigits, toIntegerAmount, toJalali, toJalaliDateKey, toPersianDigits, toPersianPunctuation, toSelectOptions, todayJalali, tomanToRial, topCities, truncateFa, validateBlocks, wholeRials, zoneOffsetMinutes };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map